import React from 'react'
import ReactDOM from 'react-dom/client'

// Supabase stubs - connects on Netlify, demo mode on file://
const SUPABASE_URL      = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_URL) || "https://fkwqzmnqflmkchiszxub.supabase.co";
const SUPABASE_ANON_KEY = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_ANON_KEY) || "sb_publishable_FjRyUh1xhKg1uDTHIZ-0bg_XNjTXpJ_";

const sbSignIn = (email, password) => {
  return fetch(SUPABASE_URL + "/auth/v1/token?grant_type=password", {
    method: "POST",
    headers: { "apikey": SUPABASE_ANON_KEY, "Content-Type": "application/json" },
    body: JSON.stringify({ email, password })
  }).then(r => r.json()).then(d => {
    if (d.access_token) {
      window._sbToken = d.access_token;
      try { localStorage.setItem("sb_token", d.access_token); } catch(e) {}
    }
    return { data: d.user ? { user: d.user } : null, error: d.error ? { message: d.error_description || d.msg || "Auth error" } : null };
  }).catch(() => ({ data: null, error: { message: "demo mode" } }));
};

const sbSignOut = () => {
  window._sbToken = null;
  try { localStorage.removeItem("sb_token"); } catch(e) {}
  return Promise.resolve({});
};

const sbGetSession = () => {
  try {
    const token = localStorage.getItem("sb_token");
    const user  = JSON.parse(localStorage.getItem("sb_user") || "null");
    return Promise.resolve({ data: { session: token ? { access_token: token, user } : null } });
  } catch(e) {}
  return Promise.resolve({ data: { session: null } });
};

const auditLog = () => Promise.resolve({});
const subscribeWaitingRoom = () => ({ unsubscribe: () => {} });
const db = {
  patients: () => ({ select: () => ({ order: () => Promise.resolve({ data: [], error: null }) }) }),
  cases:    () => ({ select: () => ({ order: () => Promise.resolve({ data: [], error: null }) }) }),
  audit:    () => ({ insert: () => Promise.resolve({}) }),
};

// Supabase object stub — needed for supabase.auth.onAuthStateChange in App()
const supabase = {
  auth: {
    onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
    signOut: sbSignOut,
  },
};

const { useState, useEffect, useRef, useCallback, useMemo } = React;


// --- DESIGN TOKENS -------------------------------------------------------------
const C = {
  // Backgrounds
  bg:         "#0D0F14",
  bgCard:     "#13161E",
  bgElevated: "#1A1E2A",
  bgHover:    "#1F2435",
  // Borders
  border:     "#252A3A",
  borderLight:"#2E3548",
  // Electric Blue -- primary accent
  blue:       "#2D7DD2",
  blueBright: "#3D8FE8",
  blueGlow:   "#2D7DD230",
  bluePale:   "#1A3A5C",
  // Text
  textPrimary:"#EDF0F7",
  textSecond: "#8A93B0",
  textMuted:  "#525A75",
  textAccent: "#3D8FE8",
  // Status
  green:      "#22C97A",
  greenBg:    "#0D2B1E",
  amber:      "#F0A832",
  amberBg:    "#2B1E0A",
  red:        "#E84040",
  redBg:      "#2B0D0D",
  purple:     "#9B6DFF",
  purpleBg:   "#1A1030",
  // Whites
  white:      "#FFFFFF",
};

// Expose as globals for other modules
const STATUS = {
  "Active":           { bg: C.greenBg,  text: C.green,  dot: C.green  },
  "In Treatment":     { bg: C.bluePale, text: C.blueBright, dot: C.blueBright },
  "MMI Reached":      { bg: C.purpleBg, text: C.purple, dot: C.purple },
  "Closed":           { bg: "#1A1A2A",  text: C.textMuted, dot: C.textMuted },
  "Pending Review":   { bg: C.amberBg,  text: C.amber,  dot: C.amber  },
  "EMC Pending":      { bg: C.amberBg,  text: C.amber,  dot: C.amber  },
  "Peer Review":      { bg: C.redBg,    text: C.red,    dot: C.red    },
  "New":              { bg: C.bluePale, text: C.blueBright, dot: C.blueBright },
};

// --- MOCK DATA -----------------------------------------------------------------
const CASES = [
  // Fields: id, firstName, lastName, patient, phone, email, dob, sex, zip, accident, insurance, policy,
  //         status, visits, emc, attorney, physician, diagnosis, icd10, cpt, created, track, mrn, mrnType,
  //         pocWeeks, pocStart, pocEnd, mmiDate, outcome, batchStatus, amtBilled, amtPaid, denialReason
  { id:"PIP-2026-0089", firstName:"Naomi Amanda", lastName:"Ramos Lopez", patient:"Naomi A. Ramos Lopez", email:"", phone:"", dob:"2000-02-14", sex:"F", zip:"33012", accident:"Apr 02, 2026", insurance:"Direct General Insurance Company", policy:"", status:"Active", visits:1, emc:"Pending", attorney:"Unrepresented", physician:"Dr. A. Falcon, MD", diagnosis:"Cervical sprain · Lumbar strain · Headache", icd10:["S13.4XXA","S33.5XXA","M54.2","G44.1"], cpt:["99203","97010","97110","97140","97530"], created:"Apr 03, 2026", track:"PIP Only", mrn:"MRN-20260404-001", mrnType:"ss4", pocWeeks:12, pocStart:"Apr 03, 2026", pocEnd:null, mmiDate:null, outcome:"PENDING", batchStatus:"Not Billed", amtBilled:0, amtPaid:0, denialReason:null, address:"187 W 50th St", city:"Hialeah", state:"FL", accidentType:"Rear-End MVA", moi:"Rear-End MVA", position:"Driver", vehicleAtImpact:"Stopped", seatbelt:"Yes", airbag:"No", drivable:"No", loc:"No", chiefComplaints:["Neck Pain","Lower Back Pain","Headache"], painScore:7, painOnset:"Immediate", allergies:["NKDA"], medications:[], pmh:["None"], erVisit:false },
  { id:"PIP-2026-0088", firstName:"Carlos", lastName:"Reyes", patient:"Carlos M. Reyes", email:"carlos.reyes@email.com", phone:"305-441-8821", dob:"1985-04-12", sex:"M", zip:"34221", address:"4521 SW 8th St", city:"Miami", state:"FL", accident:"Mar 01, 2026", insurance:"State Farm", policy:"SF-4421-X", status:"Active", visits:12, emc:"Certified", attorney:"Unrepresented", physician:"Dr. A. Falcon, MD", diagnosis:"Cervical sprain · Lumbar strain", icd10:["S13.4XXA","S33.5XXA","M54.2","M54.5"], cpt:["99213","97010","97110","97140","97530"], created:"Mar 02, 2026", track:"PIP Only", mrn:"MRN-4421-CMR", mrnType:"ss4", pocWeeks:12, pocStart:"Mar 02, 2026", pocEnd:null, mmiDate:null, outcome:"PENDING", batchStatus:"Batch 1", amtBilled:3200, amtPaid:2560, denialReason:null, moi:"Rear-End MVA", position:"Driver", vehicleAtImpact:"Stopped", seatbelt:"Yes", airbag:"Yes", drivable:"Yes", loc:"No", chiefComplaints:["Neck Pain","Lower Back Pain"], painScore:7, painOnset:"Immediate", allergies:["NKDA"], medications:[], pmh:["None"], erVisit:false },
  { id:"PIP-2026-0087", firstName:"Diana", lastName:"Whitmore", patient:"Diana L. Whitmore", email:"d.whitmore@gmail.com", phone:"786-334-5590", dob:"1978-09-30", sex:"F", zip:"34208", address:"1200 Brickell Ave Apt 3B", city:"Miami", state:"FL", accident:"Feb 26, 2026", insurance:"GEICO", policy:"GC-8812-Z", status:"Active", visits:8, emc:"Pending", attorney:"Unrepresented", physician:"Dr. A. Falcon, MD", diagnosis:"Thoracic contusion · Headache", icd10:["S23.3XXA","G44.1","M54.6"], cpt:["99214","97010","97140","97035"], created:"Feb 27, 2026", track:"PIP Only", mrn:"MRN-8812-DLW", mrnType:"ss4", pocWeeks:10, pocStart:"Feb 27, 2026", pocEnd:null, mmiDate:null, outcome:"PENDING", batchStatus:"Batch 1", amtBilled:2100, amtPaid:0, denialReason:null, moi:"Side Impact", position:"Front Passenger", vehicleAtImpact:"Moving Slowly", seatbelt:"Yes", airbag:"Yes", drivable:"No", loc:"No", chiefComplaints:["Upper Back Pain","Headache","Dizziness"], painScore:8, painOnset:"Immediate", allergies:["Penicillin"], medications:["Ibuprofen 800mg"], pmh:["HTN"], erVisit:true },
  { id:"PIP-2026-0086", firstName:"Marcus", lastName:"Johnson", patient:"Marcus T. Johnson", email:"marcus.j@yahoo.com", phone:"954-220-7743", dob:"1992-01-15", sex:"M", zip:"34222", address:"7890 NW 12th Ave", city:"Fort Lauderdale", state:"FL", accident:"Feb 20, 2026", insurance:"Allstate", policy:"AL-5591-B", status:"In Treatment", visits:18, emc:"Certified", attorney:"Unrepresented", physician:"Dr. A. Falcon, MD", diagnosis:"Cervical disc herniation C4-C5", icd10:["M50.12","M54.2","M54.12"], cpt:["99214","97010","97110","97012","97530"], created:"Feb 21, 2026", track:"PIP Only", mrn:"MRN-5591-MTJ", mrnType:"ss4", pocWeeks:16, pocStart:"Feb 21, 2026", pocEnd:null, mmiDate:null, outcome:"PENDING", batchStatus:"Batch 2", amtBilled:4800, amtPaid:3840, denialReason:null, moi:"T-Bone", position:"Driver", vehicleAtImpact:"Moving at Speed", seatbelt:"Seatbelt + Airbag", airbag:"Yes", drivable:"No", loc:"Yes", chiefComplaints:["Neck Pain","Lower Back Pain","Numbness/Tingling","Left Shoulder Pain"], painScore:9, painOnset:"Immediate", allergies:["NKDA"], medications:[], pmh:["None"], erVisit:true },
  { id:"PIP-2026-0085", firstName:"Sofia", lastName:"Mendez", patient:"Sofia A. Mendez", email:"sofia.mendez@hotmail.com", phone:"305-778-0034", dob:"1969-07-22", sex:"F", zip:"34219", address:"345 Palm Beach Blvd", city:"Doral", state:"FL", accident:"Feb 14, 2026", insurance:"Progressive", policy:"PR-3341-K", status:"MMI Reached", visits:24, emc:"Certified", attorney:"Unrepresented", physician:"Dr. A. Falcon, MD", diagnosis:"Lumbar radiculopathy L4-L5", icd10:["M54.16","M54.5","S33.5XXA","M62.830"], cpt:["99213","97010","97110","97140","97530"], created:"Feb 15, 2026", track:"PIP Only", mrn:"MRN-3341-SAM", mrnType:"ss4", pocWeeks:12, pocStart:"Feb 15, 2026", pocEnd:"May 10, 2026", mmiDate:"May 10, 2026", outcome:"IMPROVED", batchStatus:"Batch 3", amtBilled:6400, amtPaid:5120, denialReason:null, moi:"Rear-End MVA", position:"Driver", vehicleAtImpact:"Stopped", seatbelt:"Yes", airbag:"No", drivable:"Yes", loc:"No", chiefComplaints:["Lower Back Pain","Right Hip Pain","Numbness/Tingling"], painScore:6, painOnset:"Gradual", allergies:["NSAIDs"], medications:["Gabapentin 300mg","Flexeril 10mg"], pmh:["Diabetes","Arthritis"], erVisit:false },
  { id:"PIP-2026-0084", firstName:"James", lastName:"Okafor", patient:"James R. Okafor", email:"j.okafor@gmail.com", phone:"786-901-2256", dob:"1981-11-08", sex:"M", zip:"34205", address:"2100 Collins Ave", city:"Miami Beach", state:"FL", accident:"Feb 08, 2026", insurance:"State Farm", policy:"SF-9901-M", status:"Active", visits:6, emc:"Certified", attorney:"Unrepresented", physician:"Dr. A. Falcon, MD", diagnosis:"Lumbar sprain · Knee contusion", icd10:["S33.5XXA","M54.5","M25.561"], cpt:["99213","97010","97110"], created:"Feb 09, 2026", track:"PIP Only", mrn:"MRN-9901-JRO", mrnType:"ss4", pocWeeks:8, pocStart:"Feb 09, 2026", pocEnd:null, mmiDate:null, outcome:"PENDING", batchStatus:"Batch 1", amtBilled:1600, amtPaid:0, denialReason:null, moi:"Head-On", position:"Driver", vehicleAtImpact:"Moving at Speed", seatbelt:"Yes", airbag:"Yes", drivable:"No", loc:"Yes", chiefComplaints:["Lower Back Pain","Right Knee Pain","Headache"], painScore:8, painOnset:"Immediate", allergies:["NKDA"], medications:[], pmh:["None"], erVisit:true },
  { id:"PIP-2026-0083", firstName:"Patricia", lastName:"Clarke", patient:"Patricia N. Clarke", email:"p.clarke@icloud.com", phone:"305-554-1187", dob:"1975-03-19", sex:"F", zip:"34210", address:"890 Sunset Dr", city:"Coral Gables", state:"FL", accident:"Jan 28, 2026", insurance:"USAA", policy:"US-2210-Q", status:"Closed", visits:30, emc:"Certified", attorney:"Unrepresented", physician:"Dr. A. Falcon, MD", diagnosis:"Cervical + lumbar sprain", icd10:["S13.4XXA","S33.5XXA","M54.2","M54.5"], cpt:["99213","97010","97110","97530","97140"], created:"Jan 29, 2026", track:"PIP Only", mrn:"MRN-2210-PNC", mrnType:"ss4", pocWeeks:14, pocStart:"Jan 29, 2026", pocEnd:"Apr 26, 2026", mmiDate:"Apr 20, 2026", outcome:"RESOLVED", batchStatus:"Paid", amtBilled:8000, amtPaid:8000, denialReason:null, moi:"Rear-End MVA", position:"Driver", vehicleAtImpact:"Stopped", seatbelt:"Yes", airbag:"No", drivable:"Yes", loc:"No", chiefComplaints:["Neck Pain","Lower Back Pain"], painScore:5, painOnset:"Immediate", allergies:["NKDA"], medications:[], pmh:["Thyroid"], erVisit:false },
  { id:"PIP-2026-0082", firstName:"Anthony", lastName:"Russo", patient:"Anthony K. Russo", email:"anthony.russo@gmail.com", phone:"954-887-3341", dob:"1988-06-04", sex:"M", zip:"34201", address:"5600 NW 36th St", city:"Doral", state:"FL", accident:"Jan 20, 2026", insurance:"GEICO", policy:"GC-4471-T", status:"Closed", visits:22, emc:"Certified", attorney:"Unrepresented", physician:"Dr. A. Falcon, MD", diagnosis:"Soft tissue cervical strain", icd10:["S16.1XXA","M54.2","M62.830"], cpt:["99213","97010","97110","97530"], created:"Jan 21, 2026", track:"PIP Only", mrn:"MRN-4471-AKR", mrnType:"ss4", pocWeeks:10, pocStart:"Jan 21, 2026", pocEnd:"Mar 31, 2026", mmiDate:"Mar 28, 2026", outcome:"RESOLVED", batchStatus:"Paid", amtBilled:5800, amtPaid:5800, denialReason:null, moi:"Side Impact", position:"Driver", vehicleAtImpact:"Moving Slowly", seatbelt:"Seatbelt + Airbag", airbag:"Yes", drivable:"No", loc:"No", chiefComplaints:["Neck Pain","Headache","Dizziness"], painScore:6, painOnset:"Immediate", allergies:["Sulfa"], medications:["Naproxen 500mg"], pmh:["Asthma"], erVisit:false },
  { id:"PIP-2026-0081", firstName:"Elena", lastName:"Castillo", patient:"Elena V. Castillo", email:"elena.v.castillo@email.com", phone:"786-223-9910", dob:"1995-12-27", sex:"F", zip:"34215", address:"1450 Coral Way", city:"Miami", state:"FL", accident:"Jan 15, 2026", insurance:"Travelers", policy:"TR-8871-D", status:"In Treatment", visits:15, emc:"Certified", attorney:"Unrepresented", physician:"Dr. A. Falcon, MD", diagnosis:"Cervical sprain · Shoulder strain", icd10:["S13.4XXA","M54.2","M25.512","M79.1"], cpt:["99213","97010","97110","97530"], created:"Jan 16, 2026", track:"PIP Only", mrn:"MRN-8871-EVC", mrnType:"ss4", pocWeeks:12, pocStart:"Jan 16, 2026", pocEnd:null, mmiDate:null, outcome:"PENDING", batchStatus:"Batch 2", amtBilled:4000, amtPaid:3200, denialReason:null, moi:"Rollover", position:"Rear Passenger", vehicleAtImpact:"Moving at Speed", seatbelt:"Yes", airbag:"No", drivable:"No", loc:"Yes", chiefComplaints:["Neck Pain","Left Shoulder Pain","Lower Back Pain","Numbness/Tingling"], painScore:8, painOnset:"Immediate", allergies:["NKDA"], medications:[], pmh:["Anxiety"], erVisit:true },
];

const ATTORNEYS = [
  { name: "Rodriguez & Associates", cases: 14, phone: "(305) 555-0192", email: "intake@rodriguezlaw.com" },
  { name: "Smith Law Group", cases: 9, phone: "(954) 555-0341", email: "cases@smithlawfl.com" },
  { name: "Torres & Partners", cases: 6, phone: "(813) 555-0887", email: "pi@torrespartners.com" },
  { name: "Falcon Legal PA", cases: 4, phone: "(941) 555-0223", email: "intake@falconlegal.com" },
];


// ─── SPECIALIST REFERRAL CONSULTATION REPORTS ──────────────────────────────────
// Each provider carries referralCases: [{ caseId, referralDate, reason, findings, recommendation, status }]
// Linked to CASES by caseId. CaseDetail opens via onOpenCase prop.
const CONSULTATION_STATUS = {
  PENDING:    { label:'Pending',     color:'#FFA726' },
  COMPLETED:  { label:'Completed',   color:'#4CAF50' },
  SCHEDULED:  { label:'Scheduled',   color:'#64B5F6' },
  CANCELLED:  { label:'Cancelled',   color:'#EF5350' },
};

const SPECIALISTS = [
  {
    specialty: 'Orthopedic Surgery',
    icon: '🦴',
    color: '#1565C0',
    colorBg: 'rgba(21,101,192,0.10)',
    colorBorder: 'rgba(21,101,192,0.25)',
    providers: [
      { name: 'Dr. Carlos Méndez, MD', practice: 'South Florida Orthopedic Center', phone: '(305) 555-0411', fax: '(305) 555-0412', email: 'referrals@sforthopedic.com', address: '1250 SW 27th Ave, Miami, FL 33135', accepts: 'LOP · PIP · BI', cases: 11, turnaround: '5–7 business days', notes: 'Spine & trauma specialist · Accepts uninsured PI cases with LOP',
        referralCases: [
          { caseId:'PIP-2026-0088', referralDate:'Mar 10, 2026', reason:'Cervical sprain evaluation · Rule out disc injury C4-C6', findings:'MRI C-spine ordered. Mild disc bulge at C5-C6 noted. No myelopathy.', recommendation:'Continue conservative care × 6 weeks. Re-evaluate for surgical candidacy if no improvement.', status:'COMPLETED' },
          { caseId:'PIP-2026-0086', referralDate:'Mar 05, 2026', reason:'Cervical disc herniation C4-C5 · Surgical evaluation', findings:'Confirmed herniation on MRI. Moderate foraminal stenosis. Radicular symptoms L upper extremity.', recommendation:'ACDF C4-C5 recommended. Patient electing conservative management. Will re-evaluate in 90 days.', status:'COMPLETED' },
          { caseId:'PIP-2026-0081', referralDate:'Feb 28, 2026', reason:'Shoulder strain · Rule out rotator cuff tear', findings:'MRI shoulder: partial thickness supraspinatus tear. No full-thickness involvement.', recommendation:'Physical therapy × 8 weeks. Corticosteroid injection if no improvement. Surgical consult PRN.', status:'COMPLETED' },
        ]
      },
      { name: 'Dr. Ana Restrepo, MD', practice: 'Coastal Orthopedics PA', phone: '(941) 555-0722', fax: '(941) 555-0723', email: 'intake@coastalortho.com', address: '8600 Sun City Ctr Blvd, Sun City, FL 33573', accepts: 'LOP · PIP', cases: 5, turnaround: '7–10 business days', notes: 'Total joint & spine · Sarasota / Ellenton corridor',
        referralCases: [
          { caseId:'PIP-2026-0085', referralDate:'Mar 01, 2026', reason:'Lumbar radiculopathy L4-L5 · Pre-MMI evaluation', findings:'EMG confirms L4-L5 radiculopathy. Lumbar MRI: disc herniation L4-L5 with foraminal narrowing.', recommendation:'MMI reached. 12% whole person impairment per AMA Guides 6th Edition. No further surgical intervention.', status:'COMPLETED' },
          { caseId:'PIP-2026-0084', referralDate:'Feb 20, 2026', reason:'Lumbar sprain · Knee contusion evaluation', findings:'X-ray knee: no fracture. Lumbar MRI pending. Soft tissue injury pattern consistent with MVA.', recommendation:'Await MRI results. Continue PT. Orthopedic clearance in 4 weeks.', status:'SCHEDULED' },
        ]
      },
    ],
  },
  {
    specialty: 'Neurology',
    icon: '🧠',
    color: '#6A1B9A',
    colorBg: 'rgba(106,27,154,0.10)',
    colorBorder: 'rgba(106,27,154,0.25)',
    providers: [
      { name: 'Dr. Rafael Soto, MD', practice: 'NeuroFlorida Associates', phone: '(305) 555-0533', fax: '(305) 555-0534', email: 'referrals@neuroflorida.com', address: '3661 S Miami Ave, Miami, FL 33133', accepts: 'LOP · PIP · BI', cases: 8, turnaround: '5–7 business days', notes: 'Traumatic brain injury · Cervical myelopathy · EMG/NCV on-site',
        referralCases: [
          { caseId:'PIP-2026-0087', referralDate:'Mar 08, 2026', reason:'Post-traumatic headache · Thoracic contusion · TBI eval', findings:'Neurological exam: intact. Cognitive screen: mild processing delay. MRI brain: no acute pathology. EEG normal.', recommendation:'Post-concussion syndrome. Neurocognitive therapy. No driving restrictions. Re-evaluate 60 days.', status:'COMPLETED' },
          { caseId:'PIP-2026-0083', referralDate:'Feb 10, 2026', reason:'Cervical myelopathy evaluation · Closed case', findings:'NCV/EMG: bilateral median nerve slowing. Carpal tunnel syndrome superimposed. MRI C-spine: C5-C6 stenosis.', recommendation:'Neurosurgery referral for C5-C6. Wrist splinting. MMI at 90 days post-MVA.', status:'COMPLETED' },
        ]
      },
      { name: 'Dr. Priya Nair, MD', practice: 'Gulf Coast Neurology', phone: '(941) 555-0614', fax: '(941) 555-0615', email: 'intake@gulfcoastneuro.com', address: '1950 Ringling Blvd, Sarasota, FL 34236', accepts: 'LOP · PIP', cases: 4, turnaround: '7–10 business days', notes: 'Headache · Post-concussion · Nerve conduction studies',
        referralCases: [
          { caseId:'PIP-2026-0082', referralDate:'Feb 15, 2026', reason:'Persistent post-MVA headaches · Concussion evaluation', findings:'Headache diary reviewed. SCAT-5 score: 18/100. Vestibular ocular motor screening: abnormal. No focal deficits.', recommendation:'Vestibular PT. Amitriptyline 10mg QHS. Neurocognitive rest protocol × 3 weeks.', status:'COMPLETED' },
        ]
      },
    ],
  },
  {
    specialty: 'Pain Management',
    icon: '💉',
    color: '#B71C1C',
    colorBg: 'rgba(183,28,28,0.10)',
    colorBorder: 'rgba(183,28,28,0.25)',
    providers: [
      { name: 'Dr. James Kim, MD', practice: 'Florida Pain & Spine Institute', phone: '(305) 555-0877', fax: '(305) 555-0878', email: 'referrals@fpsi.com', address: '7800 SW 87th Ave, Miami, FL 33173', accepts: 'LOP · PIP · BI · Cash', cases: 16, turnaround: '3–5 business days', notes: 'Epidural · Facet blocks · Radiofrequency · Spinal cord stim · Highest referral volume',
        referralCases: [
          { caseId:'PIP-2026-0088', referralDate:'Mar 12, 2026', reason:'Cervical pain management · ESI evaluation', findings:'Cervical paraspinal tenderness. ROM limited. C5-C6 medial branch tenderness confirmed. VAS 7/10.', recommendation:'C5-C6 ESI scheduled. Home TENS unit. Gabapentin 300mg TID added to regimen.', status:'SCHEDULED' },
          { caseId:'PIP-2026-0086', referralDate:'Mar 02, 2026', reason:'Disc herniation C4-C5 · Pain management co-management', findings:'VAS 8/10 cervical. Radicular pain R arm. C4-C5 transforaminal ESI performed. Good initial response.', recommendation:'2nd ESI in 2 weeks. Continue PT. Referral to neurosurgery if pain recurs after 3rd injection.', status:'COMPLETED' },
          { caseId:'PIP-2026-0085', referralDate:'Feb 18, 2026', reason:'Lumbar radiculopathy · Pre-MMI pain management', findings:'L4-L5 transforaminal ESI × 2 performed. 60% pain reduction. VAS 4/10. ROM improved.', recommendation:'MMI declared. Residual 8% whole body impairment. Maintenance: PRN analgesics only.', status:'COMPLETED' },
        ]
      },
      { name: 'Dr. Maria Villanueva, MD', practice: 'Suncoast Pain Management', phone: '(941) 555-0341', fax: '(941) 555-0342', email: 'intake@suncoastpain.com', address: '1819 Main St, Sarasota, FL 34236', accepts: 'LOP · PIP', cases: 9, turnaround: '5–7 business days', notes: 'Trigger point · Ketamine infusion · CRPS specialist',
        referralCases: [
          { caseId:'PIP-2026-0084', referralDate:'Feb 22, 2026', reason:'Lumbar sprain · Knee contusion pain management', findings:'Trigger point injections × 4 sites: bilateral lumbar paraspinals + bilateral piriformis. Good tolerance.', recommendation:'Follow-up 3 weeks. TENS unit prescribed. Topical diclofenac 1% gel for knee.', status:'COMPLETED' },
          { caseId:'PIP-2026-0081', referralDate:'Feb 20, 2026', reason:'Shoulder pain co-management', findings:'Subacromial corticosteroid injection performed R shoulder. Immediate 40% pain reduction reported.', recommendation:'Continue PT. Repeat injection in 6 weeks PRN. Avoid overhead activities.', status:'COMPLETED' },
        ]
      },
    ],
  },
  {
    specialty: 'Chiropractic',
    icon: '🦾',
    color: '#2E7D32',
    colorBg: 'rgba(46,125,50,0.10)',
    colorBorder: 'rgba(46,125,50,0.25)',
    providers: [
      { name: 'Dr. Tony Alvarez, DC', practice: 'Doral Chiropractic & Rehab', phone: '(305) 555-0199', fax: '(305) 555-0200', email: 'intake@doralchiro.com', address: '8181 NW 36 St, Doral, FL 33166', accepts: 'PIP · LOP', cases: 22, turnaround: '2–3 business days', notes: 'Adjacent to clinic · Co-management accepted · Decompression on-site',
        referralCases: [
          { caseId:'PIP-2026-0088', referralDate:'Mar 04, 2026', reason:'Cervical / lumbar co-management · Chiropractic adjunctive care', findings:'Cervical subluxation C3-C4, C5-C6. Lumbar L3-L4. Chiropractic manipulation × 3 sessions. Good response.', recommendation:'Continue 3x/week × 4 weeks. Decompression therapy initiated for lumbar.', status:'COMPLETED' },
          { caseId:'PIP-2026-0083', referralDate:'Feb 02, 2026', reason:'Cervical and lumbar sprain · Co-treatment closed case', findings:'12 chiropractic sessions completed. ROM normalized. Patient discharged with home exercise program.', recommendation:'Case closed. Full ROM achieved. PRN visits authorized up to 3 months post-discharge.', status:'COMPLETED' },
        ]
      },
      { name: 'Dr. Sarah Bloom, DC', practice: 'Ellenton Chiropractic Center', phone: '(941) 555-0503', fax: '(941) 555-0504', email: 'referrals@ellentonchiro.com', address: '6113 US-301, Ellenton, FL 34222', accepts: 'PIP · LOP', cases: 7, turnaround: '2–4 business days', notes: 'Sarasota corridor · Extremity adjusting · Active release technique',
        referralCases: [
          { caseId:'PIP-2026-0085', referralDate:'Feb 20, 2026', reason:'Lumbar radiculopathy · Chiropractic co-management pre-MMI', findings:'ART to lumbar paraspinals and piriformis. Flexion-distraction × 8 sessions. McKenzie protocol initiated.', recommendation:'Discharged at MMI. Home exercise program provided. PRN visits up to 6 months.', status:'COMPLETED' },
        ]
      },
    ],
  },
  {
    specialty: 'Home Health',
    icon: '🏠',
    color: '#E65100',
    colorBg: 'rgba(230,81,0,0.10)',
    colorBorder: 'rgba(230,81,0,0.25)',
    providers: [
      { name: 'Sunrise Home Health LLC', practice: 'Sunrise Home Health', phone: '(305) 555-0645', fax: '(305) 555-0646', email: 'orders@sunrisehh.com', address: '2121 Ponce de León Blvd, Coral Gables, FL 33134', accepts: 'PIP · LOP · Medicare', cases: 6, turnaround: '24–48 hrs', notes: 'Skilled nursing · PT/OT at home · FL AHCA licensed · PIP-familiar billing',
        referralCases: [
          { caseId:'PIP-2026-0085', referralDate:'Apr 20, 2026', reason:'Post-MMI home PT / OT · Functional restoration', findings:'ADL assessment: mild limitations in lifting > 10 lbs and prolonged ambulation. Home PT initiated 3x/week.', recommendation:'8 weeks home PT/OT. Reassess for outpatient transition. Case management coordinated.', status:'COMPLETED' },
        ]
      },
      { name: 'Gulf Coast Home Care', practice: 'Gulf Coast Home Care', phone: '(941) 555-0782', fax: '(941) 555-0783', email: 'referrals@gulfcoasthc.com', address: '4000 Clark Rd, Sarasota, FL 34233', accepts: 'PIP · LOP', cases: 3, turnaround: '24–72 hrs', notes: 'Sarasota / Manatee county · Wound care · IV therapy · Post-surgical nursing',
        referralCases: [
          { caseId:'PIP-2026-0083', referralDate:'Apr 05, 2026', reason:'Post-discharge home nursing · Wound care assessment', findings:'Patient ambulatory with mild gait deviation. Wound site clean, no signs of infection. BP 128/82.', recommendation:'Home nursing 1x/week × 4 weeks. Wound care supplies provided. No ER visit since discharge.', status:'COMPLETED' },
        ]
      },
    ],
  },
];

// --- SHARED UI -----------------------------------------------------------------

const StatusBadge = ({ status, small }) => {
  const s = STATUS[status] || { bg: C.bgElevated, text: C.textMuted, dot: C.textMuted };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: s.bg, color: s.text,
      padding: small ? "2px 8px" : "4px 12px",
      borderRadius: 20, fontSize: small ? 10 : 11,
      fontWeight: 700, whiteSpace: "nowrap",
      letterSpacing: "0.04em", textTransform: "uppercase",
      border: `1px solid ${s.dot}22`,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.dot, boxShadow: `0 0 4px ${s.dot}` }} />
      {status}
    </span>
  );
};

const Card = ({ children, style, onClick, glow }) => (
  <div onClick={onClick} style={{
    background: C.bgCard, border: `1px solid ${glow ? C.blue + "55" : C.border}`,
    borderRadius: 12, padding: 20,
    boxShadow: glow ? `0 0 20px ${C.blueGlow}` : "none",
    cursor: onClick ? "pointer" : "default",
    transition: "all 0.2s", ...style,
  }}>{children}</div>
);

const MetricCard = ({ label, value, sub, color, icon }) => (
  <Card style={{ flex: 1, minWidth: 140 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <div style={{ fontSize: 28, fontWeight: 800, color: color || C.blueBright, letterSpacing: "-0.02em", lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.textPrimary, marginTop: 6 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{sub}</div>}
      </div>
      {icon && <span style={{ fontSize: 22, opacity: 0.4 }}>{icon}</span>}
    </div>
  </Card>
);

const Btn = ({ children, onClick, variant = "primary", style, disabled }) => {
  const variants = {
    primary: { background: C.blue, color: C.white, border: "none", boxShadow: `0 0 16px ${C.blueGlow}` },
    bright:  { background: C.blueBright, color: C.white, border: "none", boxShadow: `0 0 20px ${C.blueGlow}` },
    outline: { background: "transparent", color: C.textSecond, border: `1px solid ${C.border}` },
    ghost:   { background: "transparent", color: C.textMuted, border: "none" },
    danger:  { background: C.redBg, color: C.red, border: `1px solid ${C.red}33` },
    success: { background: C.greenBg, color: C.green, border: `1px solid ${C.green}33` },
    amber:   { background: C.amberBg, color: C.amber, border: `1px solid ${C.amber}33` },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...variants[variant], padding: "9px 18px", borderRadius: 8,
      fontSize: 12, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
      display: "inline-flex", alignItems: "center", gap: 6,
      opacity: disabled ? 0.4 : 1, transition: "all 0.15s",
      letterSpacing: "0.04em", textTransform: "uppercase", ...style,
    }}>{children}</button>
  );
};

const Label = ({ children }) => (
  <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.14em", color: C.blue, textTransform: "uppercase", marginBottom: 6 }}>
    {children}
  </div>
);

const Input = ({ label, ...props }) => (
  <div>
    {label && <label style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 5 }}>{label}</label>}
    <input {...props} style={{ width: "100%", padding: "10px 13px", background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.textPrimary, boxSizing: "border-box", outline: "none", fontFamily: "inherit", ...props.style }} />
  </div>
);

const Select = ({ label, children, ...props }) => (
  <div>
    {label && <label style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 5 }}>{label}</label>}
    <select {...props} style={{ width: "100%", padding: "10px 13px", background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.textPrimary, boxSizing: "border-box", outline: "none", fontFamily: "inherit" }}>
      {children}
    </select>
  </div>
);

// --- SIDEBAR --------------------------------------------------------------------
const NAV_ALL = [
  { id: "dashboard",       icon: "⊞", label: "Dashboard",        roles: ["physician","staff","pt","admin"] },
  { id: "patients",        icon: "👤", label: "Patients",          roles: ["physician","staff","pt","admin"] },
  { id: "billing-codes",   icon: "🏷", label: "Billing Codes",     roles: ["physician","staff","admin"] },
  { id: "xray-orders",     icon: "🔬", label: "X-Ray Orders",     roles: ["physician","admin"] },
  { id: "business-assoc",  icon: "🏥", label: "Business Associates", roles: ["admin"] },
];

const Sidebar = ({ screen, onNav, onLogout, auth, onOpenPatient }) => {
  const [ptSearch, setPtSearch] = React.useState('');
  const [ptDropdown, setPtDropdown] = React.useState(false);

  const NAV = NAV_ALL.filter(item => item.roles.includes(auth || 'staff'));
  const handleNav = (id) => {
    if (id === 'admin' && auth !== 'admin') { onNav('admin-blocked'); return; }
    if (id === 'new-case' && !['staff','admin'].includes(auth)) return;
    onNav(id);
  };

  // Patient search — min 3 chars of last name, 3 chars of first name, or phone
  const ptResults = React.useMemo(() => {
    const q = ptSearch.trim().toLowerCase();
    if (q.length < 3) return [];
    return CASES.filter(c => {
      const lastMatch  = c.lastName.toLowerCase().startsWith(q);
      const firstMatch = c.firstName.toLowerCase().startsWith(q);
      const phoneMatch = c.phone.replace(/\D/g,'').includes(q.replace(/\D/g,'')) && q.replace(/\D/g,'').length >= 3;
      return lastMatch || firstMatch || phoneMatch;
    }).slice(0, 6);
  }, [ptSearch]);

  const handlePtSelect = (c) => {
    setPtSearch('');
    setPtDropdown(false);
    onOpenPatient && onOpenPatient(c);
  };

  return (
  <div style={{
    width: 230, background: C.bgCard,
    borderRight: `1px solid ${C.border}`,
    display: 'flex', flexDirection: 'column', flexShrink: 0,
    position: 'relative',
  }}>
    {/* Logo */}
    <div style={{ padding: '22px 20px 18px', borderBottom: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.18em', color: C.blue, textTransform: 'uppercase', marginBottom: 3 }}>by RenuviaMD&reg;</div>
      <div style={{ fontSize: 22, fontWeight: 900, color: C.textPrimary, letterSpacing: '-0.02em', lineHeight: 1 }}>
        PI <span style={{ color: C.blueBright }}>Master</span>&trade;
      </div>
      <div style={{ fontSize: 9, color: C.textMuted, marginTop: 4, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Clinical Documentation</div>
    </div>

    {/* Patient Search */}
    <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}`, background: 'rgba(30,120,255,0.04)' }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6 }}>Patient Search</div>
      <div style={{ position: 'relative' }}>
        <input
          value={ptSearch}
          onChange={e => { setPtSearch(e.target.value); setPtDropdown(true); }}
          onFocus={() => setPtDropdown(true)}
          onBlur={() => setTimeout(() => setPtDropdown(false), 180)}
          placeholder="Last 3 / First 3 / Phone"
          style={{
            width: '100%', padding: '8px 28px 8px 10px',
            background: C.bgElevated, border: `1px solid ${ptSearch.length >= 3 ? C.blue : C.border}`,
            borderRadius: 8, color: C.textPrimary, fontSize: 11,
            boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit',
            transition: 'border 0.2s',
          }}
        />
        {ptSearch
          ? <button onClick={() => { setPtSearch(''); setPtDropdown(false); }}
              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 12, padding: 0 }}>x</button>
          : <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: C.textMuted, pointerEvents: 'none' }}>&#x1F464;</span>
        }
        {/* Validation hint */}
        {ptSearch.length > 0 && ptSearch.length < 3 && (
          <div style={{ fontSize: 9, color: C.amber, marginTop: 3, fontWeight: 700 }}>Type 3+ letters or phone digits</div>
        )}
        {/* Dropdown results */}
        {ptDropdown && ptResults.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 500,
            background: C.bgCard, border: `1px solid ${C.blue}55`,
            borderRadius: 8, marginTop: 4, overflow: 'hidden',
            boxShadow: `0 8px 24px ${C.blueGlow}`,
          }}>
            {ptResults.map(c => (
              <div key={c.id} onMouseDown={() => handlePtSelect(c)}
                style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: `1px solid ${C.border}`, transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = C.bgElevated}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ fontSize: 11, fontWeight: 800, color: C.textPrimary }}>{c.lastName}, {c.firstName}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 9, color: C.textMuted }}>{c.dob}</span>
                  <span style={{ fontSize: 9, color: C.textMuted }}>&middot;</span>
                  <span style={{ fontSize: 9, color: c.mrnType === 'id4' ? '#FFB74D' : '#81C784', fontWeight: 700 }}>{c.mrn}</span>
                  <span style={{ fontSize: 9, color: C.textMuted }}>&middot;</span>
                  <span style={{ fontSize: 9, color: STATUS[c.status]?.text || C.textMuted, fontWeight: 700, textTransform: 'uppercase' }}>{c.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {ptDropdown && ptSearch.length >= 3 && ptResults.length === 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 500, background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8, marginTop: 4, padding: '10px 12px', fontSize: 11, color: C.textMuted }}>
            No patient found
          </div>
        )}
      </div>
    </div>

    {/* Clinic tag */}
    <div style={{ padding: '8px 20px', borderBottom: `1px solid ${C.border}`, background: C.blueGlow }}>
      <div style={{ fontSize: 9, color: C.blue, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Clinic</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.textPrimary, marginTop: 2 }}>Wellness Healthcare Clinic</div>
      <div style={{ fontSize: 10, color: C.textMuted }}>Doral, FL &middot; Florida PIP</div>
    </div>

    {/* Nav */}
    <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto' }}>
      {NAV.map(item => (
        <button key={item.id} onClick={() => handleNav(item.id)} style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 12px', borderRadius: 8, marginBottom: 2, border: 'none',
          cursor: 'pointer', textAlign: 'left', fontSize: 12, fontWeight: 600,
          background: screen === item.id ? C.bgElevated : 'transparent',
          color: screen === item.id ? C.blueBright : C.textSecond,
          borderLeft: screen === item.id ? `2px solid ${C.blue}` : '2px solid transparent',
          transition: 'all 0.15s',
          fontFamily: 'inherit',
        }}>
          <span style={{ fontSize: 14, opacity: screen === item.id ? 1 : 0.6 }}>{item.icon}</span>
          {item.label}
        </button>
      ))}
    </nav>

    {/* User */}
    <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 9, color: C.blue, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 3 }}>
        {auth === 'physician' ? 'Treating Physician' :
         auth === 'staff'     ? 'Staff / Intake & Documentation' :
         auth === 'pt'        ? 'Physical Therapist' :
                                'System Admin / All Access'}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.textPrimary }}>Dr. A. Falcon, MD</div>
      <div style={{ fontSize: 10, color: C.textMuted }}>FL #ME 84789</div>
      <button onClick={onLogout} style={{ marginTop: 10, width: '100%', padding: '7px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.textMuted, fontSize: 10, fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'inherit' }}>
        Sign Out
      </button>
    </div>
  </div>
  );
};

// Back Button
const BackBtn = ({ onBack, label = "Back" }) => {
  if (!onBack) return null;
  return (
    <button onClick={onBack} style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "7px 14px", borderRadius: 8, border: `1px solid ${C.border}`,
      background: C.bgElevated, color: C.textSecond,
      fontSize: 12, fontWeight: 700, cursor: "pointer",
      letterSpacing: "0.04em", textTransform: "uppercase",
      transition: "all 0.15s", fontFamily: "inherit",
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = C.blue; e.currentTarget.style.color = C.blueBright; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textSecond; }}
    >
      &larr; {label}
    </button>
  );
};

const LoginScreen = ({ onLogin }) => {
  const [email, setEmail]       = React.useState("");
  const [password, setPassword] = React.useState("");
  const [loading, setLoading]   = React.useState(false);
  const [error, setError]       = React.useState("");

  const handleLogin = async () => {
    if (!email || !password) { setError("Email and password required."); return; }
    setLoading(true); setError("");
    try {
      const { data, error: authErr } = await sbSignIn(email.trim(), password);
      if (authErr) { setError(authErr.message); setLoading(false); return; }
      const e = email.toLowerCase();
      let r = "physician";
      if (e.includes("staff") || e.includes("front"))       r = "staff";
      else if (e.includes("pt") || e.includes("therapy"))   r = "pt";
      else if (e.includes("admin"))                          r = "admin";
      await auditLog(email, "LOGIN", "auth", data.user?.id, "User signed in");
      onLogin(r, email);
    } catch(e) {
      setError("Connection error. Check your network.");
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", fontFamily: "'Helvetica Neue', Arial, sans-serif", position: "relative", overflow: "hidden" }}>
      {/* Background grid */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(${C.border} 1px, transparent 1px), linear-gradient(90deg, ${C.border} 1px, transparent 1px)`, backgroundSize: "40px 40px", opacity: 0.3 }} />
      {/* Glow orb */}
      <div style={{ position: "absolute", top: "20%", left: "25%", width: 600, height: 600, background: `radial-gradient(circle, ${C.blueGlow} 0%, transparent 70%)`, pointerEvents: "none" }} />

      {/* LEFT — Hero */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "60px 80px", position: "relative", zIndex: 1 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.blue, textTransform: "uppercase", marginBottom: 10 }}>by RenuviaMD®</div>
        <div style={{ fontSize: 64, fontWeight: 900, color: C.textPrimary, letterSpacing: "-0.03em", lineHeight: 1, marginBottom: 4 }}>
          PI <span style={{ color: C.blueBright, textShadow: `0 0 30px ${C.blue}` }}>Master</span>™
        </div>
        <div style={{ fontSize: 14, color: C.textSecond, marginBottom: 48, letterSpacing: "0.02em" }}>
          Personal Injury Protection Medical Documentation System powered by RenuviaMD® Network | v 1.0
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", color: C.blue, textTransform: "uppercase", marginBottom: 12, opacity: 0.8 }}>For Clinics</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: C.textPrimary, lineHeight: 1.25, letterSpacing: "-0.02em" }}>
              PI Master™ makes PI documentation<br/>faster, cleaner, and denial-proof.
            </div>
          </div>
        </div>
        <div style={{ marginTop: 48, padding: "10px 14px", background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11, color: C.textMuted, display: "inline-flex", gap: 16 }}>
          <span style={{ color: C.green }}>● System Online</span>
          <span>·</span>
          <span>Florida PIP · HIPAA Compliant</span>
        </div>
      </div>

      {/* RIGHT — Login panel */}
      <div style={{ width: 440, background: C.bgCard, borderLeft: `1px solid ${C.border}`, display: "flex", flexDirection: "column", justifyContent: "center", padding: "48px 40px", position: "relative", zIndex: 1, overflowY: "auto" }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.textPrimary, marginBottom: 4 }}>Sign In</div>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 28 }}>Authorized personnel only · Access is role-based and audit-logged</div>

        {/* Email + Password */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5 }}>Email</div>
          <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            style={{ width: "100%", padding: "10px 13px", background: C.bgElevated, border: `1px solid ${error ? "#ef5350" : C.border}`, borderRadius: 8, fontSize: 13, color: C.textPrimary, boxSizing: "border-box", outline: "none" }} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5 }}>Password</div>
          <input type="password" value={password} onChange={e => { setPassword(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            placeholder="••••••••"
            style={{ width: "100%", padding: "10px 13px", background: C.bgElevated, border: `1px solid ${error ? "#ef5350" : C.border}`, borderRadius: 8, fontSize: 13, color: C.textPrimary, boxSizing: "border-box", outline: "none" }} />
        </div>

        {error && (
          <div style={{ padding: "8px 12px", background: "rgba(239,83,80,0.08)", border: "1px solid #ef535044", borderRadius: 8, marginBottom: 12, fontSize: 12, color: "#ef5350", fontWeight: 700 }}>
            ⚠️ {error}
          </div>
        )}

        {/* Primary sign-in button */}
        <button onClick={handleLogin} disabled={loading} style={{
          width: "100%", padding: 13, borderRadius: 8, border: "none",
          background: loading ? C.bgElevated : C.blueBright,
          color: loading ? C.textMuted : "#fff",
          fontSize: 13, fontWeight: 800, cursor: loading ? "not-allowed" : "pointer",
          letterSpacing: "0.06em", textTransform: "uppercase",
          boxShadow: loading ? "none" : `0 0 20px ${C.blueGlow}`,
          marginBottom: 16, transition: "all 0.2s",
        }}>
          {loading ? "⏳ Signing in..." : `ACCESS PI MASTER™ →`}
        </button>

        {/* Google Sign-In */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, color: C.textMuted, fontSize: 11 }}>
          <div style={{ flex: 1, height: 1, background: C.border }} />
          <span>or</span>
          <div style={{ flex: 1, height: 1, background: C.border }} />
        </div>
        <button onClick={() => setError("Google Sign-In will be enabled shortly. Use email & password to sign in.")} style={{
          width: "100%", padding: "11px 13px", borderRadius: 8, border: `1px solid ${C.border}`,
          background: C.bgElevated, color: C.textPrimary,
          fontSize: 13, fontWeight: 700, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          marginBottom: 16, transition: "all 0.2s",
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign in with Google
        </button>

        <div style={{ marginTop: 4, padding: "10px 12px", background: C.bgElevated, borderRadius: 8, fontSize: 10, color: C.textMuted, lineHeight: 1.7, border: `1px solid ${C.border}` }}>
          <strong style={{ color: C.textSecond }}>HIPAA Compliant.</strong> Access is role-based and audit-logged. Unauthorized access is prohibited under 45 CFR §164.
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TODAY'S PATIENT ROOM MODAL — opens when clicking a patient card in the queue
// Shows: In Room / Waiting / Pending Arrival · Start Visit button
// ─────────────────────────────────────────────────────────────────────────────
const ROOM_STATUSES = ['In Room', 'Waiting', 'Pending Arrival'];

// ─────────────────────────────────────────────────────────────────────────────
// STAFF DASHBOARD — Intake & Documentation Workflow
// ─────────────────────────────────────────────────────────────────────────────
const StaffDashboard = ({ onNav, onOpenCase, auth, onBack }) => {
  const [showRef, setShowRef] = React.useState(false);
  const today = new Date().toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' });

  const WORKFLOW_STEPS = [
    { num:1, icon:"\u{1F4CB}", title:"Patient Check-In",       desc:"Verify ID, collect demographics, assign to room" },
    { num:2, icon:"\u{1F697}", title:"Accident & Insurance",   desc:"Enter accident details, verify PIP carrier" },
    { num:3, icon:"\u{1F4C4}", title:"Legal Documents",        desc:"Generate consent forms, OIR, EMC \u2014 print for signature" },
    { num:4, icon:"\u{1FA7A}", title:"Medical Interview",      desc:"Chief complaints, pain score, allergies, PMH" },
    { num:5, icon:"\u2705",    title:"Mark Ready for MD",      desc:"Complete intake checklist, handoff to physician" },
    { num:6, icon:"\u{1F5A8}\uFE0F", title:"Post-Visit",      desc:"Download reports, print for patient, file for billing" },
  ];

  const ACTIVE_STATUSES = ['Active','In Treatment','Pending Review','Peer Review','EMC Pending'];
  const activePatients = CASES.filter(c => ACTIVE_STATUSES.includes(c.status));

  const getIntakeStatus = (c) => {
    if (c.visits === 0 || c.visits === 1 && c.emc === 'Pending') return { label:'Pending Intake', bg:C.amberBg, color:C.amber };
    if (c.emc === 'Pending') return { label:'Ready for MD', bg:C.greenBg, color:C.green };
    return { label:'Visit Complete', bg:C.bluePale, color:C.blueBright };
  };

  return (
    <div style={{ padding:28, maxWidth:1200, margin:'0 auto' }}>
      <BackBtn onBack={onBack} />
      <div style={{ marginBottom:8, marginTop:onBack ? 16 : 0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:28 }}>{"\u{1F4CB}"}</span>
          <div>
            <div style={{ fontSize:22, fontWeight:800, color:C.textPrimary }}>Staff Dashboard</div>
            <div style={{ fontSize:13, color:C.textSecond, fontWeight:600 }}>Intake & Documentation Workflow</div>
          </div>
        </div>
        <div style={{ fontSize:11, color:C.textMuted, marginTop:8 }}>
          PI Master\u2122 \u00B7 Wellness Healthcare Clinic Corp \u00B7 Doral, FL \u00B7 {today}
        </div>
      </div>

      {/* Section 1: Daily Workflow Steps */}
      <SectionHead title="DAILY WORKFLOW STEPS" sub="Follow these steps for each patient visit" />
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:14, marginBottom:28 }}>
        {WORKFLOW_STEPS.map(s => (
          <Card key={s.num} style={{ position:'relative', paddingLeft:28 }}>
            <div style={{ position:'absolute', top:12, left:12, width:22, height:22, borderRadius:'50%', background:C.blue, color:C.white, fontSize:11, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center' }}>{s.num}</div>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
              <span style={{ fontSize:22 }}>{s.icon}</span>
              <span style={{ fontSize:13, fontWeight:800, color:C.textPrimary }}>{s.title}</span>
            </div>
            <div style={{ fontSize:11, color:C.textSecond, lineHeight:1.5 }}>{s.desc}</div>
          </Card>
        ))}
      </div>

      {/* Section 2: Today's Patients */}
      <SectionHead title="TODAY\u2019S PATIENTS" sub={`${activePatients.length} active patients`} />
      <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:28 }}>
        {activePatients.map(c => {
          const st = getIntakeStatus(c);
          return (
            <Card key={c.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:16 }}>
                <div style={{ width:36, height:36, borderRadius:'50%', background:C.bluePale, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:C.blueBright }}>
                  {c.firstName[0]}{c.lastName[0]}
                </div>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:C.textPrimary }}>{c.patient}</div>
                  <div style={{ fontSize:11, color:C.textMuted }}>DOB: {c.dob} \u00B7 {c.insurance}</div>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <span style={{ fontSize:11, fontWeight:700, padding:'4px 10px', borderRadius:6, background:st.bg, color:st.color }}>{st.label}</span>
                <Btn variant="primary" style={{ fontSize:10, padding:'6px 14px' }} onClick={() => onOpenCase(c)}>Start Intake</Btn>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Section 3: Quick Reference */}
      <div style={{ marginBottom:8 }}>
        <div onClick={() => setShowRef(!showRef)} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginBottom:8 }}>
          <SectionHead title="QUICK REFERENCE" sub="Click to expand" />
          <span style={{ color:C.textMuted, fontSize:14, marginTop:-10 }}>{showRef ? '\u25B2' : '\u25BC'}</span>
        </div>
        {showRef && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:14 }}>
            <Card>
              <Label>Forms Checklist</Label>
              <div style={{ fontSize:11, color:C.textSecond, lineHeight:1.8 }}>
                {["\u2610 Patient Demographics Form","\u2610 Assignment of Benefits (AOB)","\u2610 HIPAA Privacy Notice","\u2610 Consent to Treat","\u2610 OIR B1-1571 (PIP Application)","\u2610 EMC / Medical Necessity Letter"].map((f,i) => <div key={i}>{f}</div>)}
              </div>
            </Card>
            <Card>
              <Label>PIP 14-Day Rule</Label>
              <div style={{ fontSize:11, color:C.textSecond, lineHeight:1.8 }}>
                <div style={{ color:C.amber, fontWeight:700, marginBottom:4 }}>CRITICAL DEADLINE</div>
                <div>Patient must receive initial treatment within 14 days of the motor vehicle accident to preserve PIP benefits under Florida Statute 627.736.</div>
                <div style={{ marginTop:8, color:C.textMuted }}>Failure to comply = loss of $10,000 PIP coverage.</div>
              </div>
            </Card>
            <Card>
              <Label>Required Documents</Label>
              <div style={{ fontSize:11, color:C.textSecond, lineHeight:1.8 }}>
                {["\u2022 Assignment of Benefits (AOB)","\u2022 HIPAA Authorization","\u2022 OIR Form B1-1571","\u2022 Emergency Medical Condition (EMC)","\u2022 Motor Vehicle Accident Report","\u2022 Insurance Declaration Page"].map((f,i) => <div key={i}>{f}</div>)}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PT DASHBOARD — Physical Therapy Documentation
// ─────────────────────────────────────────────────────────────────────────────
const PTDashboardView = ({ onNav, onOpenCase, onStartPT, auth, onBack }) => {
  const [showRef, setShowRef] = React.useState(false);
  const today = new Date().toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' });

  const PT_STEPS = [
    { num:1, icon:"\u{1F4CA}", title:"Review Patient Chart",  desc:"Check MD evaluation, treatment plan, CPT codes" },
    { num:2, icon:"\u{1F3C3}", title:"Daily Session",         desc:"Document modalities, exercises, pain scores" },
    { num:3, icon:"\u{1F916}", title:"AI Generate Note",      desc:"System creates SOAP note from selections" },
    { num:4, icon:"\u270D\uFE0F",  title:"Sign & Lock",       desc:"Review AI note, sign session documentation" },
    { num:5, icon:"\u{1F4C8}", title:"Weekly Summary",        desc:"End-of-week progress report with medical necessity" },
    { num:6, icon:"\u{1F4B3}", title:"Superbill Review",      desc:"Verify CPT codes, charges, modifier 25" },
  ];

  const ACTIVE_STATUSES = ['Active','In Treatment'];
  const activePatients = CASES.filter(c => ACTIVE_STATUSES.includes(c.status));

  const MOCK_SESSIONS = [
    { date:'Apr 04, 2026', patient:'Naomi A. Ramos Lopez', session:1, painStart:7, painEnd:6, status:'Signed' },
    { date:'Apr 04, 2026', patient:'Carlos M. Reyes',      session:12, painStart:5, painEnd:4, status:'Signed' },
    { date:'Apr 03, 2026', patient:'Marcus T. Johnson',    session:18, painStart:7, painEnd:6, status:'Signed' },
    { date:'Apr 03, 2026', patient:'Elena V. Castillo',    session:15, painStart:6, painEnd:5, status:'Draft' },
    { date:'Apr 02, 2026', patient:'Diana L. Whitmore',    session:8,  painStart:6, painEnd:5, status:'Signed' },
  ];

  const CPT_CODES = [
    { code:'97010', desc:'Hot/Cold Packs' },
    { code:'97012', desc:'Mechanical Traction' },
    { code:'97035', desc:'Ultrasound Therapy' },
    { code:'97110', desc:'Therapeutic Exercises' },
    { code:'97140', desc:'Manual Therapy' },
    { code:'97530', desc:'Therapeutic Activities' },
  ];

  return (
    <div style={{ padding:28, maxWidth:1200, margin:'0 auto' }}>
      <BackBtn onBack={onBack} />
      <div style={{ marginBottom:8, marginTop:onBack ? 16 : 0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:28 }}>{"\u{1F3C3}"}</span>
          <div>
            <div style={{ fontSize:22, fontWeight:800, color:C.textPrimary }}>PT Dashboard</div>
            <div style={{ fontSize:13, color:C.textSecond, fontWeight:600 }}>Physical Therapy Documentation</div>
          </div>
        </div>
        <div style={{ fontSize:11, color:C.textMuted, marginTop:8 }}>
          PI Master\u2122 \u00B7 Wellness Healthcare Clinic Corp \u00B7 Doral, FL \u00B7 {today}
        </div>
      </div>

      {/* Section 1: Daily Workflow */}
      <SectionHead title="DAILY WORKFLOW" sub="Follow these steps for each PT session" />
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:14, marginBottom:28 }}>
        {PT_STEPS.map(s => (
          <Card key={s.num} style={{ position:'relative', paddingLeft:28 }}>
            <div style={{ position:'absolute', top:12, left:12, width:22, height:22, borderRadius:'50%', background:C.blue, color:C.white, fontSize:11, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center' }}>{s.num}</div>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
              <span style={{ fontSize:22 }}>{s.icon}</span>
              <span style={{ fontSize:13, fontWeight:800, color:C.textPrimary }}>{s.title}</span>
            </div>
            <div style={{ fontSize:11, color:C.textSecond, lineHeight:1.5 }}>{s.desc}</div>
          </Card>
        ))}
      </div>

      {/* Section 2: Today's Schedule */}
      <SectionHead title="TODAY\u2019S SCHEDULE" sub={`${activePatients.length} patients scheduled`} />
      <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:28 }}>
        {activePatients.map(c => (
          <Card key={c.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ display:'flex', alignItems:'center', gap:16 }}>
              <div style={{ width:36, height:36, borderRadius:'50%', background:C.bluePale, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, color:C.blueBright }}>
                {c.firstName[0]}{c.lastName[0]}
              </div>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:C.textPrimary }}>{c.patient}</div>
                <div style={{ fontSize:11, color:C.textMuted }}>Session #{c.visits + 1} \u00B7 {c.visits} visits completed</div>
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:11, color:C.textSecond }}>Last pain: <span style={{ color:C.amber, fontWeight:700 }}>{c.painScore}/10</span></span>
              <Btn variant="primary" style={{ fontSize:10, padding:'6px 14px' }} onClick={() => onStartPT(c)}>Document Session</Btn>
            </div>
          </Card>
        ))}
      </div>

      {/* Section 3: Recent Sessions */}
      <SectionHead title="RECENT SESSIONS" sub="Last 5 PT sessions documented" />
      <Card style={{ marginBottom:28, padding:0, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ background:C.bgElevated, borderBottom:`1px solid ${C.border}` }}>
              {['Date','Patient','Session #','Pain Start','Pain End','Status'].map(h => (
                <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:10, fontWeight:800, color:C.textMuted, letterSpacing:'0.08em', textTransform:'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MOCK_SESSIONS.map((s,i) => (
              <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}>
                <td style={{ padding:'10px 14px', color:C.textSecond }}>{s.date}</td>
                <td style={{ padding:'10px 14px', color:C.textPrimary, fontWeight:600 }}>{s.patient}</td>
                <td style={{ padding:'10px 14px', color:C.textSecond }}>#{s.session}</td>
                <td style={{ padding:'10px 14px', color:C.amber, fontWeight:700 }}>{s.painStart}/10</td>
                <td style={{ padding:'10px 14px', color:C.green, fontWeight:700 }}>{s.painEnd}/10</td>
                <td style={{ padding:'10px 14px' }}>
                  <span style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:4, background: s.status === 'Signed' ? C.greenBg : C.amberBg, color: s.status === 'Signed' ? C.green : C.amber }}>{s.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Section 4: Quick Reference */}
      <div style={{ marginBottom:8 }}>
        <div onClick={() => setShowRef(!showRef)} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginBottom:8 }}>
          <SectionHead title="QUICK REFERENCE" sub="Click to expand" />
          <span style={{ color:C.textMuted, fontSize:14, marginTop:-10 }}>{showRef ? '\u25B2' : '\u25BC'}</span>
        </div>
        {showRef && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <Card>
              <Label>Modality CPT Codes</Label>
              <div style={{ fontSize:11, color:C.textSecond, lineHeight:2 }}>
                {CPT_CODES.map((c,i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between' }}>
                    <span style={{ color:C.blueBright, fontWeight:700, fontFamily:'monospace' }}>{c.code}</span>
                    <span>{c.desc}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card>
              <Label>Documentation Reminders</Label>
              <div style={{ fontSize:11, color:C.textSecond, lineHeight:1.8 }}>
                <div style={{ color:C.amber, fontWeight:700, marginBottom:4 }}>MEDICAL NECESSITY</div>
                <div>{"\u2022"} Document functional improvement or decline each session</div>
                <div>{"\u2022"} Include response to treatment: improved / plateau / worsened</div>
                <div>{"\u2022"} Reference specific goals from Plan of Care</div>
                <div>{"\u2022"} Note patient compliance and attendance</div>
                <div style={{ marginTop:8, color:C.textMuted }}>{"\u2022"} Use Modifier 25 when E/M + therapy same day</div>
                <div style={{ color:C.textMuted }}>{"\u2022"} Re-evaluation required every 30 days or 10 visits</div>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Section 4: Billing */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Billing & Forms</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Card style={{ cursor: 'pointer', textAlign: 'center', padding: 16 }} onClick={() => onNav('billing-codes')}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>🏷</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textPrimary }}>Billing Codes</div>
            <div style={{ fontSize: 10, color: C.textMuted, marginTop: 4 }}>CPT + ICD-10 management</div>
          </Card>
          <Card style={{ textAlign: 'center', padding: 16 }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>📋</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textPrimary }}>Superbill</div>
            <div style={{ fontSize: 10, color: C.textMuted, marginTop: 4 }}>Open patient → Billing tab</div>
          </Card>
          <Card style={{ textAlign: 'center', padding: 16 }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>🏥</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textPrimary }}>CMS-1500</div>
            <div style={{ fontSize: 10, color: C.textMuted, marginTop: 4 }}>Open patient → Billing tab</div>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD — Patient Registry only (no waiting room, no metric cards,
//              no case overview tab, no modals)
// ─────────────────────────────────────────────────────────────────────────────
const Dashboard = ({ onNav, onOpenCase, auth, onBack }) => {
  const [ptSearch,    setPtSearch]    = React.useState('');
  const [ptToggle,    setPtToggle]    = React.useState('active');
  const [ptSort,      setPtSort]     = React.useState('newest');
  const [expandedMRN, setExpandedMRN] = React.useState(null);

  // ── Status buckets ──
  const ACTIVE_STATUSES   = ['Active','In Treatment','Pending Review','Peer Review','EMC Pending'];
  const INACTIVE_STATUSES = ['Closed','MMI Reached'];

  // Alert strip data
  const pending = CASES.filter(c => ['Pending Review','EMC Pending','Peer Review'].includes(c.status));

  // ── Patient map: group CASES by MRN ──
  const patientMap = React.useMemo(() => {
    const map = {};
    CASES.forEach(c => {
      if (!map[c.mrn]) map[c.mrn] = {
        mrn: c.mrn, mrnType: c.mrnType, firstName: c.firstName, lastName: c.lastName,
        phone: c.phone, email: c.email, dob: c.dob, sex: c.sex, zip: c.zip, cases: []
      };
      map[c.mrn].cases.push(c);
    });
    return map;
  }, []);

  // ── Filtered + sorted patient list ──
  const filteredPatients = React.useMemo(() => {
    const q = ptSearch.trim().toLowerCase();
    return Object.values(patientMap).filter(pt => {
      const hasCases = ptToggle === 'active'
        ? pt.cases.some(c => ACTIVE_STATUSES.includes(c.status))
        : pt.cases.some(c => INACTIVE_STATUSES.includes(c.status));
      if (!hasCases) return false;
      if (!q) return true;
      return (
        pt.lastName.toLowerCase().includes(q) ||
        pt.firstName.toLowerCase().includes(q) ||
        pt.phone.replace(/\D/g,'').includes(q.replace(/\D/g,'')) ||
        pt.dob.includes(q) ||
        pt.mrn.toLowerCase().includes(q) ||
        (pt.zip || '').includes(q) ||
        pt.cases.some(c => c.id.toLowerCase().includes(q) || c.insurance.toLowerCase().includes(q))
      );
    }).sort((a, b) => {
      if (ptSort === 'lastName')  return a.lastName.localeCompare(b.lastName);
      if (ptSort === 'firstName') return a.firstName.localeCompare(b.firstName);
      const aDate = Math.max(...a.cases.map(c => new Date(c.created)));
      const bDate = Math.max(...b.cases.map(c => new Date(c.created)));
      return bDate - aDate;
    });
  }, [patientMap, ptToggle, ptSearch, ptSort]);

  const activePtCt   = Object.values(patientMap).filter(pt => pt.cases.some(c => ACTIVE_STATUSES.includes(c.status))).length;
  const inactivePtCt = Object.values(patientMap).filter(pt => pt.cases.some(c => INACTIVE_STATUSES.includes(c.status))).length;

  return (
    <div style={{ flex: 1, overflow: 'auto', background: C.bg }}>
      <div style={{ padding: '28px 32px' }}>

        {/* ── HEADER ── */}
        <div style={{ marginBottom: 20 }}>
          {onBack && <BackBtn onBack={onBack} />}
          <div style={{ fontSize: 9, fontWeight: 800, color: C.textMuted, letterSpacing: '0.15em',
            textTransform: 'uppercase', marginBottom: 4, marginTop: 8 }}>
            Clinical Documentation &middot; {auth === 'physician' ? 'Physician' : auth === 'staff' ? 'Staff' :
              auth === 'pt' ? 'Physical Therapist' : 'Admin'} View
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.textPrimary, letterSpacing: '-0.02em' }}>Dashboard</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3 }}>
            PI Master&trade; &middot; Wellness Healthcare Clinic Corp &middot; Doral, FL &middot; {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
        </div>

        {/* ── ALERT STRIP ── */}
        {pending.length > 0 && (
          <div style={{ background: C.amberBg, border: `1px solid ${C.amber}33`, borderRadius: 10,
            padding: '12px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 18 }}>&#x26A0;&#xFE0F;</span>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.amber }}>{pending.length} cases require attention</span>
              <span style={{ fontSize: 12, color: C.textMuted, marginLeft: 12 }}>EMC certifications / Peer review / Signatures needed</span>
            </div>
            <Btn onClick={() => onNav('cases')} variant="amber" style={{ fontSize: 11 }}>Review &rarr;</Btn>
          </div>
        )}

        {/* ── SECTION DIVIDER: All Patients ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, height: 1, background: C.border }} />
          <div style={{ fontSize: 10, fontWeight: 800, color: C.textMuted, textTransform: 'uppercase',
            letterSpacing: '0.15em', whiteSpace: 'nowrap' }}>All Patients &mdash; Patient Registry</div>
          <div style={{ flex: 1, height: 1, background: C.border }} />
        </div>

        {/* ── SEARCH BAR ── */}
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
            fontSize: 15, color: C.textMuted, pointerEvents: 'none' }}>&#x1F50D;</div>
          <input value={ptSearch} onChange={e => setPtSearch(e.target.value)}
            placeholder="Search: Last name / First name / Phone / DOB"
            style={{ width: '100%', padding: '11px 14px 11px 42px', background: C.bgElevated,
              border: `2px solid ${ptSearch ? C.blue : C.border}`, borderRadius: 10,
              color: C.textPrimary, fontSize: 12, boxSizing: 'border-box', outline: 'none',
              transition: 'border 0.2s', fontFamily: 'inherit' }} />
          {ptSearch && (
            <button onClick={() => setPtSearch('')} style={{ position: 'absolute', right: 12,
              top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none',
              color: C.textMuted, cursor: 'pointer', fontSize: 16 }}>x</button>
          )}
        </div>

        {/* ── TOGGLE (Active / Closed) + SORT ── */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', background: C.bgElevated, borderRadius: 10, padding: 3,
            border: `1px solid ${C.border}` }}>
            {[
              ['active',  'Active (' + activePtCt + ')',             C.green],
              ['inactive','Closed / MMI (' + inactivePtCt + ')',     C.textMuted],
            ].map(([val, label, col]) => (
              <button key={val} onClick={() => setPtToggle(val)} style={{
                padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                fontSize: 11, fontWeight: 700, transition: 'all 0.15s',
                background: ptToggle === val ? C.bgCard : 'transparent',
                color: ptToggle === val ? col : C.textMuted,
                boxShadow: ptToggle === val ? '0 2px 6px #0006' : 'none',
                fontFamily: 'inherit' }}>{label}</button>
            ))}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: C.textMuted,
              textTransform: 'uppercase', letterSpacing: '0.1em' }}>Sort:</span>
            {[['newest','Newest'],['lastName','Last Name'],['firstName','First Name']].map(([id, lbl]) => (
              <button key={id} onClick={() => setPtSort(id)} style={{
                padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 10, fontWeight: 700,
                background: ptSort === id ? C.blue : C.bgElevated,
                border: `1px solid ${ptSort === id ? C.blue : C.border}`,
                color: ptSort === id ? '#fff' : C.textMuted, fontFamily: 'inherit' }}>{lbl}</button>
            ))}
          </div>
        </div>

        {/* ── PATIENT CARDS ── */}
        {filteredPatients.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: C.textMuted }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>&#x1F50D;</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, marginBottom: 6 }}>No patients found</div>
            {ptSearch && <button onClick={() => setPtSearch('')} style={{ padding: '8px 16px',
              borderRadius: 8, background: C.bgElevated, border: `1px solid ${C.border}`,
              color: C.textSecond, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}>Clear Search</button>}
          </div>
        ) : filteredPatients.map(pt => {
          const visibleCases = ptToggle === 'active'
            ? pt.cases.filter(c => ACTIVE_STATUSES.includes(c.status))
            : pt.cases.filter(c => INACTIVE_STATUSES.includes(c.status));
          const isExpanded = expandedMRN === pt.mrn;
          return (
            <Card key={pt.mrn} style={{ marginBottom: 10, padding: 0, overflow: 'hidden' }}>
              {/* Patient row — click to expand cases */}
              <div style={{ padding: '12px 18px', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 10, cursor: 'pointer',
                borderBottom: isExpanded ? `1px solid ${C.border}` : 'none' }}
                onClick={() => setExpandedMRN(isExpanded ? null : pt.mrn)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', flex: 1 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: C.blueGlow,
                    border: `1px solid ${C.blue}44`, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                    {pt.sex === 'M' ? '\u2642' : '\u2640'}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
                      {pt.lastName}, {pt.firstName}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, color: C.textMuted }}>DOB {pt.dob}</span>
                      <span style={{ fontSize: 10, color: C.textMuted }}>&middot; {pt.phone}</span>
                      {pt.zip && <span style={{ fontSize: 10, color: C.textMuted }}>&middot; ZIP {pt.zip}</span>}
                    </div>
                  </div>
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 800,
                    background: pt.mrnType === 'id4' ? 'rgba(255,160,0,0.1)' : 'rgba(46,125,50,0.12)',
                    border: `1px solid ${pt.mrnType === 'id4' ? '#FFB74D44' : '#81C78444'}`,
                    color: pt.mrnType === 'id4' ? '#FFB74D' : '#81C784' }}>
                    {pt.mrn}
                  </span>
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4,
                    background: C.bgElevated, border: `1px solid ${C.border}`,
                    color: C.textMuted, fontWeight: 700 }}>
                    {visibleCases.length} case{visibleCases.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <span style={{ fontSize: 12, color: C.textMuted,
                  transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s',
                  flexShrink: 0 }}>{'\u25BC'}</span>
              </div>

              {/* Cases — expanded, newest to oldest */}
              {isExpanded && visibleCases
                .sort((a, b) => new Date(b.created) - new Date(a.created))
                .map((c, ci) => {
                  const out = OUTCOME_LABELS[c.outcome] || OUTCOME_LABELS.PENDING;
                  return (
                    <div key={c.id} style={{ padding: '10px 18px 10px 66px',
                      borderBottom: ci < visibleCases.length - 1 ? `1px solid ${C.border}` : 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 10, flexWrap: 'wrap',
                      background: ci % 2 === 0 ? 'transparent' : C.bgElevated + '33' }}>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.blue, fontWeight: 700 }}>{c.id}</span>
                        <StatusBadge status={c.status} small />
                        <span style={{ fontSize: 10, color: C.textMuted }}>{c.accident}</span>
                        <span style={{ fontSize: 10, color: C.textSecond }}>{c.insurance}</span>
                        <span style={{ fontSize: 10, color: C.textMuted }}>
                          EMC: <span style={{ color: c.emc === 'Certified' ? C.green : c.emc === 'Pending' ? C.amber : C.red, fontWeight: 700 }}>{c.emc}</span>
                        </span>
                        <span style={{ fontSize: 10, color: C.textMuted }}>{c.visits} visits</span>
                        {c.mmiDate && <span style={{ fontSize: 10, color: C.amber, fontWeight: 700 }}>MMI {c.mmiDate}</span>}
                        <span style={{ fontSize: 10, fontWeight: 700, color: out.color,
                          padding: '1px 6px', borderRadius: 4, background: out.color + '18' }}>
                          {out.label}
                        </span>
                      </div>
                      <button onClick={() => onOpenCase(c)} style={{ fontSize: 10, fontWeight: 800, color: C.blue,
                        padding: '5px 12px', borderRadius: 8, background: C.blueGlow,
                        border: `1px solid ${C.blue}33`, flexShrink: 0, cursor: 'pointer', fontFamily: 'inherit' }}>
                        Open &rarr;
                      </button>
                    </div>
                  );
              })}
            </Card>
          );
        })}

      </div>
    </div>
  );
};

const CaseList = ({ onOpenCase, onNav, onBack }) => {
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const statuses = ["All", "Active", "In Treatment", "Pending Review", "Peer Review", "MMI Reached", "Closed"];

  const filtered = CASES.filter(c => {
    const matchStatus = filter === "All" || c.status === filter;
    const q = search.toLowerCase().trim();
    if (!q) return matchStatus;
    const matchSearch = (
      c.id.toLowerCase().includes(q) ||
      c.patient.toLowerCase().includes(q) ||
      (c.insurance || "").toLowerCase().includes(q) ||
      (c.policy || "").toLowerCase().includes(q) ||
      (c.attorney || "").toLowerCase().includes(q) ||
      (c.diagnosis || "").toLowerCase().includes(q) ||
      (c.physician || "").toLowerCase().includes(q)
    );
    return matchStatus && matchSearch;
  });

  return (
    <div style={{ flex: 1, overflow: "auto", background: C.bg }}>
      <div style={{ padding: "28px 32px" }}>

        {/* Header row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div>
            <BackBtn onBack={onBack} />
            <Label>Case Management</Label>
            <div style={{ fontSize: 24, fontWeight: 800, color: C.textPrimary, letterSpacing: "-0.02em" }}>All PIP Cases</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{filtered.length} of {CASES.length} cases · Florida PIP</div>
          </div>
          <Btn onClick={() => onNav("new-case")} variant="bright">＋ New Case</Btn>
        </div>

        {/* Cases vs Patients explainer */}
        <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
          <div style={{ flex: 1, padding: "10px 14px", background: C.blueGlow, border: `1px solid ${C.blue}44`, borderRadius: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: C.blueBright, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>📋 Cases (This Screen)</div>
            <div style={{ fontSize: 11, color: C.textSecond, lineHeight: 1.5 }}>
              One record <strong style={{color:C.textPrimary}}>per accident event</strong>. Tracks the active PIP/BI workflow: insurance carrier, policy, attorney, visit count, EMC status, billing batches, and legal track. A patient may have multiple cases if they had more than one accident.
            </div>
          </div>
          <div style={{ flex: 1, padding: "10px 14px", background: "rgba(34,201,122,0.06)", border: "1px solid #22C97A33", borderRadius: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: C.green, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>👤 Patients (Separate Tab)</div>
            <div style={{ fontSize: 11, color: C.textSecond, lineHeight: 1.5 }}>
              One record <strong style={{color:C.textPrimary}}>per person</strong>. The HIPAA identity registry: name, DOB, MRN (SS4/ID4), contact info, and all their cases linked. Used for duplicate detection, patient lookup, and intake verification.
            </div>
          </div>
        </div>

        {/* Search bar */}
        <div style={{ position: "relative", marginBottom: 14 }}>
          <div style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: C.textMuted, pointerEvents: "none" }}>🔍</div>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search: Case ID · Patient name · Insurance · Policy # · Attorney · Diagnosis"
            style={{ width: "100%", padding: "12px 14px 12px 42px", background: C.bgElevated, border: `2px solid ${search ? C.blue : C.border}`, borderRadius: 10, color: C.textPrimary, fontSize: 13, boxSizing: "border-box", outline: "none", transition: "border 0.2s", fontFamily: "inherit" }}
          />
          {search && (
            <button onClick={() => setSearch("")}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 16 }}>✕</button>
          )}
        </div>

        {/* Status filter tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
          {statuses.map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{
              padding: "6px 14px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              cursor: "pointer", border: `1px solid ${filter === s ? C.blue : C.border}`,
              background: filter === s ? C.blueGlow : "transparent",
              color: filter === s ? C.blueBright : C.textMuted,
              transition: "all 0.15s", textTransform: "uppercase", letterSpacing: "0.06em",
            }}>{s}</button>
          ))}
        </div>

        {/* Results or empty state */}
        {filtered.length === 0 ? (
          <div style={{ padding: "40px", textAlign: "center", color: C.textMuted, fontSize: 13 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
            No cases match <strong style={{ color: C.textPrimary }}>"{search}"</strong>
            {search && <button onClick={() => setSearch("")} style={{ display: "block", margin: "12px auto 0", padding: "8px 16px", borderRadius: 8, background: C.bgElevated, border: `1px solid ${C.border}`, color: C.textSecond, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>Clear Search</button>}
          </div>
        ) : (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}`, background: C.bgElevated }}>
                {["Case ID", "Patient", "Accident Date", "Insurance / Policy", "Attorney", "Status", "Visits", "EMC", "Physician", "Action"].map(h => (
                  <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontSize: 9, fontWeight: 800, color: C.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={c.id} onClick={() => onOpenCase(c)} style={{ borderBottom: `1px solid ${C.border}`, cursor: "pointer", background: i % 2 === 0 ? "transparent" : C.bgElevated + "33", transition: "background 0.15s" }}>
                  <td style={{ padding: "13px 14px", fontFamily: "monospace", color: C.blue, fontWeight: 700, fontSize: 11 }}>{c.id}</td>
                  <td style={{ padding: "13px 14px" }}>
                    <div style={{ fontWeight: 700, color: C.textPrimary, whiteSpace: "nowrap" }}>{c.patient}</div>
                    <div style={{ fontSize: 10, color: C.textMuted }}>DOB: {c.dob}</div>
                  </td>
                  <td style={{ padding: "13px 14px", color: C.textSecond, fontSize: 11, whiteSpace: "nowrap" }}>{c.accident}</td>
                  <td style={{ padding: "13px 14px" }}>
                    <div style={{ color: C.textSecond, fontSize: 11 }}>{c.insurance}</div>
                    <div style={{ fontSize: 10, color: C.textMuted, fontFamily: "monospace" }}>{c.policy}</div>
                  </td>
                  <td style={{ padding: "13px 14px", color: c.attorney === "Unrepresented" ? C.textMuted : C.textSecond, fontSize: 11, whiteSpace: "nowrap" }}>{c.attorney}</td>
                  <td style={{ padding: "13px 14px" }}><StatusBadge status={c.status} small /></td>
                  <td style={{ padding: "13px 14px", fontWeight: 800, color: C.textPrimary, textAlign: "center" }}>{c.visits}</td>
                  <td style={{ padding: "13px 14px" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: c.emc === "Certified" ? C.green : c.emc === "Pending" ? C.amber : C.red, whiteSpace: "nowrap" }}>
                      {c.emc === "Certified" ? "✓ " : c.emc === "Pending" ? "⏳ " : "✗ "}{c.emc}
                    </span>
                  </td>
                  <td style={{ padding: "13px 14px", color: C.textSecond, fontSize: 11, whiteSpace: "nowrap" }}>{c.physician}</td>
                  <td style={{ padding: "13px 14px" }}>
                    <Btn onClick={e => { e.stopPropagation(); onOpenCase(c); }} variant="outline" style={{ fontSize: 10, padding: "4px 10px" }}>Open →</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        )}
      </div>
    </div>
  );
};

const NewCase = ({ onNav, onCaseCreated, onBack }) => {
  const [step, setStep] = useState(0);
  const steps = ["Patient Info", "Accident Details", "Insurance", "Attorney & Physician", "Review & Create"];
  const [form, setForm] = useState({
    firstName: "", lastName: "", dob: "", sex: "", phone: "", email: "", address: "", city: "", zip: "",
    accidentDate: "", accidentTime: "", accidentType: "Motor Vehicle Accident", accidentDesc: "", county: "Manatee",
    insurance: "", policy: "", adjuster: "", adjusterPhone: "", pipLimit: "10000",
    attorney: "Unrepresented", attorneyFirm: "", referredBy: "", physician: "Dr. A. Falcon, MD",
    track: "PIP Only", visitMode: "In-Person", teleplatform: "Doxy.me", teleLocation: "", teleConsent: false, telePresent: "Patient Only", teleAV: false,
  });
  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Validation helpers
  const emailValid  = v => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);
  const phoneValid  = v => v.replace(/\D/g,'').length === 10;
  const dobValid    = v => { if (!v) return false; const d = new Date(v); return d instanceof Date && !isNaN(d) && d < new Date(); };
  const zipValid    = v => /^\d{5}$/.test(v.trim());

  const step0Errors = {
    firstName:   !form.firstName.trim()      ? 'First name required'    : '',
    lastName:    !form.lastName.trim()       ? 'Last name required'     : '',
    dob:         !dobValid(form.dob)         ? 'Valid DOB required'     : '',
    sex:         !form.sex                   ? 'Sex required'           : '',
    phone:       !phoneValid(form.phone)     ? '10-digit phone required': '',
    email:       !emailValid(form.email)     ? 'Valid email required'   : '',
    zip:         !zipValid(form.zip)         ? '5-digit ZIP required'   : '',
  };
  const step1Errors = {
    accidentDate: !form.accidentDate ? 'Accident date required' : '',
    county:       !form.county       ? 'County required'        : '',
  };
  const step2Errors = {
    insurance: !form.insurance ? 'Insurance carrier required' : '',
    policy:    !form.policy.trim() ? 'Policy number required' : '',
  };
  const stepErrors = [step0Errors, step1Errors, step2Errors, {}, {}];
  const currentErrors = stepErrors[step] || {};
  const hasErrors = Object.values(currentErrors).some(e => e);
  const [showErrors, setShowErrors] = React.useState(false);

  const tryNext = () => {
    setShowErrors(true);
    if (!hasErrors) { setShowErrors(false); setStep(s => Math.min(s + 1, steps.length - 1)); }
  };
  const progress = (step / (steps.length - 1)) * 100;
  const accidentTypes = ["Motor Vehicle Accident", "Slip & Fall", "Motorcycle Accident", "Pedestrian Accident", "Bicycle Accident", "Work-Related Injury", "Other"];
  const insurers = ["State Farm", "GEICO", "Allstate", "Progressive", "USAA", "Travelers", "Farmers", "Liberty Mutual", "Other"];

  return (
    <div style={{ flex: 1, overflow: "auto", background: C.bg }}>
      <div style={{ padding: "28px 32px", maxWidth: 760 }}>
        <BackBtn onBack={onBack} />
        <Label>Case Management</Label>
        <div style={{ fontSize: 24, fontWeight: 800, color: C.textPrimary, letterSpacing: "-0.02em", marginBottom: 4 }}>New PIP Case</div>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 28 }}>Create a new personal injury protection case . Florida PIP</div>

        {/* Progress */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.textSecond }}>Step {step + 1} of {steps.length}: <span style={{ color: C.blueBright }}>{steps[step]}</span></span>
            <span style={{ fontSize: 11, color: C.textMuted }}>{Math.round(progress)}%</span>
          </div>
          <div style={{ height: 4, background: C.bgElevated, borderRadius: 2, overflow: "hidden" }}>
            <div style={{ width: `${progress}%`, height: "100%", background: `linear-gradient(90deg, ${C.blue}, ${C.blueBright})`, borderRadius: 2, transition: "width 0.4s", boxShadow: `0 0 8px ${C.blue}` }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
            {steps.map((s, i) => <span key={s} style={{ fontSize: 9, fontWeight: 700, color: i <= step ? C.blueBright : C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s}</span>)}
          </div>
        </div>

        <Card>
          {/* STEP 0 -- Patient Info */}
          {step === 0 && (
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.textPrimary, marginBottom: 20 }}>Patient Information</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Input label="Legal First Name *" value={form.firstName} onChange={e => u("firstName", e.target.value)} placeholder="First name" />
                <Input label="Legal Last Name *" value={form.lastName} onChange={e => u("lastName", e.target.value)} placeholder="Last name" />
                <Input label="Date of Birth *" type="date" value={form.dob} onChange={e => u("dob", e.target.value)} />
                <Select label="Sex at Birth *" value={form.sex} onChange={e => u("sex", e.target.value)}>
                  <option value="">Select...</option>
                  <option value="M">Male</option><option value="F">Female</option><option value="O">Other / Intersex</option>
                </Select>
                <Input label="Phone *" value={form.phone} onChange={e => u("phone", e.target.value)} placeholder="(___) ___-____" />
                <Input label="Email *" value={form.email} onChange={e => u("email", e.target.value)} placeholder="patient@email.com" />
                <div style={{ gridColumn: "span 2" }}>
                  <Input label="Address *" value={form.address} onChange={e => u("address", e.target.value)} placeholder="Street address" />
                </div>
                <Input label="City *" value={form.city} onChange={e => u("city", e.target.value)} placeholder="City" />
                <Input label="ZIP Code *" value={form.zip} onChange={e => u("zip", e.target.value)} placeholder="34201" />
              </div>

              {/* -- MRN / PATIENT IDENTIFIER SECTION -- */}
              <div style={{ marginTop: 20, padding: '18px 20px', background: 'rgba(21,101,192,0.06)', borderRadius: 12, border: '1px solid rgba(21,101,192,0.2)' }}>
                <div style={{ fontSize: 11, fontWeight: 900, color: '#42A5F5', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14 }}>
                  🔑 Patient Identifier (MRN)
                </div>
                {/* SS4 vs ID4 toggle */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <button type="button"
                    onClick={() => { u("mrnType", "ss4"); u("id4", ""); }}
                    style={{ flex: 1, padding: '9px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 12,
                      background: form.mrnType === 'ss4' ? 'rgba(46,125,50,0.15)' : 'rgba(255,255,255,0.04)',
                      border: `2px solid ${form.mrnType === 'ss4' ? '#66BB6A' : 'rgba(255,255,255,0.1)'}`,
                      color: form.mrnType === 'ss4' ? '#81C784' : '#607D8B' }}>
                    SS4 -- Last 4 of Social Security # &nbsp;(Preferred)
                  </button>
                  <button type="button"
                    onClick={() => { u("mrnType", "id4"); u("ss4", ""); }}
                    style={{ flex: 1, padding: '9px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 12,
                      background: form.mrnType === 'id4' ? 'rgba(255,160,0,0.12)' : 'rgba(255,255,255,0.04)',
                      border: `2px solid ${form.mrnType === 'id4' ? '#FFB74D' : 'rgba(255,255,255,0.1)'}`,
                      color: form.mrnType === 'id4' ? '#FFB74D' : '#607D8B' }}>
                    ID4 -- Last 4 of Gov't ID &nbsp;(Patient declined SS#)
                  </button>
                </div>

                {form.mrnType === 'ss4' && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#90A4AE', marginBottom: 6 }}>
                      Last 4 digits of Social Security Number <span style={{color:'#ef5350'}}>*</span>
                    </div>
                    <input
                      type="text" maxLength={4} value={form.ss4}
                      onChange={e => {
                        const v = e.target.value.replace(/\D/g,'').slice(0,4);
                        u("ss4", v);
                        if (v.length === 4) {
                          const initials = ((form.firstName||'').charAt(0) + (form.lastName||'').charAt(0)).toUpperCase() || 'XX';
                          const mrnVal = 'MRN-' + v + '-' + initials + (form.firstName||'').slice(1,2).toUpperCase();
                          u("mrn", mrnVal);
                          // Duplicate check
                          const dup = CASES.find(c2 => c2.mrn && c2.mrn.includes('MRN-'+v+'-'));
                          if (dup) { u("_dupWarning", dup.patient + ' . ' + dup.id); }
                          else { u("_dupWarning", ""); }
                        } else { u("mrn",""); u("_dupWarning",""); }
                      }}
                      placeholder="e.g. 4421"
                      style={{ width: 120, padding: '10px 14px', background: 'rgba(0,0,0,0.3)', border: `2px solid ${form.ss4.length===4 ? '#66BB6A' : 'rgba(255,255,255,0.15)'}`, borderRadius: 8, color: '#fff', fontSize: 20, fontWeight: 900, letterSpacing: '0.3em', textAlign: 'center', outline: 'none', fontFamily: 'monospace' }}
                    />
                    {form.mrn && (
                      <div style={{ marginTop: 8, fontSize: 12, fontWeight: 800, color: '#66BB6A' }}>
                        ✅ MRN assigned: <span style={{ fontFamily: 'monospace', letterSpacing: '0.08em' }}>{form.mrn}</span>
                      </div>
                    )}
                    {form._dupWarning && (
                      <div style={{ marginTop: 8, padding: '10px 14px', background: 'rgba(255,152,0,0.12)', border: '1px solid #FFB74D55', borderRadius: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 900, color: '#FFB74D' }}>⚠️ Duplicate MRN Detected</div>
                        <div style={{ fontSize: 11, color: '#CFD8DC', marginTop: 4 }}>
                          This SS4 matches existing patient: <strong>{form._dupWarning}</strong>
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <input
                            type="text" value={form._overrideReason||''}
                            onChange={e => u("_overrideReason", e.target.value)}
                            placeholder="Override reason required -- e.g. Same patient, new case . Family member . Data entry correction"
                            style={{ width: '100%', padding: '8px 10px', background: 'rgba(0,0,0,0.3)', border: '1px solid #FFB74D55', borderRadius: 6, color: '#fff', fontSize: 11, boxSizing: 'border-box', outline: 'none' }}
                          />
                          <div style={{ fontSize: 10, color: '#78909C', marginTop: 4 }}>
                            Override reason will be logged to HIPAA audit trail . Admin notified
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {form.mrnType === 'id4' && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#90A4AE', marginBottom: 6 }}>
                      Last 4 digits of Gov't ID (Driver's License . Passport . State ID) <span style={{color:'#ef5350'}}>*</span>
                    </div>
                    <input
                      type="text" maxLength={4} value={form.id4}
                      onChange={e => {
                        const v = e.target.value.replace(/\D/g,'').slice(0,4);
                        u("id4", v);
                        if (v.length === 4) {
                          const initials = ((form.firstName||'').charAt(0) + (form.lastName||'').charAt(0)).toUpperCase() || 'XX';
                          const mrnVal = 'MRN-ID-' + v + '-' + initials + (form.firstName||'').slice(1,2).toUpperCase();
                          u("mrn", mrnVal);
                        } else { u("mrn",""); }
                      }}
                      placeholder="e.g. 7832"
                      style={{ width: 120, padding: '10px 14px', background: 'rgba(0,0,0,0.3)', border: `2px solid ${form.id4.length===4 ? '#FFB74D' : 'rgba(255,255,255,0.15)'}`, borderRadius: 8, color: '#fff', fontSize: 20, fontWeight: 900, letterSpacing: '0.3em', textAlign: 'center', outline: 'none', fontFamily: 'monospace' }}
                    />
                    {form.mrn && (
                      <div style={{ marginTop: 8, fontSize: 12, fontWeight: 800, color: '#FFB74D' }}>
                        ✅ MRN assigned: <span style={{ fontFamily: 'monospace', letterSpacing: '0.08em' }}>{form.mrn}</span>
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: '#546E7A', marginTop: 8 }}>
                      ℹ️ ID4 fallback -- used when patient declines to provide Social Security Number
                    </div>
                  </div>
                )}
              </div>

            </div>
          )}

          {/* STEP 1 -- Accident Details */}
          {step === 1 && (
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.textPrimary, marginBottom: 4 }}>Accident Details</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 20 }}>Florida PIP: treatment must begin within 14 days of accident for coverage eligibility.</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Input label="Date of Accident *" type="date" value={form.accidentDate} onChange={e => u("accidentDate", e.target.value)} />
                <Input label="Time of Accident" type="time" value={form.accidentTime} onChange={e => u("accidentTime", e.target.value)} />
                <Select label="Accident Type *" value={form.accidentType} onChange={e => u("accidentType", e.target.value)}>
                  {accidentTypes.map(t => <option key={t}>{t}</option>)}
                </Select>
                <Select label="County (FL) *" value={form.county} onChange={e => u("county", e.target.value)}>
                  {["Manatee", "Sarasota", "Hillsborough", "Pinellas", "Miami-Dade", "Broward", "Palm Beach", "Orange", "Duval", "Other"].map(c => <option key={c}>{c}</option>)}
                </Select>
                <div style={{ gridColumn: "span 2" }}>
                  <label style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 5 }}>Accident Description *</label>
                  <textarea value={form.accidentDesc} onChange={e => u("accidentDesc", e.target.value)} placeholder="Brief description of accident mechanism, location, vehicle positions, injuries reported at scene..." style={{ width: "100%", height: 100, padding: "10px 13px", background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, color: C.textPrimary, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit", outline: "none" }} />
                </div>
              </div>
              {/* PIP 14-day alert */}
              {form.accidentDate && (() => {
                const acc = new Date(form.accidentDate);
                const today = new Date("2026-03-11");
                const days = Math.floor((today - acc) / 86400000);
                const remaining = 14 - days;
                return (
                  <div style={{ marginTop: 14, padding: "10px 14px", background: remaining <= 3 ? C.redBg : remaining <= 7 ? C.amberBg : C.greenBg, border: `1px solid ${remaining <= 3 ? C.red : remaining <= 7 ? C.amber : C.green}33`, borderRadius: 8, fontSize: 12, color: remaining <= 3 ? C.red : remaining <= 7 ? C.amber : C.green, fontWeight: 700 }}>
                    {remaining > 0 ? `⏳ ${remaining} days remaining in PIP 14-day treatment window` : "⚠️ PIP 14-day window has expired -- EMC certification required"}
                  </div>
                );
              })()}
            </div>
          )}

          {/* STEP 2 -- Insurance */}
          {step === 2 && (
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.textPrimary, marginBottom: 20 }}>Insurance Information</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Select label="Insurance Carrier *" value={form.insurance} onChange={e => u("insurance", e.target.value)}>
                  <option value="">Select carrier...</option>
                  {insurers.map(i => <option key={i}>{i}</option>)}
                </Select>
                <Input label="Policy Number *" value={form.policy} onChange={e => u("policy", e.target.value)} placeholder="Policy #" />
                <Input label="Adjuster Name" value={form.adjuster} onChange={e => u("adjuster", e.target.value)} placeholder="Claims adjuster name" />
                <Input label="Adjuster Phone" value={form.adjusterPhone} onChange={e => u("adjusterPhone", e.target.value)} placeholder="(___) ___-____" />
                <Select label="PIP Coverage Limit *" value={form.pipLimit} onChange={e => u("pipLimit", e.target.value)}>
                  <option value="10000">$10,000 (Standard FL PIP)</option>
                  <option value="2500">$2,500 (Non-EMC)</option>
                </Select>
              </div>
              <div style={{ marginTop: 16, padding: "12px 14px", background: C.blueGlow, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.blueBright, marginBottom: 6 }}>Florida PIP Coverage Rules</div>
                <div style={{ fontSize: 11, color: C.textSecond, lineHeight: 1.7 }}>
                  • $10,000 available if physician certifies <strong style={{ color: C.textPrimary }}>Emergency Medical Condition (EMC)</strong><br />
                  • $2,500 cap if EMC is <strong style={{ color: C.amber }}>not certified</strong><br />
                  • EMC must be certified by MD, DO, or ARNP -- within first visit<br />
                  • PIP pays 80% of medical bills . 60% of lost wages
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 -- Attorney & Physician */}
          {step === 3 && (
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.textPrimary, marginBottom: 20 }}>Attorney, Physician &amp; Case Track</div>

              {/* -- CASE TRACK -- */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Case Track . Attorney-Confirmed</div>
                <div style={{ display: "flex", gap: 12 }}>
                  {["PIP Only", "BI Litigation"].map(t => (
                    <button key={t} onClick={() => u("track", t)} style={{
                      flex: 1, padding: "14px 12px", borderRadius: 10, cursor: "pointer", border: `2px solid ${form.track === t ? (t === "BI Litigation" ? "#7B1FA2" : C.blue) : C.border}`,
                      background: form.track === t ? (t === "BI Litigation" ? "#7B1FA211" : C.blueGlow) : C.bgElevated,
                      color: form.track === t ? (t === "BI Litigation" ? "#CE93D8" : C.blueBright) : C.textMuted,
                      fontWeight: 800, fontSize: 13, textAlign: "left", transition: "all 0.2s"
                    }}>
                      <div>{t === "PIP Only" ? "📋 PIP Only" : "⚖ BI Litigation"}</div>
                      <div style={{ fontSize: 10, fontWeight: 500, marginTop: 4, opacity: 0.8 }}>
                        {t === "PIP Only" ? "Standard billing . Simple discharge . CMS-1500 to insurer" : "Full defensive documentation . FCE . MMI impairment rating"}
                      </div>
                    </button>
                  ))}
                </div>
                {form.track === "BI Litigation" && (
                  <div style={{ marginTop: 10, padding: "10px 14px", background: "#7B1FA211", border: "1px solid #7B1FA233", borderRadius: 8, fontSize: 11, color: "#CE93D8" }}>
                    ⚖ BI track activates: Waddell’s Signs . FCE trigger . Causation documentation . MMI impairment rating . Litigation package at MMI
                  </div>
                )}
                {form.track === "PIP Only" && (
                  <div style={{ marginTop: 10, padding: "10px 14px", background: C.blueGlow, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11, color: C.textSecond }}>
                    📋 PIP track: Standard SOAP . ROM functional only . CMS-1500 billing . Plain clinical file download . Simple discharge
                  </div>
                )}
              </div>

              {/* -- VISIT MODE -- */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Default Visit Mode</div>
                <div style={{ display: "flex", gap: 12 }}>
                  {["In-Person", "Telehealth"].map(m => (
                    <button key={m} onClick={() => u("visitMode", m)} style={{
                      flex: 1, padding: "12px", borderRadius: 10, cursor: "pointer",
                      border: `2px solid ${form.visitMode === m ? C.green : C.border}`,
                      background: form.visitMode === m ? C.greenBg : C.bgElevated,
                      color: form.visitMode === m ? C.green : C.textMuted,
                      fontWeight: 700, fontSize: 12, transition: "all 0.2s"
                    }}>
                      {m === "In-Person" ? "🏥 In-Person" : "💻 Telehealth"}
                    </button>
                  ))}
                </div>
                {form.visitMode === "Telehealth" && (
                  <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>

                    {/* Launch Doxy.me Button */}
                    <div style={{ padding: "14px 16px", background: "linear-gradient(135deg, #0d2d4a 0%, #0a3d62 100%)", borderRadius: 10, border: "1px solid #1565C0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 800, color: "#64B5F6", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 3 }}>HIPAA-Compliant Telehealth Platform</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#E3F2FD" }}>doxy.me . RenuviaMD™ Room</div>
                        <div style={{ fontSize: 11, color: "#90CAF9", marginTop: 2 }}>doxy.me/renuviamd . Synchronous audio + video</div>
                      </div>
                      <a href="https://doxy.me/renuviamd" target="_blank" rel="noopener noreferrer" style={{
                        display: "inline-flex", alignItems: "center", gap: 8,
                        padding: "10px 20px", borderRadius: 8, cursor: "pointer",
                        background: "#1565C0", border: "none", color: "#fff",
                        fontWeight: 800, fontSize: 12, letterSpacing: "0.06em",
                        textDecoration: "none", whiteSpace: "nowrap",
                        boxShadow: "0 0 16px rgba(21,101,192,0.5)"
                      }}>
                        ▶ LAUNCH VISIT ROOM
                      </a>
                    </div>

                    {/* Telehealth Documentation Fields */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Platform Confirmed</div>
                        <div style={{ padding: "10px 12px", background: C.bgElevated, borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, color: C.textSecond, fontWeight: 600 }}>
                          Doxy.me -- HIPAA Compliant ✓
                        </div>
                      </div>
                      <Input label="Patient City, FL *" value={form.teleLocation} onChange={e => u("teleLocation", e.target.value)} placeholder="e.g., Miami, FL" />
                    </div>

                    {/* Who is Present */}
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>Who Is Present at Patient Location</div>
                      <div style={{ display: "flex", gap: 10 }}>
                        {["Patient Only", "Patient + Spouse/Companion", "Patient + MA"].map(opt => (
                          <button key={opt} onClick={() => u("telePresent", opt)} style={{
                            flex: 1, padding: "9px 8px", borderRadius: 8, cursor: "pointer", fontSize: 11, fontWeight: 700,
                            border: `2px solid ${form.telePresent === opt ? C.blue : C.border}`,
                            background: form.telePresent === opt ? "rgba(21,101,192,0.15)" : C.bgElevated,
                            color: form.telePresent === opt ? "#64B5F6" : C.textMuted, transition: "all 0.2s"
                          }}>{opt}</button>
                        ))}
                      </div>
                    </div>

                    {/* A/V Confirmed + Consent */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: C.bgElevated, borderRadius: 8, border: `1px solid ${C.border}` }}>
                        <input type="checkbox" id="teleAV" checked={form.teleAV} onChange={e => u("teleAV", e.target.checked)} style={{ width: 14, height: 14, cursor: "pointer", accentColor: C.green }} />
                        <label htmlFor="teleAV" style={{ fontSize: 11, color: C.textSecond, cursor: "pointer", fontWeight: 600 }}>
                          ✓ Synchronous audio and video confirmed . Both physician and patient connected via Doxy.me
                        </label>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: C.bgElevated, borderRadius: 8, border: `1px solid ${form.teleConsent ? C.green : C.border}` }}>
                        <input type="checkbox" id="teleConsent" checked={form.teleConsent} onChange={e => u("teleConsent", e.target.checked)} style={{ width: 14, height: 14, cursor: "pointer", accentColor: C.green }} />
                        <label htmlFor="teleConsent" style={{ fontSize: 11, color: C.textSecond, cursor: "pointer", fontWeight: 600 }}>
                          ✓ Florida Telehealth Informed Consent on file (FL §456.47) . Patient advised of rights, limitations, and right to in-person care
                        </label>
                      </div>
                    </div>

                  </div>
                )}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Select label="Attorney Representation" value={form.attorney} onChange={e => u("attorney", e.target.value)}>
                  <option value="Unrepresented">Unrepresented</option>
                  {ATTORNEYS.map(a => <option key={a.name} value={a.name}>{a.name}</option>)}
                  <option value="Other">Other / New Firm</option>
                </Select>
                {form.attorney === "Other" && (
                  <Input label="Attorney Firm Name" value={form.attorneyFirm} onChange={e => u("attorneyFirm", e.target.value)} placeholder="Firm name" />
                )}
                <Select label="Treating Physician *" value={form.physician} onChange={e => u("physician", e.target.value)}>
                  <option>Dr. A. Falcon, MD</option>
                  <option>Dr. J. Martinez, DO</option>
                  <option>Dr. S. Patel, MD</option>
                </Select>
                <Input label="Referred By" value={form.referredBy} onChange={e => u("referredBy", e.target.value)} placeholder="Referral source" />
              </div>
              {form.attorney !== "Unrepresented" && form.track === "PIP Only" && (
                <div style={{ marginTop: 14, padding: "10px 14px", background: C.blueGlow, border: `1px solid ${C.blue}33`, borderRadius: 8, fontSize: 11, color: C.blueBright, fontWeight: 600 }}>
                  📋 PIP Only: Attorney can download plain clinical file at any time . No purchase required . Included in clinic subscription
                </div>
              )}
              {form.attorney !== "Unrepresented" && form.track === "BI Litigation" && (
                <div style={{ marginTop: 14, padding: "10px 14px", background: "#7B1FA211", border: "1px solid #7B1FA233", borderRadius: 8, fontSize: 11, color: "#CE93D8", fontWeight: 600 }}>
                  ⚖ BI Litigation: Attorney portal access . Litigation package unlocks at MMI . Included in clinic subscription
                </div>
              )}
            </div>
          )}

          {/* STEP 4 -- Review */}
          {step === 4 && (
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.textPrimary, marginBottom: 20 }}>Review &amp; Create Case</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                {[
                  { section: "Patient", fields: [["Name", `${form.firstName || "Carlos"} ${form.lastName || "Reyes"}`], ["DOB", form.dob || "1985-04-12"], ["Phone", form.phone || "(813) 555-0192"], ["City", form.city || "Ellenton, FL"], ["MRN", form.mrn || "(enter SS4 or ID4 in Step 1)"], ["ID Type", form.mrnType === "id4" ? "Gov't ID Last 4 (ID4)" : "Social Security Last 4 (SS4)"]] },
                  { section: "Accident", fields: [["Date", form.accidentDate || "Mar 01, 2026"], ["Type", form.accidentType], ["County", form.county], ["Description", form.accidentDesc ? form.accidentDesc.substring(0, 40) + "..." : "Rear-end MVA on US-301..."]] },
                  { section: "Insurance", fields: [["Carrier", form.insurance || "State Farm"], ["Policy #", form.policy || "SF-XXXX"], ["PIP Limit", `$${parseInt(form.pipLimit).toLocaleString()}`], ["Adjuster", form.adjuster || "TBD"]] },
                  { section: "Assignment", fields: [["Physician", form.physician], ["Attorney", form.attorney], ["Track", form.track], ["Visit Mode", form.visitMode], ["Case ID", "PIP-2026-0089 (auto)"]] },
                ].map(({ section, fields }) => (
                  <div key={section} style={{ background: C.bgElevated, borderRadius: 10, padding: 16 }}>
                    <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.12em", color: C.blue, textTransform: "uppercase", marginBottom: 10 }}>{section}</div>
                    {fields.map(([k, v]) => (
                      <div key={k} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 11, color: C.textMuted, minWidth: 80 }}>{k}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: C.textPrimary }}>{v}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {/* EMC alert */}
              <div style={{ padding: "12px 14px", background: C.amberBg, border: `1px solid ${C.amber}33`, borderRadius: 8, fontSize: 12, color: C.amber, fontWeight: 600, marginBottom: 14 }}>
                ⚠️ EMC certification required at first clinical visit . FL §627.736 . Determines $10,000 vs $2,500 PIP benefit
              </div>
              {/* AI note */}
              <div style={{ padding: "12px 14px", background: C.blueGlow, border: `1px solid ${C.blue}33`, borderRadius: 8, fontSize: 12, color: C.textSecond }}>
                🤖 <strong style={{ color: C.blueBright }}>Claude AI</strong> will generate the initial clinical intake note after the first visit is entered. Physician review and signature required before locking.
              </div>
            </div>
          )}

          {/* Navigation */}
          <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Btn onClick={() => step > 0 && setStep(s => s - 1)} variant="outline" disabled={step === 0}>← Back</Btn>
            <span style={{ fontSize: 10, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>Auto-saved</span>
            {step < steps.length - 1
              ? (
                <div>
                  {showErrors && hasErrors && (
                    <div style={{ marginBottom: 8 }}>
                      {Object.values(currentErrors).filter(e => e).map((e,i) => (
                        <div key={i} style={{ fontSize: 10, color: '#EF5350', fontWeight: 700, marginBottom: 2 }}>⚠ {e}</div>
                      ))}
                    </div>
                  )}
                  <Btn onClick={tryNext} variant="bright">Next →</Btn>
                </div>
              )
              : <Btn onClick={() => { onCaseCreated(); onNav("patients-cases"); }} variant="success" style={{ fontSize: 12 }}>✓ Create Case</Btn>
            }
          </div>
        </Card>
      </div>
    </div>
  );
};

// --- ATTORNEYS SCREEN ----------------------------------------------------------

// ── SUPERBILL — Auto-populated from visit data ──────────────────────────────
const SuperbillViewer = ({ caseData, visitData, onClose }) => {
  if (!caseData) return null;
  const c = caseData;
  const v = visitData || {};

  const today = new Date().toLocaleDateString('en-US', { month:'2-digit', day:'2-digit', year:'numeric' });
  const diagCodes = v.icd || c.icd10 || ['M54.5'];
  const cptCodes = v.cpt || c.cpt || ['99203'];
  const ptFreq = v.ptFreq || '3x/week';
  const ptDur = v.ptDuration || '4 weeks';

  // Auto modifier 25: when MD E/M and PT services on same day
  const EM_CODES = ['99203', '99204', '99213', '99214'];
  const PT_CODES = ['97010', '97014', '97032', '97035', '97110', '97140', '97530', '97112'];
  const hasEM = cptCodes.some(c => EM_CODES.includes(c));
  const hasPT = cptCodes.some(c => PT_CODES.includes(c));
  const sameDayMDPT = hasEM && hasPT;

  const CPT_FEES = {
    '99203': 175, '99204': 250, '99213': 110, '99214': 165,
    '97010': 15, '97014': 30, '97032': 35, '97035': 30,
    '97110': 45, '97140': 45, '97530': 45, '97112': 45,
    '72050': 85, '72070': 80, '72100': 75, '73030': 80, '73560': 75, '73510': 80,
  };

  const totalCharges = cptCodes.reduce((sum, code) => sum + (CPT_FEES[code] || 75), 0);

  const handlePrint = () => window.print();

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', zIndex:500, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:20, overflow:'auto' }}>
      <div style={{ background:'#fff', width:720, borderRadius:8, overflow:'hidden', boxShadow:'0 4px 40px rgba(0,0,0,0.5)' }}>

        {/* Header bar */}
        <div style={{ background:'#1565C0', padding:'8px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, zIndex:1 }}>
          <span style={{ color:'#fff', fontSize:12, fontWeight:800 }}>SUPERBILL — {c.lastName || c.patient}, {c.firstName || ''}</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={handlePrint} style={{ background:'#fff', border:'none', borderRadius:4, padding:'4px 12px', cursor:'pointer', fontSize:10, fontWeight:700 }}>🖨 Print</button>
            <button onClick={onClose} style={{ background:'#EF5350', border:'none', borderRadius:4, padding:'4px 12px', cursor:'pointer', fontSize:10, fontWeight:700, color:'#fff' }}>Close</button>
          </div>
        </div>

        <div style={{ padding:'20px 24px', color:'#000', fontSize:11 }}>

          {/* Facility Header */}
          <div style={{ textAlign:'center', marginBottom:16, paddingBottom:12, borderBottom:'2px solid #1565C0' }}>
            <div style={{ fontSize:16, fontWeight:900, color:'#1565C0' }}>SUPERBILL / ENCOUNTER FORM</div>
            <div style={{ fontSize:11, fontWeight:700, marginTop:4 }}>Wellness Healthcare Clinic Corp</div>
            <div style={{ fontSize:10, color:'#666' }}>8180 NW 36th St, Suite 213 · Doral, FL 33166</div>
            <div style={{ fontSize:10, color:'#666' }}>Ph: (305) 994-9500 · Fax: (786) 441-2179</div>
          </div>

          {/* Patient + Provider */}
          <div style={{ display:'flex', gap:20, marginBottom:16 }}>
            <div style={{ flex:1, padding:12, background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6 }}>
              <div style={{ fontSize:9, fontWeight:800, color:'#1565C0', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:6 }}>Patient Information</div>
              <div style={{ display:'grid', gridTemplateColumns:'90px 1fr', gap:'3px 8px', fontSize:10 }}>
                <span style={{ color:'#666' }}>Name:</span><span style={{ fontWeight:700 }}>{c.lastName || c.patient}, {c.firstName || ''}</span>
                <span style={{ color:'#666' }}>DOB:</span><span>{c.dob || ''}</span>
                <span style={{ color:'#666' }}>Sex:</span><span>{c.sex || ''}</span>
                <span style={{ color:'#666' }}>Phone:</span><span>{c.phone || ''}</span>
                <span style={{ color:'#666' }}>Insurance:</span><span style={{ fontWeight:700 }}>{c.insurance || ''}</span>
                <span style={{ color:'#666' }}>Policy #:</span><span>{c.policy || 'N/A'}</span>
                <span style={{ color:'#666' }}>DOI:</span><span>{c.accident || ''}</span>
                <span style={{ color:'#666' }}>DOS:</span><span style={{ fontWeight:700 }}>{v.dos || today}</span>
              </div>
            </div>
            <div style={{ width:260, padding:12, background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6 }}>
              <div style={{ fontSize:9, fontWeight:800, color:'#1565C0', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:6 }}>Billing Provider</div>
              <div style={{ fontSize:10, lineHeight:1.6 }}>
                <div style={{ fontWeight:700 }}>Wellness Healthcare Clinic Corp</div>
                <div>8180 NW 36th St, Suite 213</div>
                <div>Doral, FL 33166</div>
                <div>Tax ID: XX-XXXXXXX</div>
                <div style={{ marginTop:6, borderTop:'1px solid #dee2e6', paddingTop:6 }}>
                  <div style={{ fontSize:9, fontWeight:800, color:'#666', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:3 }}>Rendering Provider</div>
                  <div style={{ fontWeight:700 }}>Dr. Armando Falcon, MD</div>
                  <div>NPI: 1447295126 · FL #ME 84789</div>
                </div>
                <div style={{ marginTop:4, fontSize:9, color:'#666' }}>Case: {c.id || ''}</div>
              </div>
            </div>
          </div>

          {/* ICD-10 Diagnosis Codes */}
          <div style={{ marginBottom:16, padding:12, border:'1px solid #dee2e6', borderRadius:6 }}>
            <div style={{ fontSize:9, fontWeight:800, color:'#1565C0', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:8 }}>ICD-10 Diagnosis Codes</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              {diagCodes.map((code, i) => (
                <span key={i} style={{ fontSize:10, padding:'3px 8px', background:'#e3f2fd', border:'1px solid #90caf9', borderRadius:4, fontWeight:700, fontFamily:'monospace' }}>
                  {String.fromCharCode(65 + i)}. {code}
                </span>
              ))}
            </div>
          </div>

          {/* CPT Service Lines */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:9, fontWeight:800, color:'#1565C0', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:8 }}>CPT Procedure Codes</div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:10 }}>
              <thead>
                <tr style={{ background:'#e8edf2' }}>
                  <th style={{ padding:'6px 8px', textAlign:'left', fontSize:8, fontWeight:800, borderBottom:'2px solid #999' }}>CPT</th>
                  <th style={{ padding:'6px 8px', textAlign:'left', fontSize:8, fontWeight:800, borderBottom:'2px solid #999' }}>Description</th>
                  <th style={{ padding:'6px 8px', textAlign:'center', fontSize:8, fontWeight:800, borderBottom:'2px solid #999' }}>MOD</th>
                  <th style={{ padding:'6px 8px', textAlign:'center', fontSize:8, fontWeight:800, borderBottom:'2px solid #999' }}>DX PTR</th>
                  <th style={{ padding:'6px 8px', textAlign:'center', fontSize:8, fontWeight:800, borderBottom:'2px solid #999' }}>UNITS</th>
                  <th style={{ padding:'6px 8px', textAlign:'right', fontSize:8, fontWeight:800, borderBottom:'2px solid #999' }}>CHARGE</th>
                </tr>
              </thead>
              <tbody>
                {cptCodes.map((code, i) => {
                  const desc = {
                    '99203':'New Patient - Moderate', '99204':'New Patient - Mod/High', '99213':'Established - Low', '99214':'Established - Moderate',
                    '97010':'Hot/Cold Packs', '97014':'E-Stim (unattended)', '97032':'E-Stim (attended)', '97035':'Ultrasound',
                    '97110':'Therapeutic Exercise', '97140':'Manual Therapy', '97530':'Therapeutic Activities', '97112':'Neuromuscular Re-ed',
                    '72050':'Cervical X-Ray 3v', '72070':'Thoracic X-Ray', '72100':'Lumbar X-Ray 2v',
                    '73030':'Shoulder X-Ray', '73560':'Knee X-Ray', '73510':'Hip X-Ray',
                  }[code] || code;
                  const isEM = EM_CODES.includes(code);
                  const mod25 = isEM && sameDayMDPT ? '25' : '';
                  return (
                    <tr key={i} style={{ borderBottom:'1px solid #dee2e6' }}>
                      <td style={{ padding:'5px 8px', fontFamily:'monospace', fontWeight:700 }}>{code}</td>
                      <td style={{ padding:'5px 8px' }}>{desc}</td>
                      <td style={{ padding:'5px 8px', textAlign:'center', fontWeight: mod25 ? 700 : 400, color: mod25 ? '#1565C0' : '#666' }}>{mod25}</td>
                      <td style={{ padding:'5px 8px', textAlign:'center' }}>A</td>
                      <td style={{ padding:'5px 8px', textAlign:'center' }}>1</td>
                      <td style={{ padding:'5px 8px', textAlign:'right', fontWeight:700 }}>${(CPT_FEES[code] || 75).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop:'2px solid #000' }}>
                  <td colSpan={5} style={{ padding:'8px', textAlign:'right', fontWeight:800, fontSize:11 }}>TOTAL CHARGES:</td>
                  <td style={{ padding:'8px', textAlign:'right', fontWeight:900, fontSize:14, color:'#1565C0' }}>${totalCharges.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Modifier 25 Alert */}
          {sameDayMDPT && (
            <div style={{ marginBottom:16, padding:10, background:'#e3f2fd', border:'1px solid #90caf9', borderRadius:6, display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:14 }}>⚕️</span>
              <div style={{ fontSize:10, color:'#1565C0', fontWeight:700 }}>
                Modifier 25 auto-applied — MD E/M and PT services on same date of service.
                <span style={{ fontWeight:400, color:'#666', marginLeft:4 }}>Significant, separately identifiable E/M service (FL PIP).</span>
              </div>
            </div>
          )}

          {/* Treatment Plan */}
          <div style={{ marginBottom:16, padding:12, background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6 }}>
            <div style={{ fontSize:9, fontWeight:800, color:'#1565C0', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:6 }}>Treatment Plan</div>
            <div style={{ display:'grid', gridTemplateColumns:'120px 1fr', gap:'3px 8px', fontSize:10 }}>
              <span style={{ color:'#666' }}>PT Frequency:</span><span style={{ fontWeight:700 }}>{ptFreq}</span>
              <span style={{ color:'#666' }}>Duration:</span><span style={{ fontWeight:700 }}>{ptDur}</span>
              <span style={{ color:'#666' }}>Diagnosis:</span><span>{c.diagnosis || diagCodes.join(', ')}</span>
            </div>
          </div>

          {/* Signature */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', paddingTop:12, borderTop:'1px solid #dee2e6' }}>
            <div>
              <div style={{ fontSize:9, color:'#666', marginBottom:2 }}>Rendering Provider:</div>
              <div style={{ fontSize:10, fontWeight:700 }}>Dr. Armando Falcon, MD</div>
              <div style={{ fontSize:9, color:'#666' }}>NPI: 1447295126 · FL License: ME 84789</div>
              <div style={{ marginTop:8, fontSize:9, color:'#666' }}>Billing Provider:</div>
              <div style={{ fontSize:10, fontWeight:700 }}>Wellness Healthcare Clinic Corp</div>
              <div style={{ marginTop:6, borderTop:'1px solid #000', width:200, paddingTop:4, fontSize:8, color:'#666' }}>Physician Signature</div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:10 }}>Date: {v.dos || today}</div>
              <div style={{ fontSize:8, color:'#666', marginTop:4 }}>Generated by PI Master™</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── BILLING CODES — CPT + ICD-10 Management ─────────────────────────────────
const BillingCodesScreen = ({ onBack }) => {
  const [tab, setTab] = React.useState('cpt');
  const [search, setSearch] = React.useState('');

  // ── CPT Codes ──────────────────────────────────────────────────────────────
  const [cptCodes, setCptCodes] = React.useState([
    { code: '99203', desc: 'New Patient - Moderate Complexity', category: 'E/M', fee: 175, active: true },
    { code: '99204', desc: 'New Patient - Moderate/High Complexity', category: 'E/M', fee: 250, active: true },
    { code: '99213', desc: 'Established Patient - Low Complexity', category: 'E/M', fee: 110, active: true },
    { code: '99214', desc: 'Established Patient - Moderate Complexity', category: 'E/M', fee: 165, active: true },
    { code: '97010', desc: 'Hot/Cold Packs', category: 'PT Modality', fee: 15, active: true },
    { code: '97012', desc: 'Mechanical Traction', category: 'PT Modality', fee: 35, active: true },
    { code: '97014', desc: 'Electrical Stimulation (unattended)', category: 'PT Modality', fee: 30, active: true },
    { code: '97032', desc: 'Electrical Stimulation (attended)', category: 'PT Modality', fee: 35, active: true },
    { code: '97035', desc: 'Ultrasound', category: 'PT Modality', fee: 30, active: true },
    { code: '97110', desc: 'Therapeutic Exercise', category: 'PT Exercise', fee: 45, active: true },
    { code: '97112', desc: 'Neuromuscular Re-education', category: 'PT Exercise', fee: 45, active: true },
    { code: '97116', desc: 'Gait Training', category: 'PT Exercise', fee: 45, active: true },
    { code: '97140', desc: 'Manual Therapy', category: 'PT Manual', fee: 45, active: true },
    { code: '97530', desc: 'Therapeutic Activities', category: 'PT Exercise', fee: 45, active: true },
    { code: '72050', desc: 'Cervical Spine X-Ray, 3-view', category: 'Imaging', fee: 85, active: true },
    { code: '72070', desc: 'Thoracic Spine X-Ray', category: 'Imaging', fee: 80, active: true },
    { code: '72100', desc: 'Lumbar Spine X-Ray, 2-view', category: 'Imaging', fee: 75, active: true },
    { code: '73030', desc: 'Shoulder X-Ray', category: 'Imaging', fee: 80, active: true },
    { code: '73560', desc: 'Knee X-Ray', category: 'Imaging', fee: 75, active: true },
    { code: '73510', desc: 'Hip X-Ray', category: 'Imaging', fee: 80, active: true },
  ]);

  // ── ICD-10 Codes ───────────────────────────────────────────────────────────
  const [icdCodes, setIcdCodes] = React.useState([
    { code: 'S13.4XXA', desc: 'Cervical sprain - initial encounter', category: 'Cervical', active: true },
    { code: 'S16.1XXA', desc: 'Strain of muscle/tendon at neck level - initial', category: 'Cervical', active: true },
    { code: 'S23.3XXA', desc: 'Thoracic spine sprain - initial encounter', category: 'Thoracic', active: true },
    { code: 'S33.5XXA', desc: 'Lumbar spine sprain - initial encounter', category: 'Lumbar', active: true },
    { code: 'M54.2', desc: 'Cervicalgia', category: 'Cervical', active: true },
    { code: 'M54.5', desc: 'Low back pain', category: 'Lumbar', active: true },
    { code: 'M54.6', desc: 'Pain in thoracic spine', category: 'Thoracic', active: true },
    { code: 'M62.830', desc: 'Muscle spasm of back', category: 'General', active: true },
    { code: 'G44.1', desc: 'Vascular headache, NEC', category: 'General', active: true },
    { code: 'M79.1', desc: 'Myalgia', category: 'General', active: true },
    { code: 'M25.511', desc: 'Pain in right shoulder', category: 'Extremity', active: true },
    { code: 'M25.512', desc: 'Pain in left shoulder', category: 'Extremity', active: true },
    { code: 'M25.561', desc: 'Pain in right knee', category: 'Extremity', active: true },
    { code: 'M25.562', desc: 'Pain in left knee', category: 'Extremity', active: true },
    { code: 'S39.012A', desc: 'Strain of muscle of lower back - initial', category: 'Lumbar', active: true },
    { code: 'M53.1', desc: 'Cervicobrachial syndrome', category: 'Cervical', active: true },
    { code: 'M54.12', desc: 'Radiculopathy, cervical region', category: 'Cervical', active: true },
    { code: 'M54.16', desc: 'Radiculopathy, lumbar region', category: 'Lumbar', active: true },
    { code: 'G89.29', desc: 'Other chronic pain', category: 'General', active: true },
    { code: 'R29.898', desc: 'Other symptoms involving musculoskeletal system', category: 'General', active: true },
  ]);

  // ── ADD NEW CODE ───────────────────────────────────────────────────────────
  const [showAdd, setShowAdd] = React.useState(false);
  const [newCode, setNewCode] = React.useState({ code: '', desc: '', category: '', fee: '' });

  const handleAdd = () => {
    if (!newCode.code.trim() || !newCode.desc.trim()) return;
    if (tab === 'cpt') {
      setCptCodes(prev => [...prev, { ...newCode, fee: parseFloat(newCode.fee) || 0, active: true }]);
    } else {
      setIcdCodes(prev => [...prev, { ...newCode, active: true }]);
    }
    setNewCode({ code: '', desc: '', category: '', fee: '' });
    setShowAdd(false);
  };

  const toggleActive = (code, type) => {
    if (type === 'cpt') {
      setCptCodes(prev => prev.map(c => c.code === code ? { ...c, active: !c.active } : c));
    } else {
      setIcdCodes(prev => prev.map(c => c.code === code ? { ...c, active: !c.active } : c));
    }
  };

  const deleteCode = (code, type) => {
    if (type === 'cpt') {
      setCptCodes(prev => prev.filter(c => c.code !== code));
    } else {
      setIcdCodes(prev => prev.filter(c => c.code !== code));
    }
  };

  // ── FILTER ─────────────────────────────────────────────────────────────────
  const codes = tab === 'cpt' ? cptCodes : icdCodes;
  const q = search.toLowerCase().trim();
  const filtered = codes.filter(c =>
    c.code.toLowerCase().includes(q) ||
    c.desc.toLowerCase().includes(q) ||
    (c.category || '').toLowerCase().includes(q)
  );

  // Category groups
  const categories = [...new Set(filtered.map(c => c.category))].sort();

  // ── IN-USE CODES (from CASES mock data) ─────────────────────────────────
  const inUseCPT = React.useMemo(() => {
    const usage = {};
    CASES.forEach(c => {
      (c.cpt || []).forEach(code => {
        if (!usage[code]) usage[code] = { code, patients: [], count: 0 };
        usage[code].patients.push(c.patient || `${c.firstName} ${c.lastName}`);
        usage[code].count++;
      });
    });
    return Object.values(usage).sort((a, b) => b.count - a.count);
  }, []);

  const inUseICD = React.useMemo(() => {
    const usage = {};
    CASES.forEach(c => {
      (c.icd10 || []).forEach(code => {
        if (!usage[code]) usage[code] = { code, patients: [], count: 0 };
        usage[code].patients.push(c.patient || `${c.firstName} ${c.lastName}`);
        usage[code].count++;
      });
    });
    return Object.values(usage).sort((a, b) => b.count - a.count);
  }, []);

  const tabs = [
    { id: 'cpt', label: 'CPT Codes', count: cptCodes.length, icon: '💊' },
    { id: 'icd', label: 'ICD-10 Codes', count: icdCodes.length, icon: '🏷' },
    { id: 'in-use', label: 'In Use', count: inUseCPT.length + inUseICD.length, icon: '📊' },
  ];

  return (
    <div style={{ flex: 1, overflow: 'auto', background: C.bg }}>
      <div style={{ padding: '28px 32px' }}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <BackBtn onBack={onBack} />
          <Label>Administration</Label>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.textPrimary, letterSpacing: '-0.02em' }}>Billing Codes</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3 }}>
            PI Master™ · Manage CPT procedure codes and ICD-10 diagnosis codes
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: `1px solid ${C.border}` }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setSearch(''); }} style={{
              padding: '10px 20px', cursor: 'pointer', background: 'transparent', border: 'none',
              borderBottom: `3px solid ${tab === t.id ? C.blue : 'transparent'}`,
              color: tab === t.id ? C.blueBright : C.textMuted,
              fontWeight: tab === t.id ? 800 : 600, fontSize: 12, marginBottom: -1, fontFamily: 'inherit',
            }}>
              <span style={{ marginRight: 6 }}>{t.icon}</span>
              {t.label} ({t.count})
            </button>
          ))}
        </div>

        {/* Search + Add */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 15, color: C.textMuted, pointerEvents: 'none' }}>🔍</div>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${tab === 'cpt' ? 'CPT' : 'ICD-10'} codes...`}
              style={{ width: '100%', padding: '11px 14px 11px 42px', background: C.bgElevated, border: `2px solid ${search ? C.blue : C.border}`, borderRadius: 10, color: C.textPrimary, fontSize: 12, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }}
            />
          </div>
          <Btn onClick={() => setShowAdd(!showAdd)} variant={showAdd ? 'outline' : 'bright'}>
            {showAdd ? '✕ Cancel' : `＋ Add ${tab === 'cpt' ? 'CPT' : 'ICD-10'}`}
          </Btn>
        </div>

        {/* Add Form */}
        {showAdd && (
          <Card style={{ marginBottom: 16, padding: 16, border: `1px solid ${C.blue}44` }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.blueBright, marginBottom: 12 }}>
              Add New {tab === 'cpt' ? 'CPT Code' : 'ICD-10 Code'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: tab === 'cpt' ? '120px 1fr 150px 100px' : '120px 1fr 150px', gap: 10 }}>
              <Input label="Code *" value={newCode.code} onChange={e => setNewCode(p => ({ ...p, code: e.target.value }))} placeholder={tab === 'cpt' ? '97XXX' : 'M54.X'} />
              <Input label="Description *" value={newCode.desc} onChange={e => setNewCode(p => ({ ...p, desc: e.target.value }))} placeholder="Description" />
              <Input label="Category" value={newCode.category} onChange={e => setNewCode(p => ({ ...p, category: e.target.value }))} placeholder={tab === 'cpt' ? 'E/M, PT Modality...' : 'Cervical, Lumbar...'} />
              {tab === 'cpt' && (
                <Input label="Fee ($)" value={newCode.fee} onChange={e => setNewCode(p => ({ ...p, fee: e.target.value }))} placeholder="0.00" />
              )}
            </div>
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <Btn onClick={handleAdd} variant="success">✓ Add Code</Btn>
            </div>
          </Card>
        )}

        {/* Code List by Category */}
        {categories.map(cat => (
          <div key={cat} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{cat || 'Uncategorized'}</span>
              <div style={{ flex: 1, height: 1, background: C.border }} />
              <span style={{ color: C.textMuted, fontWeight: 400, fontSize: 9 }}>{filtered.filter(c => c.category === cat).length} codes</span>
            </div>
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}`, background: C.bgElevated }}>
                    <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 800, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Code</th>
                    <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 800, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Description</th>
                    {tab === 'cpt' && <th style={{ padding: '8px 14px', textAlign: 'right', fontSize: 9, fontWeight: 800, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Fee</th>}
                    <th style={{ padding: '8px 14px', textAlign: 'center', fontSize: 9, fontWeight: 800, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Status</th>
                    <th style={{ padding: '8px 14px', textAlign: 'center', fontSize: 9, fontWeight: 800, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', width: 80 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.filter(c => c.category === cat).map((c, i) => (
                    <tr key={c.code} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'transparent' : C.bgElevated + '33', opacity: c.active ? 1 : 0.5 }}>
                      <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: C.blueBright, fontWeight: 700, fontSize: 11 }}>{c.code}</td>
                      <td style={{ padding: '10px 14px', color: C.textPrimary }}>{c.desc}</td>
                      {tab === 'cpt' && (
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                          <input
                            type="number"
                            step="0.01"
                            value={c.fee || 0}
                            onChange={e => {
                              const val = parseFloat(e.target.value) || 0;
                              setCptCodes(prev => prev.map(x => x.code === c.code ? { ...x, fee: val } : x));
                            }}
                            style={{ width: 80, padding: '5px 8px', background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 6, color: C.green, fontWeight: 700, fontSize: 12, textAlign: 'right', outline: 'none', fontFamily: 'inherit' }}
                          />
                        </td>
                      )}
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <button onClick={() => toggleActive(c.code, tab)} style={{
                          padding: '3px 10px', borderRadius: 12, cursor: 'pointer', fontSize: 9, fontWeight: 700,
                          background: c.active ? C.greenBg : C.redBg,
                          border: `1px solid ${c.active ? C.green : C.red}33`,
                          color: c.active ? C.green : C.red,
                        }}>
                          {c.active ? '● Active' : '○ Inactive'}
                        </button>
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <button onClick={() => deleteCode(c.code, tab)} style={{
                          padding: '3px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 9, fontWeight: 700,
                          background: 'transparent', border: `1px solid ${C.red}33`, color: C.red,
                        }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        ))}

        {filtered.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: C.textMuted }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, marginBottom: 6 }}>No codes found</div>
            {search && <Btn onClick={() => setSearch('')} variant="outline">Clear Search</Btn>}
          </div>
        )}

        {/* ════════════ IN USE TAB ════════════ */}
        {tab === 'in-use' && (
          <div>
            {/* CPT In Use */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.blueBright, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                💊 CPT Codes In Use
                <span style={{ fontSize: 10, color: C.textMuted, fontWeight: 400 }}>({inUseCPT.length} codes across all cases)</span>
              </div>
              {inUseCPT.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: C.textMuted, border: `1px dashed ${C.border}`, borderRadius: 10 }}>No CPT codes in use yet</div>
              ) : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${C.border}`, background: C.bgElevated }}>
                        <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 800, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>CPT Code</th>
                        <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 800, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Description</th>
                        <th style={{ padding: '8px 14px', textAlign: 'center', fontSize: 9, fontWeight: 800, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Times Used</th>
                        <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 800, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Patients</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inUseCPT.map((u, i) => {
                        const cptInfo = cptCodes.find(c => c.code === u.code);
                        return (
                          <tr key={u.code} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'transparent' : C.bgElevated + '33' }}>
                            <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: C.blueBright, fontWeight: 700 }}>{u.code}</td>
                            <td style={{ padding: '10px 14px', color: C.textPrimary }}>{cptInfo?.desc || u.code}</td>
                            <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 800, color: C.green }}>{u.count}</td>
                            <td style={{ padding: '10px 14px', color: C.textMuted, fontSize: 10 }}>{[...new Set(u.patients)].join(', ')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Card>
              )}
            </div>

            {/* ICD-10 In Use */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.green, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                🏷 ICD-10 Codes In Use
                <span style={{ fontSize: 10, color: C.textMuted, fontWeight: 400 }}>({inUseICD.length} codes across all cases)</span>
              </div>
              {inUseICD.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: C.textMuted, border: `1px dashed ${C.border}`, borderRadius: 10 }}>No ICD-10 codes in use yet</div>
              ) : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${C.border}`, background: C.bgElevated }}>
                        <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 800, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>ICD-10</th>
                        <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 800, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Description</th>
                        <th style={{ padding: '8px 14px', textAlign: 'center', fontSize: 9, fontWeight: 800, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Times Used</th>
                        <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 9, fontWeight: 800, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Patients</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inUseICD.map((u, i) => {
                        const icdInfo = icdCodes.find(c => c.code === u.code);
                        return (
                          <tr key={u.code} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'transparent' : C.bgElevated + '33' }}>
                            <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: C.green, fontWeight: 700 }}>{u.code}</td>
                            <td style={{ padding: '10px 14px', color: C.textPrimary }}>{icdInfo?.desc || u.code}</td>
                            <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 800, color: C.blueBright }}>{u.count}</td>
                            <td style={{ padding: '10px 14px', color: C.textMuted, fontSize: 10 }}>{[...new Set(u.patients)].join(', ')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Summary */}
        <div style={{ marginTop: 20, padding: '12px 16px', background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, display: 'flex', gap: 20, fontSize: 11, color: C.textMuted }}>
          <span>Total {tab === 'cpt' ? 'CPT' : 'ICD-10'}: <strong style={{ color: C.textPrimary }}>{codes.length}</strong></span>
          <span>Active: <strong style={{ color: C.green }}>{codes.filter(c => c.active).length}</strong></span>
          <span>Inactive: <strong style={{ color: C.red }}>{codes.filter(c => !c.active).length}</strong></span>
          {tab === 'cpt' && <span>Categories: <strong style={{ color: C.textPrimary }}>{[...new Set(codes.map(c => c.category))].length}</strong></span>}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// CMS-1500 PRE-SUBMISSION AUDIT ENGINE
// Based on NUCC 1500 (02/12) standard
// ═══════════════════════════════════════════════════════════════════════════
const auditCMS1500 = (caseData, allVisits) => {
  const c = caseData || {};
  const hardStops = [];
  const softWarnings = [];
  const boxResults = {};
  let totalChecks = 0;

  const check = (box, label, pass, msg, hard = true) => {
    totalChecks++;
    boxResults[box] = { label, pass, msg: pass ? "OK" : msg };
    if (!pass) { if (hard) hardStops.push(`Box ${box} (${label}): ${msg}`); else softWarnings.push(`Box ${box} (${label}): ${msg}`); }
  };

  const EM_CODES = ["99203","99204","99205","99206","99213","99214"];
  const PT_CODES = ["97010","97012","97014","97032","97035","97110","97112","97116","97140","97530"];
  const CPT_FEES = {"97010":15,"97012":35,"97014":30,"97032":35,"97035":30,"97110":45,"97112":45,"97116":45,"97140":45,"97530":45,"99203":175,"99204":250,"99205":300,"99213":110,"99214":165};

  // ═══ SECTION 1: PATIENT / INSURED BLOCK ════════════════════════════════
  check("1", "Payer Type", true, ""); // Always PIP
  check("1a", "Insured ID", !!(c.policy), "Missing insured ID / policy number", false);
  check("2", "Patient Name", !!(c.firstName || c.patient), "Missing patient name");
  check("3", "DOB/Sex", !!(c.dob), "Missing patient date of birth");
  check("5", "Patient Address", !!(c.zip || c.address), "Missing patient address (street, city, state, ZIP)");
  check("6", "Relationship", true, ""); // Self for PIP
  check("10b", "Auto Accident", true, ""); // Always YES for PIP
  check("11", "Policy/Group", !!(c.policy || c.insurance), "Missing policy or insurance info", false);

  // ═══ SECTION 2: SIGNATURES / DATES / REFERRAL ══════════════════════════
  check("12", "Patient Signature", true, ""); // Electronic consent
  check("13", "Assignment Auth", true, ""); // Auto-accepted
  check("14", "Date of Injury", !!(c.accident), "Missing date of current illness/injury (must match DOA for PIP)");

  // ═══ SECTION 3: DIAGNOSIS ══════════════════════════════════════════════
  check("21", "Diagnosis Block", !!(c.icd10 && c.icd10.length > 0), "Missing ICD-10 diagnosis codes — no diagnoses on claim");

  // ═══ SECTION 4: SERVICE LINES (BOX 24) ═════════════════════════════════
  const hasCPT = c.cpt && c.cpt.length > 0;
  check("24", "Service Lines", hasCPT, "Missing CPT procedure codes — no service lines on claim");

  if (hasCPT) {
    // 24A - DOS
    check("24A", "Dates of Service", !!(c.accident || c.created), "Missing date of service");
    // 24B - POS
    check("24B", "Place of Service", true, ""); // Always 11 (office)
    // 24D - CPT + modifiers
    check("24D", "CPT/HCPCS", true, ""); // Already confirmed CPT exists

    // 24E - Diagnosis pointers
    if (c.icd10 && c.icd10.length > 0) {
      check("24E", "Dx Pointers", true, ""); // Pointers auto-set to A
    } else {
      check("24E", "Dx Pointers", false, "Service lines have no diagnosis pointer — Box 21 is empty");
    }

    // 24F - Charges
    const totalCharges = c.cpt.reduce((sum, code) => sum + (CPT_FEES[code] || 75), 0);
    check("24F", "Charges", totalCharges > 0, "Missing charges on service lines");

    // 24G - Units
    check("24G", "Units", true, ""); // Default 1 unit each
  }

  // ═══ MODIFIER 25 AUDIT (STRICT) ═══════════════════════════════════════
  const hasEM = hasCPT && c.cpt.some(code => EM_CODES.includes(code));
  const hasPTcodes = hasCPT && c.cpt.some(code => PT_CODES.includes(code));
  const mdVisits = (allVisits || []).filter(v => v.role === "md");

  if (hasEM && hasPTcodes) {
    // Modifier 25 IS appropriate — verify E/M is documented
    const hasSignedMD = mdVisits.some(v => v.signed);
    check("MOD25", "Modifier 25", hasSignedMD || mdVisits.length > 0,
      "Modifier 25 applied but no signed MD evaluation note supports separately identifiable E/M service");
  }

  // ═══ SECTION 5: BOTTOM CLAIM BLOCK ════════════════════════════════════
  check("25", "Tax ID", true, ""); // Clinic tax ID hardcoded
  check("26", "Patient Account", !!(c.id), "Missing patient account / case ID", false);
  check("27", "Accept Assignment", true, ""); // Always yes

  // Box 28 - Total charge = sum of 24F
  if (hasCPT) {
    const totalCharges = c.cpt.reduce((sum, code) => sum + (CPT_FEES[code] || 75), 0);
    check("28", "Total Charge", totalCharges > 0, "Total charge is zero");
  }

  check("31", "Physician Signature", true, ""); // Electronic signature
  check("32", "Service Facility", true, ""); // Wellness Healthcare Clinic
  check("32a", "Facility NPI", true, ""); // Hardcoded
  check("33", "Billing Provider", true, ""); // Wellness Healthcare Clinic Corp
  check("33a", "Billing NPI", true, ""); // 1447295126

  // ═══ CROSS-CHECKS ═════════════════════════════════════════════════════

  // Documentation audit
  const unsignedMD = mdVisits.filter(v => !v.signed);
  const ptVisits = (allVisits || []).filter(v => v.role === "pt" || v.role === "pt-daily");
  const unsignedPT = ptVisits.filter(v => !v.signed);

  if (unsignedMD.length > 0) {
    check("DOC-MD", "MD Notes Signed", false, `${unsignedMD.length} unsigned MD note(s) — Visit #${unsignedMD.map(v => v.num).join(", #")}`);
  } else {
    check("DOC-MD", "MD Notes Signed", true, "");
  }

  if (unsignedPT.length > 0) {
    check("DOC-PT", "PT Notes Signed", false, `${unsignedPT.length} unsigned PT note(s)`, false);
  }

  // No MD notes at all
  if (mdVisits.length === 0 && c.visits > 0) {
    check("DOC-EXIST", "MD Notes Exist", false, "No MD evaluation notes found for this case");
  }

  // Duplicate CPT audit
  if (hasCPT) {
    const cptCounts = {};
    c.cpt.forEach(code => { cptCounts[code] = (cptCounts[code] || 0) + 1; });
    Object.entries(cptCounts).forEach(([code, count]) => {
      if (count > 1) check("DUP-" + code, "Duplicate CPT", false, `CPT ${code} appears ${count} times on same claim — verify not duplicated`, false);
    });
  }

  // PIP timeline
  if (c.accident) {
    const days = Math.floor((new Date() - new Date(c.accident)) / 86400000);
    if (days > 365) check("PIP-TIME", "PIP Timeline", false, "Case > 1 year from accident — verify PIP eligibility", false);
  }

  // Charge math
  if (hasCPT) {
    const sumCharges = c.cpt.reduce((s, code) => s + (CPT_FEES[code] || 75), 0);
    check("MATH-28", "Charge Math", sumCharges > 0, "Box 28 total does not match sum of service line charges");
  }

  // ═══ RESULT ════════════════════════════════════════════════════════════
  const status = hardStops.length > 0 ? "hold" : softWarnings.length > 0 ? "review_recommended" : "ready_to_submit";
  const passedChecks = totalChecks - hardStops.length - softWarnings.length;

  return {
    claimId: c.id || "Unknown",
    status,
    hardStops,
    softWarnings,
    boxResults,
    readyToSubmit: hardStops.length === 0,
    totalChecks,
    passedChecks,
  };
};

// ─── CASE DETAIL — FULL PAGE VIEW ──────────────────────────────────────────
// Props: { c, onClose, onStartVisit, onStartPT }
// Full page with tabs and split-screen report viewer
// ───────────────────────────────────────────────────────────────────────────

const PlaceholderScreen = ({ title, icon, sprint, features, onBack }) => (
  <div style={{ flex: 1, overflow: 'auto', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ textAlign: 'center', maxWidth: 480 }}>
      <div style={{ marginBottom: 20 }}><BackBtn onBack={onBack} /></div>
      <div style={{ fontSize: 48, marginBottom: 16 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: C.textPrimary, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 12, color: C.blue, fontWeight: 700, marginBottom: 20, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{sprint}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'left', background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
        {(features || []).map((f, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, fontSize: 12, color: C.textSecond }}>
            <span style={{ color: C.blue }}>→</span> {f}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const OUTCOME_LABELS = {
  RESOLVED:  { label: 'Full Resolution',     color: '#22C97A' },
  IMPROVED:  { label: 'Improved',            color: '#4FC3F7' },
  PARTIAL:   { label: 'Partial Improvement', color: '#FFB74D' },
  UNCHANGED: { label: 'No Improvement',      color: '#EF5350' },
  WORSENED:  { label: 'Declined',            color: '#B71C1C' },
  SURGICAL:  { label: 'Surgical Referral',   color: '#CE93D8' },
  PENDING:   { label: 'Active / Pending',    color: '#90A4AE' },
};

const AIBadge = ({ hasNote }) => hasNote ? (
  <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, fontWeight: 800,
    background: 'rgba(155,109,255,0.15)', border: '1px solid rgba(155,109,255,0.4)',
    color: '#CE93D8', marginLeft: 4 }}>AI</span>
) : null;

const NotePanel = ({ note, loading }) => (
  <div style={{ fontSize: 11, color: C.textPrimary, padding: '14px 16px',
    background: C.bgElevated, borderRadius: 10,
    border: `1px solid ${C.border}`, lineHeight: 1.7 }}>
    {loading ? 'Generating note...' : (
      <pre style={{ fontSize: 11, color: C.textPrimary, lineHeight: 1.7, fontFamily: 'inherit',
        whiteSpace: 'pre-wrap', margin: 0, fontFamily: "Georgia, 'Times New Roman', 'Palatino Linotype', serif" }}>{note || 'No note available'}</pre>
    )}
  </div>
);

const CaseDetail = ({ c, onClose, onStartVisit, onStartPT }) => {

  // ── STATE ────────────────────────────────────────────────────────────────
  const [activeTab,     setActiveTab]     = React.useState('overview');
  const [downloading,   setDownloading]   = React.useState(null);
  const [selectedVisit, setSelectedVisit] = React.useState(null);
  const [aiNotes,       setAiNotes]       = React.useState({});
  const [showSuperbill, setShowSuperbill] = React.useState(false);
  const [showCMS1500, setShowCMS1500] = React.useState(false);
  const [aiLoading,     setAiLoading]     = React.useState(null);
  const [batches, setBatches] = React.useState([
    { id: 1, label: "Batch 1", visits: "1-8", status: "Not Sent", created: "Mar 10, 2026" },
    { id: 2, label: "Batch 2", visits: "9-16", status: "Not Sent", created: "" },
    { id: 3, label: "Batch 3", visits: "17-24", status: "Not Sent", created: "" },
  ]);
  const [selectedBatch, setSelectedBatch] = React.useState(null);
  const [packagePreview, setPackagePreview] = React.useState(null);
  const [auditResult, setAuditResult] = React.useState(null);

  // ── DERIVED ──────────────────────────────────────────────────────────────
  const outcomeInfo = OUTCOME_LABELS[c.outcome] || OUTCOME_LABELS.PENDING;

  const buildVisits = (cas) => {
    const list = [];
    const accDate = new Date(cas.accident || '2026-03-01');
    let d = new Date(accDate); d.setDate(d.getDate() + 3);
    list.push({ num: 1, type: 'MD Initial', provider: 'Dr. A. Falcon, MD',
      role: 'md', date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      cpt: '99203', signed: true });

    let weekCounter = 0;
    for (let v = 2; v <= cas.visits; v++) {
      d = new Date(d); d.setDate(d.getDate() + (v % 3 === 0 ? 7 : 4));
      const isMD = v % 5 === 0;
      const isPTWeekly = !isMD && v % 7 === 0;
      if (isPTWeekly) {
        weekCounter++;
        list.push({ num: v, type: 'PT Weekly', provider: 'James Rivera, DPT',
          role: 'pt-weekly', date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          cpt: '97110', signed: v < cas.visits, week: weekCounter });
      } else if (isMD) {
        list.push({ num: v, type: 'MD Follow-up', provider: 'Dr. A. Falcon, MD',
          role: 'md', date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          cpt: '99213', signed: v < cas.visits });
      } else {
        list.push({ num: v, type: 'PT Daily', provider: 'James Rivera, DPT',
          role: 'pt-daily', date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
          cpt: '97110', signed: v < cas.visits });
      }
    }
    return list;
  };

  const allVisits = buildVisits(c);
  const mdVisitCount = allVisits.filter(v => v.role === 'md').length;
  const ptDailyCount = allVisits.filter(v => v.role === 'pt-daily').length;
  const ptWeeklyCount = allVisits.filter(v => v.role === 'pt-weekly').length;

  // ── AI NOTE GENERATOR ────────────────────────────────────────────────────
  const generateAINote = async (visit, key) => {
    setAiLoading(key);
    const isMD = visit.role === 'md';
    const isInitial = visit.type === 'MD Initial';

    let prompt = '';
    if (isInitial) {
      prompt = `Generate a professional medical Initial Evaluation note for a Florida PIP case.
Patient: ${c.firstName} ${c.lastName}, DOB ${c.dob}, ${c.sex === 'M' ? 'Male' : 'Female'}
Accident Date: ${c.accident} | Visit Date: ${visit.date}
Diagnosis: ${c.diagnosis}
ICD-10: ${(c.icd10 || []).join(', ')} | CPT: ${(c.cpt || []).join(', ')}
Attending: Dr. A. Falcon, MD | FL #ME 84789

Sections: CHIEF COMPLAINT | HISTORY OF PRESENT ILLNESS | REVIEW OF SYSTEMS | PHYSICAL EXAMINATION | ASSESSMENT | PLAN
Professional medical language. 3-5 sentences per section.`;
    } else if (isMD) {
      prompt = `Generate a professional MD Follow-Up visit note for a Florida PIP case.
Patient: ${c.firstName} ${c.lastName} | Visit #${visit.num} | Date: ${visit.date}
Diagnosis: ${c.diagnosis} | Attending: Dr. A. Falcon, MD | FL #ME 84789

Sections: INTERVAL HISTORY | SUBJECTIVE | OBJECTIVE FINDINGS | ASSESSMENT | PLAN MODIFICATION
Professional medical language. 3-4 sentences per section.`;
    } else if (visit.role === 'pt-weekly') {
      prompt = `Generate a PT Weekly Progress Summary for a Florida PIP case.
Patient: ${c.firstName} ${c.lastName} | Week ${visit.week || '?'} | Date: ${visit.date}
Diagnosis: ${c.diagnosis} | Therapist: James Rivera, DPT

Sections: PAIN TREND | ROM PROGRESS | STRENGTH | ADL FUNCTION | WEEKLY ASSESSMENT | PLAN | MEDICAL NECESSITY STATEMENT
Professional PT language. Include realistic measurements.`;
    } else {
      prompt = `Generate a Physical Therapy Daily Session note for a Florida PIP case.
Patient: ${c.firstName} ${c.lastName} | Session #${visit.num} | Date: ${visit.date}
Diagnosis: ${c.diagnosis} | Therapist: James Rivera, DPT | CPT: ${visit.cpt}

Sections: SUBJECTIVE | OBJECTIVE | MODALITIES APPLIED | FUNCTIONAL PROGRESS | ASSESSMENT | PLAN
Professional PT language. Include realistic ROM values.`;
    }

    // Generate professional note locally from case data
    const icdCodes = c.icd10 || [];
    const cptCodes = c.cpt || [];
    const complaints = (c.chiefComplaints || []).join(", ") || c.diagnosis || "musculoskeletal injury";
    const ptName = `${c.lastName}, ${c.firstName}`;
    const he = c.sex === "M" ? "He" : "She";
    const sep = "─".repeat(55);

    const ICD_D = {"S13.4XXA":"Cervical sprain","S16.1XXA":"Cervical strain","S23.3XXA":"Thoracic sprain","S33.5XXA":"Lumbar sprain","M54.2":"Cervicalgia","M54.5":"Low back pain","M54.6":"Thoracic pain","M62.830":"Muscle spasm","G44.309":"Post-traumatic headache","M25.511":"R shoulder pain","M25.512":"L shoulder pain","M25.561":"R knee pain","M25.562":"L knee pain","M25.551":"R hip pain","M25.552":"L hip pain","M54.12":"Cervical radiculopathy","M54.16":"Lumbar radiculopathy"};
    const CPT_D = {"97010":"Hot/Cold therapy","97014":"E-Stim (unattended)","97032":"E-Stim (attended)","97035":"Ultrasound","97110":"Therapeutic exercise","97112":"Neuromuscular re-ed","97116":"Gait training","97140":"Manual therapy","97530":"Therapeutic activities","97012":"Mechanical traction","99203":"New Patient E/M","99204":"New Patient E/M (mod/high)","99213":"Established E/M","99214":"Established E/M (moderate)"};

    const header = `${"═".repeat(55)}
  WELLNESS HEALTHCARE CLINIC CORP
  8180 NW 36th St, Suite 213 · Doral, FL 33166
  Ph: (305) 994-9500 · Fax: (786) 441-2179
${"═".repeat(55)}`;

    const patientBlock = `${sep}
Patient Information
${sep}
  Patient: ${ptName}
  DOB: ${c.dob} | Sex: ${c.sex === "M" ? "Male" : "Female"}
  Insurance: ${c.insurance || "N/A"} | Policy: ${c.policy || "N/A"}
  Case ID: ${c.id} | DOA: ${c.accident}`;

    const dxBlock = `${sep}
Diagnoses (ICD-10)
${sep}
${icdCodes.map(code => `  ${code} — ${ICD_D[code] || code}`).join("\n")}`;

    const cptBlock = `${sep}
Procedures (CPT)
${sep}
${cptCodes.map(code => `  ${code} — ${CPT_D[code] || code}`).join("\n")}`;

    const sigBlock = `${sep}

Armando Falcon, MD
FL License: ME 84789 | NPI: 1447295126
Wellness Healthcare Clinic Corp
8180 NW 36th St, Suite 213, Doral, FL 33166

Signature: _________________________ Date: ${visit.date}
${sep}
Generated by PI Master — RenuviaMD Network`;

    let note = "";
    if (isInitial) {
      note = `${header}

FLORIDA PIP INITIAL EVALUATION REPORT
Visit #1 | Date: ${visit.date}

${patientBlock}

${sep}
Chief Complaints
${sep}
${(c.chiefComplaints || []).map(cc => `  - ${cc}`).join("\n") || "  - " + complaints}
  Pain Severity: ${c.painScore || 7}/10
  Onset: ${c.painOnset || "Immediate"} post-traumatic

${sep}
History of Present Illness
${sep}
${c.firstName} ${c.lastName} is a ${c.sex === "M" ? "male" : "female"} presenting for initial evaluation following a motor vehicle accident (${c.moi || "MVA"}) on ${c.accident}. ${he} reports ${c.painOnset === "Immediate" ? "immediate" : "gradual"} onset of ${complaints.toLowerCase()} following the collision. Symptoms are persistent and exacerbated by movement, limiting activities of daily living. ADL: Conserved.

${sep}
Physical Examination
${sep}
  General: Alert and oriented x4. Ambulatory with ${parseInt(c.painScore) >= 7 ? "antalgic" : "cautious"} gait.
  Neurological: Grossly intact. DTRs symmetric. Motor 5/5.
  Spine: Paraspinal tenderness. Muscle spasm present. ROM decreased in affected regions.

${dxBlock}

${sep}
Medical Necessity
${sep}
The patient sustained acute traumatic injuries directly related to the motor vehicle accident. Physical therapy is medically necessary to reduce pain, restore function, and prevent chronicity.

${sep}
Treatment Plan
${sep}
  PT Frequency: ${c.pocWeeks ? "3-5x/week" : "3x/week"}
  Duration: ${c.pocWeeks || 4} weeks

${cptBlock}

${sigBlock}`;
    } else if (isMD) {
      note = `${header}

FLORIDA PIP FOLLOW-UP EVALUATION REPORT
Visit #${visit.num} | Date: ${visit.date}

${patientBlock}

${sep}
Interval History
${sep}
Patient returns for follow-up evaluation. ${he} reports gradual improvement in symptoms since last visit. Pain levels have decreased from baseline. ${he} continues to participate in physical therapy as prescribed. ADL: Conserved.

${sep}
Current Complaints
${sep}
${(c.chiefComplaints || []).filter((_, i) => i < 4).map(cc => `  - ${cc} (improving)`).join("\n")}

${sep}
Physical Examination (Focused)
${sep}
  General: Alert and oriented. Ambulatory.
  Spine: Mild residual paraspinal tenderness. Spasm diminished. ROM improving but not yet normalized.

${dxBlock}

${sep}
Medical Necessity for Continued Treatment
${sep}
Continued physical therapy remains medically necessary. The patient demonstrates ongoing improvement but has not reached pre-accident functional baseline. Discontinuation would result in regression.

${cptBlock}

${sep}
Plan
${sep}
  Continue PT at current frequency.
  Reassess in 2-4 weeks.
  Adjust treatment plan as indicated.

${sigBlock}`;
    } else if (visit.role === "pt-weekly") {
      const weekNum = visit.week || Math.ceil(visit.num / 3);
      note = `${header}

PT WEEKLY PROGRESS SUMMARY
Week ${weekNum} | Date: ${visit.date}

${patientBlock}

${dxBlock}

${sep}
Weekly Progress
${sep}
  Pain Trend: ${c.painScore || 7}/10 baseline → improving
  ROM Progress: Improving — measurable gains in affected regions
  Strength: Progressive tolerance to therapeutic exercise
  ADL Status: Conserved
  Functional Progress: Improving
  Overall Status: On Track

${sep}
Modalities Applied This Week
${sep}
${cptCodes.filter(code => !code.startsWith("99")).map(code => `  ${code} — ${CPT_D[code] || code}`).join("\n")}

${sep}
Weekly Assessment
${sep}
Patient demonstrates consistent measurable progress toward functional goals established at evaluation. Physical therapy continues to be medically necessary as patient has not reached pre-accident functional baseline.

${sep}
Medical Necessity for Continued Treatment
${sep}
Continued physical therapy is medically necessary due to:
  - Documented ROM deficits below pre-accident baseline
  - Objective muscle spasm and tenderness on examination
  - Measurable functional limitations in affected regions
  - Week-over-week objective improvement confirming therapeutic benefit

${sep}
Plan for Next Week
${sep}
  Continue treatment protocol. Progress exercise intensity.
  Reassess short-term goals.

${sigBlock}`;
    } else {
      const sessionNum = visit.num;
      const basePain = parseInt(c.painScore) || 7;
      const endPain = Math.max(basePain - 2, 3);
      note = `${header}

PT DAILY SESSION NOTE
Session #${sessionNum} | Date: ${visit.date}
Therapist: James Rivera, DPT

${patientBlock}

${dxBlock}

${sep}
Subjective
${sep}
  Patient reports pain level ${basePain}/10 at start of session.
  Patient compliance: Compliant.
  Barriers to recovery: None reported.

${sep}
Treatment Provided
${sep}
  Modalities Applied:
${cptCodes.filter(code => !code.startsWith("99")).map(code => `    ${code} — ${CPT_D[code] || code}`).join("\n")}

  Therapeutic Exercises:
    Cervical AROM, Lumbar Stabilization, Core Strengthening
    Postural Training, Stretching — Hamstrings

${sep}
Objective / Response to Treatment
${sep}
  Pain at end of session: ${endPain}/10 (improved ${basePain - endPain} points)
  Response to treatment: Tolerated Well
  Functional progress: Improving
  Patient tolerated all modalities and exercises without adverse reaction.

${sep}
Assessment
${sep}
Patient continues to demonstrate functional deficits consistent with injuries sustained in MVA on ${c.accident}. Physical therapy is medically necessary at current frequency to restore pre-accident functional baseline and prevent chronicity. ADL: Conserved. Discharge at this time would result in measurable functional loss.

${sep}
Plan
${sep}
  Continue current treatment protocol per physician order.
  Progress exercise difficulty as tolerated.
  Patient education: home exercise program and posture.
  Next session per schedule.

${sigBlock}`;
    }

    setAiNotes(prev => ({ ...prev, [key]: note }));
    setAiLoading(null);
  };

  // ── PDF GENERATOR ────────────────────────────────────────────────────────
  const generatePDF = (cas, type) => {
    const key = cas.id + type;
    setDownloading(key);
    setTimeout(() => {
      try {
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
        const W = 612, mgL = 50, mgR = 562, lineH = 16;
        const clr = { navy: '#0D47A1', blue: '#1565C0', teal: '#00695C', amber: '#E65100',
                      red: '#B71C1C', green: '#1B5E20', gray: '#455A64', light: '#ECEFF1', border: '#90A4AE' };
        const hex2rgb = h => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
        const setColor = (h, fill = false) => { const [r, g, b] = hex2rgb(h); fill ? doc.setFillColor(r, g, b) : doc.setTextColor(r, g, b); };
        const drawLine = (x1, y1, x2, y2, h = clr.border) => { doc.setDrawColor(...hex2rgb(h)); doc.line(x1, y1, x2, y2); };
        const rect = (x, y, w, h, col) => { doc.setFillColor(...hex2rgb(col)); doc.rect(x, y, w, h, 'F'); };
        const txt = (text, x, _y, col = clr.gray, size = 10, bold = false) => {
          doc.setFontSize(size); doc.setFont('helvetica', bold ? 'bold' : 'normal');
          setColor(col); doc.text(String(text || '--'), x, _y);
        };
        let y = 50;
        const newPage = () => { doc.addPage(); y = 50; drawHeader(false); };
        const checkPage = (n = 30) => { if (y + n > 740) newPage(); };

        const drawHeader = (first = true) => {
          rect(0, 0, W, 56, clr.navy);
          txt('RenuviaMD Gate', mgL, 22, '#FFFFFF', 15, true);
          txt('Wellness Healthcare Clinic Corp  |  8180 NW 36 St, Suite 213  |  Doral, FL 33166  |  305-994-9500', mgL, 36, '#90CAF9', 8);
          const lbl = type === 'summary' ? 'CASE SUMMARY REPORT' : 'FULL CLINICAL CHART';
          txt(first ? lbl : lbl + ' -- continued', mgL, 50, '#FFFFFF', 9, true);
          txt('Generated: ' + new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) + '  |  CONFIDENTIAL', mgR, 50, '#90CAF9', 8);
          y = 75;
        };
        drawHeader(true);

        const section = (title, col = clr.navy) => {
          checkPage(30); y += 8;
          rect(mgL - 2, y, W - mgL * 2 + 4, 16, col);
          txt(title.toUpperCase(), mgL + 4, y + 11, '#FFFFFF', 9, true);
          y += 22;
        };
        const kvGrid = (pairs) => {
          const colW = (W - mgL * 2) / 2;
          pairs.forEach(([lbl, val, vcol], idx) => {
            checkPage(16);
            const x = idx % 2 === 0 ? mgL + 4 : mgL + colW + 4;
            if (idx % 2 === 0 && idx > 0) y += lineH;
            if (idx % 2 === 0) rect(mgL - 2, y - 11, W - mgL * 2 + 4, lineH, '#F5F7FA');
            txt(lbl, x, y, clr.gray, 9);
            txt(val || '--', x + 90, y, vcol || clr.navy, 9, !!vcol);
            if (idx % 2 === 1) y += lineH;
          });
          if (pairs.length % 2 !== 0) y += lineH;
          y += 4;
        };

        // Patient header
        rect(mgL - 2, y, W - mgL * 2 + 4, 36, clr.light);
        drawLine(mgL - 2, y, mgR + 2, y, clr.blue);
        txt(cas.lastName + ', ' + cas.firstName, mgL + 4, y + 13, clr.navy, 13, true);
        txt('DOB: ' + cas.dob + '  |  Sex: ' + (cas.sex === 'M' ? 'Male' : 'Female') + '  |  MRN: ' + cas.mrn + '  |  Phone: ' + cas.phone, mgL + 4, y + 26, clr.gray, 9);
        txt('Case: ' + cas.id + '  |  Status: ' + cas.status, mgR - 80, y + 13, clr.blue, 9, true);
        y += 46;

        section('1 | Accident & Case Information', clr.navy);
        kvGrid([['Case ID', cas.id], ['Status', cas.status], ['Accident Date', cas.accident], ['County', cas.county || 'Miami-Dade'], ['Created', cas.created]]);

        section('2 | Patient Demographics', clr.teal);
        kvGrid([['Last Name', cas.lastName], ['First Name', cas.firstName], ['DOB', cas.dob], ['Sex', cas.sex === 'M' ? 'Male' : 'Female'], ['ZIP', cas.zip], ['Phone', cas.phone], ['MRN', cas.mrn]]);

        section('3 | Insurance & PIP Coverage', clr.blue);
        kvGrid([['Insurance Carrier', cas.insurance], ['Policy Number', cas.policy], ['PIP Benefit Limit', '$10,000'], ['EMC Status', cas.emc]]);

        section('4 | Clinical Information', clr.teal);
        kvGrid([['Attending Physician', cas.physician], ['Total Visits', String(cas.visits)], ['Diagnosis', cas.diagnosis], ['POC Duration', cas.pocWeeks + ' weeks'], ['ICD-10 Codes', (cas.icd10 || []).join(', ')], ['CPT Codes', (cas.cpt || []).join(', ')], ['Outcome', (OUTCOME_LABELS[cas.outcome] || OUTCOME_LABELS.PENDING).label]]);

        section('5 | Billing Summary (PIP 80%)', clr.amber);
        const billed = cas.amtBilled || 3240, paid = cas.amtPaid || 0;
        kvGrid([['Total Billed', '$' + billed.toLocaleString()], ['PIP 80% Expected', '$' + Math.round(billed * 0.8).toLocaleString()], ['Amount Paid', paid > 0 ? '$' + paid.toLocaleString() : 'Pending'], ['Balance', '$' + (billed - paid).toLocaleString()]]);

        if (type === 'full') {
          const visits = buildVisits(cas);
          section('6 | Visit Notes', clr.navy);
          checkPage(40);
          rect(mgL - 2, y, W - mgL * 2 + 4, 14, clr.navy);
          [['#', 0], ['Date', 30], ['Type', 110], ['Provider', 220], ['CPT', 380], ['Status', 440]].forEach(([h, x]) => txt(h, mgL + 4 + x, y + 10, '#FFF', 8, true));
          y += 18;
          visits.forEach((v, i) => {
            checkPage(16);
            if (i % 2 === 0) rect(mgL - 2, y - 11, W - mgL * 2 + 4, 14, '#F5F7FA');
            txt(String(v.num).padStart(2, '0'), mgL + 4, y, clr.gray, 9);
            txt(v.date, mgL + 34, y, clr.gray, 9);
            txt(v.type, mgL + 114, y, clr.navy, 9);
            txt(v.provider, mgL + 224, y, clr.gray, 9);
            txt(v.cpt, mgL + 384, y, clr.teal, 9);
            txt(v.signed ? 'Signed' : 'Draft', mgL + 444, y, v.signed ? clr.green : clr.gray, 9);
            y += lineH;
          });
        }

        // Signature block
        checkPage(70); y += 10;
        drawLine(mgL, y, mgR, y); y += 20;
        txt('Attending Physician Certification', mgL + 4, y, clr.navy, 9, true); y += lineH;
        txt('I certify the information in this ' + (type === 'summary' ? 'case summary' : 'full clinical chart') + ' is accurate and complete.', mgL + 4, y, clr.gray, 9); y += lineH + 10;
        drawLine(mgL + 4, y, mgL + 200, y); drawLine(mgR - 150, y, mgR - 4, y); y += 12;
        txt('Armando Falcon, MD  |  FL #ME 84789', mgL + 4, y, clr.navy, 9);
        txt('Date', mgR - 150, y, clr.gray, 9);

        const total = doc.getNumberOfPages();
        for (let p = 1; p <= total; p++) {
          doc.setPage(p); doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(150, 150, 150);
          doc.text('RenuviaMD Gate  |  CONFIDENTIAL  |  ' + cas.id + '  |  Page ' + p + ' of ' + total, 306, 755, { align: 'center' });
        }
        doc.save((type === 'summary' ? 'CaseSummary_' : 'FullChart_') + cas.id + '_' + cas.lastName + '.pdf');
      } catch (e) { console.error('PDF error:', e); alert('PDF error: ' + e.message); }
      setDownloading(null);
    }, 100);
  };

  // ── VISIT TYPE BADGE ─────────────────────────────────────────────────────
  const visitTypeBadge = (type) => {
    const map = {
      'MD Initial':  { bg: C.blueGlow, color: C.blueBright, border: C.blue },
      'MD Follow-up': { bg: C.blueGlow, color: C.blueBright, border: C.blue },
      'PT Daily':    { bg: '#00695C18', color: '#4DB6AC', border: '#00695C' },
      'PT Weekly':   { bg: '#E6510018', color: '#FFAB91', border: '#E65100' },
    };
    const s = map[type] || map['PT Daily'];
    return (
      <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 5,
        background: s.bg, color: s.color, border: `1px solid ${s.border}44`,
        textTransform: 'uppercase', letterSpacing: '0.06em' }}>{type}</span>
    );
  };

  // ── HELPER: info row ─────────────────────────────────────────────────────
  const infoRow = (k, v, vc = null) => (
    <div key={k} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 11, color: C.textMuted, minWidth: 110, flexShrink: 0 }}>{k}</span>
      <span style={{ fontSize: 11, fontWeight: 600, color: vc || C.textPrimary }}>{v || '--'}</span>
    </div>
  );

  // ── TABS CONFIG ──────────────────────────────────────────────────────────
  const TABS = [
    { id: 'overview',  label: 'Overview' },
    { id: 'visits',    label: 'Visit History (' + allVisits.length + ')' },
    { id: 'billing',   label: 'Billing' },
    { id: 'documents', label: 'Documents' },
  ];

  // ── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div style={{ position: 'fixed', inset: 0, background: C.bg, zIndex: 300, display: 'flex', flexDirection: 'column', fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>

      {/* ══ TOP HEADER ══ */}
      <div style={{ padding: '16px 28px', borderBottom: `1px solid ${C.border}`, background: C.bgCard, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
        <div style={{ flex: 1 }}>
          {/* Back button */}
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 700, padding: 0, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 14 }}>&larr;</span> Back to Patients
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: C.textPrimary }}>{c.lastName}, {c.firstName}</span>
            <span style={{ fontFamily: 'monospace', fontSize: 12, color: C.blue, fontWeight: 700, background: C.blueGlow, padding: '2px 8px', borderRadius: 5 }}>{c.id}</span>
            <StatusBadge status={c.status} small />
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5, background: C.bgElevated, color: C.textSecond, border: `1px solid ${C.border}` }}>{c.insurance}</span>
          </div>

          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: C.textSecond }}>DOB: {c.dob}</span>
            <span style={{ fontSize: 11, color: C.textSecond }}>Phone: {c.phone}</span>
            <span style={{ fontSize: 11, color: C.textMuted }}>{c.sex === 'M' ? 'Male' : 'Female'}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginTop: 20 }}>
          <button onClick={() => onStartVisit && onStartVisit(c)} style={{
            padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 800,
            background: C.blue, border: 'none', color: '#fff', letterSpacing: '0.04em',
          }}>MD Visit</button>
          <button onClick={() => { setActiveTab('visits'); }} style={{
            padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 800,
            background: 'transparent', border: `1px solid #E65100`, color: '#FFAB91', letterSpacing: '0.04em',
          }}>PT Sessions</button>
          <button onClick={() => onStartPT && onStartPT(c)} style={{
            padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 800,
            background: '#E65100', border: 'none', color: '#fff', letterSpacing: '0.04em',
          }}>+ New PT Session</button>
        </div>
      </div>

      {/* ══ TAB BAR ══ */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, background: C.bgElevated, flexShrink: 0, padding: '0 28px' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => { setActiveTab(t.id); setSelectedVisit(null); }} style={{
            padding: '12px 18px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
            fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
            background: activeTab === t.id ? C.bgCard : 'transparent',
            color: activeTab === t.id ? C.blueBright : C.textMuted,
            borderBottom: `2px solid ${activeTab === t.id ? C.blue : 'transparent'}`,
            letterSpacing: '0.02em', transition: 'all 0.15s',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ══ TAB BODY ══ */}
      <div style={{ flex: 1, overflow: 'auto' }}>

        {/* ════ OVERVIEW ════ */}
        {activeTab === 'overview' && (
          <div style={{ padding: '24px 28px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

              {/* Patient Profile */}
              <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', color: C.blue, textTransform: 'uppercase', marginBottom: 10 }}>Patient Profile</div>
                {[['DOB', c.dob], ['Sex', c.sex === 'M' ? 'Male' : 'Female'], ['Phone', c.phone], ['ZIP', c.zip], ['Accident Date', c.accident], ['County', c.county || 'Miami-Dade']].map(([k, v]) => infoRow(k, v))}
              </div>

              {/* Insurance */}
              <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', color: C.blue, textTransform: 'uppercase', marginBottom: 10 }}>Insurance</div>
                {[['Carrier', c.insurance], ['Policy #', c.policy], ['PIP Limit', '$10,000'],
                  ['EMC Status', c.emc]].map(([k, v]) => infoRow(k, v, k === 'EMC Status' ? (v === 'Certified' ? C.green : v === 'Pending' ? C.amber : C.red) : null))}
              </div>

              {/* Clinical Info */}
              <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', color: C.blue, textTransform: 'uppercase', marginBottom: 10 }}>Clinical</div>
                {[['Diagnosis', c.diagnosis], ['ICD-10', (c.icd10 || []).join(' | ')], ['CPT', (c.cpt || []).join(' | ')],
                  ['Physician', c.physician], ['Total Visits', c.visits], ['POC', c.pocWeeks + ' wks']].map(([k, v]) => infoRow(k, v))}
                {c.mmiDate && infoRow('MMI Date', c.mmiDate, C.amber)}
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <span style={{ fontSize: 11, color: C.textMuted, minWidth: 110 }}>Outcome</span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: outcomeInfo.color }}>{outcomeInfo.label}</span>
                </div>
              </div>

              {/* Case Info */}
              <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', color: C.blue, textTransform: 'uppercase', marginBottom: 10 }}>Case</div>
                {[['Case ID', c.id], ['Status', c.status], ['Created', c.created], ['MRN', c.mrn]].map(([k, v]) => infoRow(k, v))}
              </div>
            </div>
          </div>
        )}

        {/* ════ VISIT HISTORY ════ */}
        {activeTab === 'visits' && (
          <div style={{ display: 'flex', height: '100%' }}>
            {/* Left: Timeline */}
            <div style={{
              width: selectedVisit ? '40%' : '100%',
              borderRight: selectedVisit ? `1px solid ${C.border}` : 'none',
              overflow: 'auto', padding: '16px 20px', transition: 'width 0.2s',
            }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 14 }}>
                All Visits | {allVisits.length} total | MD: {mdVisitCount} | PT Daily: {ptDailyCount} | PT Weekly: {ptWeeklyCount}
              </div>

              {allVisits.map(v => {
                const key = v.role + '-' + c.id + '-' + v.num;
                const hasNote = !!aiNotes[key];
                const isSelected = selectedVisit && selectedVisit.key === key;
                return (
                  <div key={key} onClick={() => setSelectedVisit({ ...v, key })}
                    style={{
                      padding: '12px 14px', marginBottom: 6, borderRadius: 10, cursor: 'pointer',
                      background: isSelected ? C.blueGlow : C.bgElevated,
                      border: `1px solid ${isSelected ? C.blue : C.border}`,
                      transition: 'all 0.15s',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: 8, background: C.bgCard,
                          border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: 10, fontWeight: 800, color: C.blue, flexShrink: 0,
                        }}>{String(v.num).padStart(2, '0')}</div>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: C.textPrimary }}>{v.date}</div>
                          <div style={{ fontSize: 10, color: C.textMuted }}>{v.provider}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {visitTypeBadge(v.type)}
                        <span style={{ fontSize: 9, color: v.signed ? C.green : C.amber, fontWeight: 700 }}>
                          {v.signed ? 'Signed' : 'Draft'}
                        </span>
                        <AIBadge hasNote={hasNote} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right: Report Viewer */}
            {selectedVisit && (
              <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px', background: C.bgCard }}>
                {/* Close panel button */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: C.textPrimary }}>{selectedVisit.type} -- Visit #{selectedVisit.num}</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>{selectedVisit.date} | {selectedVisit.provider} | CPT {selectedVisit.cpt}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => {
                      const key = selectedVisit.key;
                      if (!aiNotes[key]) generateAINote(selectedVisit, key);
                    }} disabled={aiLoading === selectedVisit.key}
                      style={{
                        padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 800,
                        background: aiLoading === selectedVisit.key ? C.bgElevated : 'linear-gradient(135deg, #2D7DD2, #0D47A1)',
                        border: `1px solid ${C.blue}`, color: aiLoading === selectedVisit.key ? C.textMuted : '#fff',
                      }}>
                      {aiLoading === selectedVisit.key ? 'Generating...' : aiNotes[selectedVisit.key] ? 'Regenerate' : 'Generate AI Note'}
                    </button>
                    <button onClick={() => setSelectedVisit(null)} style={{
                      padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                      background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted,
                    }}>Close Panel</button>
                  </div>
                </div>

                {/* Note content */}
                {aiNotes[selectedVisit.key] ? (
                  <NotePanel note={aiNotes[selectedVisit.key]} loading={aiLoading === selectedVisit.key} />
                ) : (
                  <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted }}>
                    <div style={{ fontSize: 32, marginBottom: 10 }}>AI</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.textSecond, marginBottom: 4 }}>No note generated yet</div>
                    <div style={{ fontSize: 11 }}>Click "Generate AI Note" to create a clinical note for this visit</div>
                  </div>
                )}

                {/* PDF Download */}
                {aiNotes[selectedVisit.key] && (
                  <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
                    <button onClick={() => {
                      try {
                        const { jsPDF } = window.jspdf;
                        const doc = new jsPDF();
                        doc.setFontSize(10);
                        const lines = doc.splitTextToSize(aiNotes[selectedVisit.key], 180);
                        doc.text(lines, 15, 20);
                        doc.save('Note_' + c.id + '_Visit' + selectedVisit.num + '.pdf');
                      } catch (e) { alert('jsPDF not loaded'); }
                    }} style={{
                      padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                      background: C.bgElevated, border: `1px solid ${C.border}`, color: C.textSecond,
                    }}>Download PDF</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ════ BILLING ════ */}
        {activeTab === 'billing' && (
          <div style={{ padding: 28 }}>

            {/* ── SECTION A: BATCH OVERVIEW ── */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.textPrimary, marginBottom: 4 }}>Insurance Billing Packages</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 18 }}>Bundle visit notes, superbill, and CMS-1500 for insurance submission</div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                {batches.map(batch => {
                  const statusColors = {
                    'Not Sent': { bg: C.amberBg, text: C.amber },
                    'Sent':     { bg: C.bluePale, text: C.blueBright },
                    'Paid':     { bg: C.greenBg, text: C.green },
                    'Denied':   { bg: C.redBg, text: C.red },
                  };
                  const sc = statusColors[batch.status] || statusColors['Not Sent'];
                  return (
                    <div key={batch.id} style={{
                      padding: 18, background: C.bgElevated, borderRadius: 12,
                      border: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 10,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: C.textPrimary }}>{batch.label}</div>
                        <span style={{
                          fontSize: 9, fontWeight: 800, padding: '3px 8px', borderRadius: 6,
                          background: sc.bg, color: sc.text, textTransform: 'uppercase', letterSpacing: '0.06em',
                        }}>{batch.status}</span>
                      </div>
                      <div style={{ fontSize: 11, color: C.textSecond }}>Visits {batch.visits}</div>
                      {batch.created && (
                        <div style={{ fontSize: 10, color: C.textMuted }}>Created: {batch.created}</div>
                      )}

                      {/* Status toggle buttons */}
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                        {['Not Sent', 'Sent', 'Paid', 'Denied'].map(s => (
                          <button key={s} onClick={() => {
                            setBatches(prev => prev.map(b => b.id === batch.id ? { ...b, status: s, created: s !== 'Not Sent' && !b.created ? new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : b.created } : b));
                          }} style={{
                            fontSize: 8, fontWeight: 700, padding: '3px 7px', borderRadius: 4, cursor: 'pointer',
                            background: batch.status === s ? (statusColors[s]?.bg || C.bgCard) : C.bgCard,
                            color: batch.status === s ? (statusColors[s]?.text || C.textMuted) : C.textMuted,
                            border: `1px solid ${batch.status === s ? (statusColors[s]?.text || C.border) + '44' : C.border}`,
                            textTransform: 'uppercase', letterSpacing: '0.05em',
                          }}>{s}</button>
                        ))}
                      </div>

                      {/* Build / View Package button */}
                      <button onClick={() => {
                        const result = auditCMS1500(c, allVisits);
                        setAuditResult(result);
                        if (!result.readyToSubmit) return;
                        setSelectedBatch(batch);
                        const ICD_DESC_MAP = {
                          'S13.4XXA': 'Sprain of ligaments of cervical spine, initial',
                          'S33.5XXA': 'Sprain of ligaments of lumbar spine, initial',
                          'M54.2':   'Cervicalgia',
                          'M54.5':   'Low back pain',
                          'M54.6':   'Pain in thoracic spine',
                          'M50.12':  'Cervical disc disorder at C4-C5, with radiculopathy',
                          'M54.12':  'Radiculopathy, cervical region',
                          'M54.16':  'Radiculopathy, lumbar region',
                          'G44.1':   'Vascular headache, not elsewhere classified',
                          'S23.3XXA':'Sprain of ligaments of thoracic spine, initial',
                          'M25.561': 'Pain in right knee',
                          'M25.512': 'Pain in left shoulder',
                          'M79.1':   'Myalgia',
                          'M62.830': 'Muscle spasm of back',
                          'S16.1XXA':'Strain of muscle/tendon at neck level, initial',
                        };
                        const CPT_DESC_MAP = {
                          '99203': 'Office visit, new patient (Level 3)',
                          '99213': 'Office visit, established patient (Level 3)',
                          '99214': 'Office visit, established patient (Level 4)',
                          '97010': 'Hot/Cold Packs',
                          '97012': 'Mechanical Traction',
                          '97014': 'E-Stim (unattended)',
                          '97032': 'E-Stim (attended)',
                          '97035': 'Ultrasound Therapy',
                          '97110': 'Therapeutic Exercise',
                          '97116': 'Gait Training',
                          '97140': 'Manual Therapy',
                          '97530': 'Therapeutic Activities',
                        };
                        const CPT_CHARGES = {
                          '99203': 150, '99213': 100, '99214': 125,
                          '97010': 25, '97012': 40, '97014': 35, '97032': 45,
                          '97035': 40, '97110': 55, '97116': 55, '97140': 60, '97530': 55,
                        };
                        const rangeNums = batch.visits.split('-').map(Number);
                        const batchVisits = allVisits.filter(v => v.num >= rangeNums[0] && v.num <= rangeNums[1]);
                        const batchPTDaily = batchVisits.filter(v => v.role === 'pt-daily').length;
                        const batchPTWeekly = batchVisits.filter(v => v.role === 'pt-weekly').length;
                        const batchMD = batchVisits.filter(v => v.role === 'md').length;
                        const totalCharges = batchVisits.reduce((sum, v) => sum + (CPT_CHARGES[v.cpt] || 75), 0) + ((c.cpt || []).reduce((s, code) => s + (CPT_CHARGES[code] || 50), 0) * batchVisits.length / 3);
                        const batchAmount = Math.round(totalCharges);
                        const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

                        const icdLines = (c.icd10 || []).map(code => `  ${code} — ${ICD_DESC_MAP[code] || code}`).join('\n');
                        const cptLines = (c.cpt || []).map(code => `  ${code} — ${CPT_DESC_MAP[code] || code}    $${CPT_CHARGES[code] || 50}`).join('\n');
                        const visitLines = batchVisits.map(v => {
                          let desc = v.type === 'MD Initial' ? 'MD Initial Evaluation' : v.type === 'MD Follow-up' ? 'MD Follow-Up' : v.type === 'PT Weekly' ? `PT Weekly Summary — Week ${v.week || ''}` : 'PT Daily Session';
                          return `  Visit ${v.num}: ${desc} — ${v.date}${v.role === 'md' ? ' — Dr. A. Falcon, MD' : ' — James Rivera, DPT'}`;
                        }).join('\n');

                        const preview = `${'═'.repeat(55)}
WELLNESS HEALTHCARE CLINIC CORP
8180 NW 36th St, Suite 213 · Doral, FL 33166
Ph: (305) 994-9500 · Fax: (786) 441-2179
${'═'.repeat(55)}

INSURANCE BILLING PACKAGE
Batch ${batch.id} — Visits ${batch.visits}
${'─'.repeat(55)}

COVER SHEET
  Patient: ${c.firstName} ${c.lastName}
  DOB: ${c.dob}
  Case ID: ${c.id}
  Insurance: ${c.insurance || 'N/A'}
  Policy: ${c.policy || 'N/A'}
  Date of Accident: ${c.accident}
  Attending Physician: Dr. Armando Falcon, MD
  NPI: 1447295126

  Billing Provider: Wellness Healthcare Clinic Corp
  Tax ID: XX-XXXXXXX

  Visit Range: ${batch.visits}
  Total Visits in Batch: ${batchVisits.length}

${'─'.repeat(55)}
DIAGNOSES (ICD-10)
${'─'.repeat(55)}
${icdLines}

${'─'.repeat(55)}
PROCEDURES (CPT)
${'─'.repeat(55)}
${cptLines}

${'─'.repeat(55)}
VISIT NOTES INCLUDED
${'─'.repeat(55)}
${visitLines}

${'─'.repeat(55)}
BILLING SUMMARY
${'─'.repeat(55)}
  Total Charges: $${batchAmount.toLocaleString()}
  Insurance: ${c.insurance || 'N/A'}
  PIP Limit: $10,000
  Amount Previously Billed: $${(c.amtBilled || 0).toLocaleString()}
  This Batch: $${batchAmount.toLocaleString()}

${'─'.repeat(55)}
DOCUMENTS IN THIS PACKAGE:
  1. Cover Sheet (this document)
  2. MD Initial Evaluation Report${batchMD > 1 ? ` + ${batchMD - 1} Follow-Up(s)` : ''}
  3. PT Daily Session Notes (x${batchPTDaily})
  4. PT Weekly Summary (x${batchPTWeekly})
  5. Superbill
  6. CMS-1500 Claim Form

${'─'.repeat(55)}
Submitted by: Wellness Healthcare Clinic Corp
Date: ${today}
${'─'.repeat(55)}`;
                        setPackagePreview(preview);
                      }} style={{
                        marginTop: 'auto', padding: '9px 0', borderRadius: 8, cursor: 'pointer',
                        background: batch.status === 'Not Sent' ? C.blueGlow : C.bgCard,
                        border: `1px solid ${batch.status === 'Not Sent' ? C.blue + '44' : C.border}`,
                        color: batch.status === 'Not Sent' ? C.blueBright : C.textSecond,
                        fontSize: 11, fontWeight: 700, textAlign: 'center',
                      }}>
                        {batch.status === 'Not Sent' ? 'Build Package' : 'View Package'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── CMS-1500 AUDIT RESULTS ── */}
            {auditResult && (
              <div style={{ marginBottom: 20, padding: 16, background: C.bgElevated, borderRadius: 12, border: `1px solid ${auditResult.status === 'ready_to_submit' ? C.green : auditResult.status === 'hold' ? C.red : C.amber}44` }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>{auditResult.status === 'ready_to_submit' ? '\u2705' : auditResult.status === 'hold' ? '\uD83D\uDED1' : '\u26A0\uFE0F'}</span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: auditResult.status === 'ready_to_submit' ? C.green : auditResult.status === 'hold' ? C.red : C.amber }}>
                        {auditResult.status === 'ready_to_submit' ? 'READY TO SUBMIT' : auditResult.status === 'hold' ? 'CLAIM ON HOLD' : 'REVIEW RECOMMENDED'}
                      </div>
                      <div style={{ fontSize: 10, color: C.textMuted }}>CMS-1500 Pre-Submission Audit \u00B7 NUCC 02/12 Standard</div>
                    </div>
                  </div>
                  <button onClick={() => setAuditResult(null)} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 10, cursor: 'pointer', background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted }}>Dismiss</button>
                </div>

                {/* Progress bar */}
                <div style={{ height: 4, background: C.bgCard, borderRadius: 2, overflow: 'hidden', marginBottom: 14 }}>
                  <div style={{ width: `${(auditResult.passedChecks / auditResult.totalChecks) * 100}%`, height: '100%', background: auditResult.status === 'ready_to_submit' ? C.green : auditResult.status === 'hold' ? C.red : C.amber, borderRadius: 2, transition: 'width 0.4s' }} />
                </div>

                {/* Hard Stops */}
                {auditResult.hardStops.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: C.red, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Hard Stops ({auditResult.hardStops.length}) \u2014 Must fix before submission</div>
                    {auditResult.hardStops.map((err, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 10px', background: C.redBg, borderRadius: 6, marginBottom: 4, fontSize: 11, color: '#EF9A9A', alignItems: 'flex-start' }}>
                        <span style={{ color: C.red, fontWeight: 800, flexShrink: 0 }}>\u2717</span>
                        <span>{err}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Soft Warnings */}
                {auditResult.softWarnings.length > 0 && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 800, color: C.amber, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Warnings ({auditResult.softWarnings.length}) \u2014 Review recommended</div>
                    {auditResult.softWarnings.map((warn, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 10px', background: C.amberBg, borderRadius: 6, marginBottom: 4, fontSize: 11, color: '#FFCC80', alignItems: 'flex-start' }}>
                        <span style={{ color: C.amber, fontWeight: 800, flexShrink: 0 }}>\u26A0</span>
                        <span>{warn}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* All Clear */}
                {auditResult.hardStops.length === 0 && auditResult.softWarnings.length === 0 && (
                  <div style={{ padding: '10px 14px', background: C.greenBg, borderRadius: 8, border: `1px solid ${C.green}33`, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14 }}>\u2705</span>
                    <span style={{ fontSize: 12, color: C.green, fontWeight: 700 }}>All checks passed. Claim is ready for batch submission.</span>
                  </div>
                )}

                {/* Proceed button if ready */}
                {auditResult.readyToSubmit && !packagePreview && (
                  <div style={{ marginTop: 12, textAlign: 'right' }}>
                    <button onClick={() => { setSelectedBatch(batches[0]); /* generate package */ }} style={{
                      padding: '10px 24px', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer',
                      background: C.green, color: '#fff', border: 'none', boxShadow: `0 0 12px ${C.green}44`,
                    }}>Proceed to Build Package \u2192</button>
                  </div>
                )}
              </div>
            )}

            {/* ── SECTION B: QUICK ACCESS ── */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 12 }}>Quick Access</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {/* Superbill Card */}
                <div onClick={() => setShowSuperbill(true)} style={{
                  padding: 18, background: C.bgElevated, borderRadius: 12, cursor: 'pointer',
                  border: `1px solid ${C.border}`, transition: 'all 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.blue}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>📋</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.textPrimary, marginBottom: 3 }}>Superbill</div>
                  <div style={{ fontSize: 10, color: C.textMuted, lineHeight: 1.6 }}>
                    Auto-populated encounter form with ICD-10, CPT codes, charges, and modifier 25 logic
                  </div>
                  <div style={{ marginTop: 10, fontSize: 10, fontWeight: 700, color: C.blueBright }}>Open Superbill →</div>
                </div>
                {/* CMS-1500 Card */}
                <div onClick={() => setShowCMS1500(true)} style={{
                  padding: 18, background: C.bgElevated, borderRadius: 12, cursor: 'pointer',
                  border: `1px solid ${C.border}`, transition: 'all 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.blue}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>🏥</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.textPrimary, marginBottom: 3 }}>CMS-1500 Claim Form</div>
                  <div style={{ fontSize: 10, color: C.textMuted, lineHeight: 1.6 }}>
                    HCFA form pre-populated for PIP insurance submission
                  </div>
                  <div style={{ marginTop: 10, fontSize: 10, fontWeight: 700, color: C.blueBright }}>View CMS-1500 →</div>
                </div>
              </div>
            </div>

            {/* ── SECTION C: BILLING SUMMARY ── */}
            <div style={{ padding: 16, background: C.bgElevated, borderRadius: 10, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.textPrimary, marginBottom: 12 }}>Billing Summary</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div style={{ padding: 12, background: C.bgCard, borderRadius: 8 }}>
                  <div style={{ fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>ICD-10 Codes</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>{(c.icd10 || []).length} diagnoses</div>
                  <div style={{ fontSize: 10, color: C.textMuted, marginTop: 4 }}>{(c.icd10 || []).join(', ')}</div>
                </div>
                <div style={{ padding: 12, background: C.bgCard, borderRadius: 8 }}>
                  <div style={{ fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>CPT Codes</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>{(c.cpt || []).length} procedures</div>
                  <div style={{ fontSize: 10, color: C.textMuted, marginTop: 4 }}>{(c.cpt || []).join(', ')}</div>
                </div>
                <div style={{ padding: 12, background: C.bgCard, borderRadius: 8 }}>
                  <div style={{ fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Insurance</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>{c.insurance || 'N/A'}</div>
                  <div style={{ fontSize: 10, color: C.textMuted, marginTop: 4 }}>Policy: {c.policy || 'N/A'}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div style={{ padding: 12, background: C.bgCard, borderRadius: 8 }}>
                  <div style={{ fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Total Visits</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>{c.visits}</div>
                </div>
                <div style={{ padding: 12, background: C.bgCard, borderRadius: 8 }}>
                  <div style={{ fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Amount Billed</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.amber }}>${(c.amtBilled || 0).toLocaleString()}</div>
                </div>
                <div style={{ padding: 12, background: C.bgCard, borderRadius: 8 }}>
                  <div style={{ fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Amount Paid</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>${(c.amtPaid || 0).toLocaleString()}</div>
                </div>
              </div>
            </div>

            {/* ── PACKAGE PREVIEW MODAL ── */}
            {packagePreview && selectedBatch && (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.75)', zIndex: 9999,
                display: 'flex', justifyContent: 'center', alignItems: 'center',
              }} onClick={(e) => { if (e.target === e.currentTarget) { setPackagePreview(null); setSelectedBatch(null); } }}>
                <div style={{
                  width: 720, maxHeight: '90vh', background: C.bgCard, borderRadius: 16,
                  border: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column',
                  overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
                }}>
                  {/* Modal header */}
                  <div style={{
                    padding: '16px 24px', borderBottom: `1px solid ${C.border}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: C.bgElevated,
                  }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
                        Billing Package — {selectedBatch.label}
                      </div>
                      <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>
                        Visits {selectedBatch.visits} · {c.firstName} {c.lastName} · {c.id}
                      </div>
                    </div>
                    <button onClick={() => { setPackagePreview(null); setSelectedBatch(null); }} style={{
                      width: 28, height: 28, borderRadius: 8, background: C.bgCard,
                      border: `1px solid ${C.border}`, color: C.textMuted, cursor: 'pointer',
                      fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>×</button>
                  </div>

                  {/* Preview content */}
                  <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
                    <pre style={{
                      fontFamily: "'Georgia', 'Times New Roman', serif",
                      fontSize: 11, lineHeight: 1.7, color: '#1a1a1a',
                      background: '#FFFFFF', padding: 28, borderRadius: 10,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      border: '1px solid #e0e0e0',
                    }}>{packagePreview}</pre>
                  </div>

                  {/* Modal footer */}
                  <div style={{
                    padding: '14px 24px', borderTop: `1px solid ${C.border}`,
                    display: 'flex', gap: 10, justifyContent: 'flex-end', background: C.bgElevated,
                  }}>
                    <button onClick={() => {
                      const w = window.open('', '_blank');
                      w.document.write(`<html><head><title>Billing Package - ${selectedBatch.label}</title><style>body{font-family:Georgia,serif;font-size:12px;line-height:1.7;padding:40px;white-space:pre-wrap;}</style></head><body>${packagePreview.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</body></html>`);
                      w.document.close();
                      w.print();
                    }} style={{
                      padding: '8px 18px', borderRadius: 8, cursor: 'pointer',
                      background: C.bgCard, border: `1px solid ${C.border}`,
                      color: C.textSecond, fontSize: 11, fontWeight: 700,
                    }}>Print</button>
                    <button onClick={() => {
                      const blob = new Blob([packagePreview], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${c.id}_${selectedBatch.label.replace(/\s/g, '_')}_Package.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }} style={{
                      padding: '8px 18px', borderRadius: 8, cursor: 'pointer',
                      background: C.bgCard, border: `1px solid ${C.border}`,
                      color: C.textSecond, fontSize: 11, fontWeight: 700,
                    }}>Download</button>
                    <button onClick={() => {
                      setBatches(prev => prev.map(b => b.id === selectedBatch.id ? {
                        ...b, status: 'Sent',
                        created: b.created || new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      } : b));
                      setSelectedBatch(prev => ({ ...prev, status: 'Sent' }));
                    }} style={{
                      padding: '8px 18px', borderRadius: 8, cursor: 'pointer',
                      background: C.blueGlow, border: `1px solid ${C.blue}44`,
                      color: C.blueBright, fontSize: 11, fontWeight: 700,
                    }}>Mark as Sent</button>
                    <button onClick={() => { setPackagePreview(null); setSelectedBatch(null); }} style={{
                      padding: '8px 18px', borderRadius: 8, cursor: 'pointer',
                      background: C.bgCard, border: `1px solid ${C.border}`,
                      color: C.textMuted, fontSize: 11, fontWeight: 700,
                    }}>Close</button>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {/* ════ DOCUMENTS ════ */}
        {activeTab === 'documents' && (
          <div style={{ padding: '24px 28px' }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 16 }}>
              Patient File Downloads -- {c.id}
            </div>

            {/* PDF Download Buttons */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
              <button onClick={() => generatePDF(c, 'summary')} disabled={downloading === c.id + 'summary'}
                style={{
                  padding: '18px 20px', borderRadius: 12, background: C.blueGlow,
                  border: `1px solid ${C.blue}44`, cursor: 'pointer', textAlign: 'left',
                  opacity: downloading === c.id + 'summary' ? 0.6 : 1, transition: 'all 0.15s',
                }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.blueBright, marginBottom: 4 }}>
                  {downloading === c.id + 'summary' ? 'Generating...' : 'Case Summary PDF'}
                </div>
                <div style={{ fontSize: 10, color: C.textMuted, lineHeight: 1.5, marginBottom: 6 }}>
                  Demographics | Insurance | Clinical overview | Billing totals | Signature
                </div>
                <div style={{ fontSize: 9, fontWeight: 800, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Download PDF</div>
              </button>

              <button onClick={() => generatePDF(c, 'full')} disabled={downloading === c.id + 'full'}
                style={{
                  padding: '18px 20px', borderRadius: 12, background: '#1B5E2011',
                  border: `1px solid #2E7D3244`, cursor: 'pointer', textAlign: 'left',
                  opacity: downloading === c.id + 'full' ? 0.6 : 1, transition: 'all 0.15s',
                }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.green, marginBottom: 4 }}>
                  {downloading === c.id + 'full' ? 'Generating...' : 'Full Clinical Chart PDF'}
                </div>
                <div style={{ fontSize: 10, color: C.textMuted, lineHeight: 1.5, marginBottom: 6 }}>
                  All visits | MD & PT AI notes | Diagnoses | Codes | Billing
                </div>
                <div style={{ fontSize: 9, fontWeight: 800, color: C.green, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Download PDF</div>
              </button>
            </div>

            {/* AI Notes Status */}
            <div style={{ padding: '14px 16px', borderRadius: 10, background: C.bgElevated, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>
                AI Notes Status -- included in Full Chart PDF
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  ['MD Visits on file', mdVisitCount, allVisits.filter(v => v.role === 'md' && aiNotes[v.role + '-' + c.id + '-' + v.num]).length, C.blue],
                  ['PT Sessions on file', ptDailyCount + ptWeeklyCount, allVisits.filter(v => (v.role === 'pt-daily' || v.role === 'pt-weekly') && aiNotes[v.role + '-' + c.id + '-' + v.num]).length, '#00695C'],
                ].map(([label, total, done, accent]) => (
                  <div key={label} style={{ padding: '10px 12px', borderRadius: 8, background: C.bgCard, border: `1px solid ${done === total && total > 0 ? accent + '44' : C.border}` }}>
                    <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 18, fontWeight: 900, color: done === total && total > 0 ? accent : done > 0 ? C.amber : C.textMuted }}>
                      {done}/{total}
                    </div>
                    <div style={{ fontSize: 9, color: C.textMuted }}>notes generated</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      {showSuperbill && <SuperbillViewer caseData={c} onClose={() => setShowSuperbill(false)} />}
      {showCMS1500 && <CMS1500Viewer caseData={c} onClose={() => setShowCMS1500(false)} />}
      </div>
    </div>
  );
};

/* ============================================================================
   MD VISIT MODULE -- PI Master / PI Master
   Full-page physician evaluation form
   All binary inputs (pills/checkboxes/selects) -- AI generates narrative
   ========================================================================= */

// ── PILL SELECT (reusable) ──────────────────────────────────────────────────
const PillSelect = ({ label, options, value, onChange, multi = false, disabled = false }) => {
  const selected = multi ? (value || []) : value;
  const isSelected = opt => multi ? selected.includes(opt) : selected === opt;
  const toggle = opt => {
    if (disabled) return;
    if (multi) {
      const arr = selected.includes(opt) ? selected.filter(v => v !== opt) : [...selected, opt];
      onChange(arr);
    } else {
      onChange(selected === opt ? '' : opt);
    }
  };
  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <div style={{
          fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase",
          letterSpacing: "0.1em", marginBottom: 7,
        }}>{label}{multi ? <span style={{ color: C.textMuted, fontSize: 9, marginLeft: 6, opacity: 0.6 }}>(multi)</span> : null}</div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {options.map(opt => {
          const lbl = typeof opt === 'object' ? opt.label : opt;
          const val = typeof opt === 'object' ? opt.value : opt;
          const sel = isSelected(val);
          return (
            <button key={val} onClick={() => toggle(val)} disabled={disabled} style={{
              padding: "7px 14px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              cursor: disabled ? "not-allowed" : "pointer",
              background: sel ? C.bluePale : C.bgElevated,
              color: sel ? C.blueBright : C.textMuted,
              border: `1.5px solid ${sel ? C.blue : C.border}`,
              boxShadow: sel ? `0 0 10px ${C.blueGlow}` : "none",
              transition: "all 0.15s", opacity: disabled ? 0.5 : 1,
              letterSpacing: "0.02em",
            }}>{lbl}</button>
          );
        })}
      </div>
    </div>
  );
};

// ── PAIN SCORE ROW ──────────────────────────────────────────────────────────
const PainScoreRow = ({ value, onChange, disabled }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 7 }}>
      Pain Score (0-10)
    </div>
    <div style={{ display: "flex", gap: 4 }}>
      {[0,1,2,3,4,5,6,7,8,9,10].map(n => {
        const sel = parseInt(value) === n;
        const painColor = n <= 3 ? C.green : n <= 6 ? C.amber : C.red;
        return (
          <button key={n} onClick={() => !disabled && onChange(String(n))} disabled={disabled} style={{
            width: 36, height: 36, borderRadius: 8, fontSize: 13, fontWeight: 800,
            cursor: disabled ? "not-allowed" : "pointer",
            background: sel ? painColor + "22" : C.bgElevated,
            color: sel ? painColor : C.textMuted,
            border: `2px solid ${sel ? painColor : C.border}`,
            boxShadow: sel ? `0 0 12px ${painColor}30` : "none",
            transition: "all 0.15s",
          }}>{n}</button>
        );
      })}
    </div>
  </div>
);

// ── SECTION HEADER ──────────────────────────────────────────────────────────
const SectionHead = ({ title, sub, color }) => (
  <div style={{ marginBottom: 14, marginTop: 6 }}>
    <div style={{ fontSize: 13, fontWeight: 800, color: color || C.blueBright, letterSpacing: "0.03em" }}>{title}</div>
    {sub && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{sub}</div>}
  </div>
);

// ── STEP DEFINITIONS ────────────────────────────────────────────────────────
const STAFF_STEPS = [
  { id: "patient",   icon: "\u{1F464}", label: "Patient" },
  { id: "accident",  icon: "\u{1F697}", label: "Accident" },
  { id: "legal",     icon: "\u{1F4C4}", label: "Legal Docs" },
];
const MD_STEPS = [
  { id: "interview",  icon: "\u{1F399}\uFE0F", label: "Interview" },
  { id: "exam",       icon: "\u{1FA7A}", label: "Exam" },
  { id: "dx",         icon: "\u{1F4CA}", label: "Dx + Plan" },
  { id: "aiNote",     icon: "\u{2728}",  label: "AI Note" },
];
const ALL_STEPS = [...STAFF_STEPS, ...MD_STEPS];

// ── MAIN COMPONENT ──────────────────────────────────────────────────────────
const MDVisitModule = ({ caseData, onClose, onSave, auth }) => {
  const isMD    = auth === "physician" || auth === "admin";
  const isStaff = auth === "staff" || auth === "admin";

  // Visit type selector
  const [visitType, setVisitType] = React.useState(null); // null = show selector
  const isInitial = visitType === "initial";
  const isFinal = visitType === "final";

  // State
  const [phase, setPhase]         = React.useState(isMD ? "md" : "staff");
  const [step, setStep]           = React.useState(isMD ? (visitType === "follow_up" ? "status" : visitType === "final" ? "finalStatus" : "interview") : "patient");
  const [locked, setLocked]       = React.useState(false);
  const [draftSaved, setDraftSaved] = React.useState(false);
  const [generatedNote, setGeneratedNote] = React.useState("");
  const [signed, setSigned]       = React.useState(false);
  const [pdfPreview, setPdfPreview] = React.useState(false);
  const [docStatus, setDocStatus] = React.useState({ patientForms: false, oirAffidavit: false, emcCert: false });
  const [previewDoc, setPreviewDoc] = React.useState(null);
  const [romModal, setRomModal] = React.useState(null);
  const [medList, setMedList]     = React.useState([]);
  const [medInput, setMedInput]   = React.useState("");

  // Follow-up specific state
  const [regionStatus, setRegionStatus] = React.useState({});  // { cervical: "Improved", shoulder: "Persistent", etc. }
  const [icdStatus, setIcdStatus] = React.useState({});  // { "S13.4XXA": "Stable", "M54.2": "Improved", etc. }
  const [cptStatus, setCptStatus] = React.useState({});  // { "97010": "Continue", "97110": "Stop", etc. }
  const [finalRegionStatus, setFinalRegionStatus] = React.useState({});
  const [mmiReached, setMmiReached] = React.useState(null); // null = not selected, true/false
  const [funcStatus, setFuncStatus] = React.useState({
    work: "", driving: "", sleep: "", mobility: ""
  });
  const [previousPain, setPreviousPain] = React.useState(() => {
    // Initialize from caseData baseline if available, otherwise use defaults based on complaints
    const cc = caseData?.chiefComplaints || [];
    const basePain = parseInt(caseData?.painScore) || 7;
    const defaults = {};
    if (cc.includes("Neck Pain")) defaults.cervical = basePain;
    if (cc.includes("Mid-Back Pain") || cc.includes("Upper Back Pain")) defaults.thoracic = basePain;
    if (cc.includes("Low Back Pain") || cc.includes("Lower Back Pain")) defaults.lumbar = basePain;
    if (cc.some(c => c.includes("Shoulder"))) defaults.shoulder = basePain;
    if (cc.some(c => c.includes("Elbow"))) defaults.elbow = basePain;
    if (cc.some(c => c.includes("Wrist"))) defaults.wrist = basePain;
    if (cc.some(c => c.includes("Hand"))) defaults.hand = basePain;
    if (cc.some(c => c.includes("Hip"))) defaults.hip = basePain;
    if (cc.some(c => c.includes("Knee"))) defaults.knee = basePain;
    if (cc.some(c => c.includes("Ankle"))) defaults.ankle = basePain;
    return defaults;
  });

  // Form
  const [f, setF] = React.useState({
    // Patient
    ptFirst: caseData?.firstName || "", ptLast: caseData?.lastName || "",
    ptDOB: caseData?.dob || "", ptPhone: caseData?.phone || "",
    ptSex: caseData?.sex || "M",
    ptEmail: caseData?.email || "", ptZip: caseData?.zip || "",
    idType: "", idPhotoName: "",
    // Accident
    doa: caseData?.accident || "", dofv: new Date().toISOString().split("T")[0],
    moi: caseData?.moi || "", patientPosition: caseData?.position || "", vehicleAtImpact: caseData?.vehicleAtImpact || "", seatbelt: caseData?.seatbelt || "",
    airbag: caseData?.airbag || "", vehicleDrivable: caseData?.drivable || "", loc: caseData?.loc || "",
    // Insurance
    pipCarrier: caseData?.insurance || "",
    // Interview
    chiefComplaints: caseData?.chiefComplaints || [], painScore: String(caseData?.painScore || "7"), painOnset: caseData?.painOnset || "Immediate",
    allergies: caseData?.allergies || ["NKDA"], medications: caseData?.medications || [], pmh: caseData?.pmh || [],
    erVisit: caseData?.erVisit ? "Yes" : "", erFacility: "", priorAccidents: "", priorAccidentYear: "",
    // Exam - HEENT/General
    generalStatus: "Alert & Oriented x4", ambulation: "Normal Gait",
    heent: ["Normocephalic", "Atraumatic", "PERRL"], neuro: ["Grossly Intact", "DTRs Symmetric", "Sensation Intact", "Motor 5/5", "Babinski Negative"],
    // Spine - pain, onset, palpation, ROM
    pain_cervical: "", painOnset_cervical: "", palp_cervical: [], rom_cervical: "",
    pain_thoracic: "", painOnset_thoracic: "", palp_thoracic: [], rom_thoracic: "",
    pain_lumbar: "", painOnset_lumbar: "", palp_lumbar: [], rom_lumbar: "",
    // Spine - functional ROM
    r_cflex: "", r_cext: "", r_clatR: "", r_clatL: "", r_crotR: "", r_crotL: "",
    r_lflex: "", r_lext: "", r_lsbR: "", r_lsbL: "",
    // Extremities - pain, onset, palpation, ROM
    pain_shoulder: "", painOnset_shoulder: "", palp_shoulder: [], rom_shoulder: "",
    pain_elbow: "", painOnset_elbow: "", palp_elbow: [], rom_elbow: "",
    pain_wrist: "", painOnset_wrist: "", palp_wrist: [], rom_wrist: "",
    pain_hand: "", painOnset_hand: "", palp_hand: [], rom_hand: "",
    pain_hip: "", painOnset_hip: "", palp_hip: [], rom_hip: "",
    pain_knee: "", painOnset_knee: "", palp_knee: [], rom_knee: "",
    pain_ankle: "", painOnset_ankle: "", palp_ankle: [], rom_ankle: "",
    // Extremities - functional ROM
    r_sohR: "", r_sohL: "", r_sbhR: "", r_sbhL: "", r_stirR: "", r_stirL: "",
    r_etsR: "", r_etsL: "", r_edkR: "", r_edkL: "",
    r_wbR: "", r_wbL: "", r_wfR: "", r_wfL: "",
    r_hfR: "", r_hfL: "", r_hpR: "", r_hpL: "",
    r_hcarR: "", r_hcarL: "", r_hssR: "", r_hssL: "",
    r_kchR: "", r_kchL: "", r_ksuR: "", r_ksuL: "",
    r_ahtwR: "", r_ahtwL: "", r_attR: "", r_attL: "",
    // Dx + Plan
    icdCodes: [], icdLat: {}, emCode: "", cptCodes: [],
    ptFreq: "", ptDuration: "", followUp: "",
    rxMedications: "", referrals: "",
    xrayOrders: [],
  });

  const u = (k, v) => {
    setF(prev => ({ ...prev, [k]: v }));
    setDraftSaved(false);
  };

  // Auto-save on change
  React.useEffect(() => {
    const t = setTimeout(() => setDraftSaved(true), 800);
    return () => clearTimeout(t);
  }, [f]);

  // Dynamic MD steps — follow-up visits get a different workflow
  const mdSteps = isInitial ? MD_STEPS : isFinal ? [
    { id: "finalStatus",  icon: "\u{1F4CB}", label: "Final Status" },
    { id: "finalExam",    icon: "\u{1FA7A}", label: "Final Exam" },
    { id: "finalFunc",    icon: "\u{1F3C3}", label: "Function" },
    { id: "mmi",          icon: "\u{2695}\u{FE0F}", label: "MMI" },
    { id: "finalNote",    icon: "\u{2728}",  label: "Discharge Note" },
  ] : [
    { id: "status",     icon: "\u{1F4CB}", label: "Region Status" },
    { id: "fuExam",     icon: "\u{1FA7A}", label: "Focused Exam" },
    { id: "ptReview",   icon: "\u{1F3C3}", label: "PT Review" },
    { id: "fuDx",       icon: "\u{1F4CA}", label: "Assessment" },
    { id: "fuNote",     icon: "\u{2728}",  label: "AI Note" },
  ];

  // Navigation helpers
  const allSteps = phase === "staff" ? STAFF_STEPS : (phase === "md" ? mdSteps : [...STAFF_STEPS, ...mdSteps]);
  const stepList = phase === "staff" ? STAFF_STEPS : mdSteps;
  const curIdx = stepList.findIndex(s => s.id === step);
  const canPrev = curIdx > 0;
  const canNext = curIdx < stepList.length - 1;
  const goPrev = () => { if (canPrev) setStep(stepList[curIdx - 1].id); };
  const goNext = () => { if (canNext) setStep(stepList[curIdx + 1].id); };

  const handoffToMD = () => { setPhase("md"); setStep(isInitial ? "interview" : "status"); };

  // ── SOAP NOTE GENERATOR ────────────────────────────────────────────────
  const generateSOAP = () => {
    const allergiesStr = f.allergies.length ? f.allergies.join(", ") : "NKDA";
    const pmhStr = f.pmh.length ? f.pmh.join(", ") : "None reported";
    const medsStr = medList.length ? medList.join(", ") : "None";
    const he = f.ptSex === "M" ? "He" : "She";
    const his = f.ptSex === "M" ? "his" : "her";
    const him = f.ptSex === "M" ? "him" : "her";

    // Calculate age
    const calcAge = (dob) => { if (!dob) return ""; const b = new Date(dob); const t = new Date(); let a = t.getFullYear() - b.getFullYear(); if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--; return a; };
    const age = calcAge(f.ptDOB);
    const dos = new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

    // ── ICD-10 DESCRIPTION MAP ──
    const ICD_DESC = {
      "S13.4XXA": "Cervical sprain", "S16.1XXA": "Cervical strain", "S23.3XXA": "Thoracic sprain",
      "S29.012A": "Thoracic strain", "S33.5XXA": "Lumbar sprain", "S39.012A": "Lumbar strain",
      "M54.2": "Cervicalgia", "M54.5": "Low back pain", "M54.6": "Thoracic pain",
      "M62.830": "Muscle spasm", "G44.309": "Post-traumatic headache",
      "M54.12": "Cervical radiculopathy", "M54.16": "Lumbar radiculopathy",
      "M54.4": "Lumbago with sciatica",
      "M25.511": "R shoulder pain", "M25.512": "L shoulder pain",
      "M25.521": "Elbow pain", "M25.531": "Wrist pain",
      "M25.551": "R hip pain", "M25.552": "L hip pain",
      "M25.561": "R knee pain", "M25.562": "L knee pain",
      "M25.571": "Ankle pain", "M79.641": "Hand pain",
      "S43.421A": "Shoulder sprain", "S46.011A": "Shoulder strain",
      "S53.401A": "Elbow sprain", "S56.111A": "Elbow strain",
      "S63.501A": "Wrist sprain", "S66.111A": "Wrist strain",
      "S63.90XA": "Hand sprain", "S66.911A": "Hand strain",
      "S73.101A": "Hip sprain", "S76.011A": "Hip strain",
      "S83.401A": "Knee sprain", "S86.811A": "Knee strain",
      "S93.401A": "Ankle sprain", "S96.811A": "Ankle strain",
      "F43.10": "PTSD", "F41.1": "GAD", "F32.9": "Depression"
    };

    // ── CPT DESCRIPTIONS + JUSTIFICATIONS ──
    const CPT_DESC = {
      "97140": "Manual therapy",
      "97110": "Therapeutic exercise",
      "97530": "Therapeutic activities",
      "97014": "Electrical stimulation (unattended)",
      "97010": "Hot/Cold therapy",
      "97035": "Ultrasound therapy",
      "97032": "Electrical stimulation (attended)",
      "97012": "Mechanical traction",
      "97112": "Neuromuscular re-education",
      "97116": "Gait training",
      "99203": "New patient E/M (moderate)",
      "99204": "New patient E/M (mod/high)",
      "99213": "Established patient E/M (low)",
      "99214": "Established patient E/M (moderate)"
    };
    const CPT_JUST = {
      "97140": "indicated for spinal joint restriction and soft tissue dysfunction due to sprain/strain injuries documented on examination",
      "97110": "restores strength and range of motion deficits documented in cervical and lumbar examination findings",
      "97530": "improves functional movement patterns compromised by acute traumatic injury sustained in the motor vehicle accident",
      "97014": "reduces acute pain and muscle spasm documented on palpation of the paraspinal musculature",
      "97010": "thermal therapy for pain modulation and tissue healing in areas of documented tenderness and inflammation",
      "97035": "deep heating to reduce inflammation in injured soft tissue structures identified on physical examination",
      "97032": "neuromuscular electrical stimulation for pain control and muscle re-education of affected spinal regions",
      "97012": "mechanical distraction to relieve spinal compression and radicular symptoms documented in the neurological examination",
      "97112": "neuromuscular re-education to restore proprioception and motor patterns disrupted by traumatic musculoskeletal injury"
    };

    // ── IMAGING MAP ──
    const IMAGING_MAP = {
      "72052": { region: "Cervical Spine", views: "AP, Lateral, Obliques", justification: "Post-traumatic neck pain with decreased range of motion, paraspinal tenderness, and/or muscle spasm following motor vehicle accident. Rule out fracture, dislocation, or instability." },
      "72072": { region: "Thoracic Spine", views: "AP, Lateral", justification: "Post-traumatic mid-back pain with muscle spasm and restricted thoracic mobility following motor vehicle accident. Rule out fracture or structural injury." },
      "72100": { region: "Lumbar Spine", views: "AP, Lateral", justification: "Post-traumatic low back pain with decreased range of motion, tenderness, and/or muscle spasm following motor vehicle accident. Rule out fracture, dislocation, or instability." },
      "73030": { region: "Shoulder", views: "AP, Axillary, Y-view", justification: "Post-traumatic shoulder pain with decreased range of motion and tenderness following trauma. Rule out fracture, dislocation, or joint pathology." },
      "73080": { region: "Elbow", views: "AP, Lateral", justification: "Post-traumatic elbow pain with tenderness and decreased motion following trauma. Rule out fracture, dislocation, or joint injury." },
      "73110": { region: "Wrist", views: "PA, Lateral, Oblique", justification: "Post-traumatic wrist pain with tenderness and reduced motion following trauma. Rule out fracture or acute bony injury." },
      "73130": { region: "Hand", views: "PA, Lateral, Oblique", justification: "Post-traumatic hand pain with tenderness, swelling, and/or decreased functional use following trauma. Rule out fracture or acute structural injury." },
      "73502": { region: "Hip", views: "AP Pelvis, Frog Lateral", justification: "Post-traumatic hip pain with painful ambulation and restricted motion following trauma. Rule out fracture, dislocation, or acute osseous injury." },
      "73562": { region: "Knee", views: "AP, Lateral, Sunrise", justification: "Post-traumatic knee pain with swelling, tenderness, and/or limited motion following trauma. Rule out fracture, effusion, or joint injury." },
      "73600": { region: "Ankle", views: "AP, Lateral, Mortise", justification: "Post-traumatic ankle pain with weight-bearing difficulty, tenderness, and/or swelling following trauma. Rule out fracture or joint disruption." },
      "73620": { region: "Foot", views: "AP, Lateral, Oblique", justification: "Post-traumatic foot pain with gait disturbance, tenderness, and/or swelling following trauma. Rule out fracture or acute structural injury." }
    };

    // ── FUNCTIONAL ROM NARRATIVE BUILDER ──
    const romNarrative = (val, fullDesc, partialDesc, limitedDesc, unableDesc) => {
      if (!val) return null;
      if (val === "Full") return fullDesc;
      if (val === "Partial") return partialDesc;
      if (val === "Limited") return limitedDesc;
      if (val === "Unable") return unableDesc;
      return null;
    };

    // ── SPINE EXAM BUILDER (physician-level narrative) ──
    const buildSpineNarrative = (region, painKey, palpKey, romKey, spasmKey, romDefs) => {
      const pain = f[painKey];
      const palp = f[palpKey] || [];
      const rom = f[romKey];
      if (!pain && palp.length === 0 && !rom) return null;

      let lines = [];
      lines.push(`${region}:`);
      if (pain) lines.push(`  Pain: ${pain}/10.`);

      // Palpation narrative
      if (palp.length) {
        const hasTenderness = palp.some(p => p.toLowerCase().includes("tenderness") || p.toLowerCase().includes("tender"));
        const hasSpasm = palp.some(p => p.toLowerCase().includes("spasm"));
        const palpDesc = palp.join(", ");
        lines.push(`  Palpation: ${palpDesc}. ${hasSpasm ? "Muscle spasm is present." : "No involuntary muscle spasm appreciated."}`);
      } else {
        lines.push("  Palpation: Non-tender to palpation, no spasm appreciated.");
      }

      // Functional ROM narrative
      const romParts = [];
      if (romDefs && romDefs.length) {
        romDefs.forEach(rd => {
          const desc = romNarrative(
            f[rd.key],
            rd.fullText,
            rd.partialText,
            rd.limitedText,
            rd.unableText
          );
          if (desc) romParts.push(desc);
        });
      }
      if (romParts.length) {
        lines.push(`  Range of Motion: ${rom || "Decreased"}. ${romParts.join(". ")}.`);
      } else {
        lines.push(`  Range of Motion: ${rom || "Within normal limits"}.`);
      }
      return lines.join("\n");
    };

    const cervicalRomDefs = [
      { key: "r_cflex", fullText: "Cervical flexion within normal limits", partialText: "Restricted cervical flexion with pain at mid-range, limiting ability to look down and read", limitedText: "Severely restricted cervical flexion, patient unable to approximate chin to chest", unableText: "Unable to perform cervical flexion due to pain and guarding" },
      { key: "r_cext", fullText: "Cervical extension within normal limits", partialText: "Restricted cervical extension with pain, limiting ability to look upward", limitedText: "Severely restricted cervical extension with reproduction of neck pain", unableText: "Unable to perform cervical extension due to pain and muscle guarding" },
      { key: "r_clatR", fullText: "Right lateral flexion within normal limits", partialText: "Restricted right lateral cervical flexion with discomfort at end range", limitedText: "Severely limited right lateral flexion of the cervical spine", unableText: "Unable to perform right lateral cervical flexion" },
      { key: "r_clatL", fullText: "Left lateral flexion within normal limits", partialText: "Restricted left lateral cervical flexion with discomfort at end range", limitedText: "Severely limited left lateral flexion of the cervical spine", unableText: "Unable to perform left lateral cervical flexion" },
      { key: "r_crotR", fullText: "Right cervical rotation within normal limits", partialText: "Restricted right cervical rotation limiting ability to turn head while driving and check blind spots", limitedText: "Severely restricted right cervical rotation, patient unable to turn head past midline", unableText: "Unable to perform right cervical rotation due to pain and guarding" },
      { key: "r_crotL", fullText: "Left cervical rotation within normal limits", partialText: "Restricted left cervical rotation limiting ability to turn head while driving and perform sustained visual tasks", limitedText: "Severely restricted left cervical rotation, patient unable to turn head past midline", unableText: "Unable to perform left cervical rotation due to pain and guarding" }
    ];
    const lumbarRomDefs = [
      { key: "r_lflex", fullText: "Lumbar flexion within normal limits", partialText: "Restricted lumbar flexion with pain at mid-range, limiting ability to bend forward for dressing and hygiene tasks", limitedText: "Severely restricted lumbar flexion, patient unable to reach below knee level", unableText: "Unable to perform lumbar flexion due to pain and guarding" },
      { key: "r_lext", fullText: "Lumbar extension within normal limits", partialText: "Restricted lumbar extension with pain, limiting ability to stand upright for prolonged periods", limitedText: "Severely restricted lumbar extension with reproduction of low back pain", unableText: "Unable to perform lumbar extension due to pain and protective splinting" },
      { key: "r_lsbR", fullText: "Right lateral bending within normal limits", partialText: "Restricted right lateral lumbar flexion with discomfort limiting reaching and bending tasks", limitedText: "Severely restricted right lateral lumbar bending", unableText: "Unable to perform right lateral lumbar bending" },
      { key: "r_lsbL", fullText: "Left lateral bending within normal limits", partialText: "Restricted left lateral lumbar flexion with discomfort limiting reaching and bending tasks", limitedText: "Severely restricted left lateral lumbar bending", unableText: "Unable to perform left lateral lumbar bending" }
    ];

    const spineFindings = [
      buildSpineNarrative("Cervical Spine", "pain_cervical", "palp_cervical", "rom_cervical", "spasm_cervical", cervicalRomDefs),
      buildSpineNarrative("Thoracic Spine", "pain_thoracic", "palp_thoracic", "rom_thoracic", "spasm_thoracic", []),
      buildSpineNarrative("Lumbar Spine", "pain_lumbar", "palp_lumbar", "rom_lumbar", "spasm_lumbar", lumbarRomDefs),
    ].filter(Boolean).join("\n\n");

    // ── JOINT / EXTREMITY EXAM BUILDER ──
    const buildJointNarrative = (region, painKey, palpKey, romKey, swellingKey, romDefs) => {
      const pain = f[painKey];
      const palp = f[palpKey] || [];
      const rom = f[romKey];
      if (!pain && palp.length === 0 && !rom) return null;

      let lines = [];
      lines.push(`${region}:`);
      if (pain) lines.push(`  Pain: ${pain}/10.`);
      if (palp.length) {
        lines.push(`  Palpation: ${palp.join(", ")}. ${f[swellingKey] ? "Swelling noted." : "No visible swelling."}`);
      } else {
        lines.push("  Palpation: Non-tender, no swelling appreciated.");
      }
      const romParts = [];
      if (romDefs && romDefs.length) {
        romDefs.forEach(rd => {
          const desc = romNarrative(f[rd.key], rd.fullText, rd.partialText, rd.limitedText, rd.unableText);
          if (desc) romParts.push(desc);
        });
      }
      if (romParts.length) {
        lines.push(`  Range of Motion: ${rom || "Decreased"}. ${romParts.join(". ")}.`);
      } else {
        lines.push(`  Range of Motion: ${rom || "Within normal limits"}.`);
      }
      return lines.join("\n");
    };

    const shoulderRomDefs = [
      { key: "r_sohR", fullText: "Right overhead reach within normal limits", partialText: "Restricted right overhead reach with pain, limiting ability to reach shelves and perform grooming", limitedText: "Severely restricted right overhead motion", unableText: "Unable to raise right arm overhead due to pain" },
      { key: "r_sohL", fullText: "Left overhead reach within normal limits", partialText: "Restricted left overhead reach with pain, limiting ability to reach shelves and perform grooming", limitedText: "Severely restricted left overhead motion", unableText: "Unable to raise left arm overhead due to pain" },
      { key: "r_sbhR", fullText: "Right behind-back reach within normal limits", partialText: "Restricted right internal rotation limiting ability to reach behind back for dressing", limitedText: "Severely limited right behind-back reach", unableText: "Unable to reach behind back with right hand" },
      { key: "r_sbhL", fullText: "Left behind-back reach within normal limits", partialText: "Restricted left internal rotation limiting ability to reach behind back for dressing", limitedText: "Severely limited left behind-back reach", unableText: "Unable to reach behind back with left hand" },
      { key: "r_stirR", fullText: "Right shoulder internal rotation within normal limits", partialText: "Restricted right shoulder internal rotation with pain at mid-range", limitedText: "Severely restricted right shoulder internal rotation", unableText: "Unable to perform right shoulder internal rotation" },
      { key: "r_stirL", fullText: "Left shoulder internal rotation within normal limits", partialText: "Restricted left shoulder internal rotation with pain at mid-range", limitedText: "Severely restricted left shoulder internal rotation", unableText: "Unable to perform left shoulder internal rotation" }
    ];
    const elbowRomDefs = [
      { key: "r_etsR", fullText: "Right elbow flexion/extension within normal limits", partialText: "Restricted right elbow flexion with pain, limiting ability to carry objects and feed self", limitedText: "Severely restricted right elbow motion", unableText: "Unable to flex right elbow due to pain" },
      { key: "r_etsL", fullText: "Left elbow flexion/extension within normal limits", partialText: "Restricted left elbow flexion with pain, limiting ability to carry objects and feed self", limitedText: "Severely restricted left elbow motion", unableText: "Unable to flex left elbow due to pain" },
      { key: "r_edkR", fullText: "Right forearm pronation/supination within normal limits", partialText: "Restricted right forearm rotation limiting ability to turn doorknobs and use tools", limitedText: "Severely restricted right forearm rotation", unableText: "Unable to rotate right forearm" },
      { key: "r_edkL", fullText: "Left forearm pronation/supination within normal limits", partialText: "Restricted left forearm rotation limiting ability to turn doorknobs and use tools", limitedText: "Severely restricted left forearm rotation", unableText: "Unable to rotate left forearm" }
    ];
    const wristRomDefs = [
      { key: "r_wbR", fullText: "Right wrist extension within normal limits", partialText: "Restricted right wrist extension with pain, limiting weight-bearing through the hand", limitedText: "Severely restricted right wrist extension", unableText: "Unable to extend right wrist due to pain" },
      { key: "r_wbL", fullText: "Left wrist extension within normal limits", partialText: "Restricted left wrist extension with pain, limiting weight-bearing through the hand", limitedText: "Severely restricted left wrist extension", unableText: "Unable to extend left wrist due to pain" },
      { key: "r_wfR", fullText: "Right wrist flexion within normal limits", partialText: "Restricted right wrist flexion with pain, limiting grip and fine motor tasks", limitedText: "Severely restricted right wrist flexion", unableText: "Unable to flex right wrist due to pain" },
      { key: "r_wfL", fullText: "Left wrist flexion within normal limits", partialText: "Restricted left wrist flexion with pain, limiting grip and fine motor tasks", limitedText: "Severely restricted left wrist flexion", unableText: "Unable to flex left wrist due to pain" }
    ];
    const handRomDefs = [
      { key: "r_hfR", fullText: "Right hand grip strength within normal limits", partialText: "Decreased right grip strength, limiting ability to grasp objects and open containers", limitedText: "Severely decreased right grip strength", unableText: "Unable to form a fist with right hand due to pain" },
      { key: "r_hfL", fullText: "Left hand grip strength within normal limits", partialText: "Decreased left grip strength, limiting ability to grasp objects and open containers", limitedText: "Severely decreased left grip strength", unableText: "Unable to form a fist with left hand due to pain" },
      { key: "r_hpR", fullText: "Right pinch strength within normal limits", partialText: "Decreased right pinch strength, limiting fine motor tasks such as buttoning and writing", limitedText: "Severely decreased right pinch strength", unableText: "Unable to perform pinch with right hand" },
      { key: "r_hpL", fullText: "Left pinch strength within normal limits", partialText: "Decreased left pinch strength, limiting fine motor tasks such as buttoning and writing", limitedText: "Severely decreased left pinch strength", unableText: "Unable to perform pinch with left hand" }
    ];
    const hipRomDefs = [
      { key: "r_hcarR", fullText: "Right hip flexion within normal limits for car entry", partialText: "Restricted right hip flexion with pain, limiting ability to enter/exit vehicle and climb stairs", limitedText: "Severely restricted right hip flexion", unableText: "Unable to flex right hip due to pain" },
      { key: "r_hcarL", fullText: "Left hip flexion within normal limits for car entry", partialText: "Restricted left hip flexion with pain, limiting ability to enter/exit vehicle and climb stairs", limitedText: "Severely restricted left hip flexion", unableText: "Unable to flex left hip due to pain" },
      { key: "r_hssR", fullText: "Right hip abduction within normal limits", partialText: "Restricted right hip abduction with pain, limiting lateral stepping and gait stability", limitedText: "Severely restricted right hip abduction", unableText: "Unable to abduct right hip due to pain" },
      { key: "r_hssL", fullText: "Left hip abduction within normal limits", partialText: "Restricted left hip abduction with pain, limiting lateral stepping and gait stability", limitedText: "Severely restricted left hip abduction", unableText: "Unable to abduct left hip due to pain" }
    ];
    const kneeRomDefs = [
      { key: "r_kchR", fullText: "Right knee flexion within normal limits for sit-to-stand", partialText: "Restricted right knee flexion with pain, limiting ability to rise from seated position and descend stairs", limitedText: "Severely restricted right knee flexion", unableText: "Unable to fully flex right knee due to pain" },
      { key: "r_kchL", fullText: "Left knee flexion within normal limits for sit-to-stand", partialText: "Restricted left knee flexion with pain, limiting ability to rise from seated position and descend stairs", limitedText: "Severely restricted left knee flexion", unableText: "Unable to fully flex left knee due to pain" },
      { key: "r_ksuR", fullText: "Right knee extension within normal limits for step-up", partialText: "Restricted right knee extension with pain, limiting ability to climb stairs and walk on inclines", limitedText: "Severely restricted right knee extension", unableText: "Unable to fully extend right knee" },
      { key: "r_ksuL", fullText: "Left knee extension within normal limits for step-up", partialText: "Restricted left knee extension with pain, limiting ability to climb stairs and walk on inclines", limitedText: "Severely restricted left knee extension", unableText: "Unable to fully extend left knee" }
    ];
    const ankleRomDefs = [
      { key: "r_ahtwR", fullText: "Right ankle dorsiflexion within normal limits", partialText: "Restricted right ankle dorsiflexion with pain, limiting heel-to-toe gait pattern", limitedText: "Severely restricted right ankle dorsiflexion", unableText: "Unable to dorsiflex right ankle due to pain" },
      { key: "r_ahtwL", fullText: "Left ankle dorsiflexion within normal limits", partialText: "Restricted left ankle dorsiflexion with pain, limiting heel-to-toe gait pattern", limitedText: "Severely restricted left ankle dorsiflexion", unableText: "Unable to dorsiflex left ankle due to pain" },
      { key: "r_attR", fullText: "Right ankle plantarflexion within normal limits", partialText: "Restricted right ankle plantarflexion, limiting ability to push off during gait and stand on tiptoes", limitedText: "Severely restricted right ankle plantarflexion", unableText: "Unable to plantarflex right ankle" },
      { key: "r_attL", fullText: "Left ankle plantarflexion within normal limits", partialText: "Restricted left ankle plantarflexion, limiting ability to push off during gait and stand on tiptoes", limitedText: "Severely restricted left ankle plantarflexion", unableText: "Unable to plantarflex left ankle" }
    ];

    const extFindings = [
      buildJointNarrative("Shoulder", "pain_shoulder", "palp_shoulder", "rom_shoulder", "swelling_shoulder", shoulderRomDefs),
      buildJointNarrative("Elbow", "pain_elbow", "palp_elbow", "rom_elbow", "swelling_elbow", elbowRomDefs),
      buildJointNarrative("Wrist", "pain_wrist", "palp_wrist", "rom_wrist", "swelling_wrist", wristRomDefs),
      buildJointNarrative("Hand/Grip", "pain_hand", "palp_hand", "rom_hand", "swelling_hand", handRomDefs),
      buildJointNarrative("Hip", "pain_hip", "palp_hip", "rom_hip", "swelling_hip", hipRomDefs),
      buildJointNarrative("Knee", "pain_knee", "palp_knee", "rom_knee", "swelling_knee", kneeRomDefs),
      buildJointNarrative("Ankle", "pain_ankle", "palp_ankle", "rom_ankle", "swelling_ankle", ankleRomDefs),
    ].filter(Boolean).join("\n\n");

    // ── FUNCTIONAL LIMITATIONS (enhanced) ──
    const funcLimitations = [];
    if (f.rom_cervical === "Decreased" || f.pain_cervical >= 5) {
      funcLimitations.push("Driving: Restricted cervical rotation limits the ability to check mirrors and blind spots safely");
      funcLimitations.push("Work: Difficulty with sustained computer use, reading, and desk tasks due to cervical pain");
      funcLimitations.push("Sleep: Difficulty finding a comfortable sleeping position due to neck pain; reports frequent waking");
    }
    if (f.rom_lumbar === "Decreased" || f.pain_lumbar >= 5) {
      funcLimitations.push("Lifting/Bending/Reaching: Unable to lift more than 10 lbs or bend at the waist without significant pain exacerbation");
      funcLimitations.push("Mobility: Prolonged standing and walking tolerance reduced; requires frequent positional changes");
      funcLimitations.push("Work: Limited ability to perform occupational duties requiring bending, lifting, or prolonged sitting");
    }
    if (f.rom_thoracic === "Decreased" || f.pain_thoracic >= 5) {
      funcLimitations.push("ADLs: Deep breathing and torso rotation restricted, limiting twisting and reaching activities");
    }
    if (f.rom_shoulder === "Decreased" || f.pain_shoulder >= 5) {
      funcLimitations.push("ADLs: Overhead reaching and dressing impaired; difficulty with grooming and hygiene tasks");
    }
    if (f.rom_hip === "Decreased" || f.pain_hip >= 5) {
      funcLimitations.push("Mobility: Ambulation and stair climbing limited by hip pain; altered gait pattern observed");
    }
    if (f.rom_knee === "Decreased" || f.pain_knee >= 5) {
      funcLimitations.push("Mobility: Stair negotiation and prolonged walking limited; difficulty with sit-to-stand transfers");
    }
    if (f.rom_ankle === "Decreased" || f.pain_ankle >= 5) {
      funcLimitations.push("Mobility: Gait deviation noted secondary to ankle pain; limited weight-bearing tolerance");
    }
    if (funcLimitations.length === 0) {
      funcLimitations.push("ADLs: General limitation in activities of daily living including household chores, self-care, and recreational activities");
      funcLimitations.push("Sleep: Disturbed sleep pattern due to musculoskeletal pain");
      funcLimitations.push("Driving: Reduced driving tolerance due to pain with prolonged seated posture");
    }

    // ── MEDICATION SECTION BUILDER ──
    const buildMedSection = () => {
      if (!f.rxMedications) return "";
      const meds = f.rxMedications.split(/[,;\n]+/).map(m => m.trim()).filter(Boolean);
      if (meds.length === 0) return "";
      const medDetails = {
        "Cyclobenzaprine 10mg": { sig: "10 mg by mouth three times daily as needed for muscle spasm", rationale: "Prescribed for documented paraspinal muscle spasm and myofascial pain to facilitate participation in physical therapy." },
        "Methocarbamol 750mg": { sig: "750 mg by mouth three times daily as needed for muscle spasm", rationale: "Prescribed for documented muscle spasm and musculoskeletal pain to reduce guarding and improve range of motion." },
        "Naproxen 500mg": { sig: "500 mg by mouth twice daily with food", rationale: "Anti-inflammatory prescribed for acute soft tissue inflammation and pain secondary to traumatic sprain/strain injury." },
        "Meloxicam 15mg": { sig: "15 mg by mouth once daily with food", rationale: "Anti-inflammatory prescribed for musculoskeletal pain and inflammation to improve functional mobility." },
        "Ibuprofen 800mg": { sig: "800 mg by mouth three times daily with food", rationale: "Anti-inflammatory prescribed for acute post-traumatic musculoskeletal pain and tissue inflammation." },
        "Diclofenac Gel 1%": { sig: "Apply topically to affected area(s) four times daily", rationale: "Topical NSAID prescribed for localized pain relief over areas of documented tenderness, minimizing systemic side effects." },
        "Tramadol 50mg": { sig: "50 mg by mouth every 6 hours as needed for moderate to severe pain", rationale: "Prescribed for moderate to severe pain not adequately controlled by anti-inflammatory medications alone." },
        "Gabapentin 300mg": { sig: "300 mg by mouth three times daily, titrate as tolerated", rationale: "Prescribed for neuropathic pain component and radicular symptoms documented on neurological examination." },
        "Tizanidine 4mg": { sig: "4 mg by mouth at bedtime as needed for muscle spasm", rationale: "Prescribed for nocturnal muscle spasm and sleep disruption secondary to musculoskeletal injury." }
      };
      let section = "Medications Prescribed:\n";
      meds.forEach(med => {
        const detail = medDetails[med];
        if (detail) {
          section += `  ${med}\n    Sig: ${detail.sig}\n    Rationale: ${detail.rationale}\n`;
        } else {
          section += `  ${med}\n    Sig: As directed\n    Rationale: Prescribed for symptomatic management of injuries sustained in the motor vehicle accident.\n`;
        }
      });
      return section;
    };

    // ── CPT-ICD JUSTIFICATION BUILDER ──
    const buildCptJustification = () => {
      if (!f.cptCodes.length) return "  Pending physician determination.";
      const diagStr = f.icdCodes.length ? f.icdCodes.map(c => `${c} (${ICD_DESC[c] || c})`).join(", ") : "pending";
      let lines = [];
      f.cptCodes.forEach(cpt => {
        const desc = CPT_DESC[cpt] || cpt;
        const just = CPT_JUST[cpt];
        if (just) {
          lines.push(`  ${cpt} (${desc}): ${just}.`);
          lines.push(`    Linked diagnoses: ${diagStr}`);
        } else {
          lines.push(`  ${cpt} (${desc}): Medically necessary for treatment of documented injuries.`);
        }
      });
      return lines.join("\n");
    };

    // ── IMAGING ORDERS BUILDER ──
    const buildImagingOrders = () => {
      if (!f.xrayOrders || !f.xrayOrders.length) return "";
      let section = `${"─".repeat(60)}\nImaging Orders\n${"─".repeat(60)}\nReferral Facility: MAZEL DIAGNOSTIC CENTER INC.\n  8150 S.W. 8TH STREET SUITE 119 · MIAMI, FL 33144\n  Phone: 305-261-3020 · Fax: 305-261-3070\nOrdering Physician: Armando Falcon, MD · NPI 1447295126\nOrdering Facility: Wellness Healthcare Clinic Corp · Doral, FL 33166\n`;
      f.xrayOrders.forEach(cpt => {
        const img = IMAGING_MAP[cpt];
        if (img) {
          section += `\n  ${cpt} — ${img.region} X-ray\n`;
          section += `  Views: ${img.views}\n`;
          section += `  Clinical Justification: ${img.justification}\n`;
          // Link relevant ICD codes
          const linkedIcds = f.icdCodes.filter(icd => {
            const desc = (ICD_DESC[icd] || "").toLowerCase();
            const reg = img.region.toLowerCase();
            return desc.includes(reg.split(" ")[0].toLowerCase()) || desc.includes("sprain") || desc.includes("strain") || desc.includes("pain");
          });
          if (linkedIcds.length) {
            section += `  Linked ICD-10 Codes: ${linkedIcds.map(c => `${c} (${ICD_DESC[c] || c})`).join(", ")}\n`;
          }
        } else {
          section += `\n  ${cpt} — Diagnostic imaging study ordered\n`;
        }
      });
      section += `\nAdvanced imaging such as MRI will be considered if symptoms persist or neurological deficits develop.\n`;
      return section;
    };

    // ── BUILD THE COMPLETE NOTE ──
    const note = `${"═".repeat(60)}
  WELLNESS HEALTHCARE CLINIC CORP
  8180 NW 36th St, Suite 213 · Doral, FL 33166
  Ph: (305) 994-9500 · Fax: (786) 441-2179
  Armando Falcon, MD · FL License ME 84789 · NPI 1447295126
${"═".repeat(60)}

FLORIDA PIP ${isInitial ? "INITIAL EVALUATION" : "FOLLOW-UP EVALUATION"} REPORT
${"─".repeat(60)}

Patient Information
  Name: ${f.ptFirst} ${f.ptLast}
  DOB: ${f.ptDOB}${age ? ` (Age: ${age})` : ""}
  Sex: ${f.ptSex === "M" ? "Male" : "Female"}
  Date of Accident (DOA): ${f.doa || "N/A"}
  Date of ${isInitial ? "Initial Evaluation" : "Follow-Up"}: ${dos}
  Insurance Carrier: ${f.pipCarrier || "N/A"}
  Case Type: Florida Personal Injury Protection (PIP)
  Case ID: ${caseData?.id || "N/A"}

${"─".repeat(60)}
Accident History
${"─".repeat(60)}
The patient was involved in a ${(f.moi || "motor vehicle accident").toLowerCase()} on ${f.doa || "[DOA]"} while positioned as ${f.patientPosition === "Driver" ? "the driver" : f.patientPosition === "Front Passenger" ? "a front-seat passenger" : "a rear-seat passenger"}. The vehicle was ${(f.vehicleAtImpact || "moving").toLowerCase()} at the time of impact. The patient ${f.seatbelt?.includes("Yes") || f.seatbelt?.includes("Seatbelt") ? "was restrained with seatbelt" : "was not wearing a seatbelt"}${f.airbag === "Yes" ? ", and airbags deployed" : ", and airbags did not deploy"}.
The vehicle was ${f.vehicleDrivable === "Yes" ? "drivable" : "not drivable"} after the collision. The patient ${f.loc === "Yes" ? "reports loss of consciousness" : "denies loss of consciousness"}.

Previous Medical Attention
${f.erVisit === "Yes" ? "The patient sought prior medical evaluation following the accident, presenting to the emergency room before presenting today." : "The patient did not seek prior medical evaluation following the accident before presenting today."}

${"─".repeat(60)}
Chief Complaints
${"─".repeat(60)}
${f.chiefComplaints.length ? f.chiefComplaints.map(c => `  • ${c}`).join("\n") : "  • Not specified"}
  Pain Severity: ${f.painScore}/10
  Onset: ${f.painOnset || "Immediate"} post-traumatic

${"─".repeat(60)}
History of Present Illness
${"─".repeat(60)}
${f.ptFirst} ${f.ptLast} is a ${age}-year-old ${f.ptSex === "M" ? "male" : "female"} presenting for ${isInitial ? "initial evaluation" : "follow-up evaluation"} following a motor vehicle accident on ${f.doa || "[DOA]"}. ${he} reports ${f.painOnset === "Immediate" ? "immediate" : f.painOnset === "Gradual" ? "gradual" : "delayed"} onset of ${f.chiefComplaints.length ? f.chiefComplaints.slice(0, 3).join(", ").toLowerCase() : "musculoskeletal pain"} following the collision. Symptoms are described as ${parseInt(f.painScore) >= 7 ? "severe and constant" : parseInt(f.painScore) >= 4 ? "moderate and persistent" : "mild but persistent"}, rated ${f.painScore}/10, and are exacerbated by movement, positional changes, and activities of daily living. The clinical presentation is consistent with acute traumatic musculoskeletal injury directly resulting from the motor vehicle accident.

${"─".repeat(60)}
Past Medical History
${"─".repeat(60)}
${pmhStr === "None" || pmhStr === "None reported" ? "  No significant past medical history reported." : f.pmh.map(p => `  • ${p}`).join("\n")}

Allergies: ${allergiesStr}
Current Medications: ${medsStr}
${f.priorAccidents === "Yes" ? `\nPrior Accidents: Patient reports prior motor vehicle accident in ${f.priorAccidentYear || "[year]"}.` : ""}

${"─".repeat(60)}
Review of Systems
${"─".repeat(60)}
  Musculoskeletal: Positive for ${f.chiefComplaints.length ? f.chiefComplaints.join(", ").toLowerCase() : "pain"}
  Neurological: ${f.chiefComplaints.some(c => ["Headache", "Dizziness", "Numbness/Tingling"].includes(c)) ? "Positive for " + f.chiefComplaints.filter(c => ["Headache", "Dizziness", "Numbness/Tingling"].includes(c)).join(", ").toLowerCase() : "Negative for headache, dizziness, numbness, and tingling"}
  Psychological: ${f.icdCodes.some(c => ["F43.10", "F41.1", "F32.9"].includes(c)) ? "Positive for anxiety, sleep disturbance, and/or mood changes following the accident" : "Negative for significant anxiety, depression, or PTSD symptoms"}
  All other systems: Reviewed and negative unless otherwise stated

${"─".repeat(60)}
Physical Examination
${"─".repeat(60)}

General Appearance
  Patient is ${(f.generalStatus || "alert and oriented").toLowerCase()}, in ${parseInt(f.painScore) >= 7 ? "moderate to severe" : parseInt(f.painScore) >= 4 ? "moderate" : "mild"} distress secondary to pain. ${he} is well-nourished, well-developed, and cooperative with the examination.
  Ambulation: ${f.ambulation || "Normal gait"}. ${f.ambulation === "Antalgic gait" || f.ambulation === "Guarded" ? `${he} demonstrates a guarded gait pattern consistent with ${his} reported musculoskeletal complaints.` : ""}

Neurological Examination
${f.neuro.length ? f.neuro.map(n => `  • ${n}`).join("\n") : "  • Deep tendon reflexes symmetric and within normal limits bilaterally\n  • Sensation grossly intact to light touch\n  • Motor strength 5/5 in upper and lower extremities unless otherwise noted"}

HEENT
${f.heent.length ? f.heent.map(h => `  • ${h}`).join("\n") : "  • Normocephalic, atraumatic\n  • Pupils equal, round, reactive to light\n  • No facial asymmetry"}

${"─".repeat(60)}
SPINE EXAMINATION
${"─".repeat(60)}
${spineFindings || "  No spinal complaints reported or examined."}

${"─".repeat(60)}
EXTREMITY / JOINT EXAMINATION
${"─".repeat(60)}
${extFindings || "  No extremity complaints reported or examined."}

${"─".repeat(60)}
Assessment / Diagnoses (ICD-10)
${"─".repeat(60)}
${f.icdCodes.length ? f.icdCodes.map(c => `  ${c} — ${ICD_DESC[c] || c}`).join("\n") : "  Pending physician assessment"}

${"─".repeat(60)}
Medical Necessity Statement
${"─".repeat(60)}
${isInitial ? `The patient sustained acute traumatic musculoskeletal injuries directly resulting from the motor vehicle accident of ${f.doa || "[DOA]"}. The mechanism of injury — ${(f.moi || "motor vehicle collision").toLowerCase()} — is consistent with the pattern of sprain/strain injuries documented on physical examination.

Objective findings substantiating the diagnoses include: restricted range of motion, paraspinal and periarticular tenderness to palpation, muscle spasm, and pain with functional testing. These findings are of acute traumatic origin and are not attributable to any pre-existing condition.

The patient presents with ${parseInt(f.painScore) >= 7 ? "moderate to severe" : "moderate"} pain (${f.painScore}/10) and measurable functional impairment affecting activities of daily living, work capacity, driving ability, and sleep quality.

Treatment is medically necessary to:
  • Reduce acute inflammation, pain, and muscle spasm
  • Restore pre-injury musculoskeletal function and range of motion
  • Prevent the development of chronic pain syndromes and myofascial adhesions
  • Improve mobility, functional independence, and quality of life
  • Enable return to pre-accident work and daily activities

Without appropriate and timely intervention, the patient is at significant risk for chronic pain, prolonged disability, deconditioning, and delayed recovery. The prescribed treatment is consistent with evidence-based guidelines and Florida PIP treatment standards.`
: `Continuance of treatment is medically necessary. The patient continues to demonstrate objective clinical findings including pain (${f.painScore}/10), restricted range of motion, and functional limitations directly attributable to the motor vehicle accident of ${f.doa || "[DOA]"}.

The patient has shown ${parseInt(f.painScore) <= 5 ? "measurable improvement" : "limited but ongoing progress"} with the current treatment protocol. Discontinuation of therapy at this time would result in regression of functional gains and increased risk of chronic pain syndrome.

Continued physical therapy is necessary to:
  • Maintain and build upon functional progress achieved to date
  • Continue restoration of pre-accident range of motion and strength
  • Prevent deconditioning, myofascial adhesion, and chronic pain development
  • Achieve maximum medical improvement (MMI)`}

${"─".repeat(60)}
Functional Limitations
${"─".repeat(60)}
As a direct result of injuries sustained in the motor vehicle accident, the patient demonstrates the following functional limitations tied to objective examination findings:
${funcLimitations.map(l => `  • ${l}`).join("\n")}

${"─".repeat(60)}
Treatment Plan
${"─".repeat(60)}

Physical Therapy
  Frequency: ${f.ptFreq || "3x/week"}
  Duration: ${f.ptDuration || "4 weeks"}

${"─".repeat(60)}
CPT Code Justification
${"─".repeat(60)}
${buildCptJustification()}

${buildMedSection()}
${f.referrals ? `Referrals:\n  ${f.referrals}\n` : ""}
${isInitial ? buildImagingOrders() : ""}
${"─".repeat(60)}
Patient Counseling & Education
${"─".repeat(60)}
The patient was counseled regarding:
  • Injury Education: The nature, mechanism, and expected clinical course of post-traumatic sprain/strain injuries were explained. The patient was informed that symptoms may initially worsen before improvement and that recovery typically requires 6-12 weeks of consistent treatment.
  • Activity Modification: The patient was advised to avoid heavy lifting (>10 lbs), prolonged static postures, and high-impact activities. Ergonomic recommendations for workstation setup and sleeping position were provided.
  • Medication Precautions: ${f.rxMedications ? "The patient was counseled on potential side effects of prescribed medications including drowsiness, GI upset, and the importance of taking anti-inflammatories with food. The patient was advised not to drive or operate heavy machinery if experiencing sedation." : "General guidance on over-the-counter analgesic use and avoidance of excessive NSAID use was provided."}
  • Treatment Compliance: The importance of adhering to the prescribed physical therapy schedule was emphasized. The patient was informed that gaps in treatment may delay recovery and negatively impact clinical outcomes.
The patient verbalized understanding of ${his} injuries, treatment plan, and activity restrictions, and expressed agreement to proceed with the recommended course of care.

${"─".repeat(60)}
Prognosis
${"─".repeat(60)}
${parseInt(f.painScore) >= 7 ? "Fair, dependent upon compliance with the prescribed treatment plan. Given the severity of presenting symptoms, early and consistent intervention is expected to improve functional outcomes and reduce the risk of chronicity. The patient will be monitored for neurological changes warranting advanced imaging." : "Good to fair, dependent upon compliance with the prescribed treatment plan. Continued progress is expected with consistent therapy participation. The patient will be monitored for clinical plateau or regression warranting modification of the treatment approach."}

${"─".repeat(60)}
Follow-Up Plan
${"─".repeat(60)}
  Re-evaluation in ${f.followUp || "4 weeks"} to assess clinical progress and adjust the treatment plan accordingly.
  Advanced imaging such as MRI will be considered if symptoms persist or neurological deficits develop.

${"═".repeat(60)}
Examining Physician: Dr. Armando Falcon, MD
FL License: ME 84789  |  NPI: 1447295126
Facility: Wellness Healthcare Clinic Corp
8180 NW 36th St, Suite 213, Doral, FL 33166
Ph: (305) 994-9500  |  Fax: (786) 441-2179

Signature: _________________________ Date: ${dos}
${"═".repeat(60)}
Generated by PI Master™ · RenuviaMD® Network`;

    setGeneratedNote(note);
  };

  // ── FOLLOW-UP SOAP NOTE GENERATOR ──────────────────────────────────────
  const generateFollowUpSOAP = () => {
    const he = f.ptSex === "M" ? "He" : "She";
    const his = f.ptSex === "M" ? "his" : "her";
    const calcAge = (dob) => { if (!dob) return ""; const b = new Date(dob); const t = new Date(); let a = t.getFullYear() - b.getFullYear(); if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--; return a; };
    const age = calcAge(f.ptDOB);
    const dos = new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

    const ICD_DESC_GEN = {
      "S13.4XXA": "Cervical sprain", "S16.1XXA": "Cervical strain", "S23.3XXA": "Thoracic sprain",
      "S29.012A": "Thoracic strain", "S33.5XXA": "Lumbar sprain", "S39.012A": "Lumbar strain",
      "M54.2": "Cervicalgia", "M54.5": "Low back pain", "M54.6": "Thoracic pain",
      "M62.830": "Muscle spasm", "G44.309": "Post-traumatic headache",
      "M54.12": "Cervical radiculopathy", "M54.16": "Lumbar radiculopathy", "M54.4": "Lumbago with sciatica",
      "M25.511": "R shoulder pain", "M25.512": "L shoulder pain",
      "M25.521": "Elbow pain", "M25.531": "Wrist pain",
      "M25.551": "R hip pain", "M25.552": "L hip pain",
      "M25.561": "R knee pain", "M25.562": "L knee pain",
      "M25.571": "Ankle pain", "M79.641": "Hand pain",
      "S43.421A": "Shoulder sprain", "S46.011A": "Shoulder strain",
      "S53.401A": "Elbow sprain", "S56.111A": "Elbow strain",
      "S63.501A": "Wrist sprain", "S66.111A": "Wrist strain",
      "S63.90XA": "Hand sprain", "S66.911A": "Hand strain",
      "S73.101A": "Hip sprain", "S76.011A": "Hip strain",
      "S83.401A": "Knee sprain", "S86.811A": "Knee strain",
      "S93.401A": "Ankle sprain", "S96.811A": "Ankle strain",
      "F43.10": "PTSD", "F41.1": "GAD", "F32.9": "Depression",
    };

    const ICD_TO_REGION_GEN = {
      "S13.4XXA": "cervical", "S16.1XXA": "cervical", "M54.2": "cervical", "M54.12": "cervical", "G44.309": "cervical",
      "S23.3XXA": "thoracic", "S29.012A": "thoracic", "M54.6": "thoracic",
      "S33.5XXA": "lumbar", "S39.012A": "lumbar", "M54.5": "lumbar", "M54.16": "lumbar", "M54.4": "lumbar", "M62.830": "lumbar",
      "M25.511": "shoulder", "M25.512": "shoulder", "S43.421A": "shoulder", "S46.011A": "shoulder",
      "M25.521": "elbow", "S53.401A": "elbow", "S56.111A": "elbow",
      "M25.531": "wrist", "S63.501A": "wrist", "S66.111A": "wrist",
      "M79.641": "hand", "S63.90XA": "hand", "S66.911A": "hand",
      "M25.551": "hip", "M25.552": "hip", "S73.101A": "hip", "S76.011A": "hip",
      "M25.561": "knee", "M25.562": "knee", "S83.401A": "knee", "S86.811A": "knee",
      "M25.571": "ankle", "S93.401A": "ankle", "S96.811A": "ankle",
    };

    const REGION_LABELS = {
      cervical: "Cervical Spine", thoracic: "Thoracic Spine", lumbar: "Lumbar Spine",
      shoulder: "Shoulder", elbow: "Elbow", wrist: "Wrist", hand: "Hand",
      hip: "Hip", knee: "Knee", ankle: "Ankle",
    };

    // Categorize regions by status
    const improved = Object.entries(regionStatus).filter(([,s]) => s === "Improved").map(([r]) => REGION_LABELS[r] || r);
    const persistent = Object.entries(regionStatus).filter(([,s]) => s === "Persistent").map(([r]) => REGION_LABELS[r] || r);
    const worsened = Object.entries(regionStatus).filter(([,s]) => s === "Worsened").map(([r]) => REGION_LABELS[r] || r);
    const resolved = Object.entries(regionStatus).filter(([,s]) => s === "Resolved").map(([r]) => REGION_LABELS[r] || r);

    // Overall trend
    const overallTrend = improved.length >= persistent.length + worsened.length ? "improvement" :
                         worsened.length > improved.length ? "worsening" : "persistence of symptoms";
    const painTrend = improved.length > 0 ? "decreased" : worsened.length > 0 ? "increased" : "remained stable";

    // Non-resolved regions for exam
    const nonResolved = Object.entries(regionStatus).filter(([,s]) => s !== "Resolved").map(([r]) => r);

    // Build Physical Exam section
    const buildExamSection = () => {
      let lines = [];
      nonResolved.forEach(regionId => {
        const label = REGION_LABELS[regionId] || regionId;
        const painKey = `pain_${regionId}`;
        const palpKey = `palp_${regionId}`;
        const romKey = `rom_${regionId}`;
        const prevPain = previousPain[regionId] || 7;
        const curPain = f[painKey] || "N/A";
        const palp = (f[palpKey] || []).join(", ") || "No tenderness";
        const rom = f[romKey] || "Not assessed";

        lines.push(`  ${label}`);
        lines.push(`    Pain: ${curPain}/10 (previously ${prevPain}/10)`);
        lines.push(`    Palpation: ${palp}`);
        lines.push(`    ROM: ${rom}`);

        // Include functional ROM details if decreased
        if (rom === "Decreased") {
          const romTests = {
            cervical: [["r_cflex","Flexion"],["r_cext","Extension"],["r_clatR","Lat Flexion R"],["r_clatL","Lat Flexion L"],["r_crotR","Rotation R"],["r_crotL","Rotation L"]],
            lumbar: [["r_lflex","Flexion"],["r_lext","Extension"],["r_lsbR","Side Bend R"],["r_lsbL","Side Bend L"]],
            shoulder: [["r_sohR","Overhead R"],["r_sohL","Overhead L"],["r_sbhR","Behind Head R"],["r_sbhL","Behind Head L"]],
            knee: [["r_kchR","Chair Rise R"],["r_kchL","Chair Rise L"]],
            ankle: [["r_ahtwR","Heel-Toe Walk R"],["r_ahtwL","Heel-Toe Walk L"]],
            hip: [["r_hcarR","Get In Car R"],["r_hcarL","Get In Car L"]],
            wrist: [["r_wbR","Wrist Back R"],["r_wbL","Wrist Back L"]],
            elbow: [["r_etsR","Touch Shoulder R"],["r_etsL","Touch Shoulder L"]],
            hand: [["r_hfR","Fist R"],["r_hfL","Fist L"]],
          };
          const tests = romTests[regionId] || [];
          const results = tests.filter(([key]) => f[key]).map(([key, lbl]) => `${lbl}: ${f[key]}`);
          if (results.length) {
            lines.push(`    Functional ROM: ${results.join(", ")}`);
          }
        }
        lines.push("");
      });
      return lines.join("\n");
    };

    // Build Assessment section
    const icdCodes = f.icdCodes || caseData?.icd10 || [];
    const buildAssessment = () => {
      let lines = [];
      icdCodes.forEach(code => {
        const desc = ICD_DESC_GEN[code] || code;
        const region = ICD_TO_REGION_GEN[code];
        const status = region ? (regionStatus[region] || "Persistent") : "Persistent";
        lines.push(`  ${code} ${desc} \u2014 ${status}`);
      });
      return lines.join("\n");
    };

    // Non-resolved region list for medical necessity
    const nonResolvedLabels = nonResolved.map(r => REGION_LABELS[r] || r);

    // Objective findings summary
    const objectiveFindings = [];
    nonResolved.forEach(r => {
      if (f[`rom_${r}`] === "Decreased") objectiveFindings.push(`decreased ROM in ${REGION_LABELS[r] || r}`);
      if ((f[`palp_${r}`] || []).length > 0) objectiveFindings.push(`tenderness in ${REGION_LABELS[r] || r}`);
    });

    const basePainScore = parseInt(caseData?.painScore) || 7;
    const currentPains = nonResolved.map(r => parseInt(f[`pain_${r}`]) || 0).filter(p => p > 0);
    const avgCurrentPain = currentPains.length ? Math.round(currentPains.reduce((a,b) => a+b, 0) / currentPains.length) : basePainScore - 2;

    const note = `${"═".repeat(60)}
  WELLNESS HEALTHCARE CLINIC CORP
  8180 NW 36th St, Suite 213 \u00B7 Doral, FL 33166
  Ph: (305) 994-9500 \u00B7 Fax: (786) 441-2179
  Armando Falcon, MD \u00B7 FL License ME 84789 \u00B7 NPI 1447295126
${"═".repeat(60)}

FLORIDA PIP FOLLOW-UP EVALUATION REPORT
${"─".repeat(60)}
Visit #${caseData?.visits || 2}  |  Date of Service: ${dos}

${"─".repeat(60)}
Patient Information
${"─".repeat(60)}
  Name: ${f.ptLast || "___"}, ${f.ptFirst || "___"}
  DOB: ${f.ptDOB || "___"}  |  Age: ${age}  |  Sex: ${f.ptSex || "___"}
  Date of Accident: ${f.doa || "___"}
  PIP Carrier: ${f.pipCarrier || "___"}

${"─".repeat(60)}
History Since Initial Visit
${"─".repeat(60)}
Since the ${caseData?.visits === 2 ? "initial evaluation" : "last follow-up"}, the patient reports:
${improved.length ? `  Improvement: ${improved.join(", ")}\n` : ""}${persistent.length ? `  Persistent: ${persistent.join(", ")}\n` : ""}${worsened.length ? `  Worsened: ${worsened.join(", ")}\n` : ""}${resolved.length ? `  Resolved: ${resolved.join(", ")}\n` : ""}  ${worsened.length === 0 ? "No worsening symptoms reported." : ""}
  Pain intensity has ${painTrend} overall, with ${improved.length > 0 ? "improved" : "limited"} tolerance to activities of daily living.

${"─".repeat(60)}
Current Complaints
${"─".repeat(60)}
${[...improved, ...persistent, ...worsened].map(r => `  - ${r} pain`).join("\n") || "  No active complaints"}

${"─".repeat(60)}
Focused Physical Examination
${"─".repeat(60)}
${buildExamSection()}
${"─".repeat(60)}
Therapy Progress Review
${"─".repeat(60)}
  Review of physical therapy notes indicates:
    Pain reduction during sessions (${basePainScore}/10 → ${avgCurrentPain}/10)
    Patient tolerated all modalities without adverse reaction
    No adverse reactions reported

  Weekly summary demonstrates:
    ROM: Improving
    Strength: Improving
    ADL: Conserved

${"─".repeat(60)}
Assessment
${"─".repeat(60)}
${buildAssessment()}

  Overall, the patient demonstrates ${improved.length > worsened.length ? "measurable improvement with continued deficits" : worsened.length > 0 ? "ongoing deficits requiring continued intervention" : "limited progress with persistent functional limitations"}.

${"─".repeat(60)}
Medical Necessity for Continued Treatment
${"─".repeat(60)}
  Continued physical therapy remains medically necessary due to:
  \u2022 Persistent pain and functional limitations in ${nonResolvedLabels.join(", ") || "affected regions"}
  \u2022 Objective findings including ${objectiveFindings.length ? objectiveFindings.join(", ") : "tenderness on palpation and decreased ROM"}
  \u2022 Incomplete recovery \u2014 patient has not reached pre-accident functional baseline
  \u2022 Documented improvement with therapy supporting continued therapeutic benefit
  \u2022 Discontinuation at this time would result in regression of functional gains

${"─".repeat(60)}
Plan of Care (Continuation)
${"─".repeat(60)}
  Continue physical therapy ${f.ptFreq || "3x/week"} per week
  Duration: ${f.ptDuration || "4 weeks"}
  Focus: ROM restoration, strengthening, neuromuscular re-education
  Reassess in ${f.followUp || "4 weeks"}
  ${f.rxMedications ? `Medications: ${f.rxMedications}\n  ` : ""}Progress treatment intensity as tolerated
  Consider further diagnostic evaluation if progress plateaus

Maximum Medical Improvement (MMI / ME):
The patient has not reached Maximum Medical Improvement and requires continued treatment.

${"─".repeat(60)}
Physician Signature

Armando Falcon, MD
FL License: ME 84789  |  NPI: 1447295126
Wellness Healthcare Clinic Corp
8180 NW 36th St, Suite 213, Doral, FL 33166

Signature: _________________________ Date: ${dos}
${"─".repeat(60)}
Generated by PI Master — RenuviaMD Network`;

    setGeneratedNote(note);
  };

  const generateFinalSOAP = () => {
    const he = f.ptSex === "M" ? "He" : "She";
    const his = f.ptSex === "M" ? "his" : "her";
    const calcAge = (dob) => { if (!dob) return ""; const b = new Date(dob); const t = new Date(); let a = t.getFullYear() - b.getFullYear(); if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--; return a; };
    const age = calcAge(f.ptDOB);
    const dos = new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

    const ICD_DESC_FINAL = {
      "S13.4XXA": "Cervical sprain", "S16.1XXA": "Cervical strain", "S23.3XXA": "Thoracic sprain",
      "S29.012A": "Thoracic strain", "S33.5XXA": "Lumbar sprain", "S39.012A": "Lumbar strain",
      "M54.2": "Cervicalgia", "M54.5": "Low back pain", "M54.6": "Thoracic pain",
      "M62.830": "Muscle spasm", "G44.309": "Post-traumatic headache",
      "M54.12": "Cervical radiculopathy", "M54.16": "Lumbar radiculopathy", "M54.4": "Lumbago with sciatica",
      "M25.511": "R shoulder pain", "M25.512": "L shoulder pain",
      "M25.521": "Elbow pain", "M25.531": "Wrist pain",
      "M25.551": "R hip pain", "M25.552": "L hip pain",
      "M25.561": "R knee pain", "M25.562": "L knee pain",
      "M25.571": "Ankle pain", "M79.641": "Hand pain",
      "S43.421A": "Shoulder sprain", "S46.011A": "Shoulder strain",
      "S53.401A": "Elbow sprain", "S56.111A": "Elbow strain",
      "S63.501A": "Wrist sprain", "S66.111A": "Wrist strain",
      "S63.90XA": "Hand sprain", "S66.911A": "Hand strain",
      "S73.101A": "Hip sprain", "S76.011A": "Hip strain",
      "S83.401A": "Knee sprain", "S86.811A": "Knee strain",
      "S93.401A": "Ankle sprain", "S96.811A": "Ankle strain",
      "F43.10": "PTSD", "F41.1": "GAD", "F32.9": "Depression",
    };

    const ICD_TO_REGION_FINAL = {
      "S13.4XXA": "cervical", "S16.1XXA": "cervical", "M54.2": "cervical", "M54.12": "cervical", "G44.309": "cervical",
      "S23.3XXA": "thoracic", "S29.012A": "thoracic", "M54.6": "thoracic",
      "S33.5XXA": "lumbar", "S39.012A": "lumbar", "M54.5": "lumbar", "M54.16": "lumbar", "M54.4": "lumbar", "M62.830": "lumbar",
      "M25.511": "shoulder", "M25.512": "shoulder", "S43.421A": "shoulder", "S46.011A": "shoulder",
      "M25.521": "elbow", "S53.401A": "elbow", "S56.111A": "elbow",
      "M25.531": "wrist", "S63.501A": "wrist", "S66.111A": "wrist",
      "M79.641": "hand", "S63.90XA": "hand", "S66.911A": "hand",
      "M25.551": "hip", "M25.552": "hip", "S73.101A": "hip", "S76.011A": "hip",
      "M25.561": "knee", "M25.562": "knee", "S83.401A": "knee", "S86.811A": "knee",
      "M25.571": "ankle", "S93.401A": "ankle", "S96.811A": "ankle",
    };

    const REGION_LABELS_FINAL = {
      cervical: "Cervical Spine", thoracic: "Thoracic Spine", lumbar: "Lumbar Spine",
      shoulder: "Shoulder", elbow: "Elbow", wrist: "Wrist", hand: "Hand",
      hip: "Hip", knee: "Knee", ankle: "Ankle",
    };

    // Categorize regions by final status
    const fResolved = Object.entries(finalRegionStatus).filter(([,s]) => s === "Resolved").map(([r]) => REGION_LABELS_FINAL[r] || r);
    const fImproved = Object.entries(finalRegionStatus).filter(([,s]) => s === "Improved with Residual").map(([r]) => REGION_LABELS_FINAL[r] || r);
    const fPersistent = Object.entries(finalRegionStatus).filter(([,s]) => s === "Persistent").map(([r]) => REGION_LABELS_FINAL[r] || r);
    const fPlateaued = Object.entries(finalRegionStatus).filter(([,s]) => s === "Plateaued").map(([r]) => REGION_LABELS_FINAL[r] || r);
    const fWorsened = Object.entries(finalRegionStatus).filter(([,s]) => s === "Worsened").map(([r]) => REGION_LABELS_FINAL[r] || r);

    // Course of treatment narrative
    const courseNarrative = (() => {
      let parts = [];
      if (fResolved.length) parts.push(`${fResolved.join(", ")} ${fResolved.length === 1 ? "has" : "have"} fully resolved`);
      if (fImproved.length) parts.push(`${fImproved.join(", ")} ${fImproved.length === 1 ? "has" : "have"} improved with residual symptoms`);
      if (fPersistent.length) parts.push(`${fPersistent.join(", ")} ${fPersistent.length === 1 ? "remains" : "remain"} persistent`);
      if (fPlateaued.length) parts.push(`${fPlateaued.join(", ")} ${fPlateaued.length === 1 ? "has" : "have"} plateaued`);
      if (fWorsened.length) parts.push(`${fWorsened.join(", ")} ${fWorsened.length === 1 ? "has" : "have"} worsened`);
      return parts.join("; ") + ".";
    })();

    const overallPTResponse = fResolved.length + fImproved.length >= fPersistent.length + fPlateaued.length + fWorsened.length
      ? "the patient responded favorably to conservative treatment overall"
      : fWorsened.length > 0
      ? "limited overall response to conservative treatment with some areas of worsening"
      : "variable response to conservative treatment with ongoing deficits";

    // Non-resolved regions for current complaints
    const nonResolvedFinal = Object.entries(finalRegionStatus).filter(([,s]) => s !== "Resolved").map(([r]) => r);

    // Build current complaints
    const buildCurrentComplaints = () => {
      if (nonResolvedFinal.length === 0) return "  No active complaints. All regions resolved.";
      return nonResolvedFinal.map(regionId => {
        const label = REGION_LABELS_FINAL[regionId] || regionId;
        const painKey = `pain_${regionId}`;
        const curPain = f[painKey] || "N/A";
        const severity = parseInt(curPain) >= 7 ? "severe" : parseInt(curPain) >= 4 ? "moderate" : "mild";
        return `  - ${label}: ${severity} pain (${curPain}/10)`;
      }).join("\n");
    };

    // Build final exam section (ALL regions)
    const allRegionIds = Object.keys(finalRegionStatus);
    const buildFinalExamSection = () => {
      let lines = [];
      allRegionIds.forEach(regionId => {
        const label = REGION_LABELS_FINAL[regionId] || regionId;
        const painKey = `pain_${regionId}`;
        const palpKey = `palp_${regionId}`;
        const romKey = `rom_${regionId}`;
        const prevPain = previousPain[regionId] || 7;
        const curPain = f[painKey] || "N/A";
        const palp = (f[palpKey] || []).join(", ") || "No tenderness";
        const rom = f[romKey] || "Not assessed";
        const spasm = (f[palpKey] || []).includes("Muscle Spasm") ? "Present" : "Absent";

        lines.push(`  ${label}`);
        lines.push(`    Pain: ${curPain}/10 (initially ${prevPain}/10)`);
        lines.push(`    Palpation: ${palp}`);
        lines.push(`    Spasm: ${spasm}`);
        lines.push(`    ROM: ${rom}`);

        if (rom === "Decreased") {
          const romTests = {
            cervical: [["r_cflex","Flexion"],["r_cext","Extension"],["r_clatR","Lat Flexion R"],["r_clatL","Lat Flexion L"],["r_crotR","Rotation R"],["r_crotL","Rotation L"]],
            lumbar: [["r_lflex","Flexion"],["r_lext","Extension"],["r_lsbR","Side Bend R"],["r_lsbL","Side Bend L"]],
            shoulder: [["r_sohR","Overhead R"],["r_sohL","Overhead L"],["r_sbhR","Behind Head R"],["r_sbhL","Behind Head L"]],
            knee: [["r_kchR","Chair Rise R"],["r_kchL","Chair Rise L"]],
            ankle: [["r_ahtwR","Heel-Toe Walk R"],["r_ahtwL","Heel-Toe Walk L"]],
            hip: [["r_hcarR","Get In Car R"],["r_hcarL","Get In Car L"]],
            wrist: [["r_wbR","Wrist Back R"],["r_wbL","Wrist Back L"]],
            elbow: [["r_etsR","Touch Shoulder R"],["r_etsL","Touch Shoulder L"]],
            hand: [["r_hfR","Fist R"],["r_hfL","Fist L"]],
          };
          const tests = romTests[regionId] || [];
          const results = tests.filter(([key]) => f[key]).map(([key, lbl]) => `${lbl}: ${f[key]}`);
          if (results.length) {
            lines.push(`    Functional ROM: ${results.join(", ")}`);
          }
        }
        lines.push("");
      });
      return lines.join("\n");
    };

    // Build final regional assessment
    const icdCodes = f.icdCodes || caseData?.icd10 || [];
    const buildFinalAssessment = () => {
      let lines = [];
      icdCodes.forEach(code => {
        const desc = ICD_DESC_FINAL[code] || code;
        const region = ICD_TO_REGION_FINAL[code];
        const status = region ? (finalRegionStatus[region] || "Persistent") : "Persistent";
        lines.push(`  ${code} ${desc} \u2014 ${status}`);
      });
      return lines.join("\n");
    };

    // Overall assessment narrative
    const buildOverallAssessment = () => {
      const total = allRegionIds.length;
      const resolvedCount = fResolved.length;
      const improvedCount = fImproved.length;
      const persistentCount = fPersistent.length;
      const plateauedCount = fPlateaued.length;
      const worsenedCount = fWorsened.length;

      if (resolvedCount === total) {
        return "All initially injured regions have fully resolved. The patient has returned to pre-accident functional baseline.";
      } else if (resolvedCount + improvedCount >= persistentCount + plateauedCount + worsenedCount) {
        return `Of ${total} initially injured ${total === 1 ? "region" : "regions"}, ${resolvedCount} ${resolvedCount === 1 ? "has" : "have"} resolved and ${improvedCount} ${improvedCount === 1 ? "has" : "have"} improved with residual symptoms. ${persistentCount + plateauedCount + worsenedCount > 0 ? `${persistentCount + plateauedCount + worsenedCount} ${persistentCount + plateauedCount + worsenedCount === 1 ? "region remains" : "regions remain"} with ongoing deficits.` : ""} Overall, the patient has demonstrated meaningful clinical improvement with conservative care.`;
      } else {
        return `Of ${total} initially injured ${total === 1 ? "region" : "regions"}, ${persistentCount + plateauedCount + worsenedCount} ${persistentCount + plateauedCount + worsenedCount === 1 ? "continues" : "continue"} to demonstrate ongoing deficits despite conservative treatment. ${resolvedCount + improvedCount > 0 ? `${resolvedCount + improvedCount} ${resolvedCount + improvedCount === 1 ? "region has" : "regions have"} shown improvement or resolution.` : ""} The patient has not returned to pre-accident functional baseline.`;
      }
    };

    // MMI section
    const mmiSection = mmiReached === true
      ? "The patient has reached Maximum Medical Improvement (MMI)."
      : mmiReached === false
      ? "The patient has not reached Maximum Medical Improvement (MMI)."
      : "MMI determination: Pending";

    const mmiDisclaimer = "Determination of Maximum Medical Improvement is based on the patient's history, objective findings, and response to treatment, within a reasonable degree of medical probability. Any impairment rating should be established in accordance with the AMA Guides.";

    // Medical necessity
    const medNecessity = mmiReached === true
      ? "At this time, continued supervised physical therapy is no longer medically necessary, as the patient has achieved maximum therapeutic benefit from conservative care."
      : "Continued physical therapy remains medically necessary due to persistent symptoms, residual objective findings, functional limitations, and continued measurable response to treatment.";

    // Disposition section
    const dispositionTitle = mmiReached === true ? "Discharge / Continuity of Care" : "Plan of Care Continuation";
    const dispositionText = mmiReached === true
      ? `The patient is discharged from active supervised physical therapy.\n  The patient has been educated on a home exercise program (HEP) for maintenance.\n  The patient is advised to follow up as needed for any recurrence or worsening of symptoms.\n  Referral for specialist evaluation if new symptoms develop or functional decline is noted.`
      : `Continue physical therapy ${f.ptFreq || "3x/week"} per week.\n  Duration: ${f.ptDuration || "4 weeks"}\n  Focus: ROM restoration, strengthening, neuromuscular re-education\n  Reassess at next follow-up visit.\n  Progress treatment intensity as tolerated.\n  Consider further diagnostic evaluation if progress plateaus.`;

    const note = `${"=".repeat(60)}
  WELLNESS HEALTHCARE CLINIC CORP
  8180 NW 36th St, Suite 213 \u00B7 Doral, FL 33166
  Ph: (305) 994-9500 \u00B7 Fax: (786) 441-2179
  Armando Falcon, MD \u00B7 FL License ME 84789 \u00B7 NPI 1447295126
${"=".repeat(60)}

FINAL VISIT / DISCHARGE NOTE
${"─".repeat(60)}

Patient Information
  Name: ${f.ptLast || "___"}, ${f.ptFirst || "___"}
  DOB: ${f.ptDOB || "___"}  |  Age: ${age}  |  Sex: ${f.ptSex || "___"}
  Date of Accident: ${f.doa || "___"}
  Date of Final Visit: ${dos}
  Insurance: ${f.pipCarrier || "___"}

${"─".repeat(60)}
Course of Treatment Summary
${"─".repeat(60)}
The patient completed a course of conservative treatment following injuries sustained in a motor vehicle accident on ${f.doa || "___"}. ${courseNarrative} Review of therapy documentation indicates ${overallPTResponse}.

${"─".repeat(60)}
Current Complaints
${"─".repeat(60)}
${buildCurrentComplaints()}

${"─".repeat(60)}
Final Focused Physical Examination
${"─".repeat(60)}
${buildFinalExamSection()}
${"─".repeat(60)}
Final Regional Assessment
${"─".repeat(60)}
${buildFinalAssessment()}

${"─".repeat(60)}
Overall Assessment
${"─".repeat(60)}
${buildOverallAssessment()}

${"─".repeat(60)}
Functional Status
${"─".repeat(60)}
  Activities of Daily Living (ADLs): Conserved
  Work Tolerance: ${funcStatus.work || "Not assessed"}
  Driving: ${funcStatus.driving || "Not assessed"}
  Sleep: ${funcStatus.sleep || "Not assessed"}
  Mobility: ${funcStatus.mobility || "Not assessed"}

${"─".repeat(60)}
Maximum Medical Improvement (MMI)
${"─".repeat(60)}
${mmiSection}

${mmiDisclaimer}

${"─".repeat(60)}
Medical Necessity
${"─".repeat(60)}
${medNecessity}

${"─".repeat(60)}
${dispositionTitle}
${"─".repeat(60)}
  ${dispositionText}

${"─".repeat(60)}
Physician Signature

Armando Falcon, MD
FL License: ME 84789  |  NPI: 1447295126
Wellness Healthcare Clinic Corp

Signature: _________________________ Date: ${dos}
${"─".repeat(60)}
Generated by PI Master \u2014 RenuviaMD Network`;

    setGeneratedNote(note);
  };

  const handleSign = () => {
    setLocked(true);
    setSigned(true);
    if (onSave) onSave({ ...f, medications: medList, signed: true, signedAt: new Date().toISOString() });
  };

  // ── RENDER STEP CONTENT ────────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {

      // ════════════════════════════════════════════════════════════════════
      // STAFF STEP 1: PATIENT DEMOGRAPHICS
      // ════════════════════════════════════════════════════════════════════
      case "patient":
        return (
          <div>
            <SectionHead title="Patient Demographics" sub="Pre-filled from intake — verify and update" />

            {/* Patient Information */}
            <div style={{ padding: 16, background: C.bgElevated, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: C.textPrimary }}>Patient Information</span>
                <span style={{ fontSize: 9, fontWeight: 800, padding: "3px 8px", borderRadius: 4, background: C.greenBg, border: `1px solid ${C.green}33`, color: C.green }}>STAFF</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                <Input label="Last Name *" value={f.ptLast} onChange={e => u("ptLast", e.target.value)} disabled={locked} />
                <Input label="First Name *" value={f.ptFirst} onChange={e => u("ptFirst", e.target.value)} disabled={locked} />
                <Input label="Date of Birth *" type="date" value={f.ptDOB} onChange={e => u("ptDOB", e.target.value)} disabled={locked} />
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5 }}>Sex</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {["M", "F", "Other"].map(s => {
                      const on = f.ptSex === s;
                      return (
                        <button key={s} onClick={() => !locked && u("ptSex", s)} style={{
                          flex: 1, padding: "10px 0", borderRadius: 8, cursor: locked ? "default" : "pointer",
                          fontSize: 12, fontWeight: 700,
                          border: `1.5px solid ${on ? C.blue : C.border}`,
                          background: on ? C.blueGlow : "transparent",
                          color: on ? C.blueBright : C.textMuted,
                        }}>{on ? "✓ " : ""}{s}</button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <Input label="Phone *" value={f.ptPhone} onChange={e => u("ptPhone", e.target.value)} disabled={locked} placeholder="(305) 555-0001" />
                <Input label="Email" value={f.ptEmail} onChange={e => u("ptEmail", e.target.value)} disabled={locked} placeholder="patient@email.com" />
                <Input label="ZIP Code" value={f.ptZip} onChange={e => u("ptZip", e.target.value)} disabled={locked} placeholder="33166" />
              </div>
            </div>

            {/* Government ID Verification */}
            <div style={{ padding: 16, background: C.bgElevated, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: C.textPrimary }}>Government ID Verification</span>
                <span style={{ fontSize: 9, fontWeight: 800, padding: "3px 8px", borderRadius: 4, background: C.blueGlow, border: `1px solid ${C.blue}33`, color: C.blueBright }}>REQUIRED</span>
              </div>
              <PillSelect label="ID Type" options={["FL Driver License", "Passport", "State ID", "Other Govt ID"]} value={f.idType} onChange={v => u("idType", v)} disabled={locked} />
              <div style={{ marginTop: 12, padding: 24, background: C.bgCard, borderRadius: 10, border: `2px dashed ${f.idPhotoName ? C.green : C.border}`, textAlign: "center" }}>
                {f.idPhotoName ? (
                  <div>
                    <div style={{ fontSize: 14, color: C.green, fontWeight: 700, marginBottom: 4 }}>✓ {f.idPhotoName}</div>
                    <button onClick={() => u("idPhotoName", "")} style={{ padding: "4px 12px", borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: "pointer", background: C.bgElevated, border: `1px solid ${C.border}`, color: C.textMuted }}>Remove</button>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 14, marginBottom: 4 }}>📷</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.textPrimary, marginBottom: 4 }}>Government ID Photo</div>
                    <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 12 }}>On mobile: tap button — camera opens directly. Desktop: file picker opens.</div>
                    <label style={{
                      display: "inline-block", padding: "10px 24px", borderRadius: 8, fontSize: 12, fontWeight: 800,
                      cursor: "pointer", background: C.blue, color: C.white, border: "none",
                      boxShadow: `0 0 16px ${C.blueGlow}`,
                    }}>
                      📷 Take Photo / Upload ID
                      <input type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                        onChange={e => { if (e.target.files?.[0]) u("idPhotoName", e.target.files[0].name); }} />
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* PIP Insurance */}
            <div style={{ padding: 16, background: C.bgElevated, borderRadius: 10, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: C.textPrimary, marginBottom: 12 }}>PIP Insurance</div>
              <Select label="PIP Insurance Carrier" value={f.pipCarrier} onChange={e => u("pipCarrier", e.target.value)} disabled={locked}>
                <option value="">-- Select Carrier --</option>
                {["State Farm", "GEICO", "Allstate", "Progressive", "USAA", "Direct General Insurance Company", "Travelers", "Farmers", "Liberty Mutual", "Other"].map(c =>
                  <option key={c} value={c}>{c}</option>
                )}
              </Select>
            </div>
          </div>
        );

      // ════════════════════════════════════════════════════════════════════
      // STAFF STEP 2: ACCIDENT DETAILS
      // ════════════════════════════════════════════════════════════════════
      case "accident":
        return (
          <div>
            <SectionHead title="Accident Details" sub="All binary selections -- no free text" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <Input label="Date of Accident" type="date" value={f.doa} onChange={e => u("doa", e.target.value)} disabled={locked} />
              <Input label="Date of First Visit" type="date" value={f.dofv} onChange={e => u("dofv", e.target.value)} disabled={locked} />
            </div>
            <PillSelect label="Mechanism of Injury" options={["Rear-End MVA", "Side Impact", "T-Bone", "Rollover", "Head-On"]} value={f.moi} onChange={v => u("moi", v)} disabled={locked} />
            <PillSelect label="Patient Position" options={["Driver", "Front Passenger", "Rear Passenger"]} value={f.patientPosition} onChange={v => u("patientPosition", v)} disabled={locked} />
            <PillSelect label="Vehicle at Impact" options={["Stopped", "Moving Slowly", "Moving at Speed", "Unknown"]} value={f.vehicleAtImpact} onChange={v => u("vehicleAtImpact", v)} disabled={locked} />
            <PillSelect label="Seatbelt" options={[
              { label: "Yes -- Seatbelt worn", value: "Yes -- Seatbelt worn" },
              { label: "No -- Unrestrained", value: "No -- Unrestrained" },
              { label: "Seatbelt + Airbag", value: "Seatbelt + Airbag" },
            ]} value={f.seatbelt} onChange={v => u("seatbelt", v)} disabled={locked} />
            <PillSelect label="Airbag Deployed" options={["No", "Yes"]} value={f.airbag} onChange={v => u("airbag", v)} disabled={locked} />
            <PillSelect label="Vehicle Drivable" options={["Yes", "No"]} value={f.vehicleDrivable} onChange={v => u("vehicleDrivable", v)} disabled={locked} />
            <PillSelect label="Loss of Consciousness" options={["No", "Yes"]} value={f.loc} onChange={v => u("loc", v)} disabled={locked} />
          </div>
        );

      // Insurance step removed — merged into Patient step

      // ════════════════════════════════════════════════════════════════════
      // STAFF STEP 4: LEGAL DOCUMENTS
      // ════════════════════════════════════════════════════════════════════
      case "legal": {
        const generateDocContent = (key) => {
          const ptName = `${f.ptLast}, ${f.ptFirst}`;
          const dos = f.dofv || new Date().toISOString().split("T")[0];
          if (key === "patientForms") return `PATIENT CONSENT FORMS\n${"─".repeat(50)}\n\nPatient: ${ptName}\nDOB: ${f.ptDOB}\nDate: ${dos}\n\nI, ${f.ptFirst} ${f.ptLast}, hereby consent to evaluation and treatment at Wellness Healthcare Clinic Corp for injuries sustained in a motor vehicle accident on ${f.doa}.\n\nI understand that my treatment will be billed to my PIP insurance carrier (${f.pipCarrier || "N/A"}).\n\nI authorize the release of medical information necessary for insurance claims processing.\n\n\nPatient Signature: _________________________ Date: ${dos}\n\nWitness: _________________________ Date: ${dos}\n\n${"─".repeat(50)}\nWellness Healthcare Clinic Corp\n8180 NW 36th St, Suite 213, Doral, FL 33166\nPh: (305) 994-9500 | Fax: (786) 441-2179`;
          if (key === "oirAffidavit") return `OIR FORM B1-1571 + SWORN AFFIDAVIT\n${"─".repeat(50)}\nFLORIDA OFFICE OF INSURANCE REGULATION\n\nPatient: ${ptName}\nDOB: ${f.ptDOB}\nDate of Accident: ${f.doa}\nDate of Service: ${dos}\nInsurance Carrier: ${f.pipCarrier || "N/A"}\n\nSWORN STATEMENT UNDER OATH\n\nI, Dr. Armando Falcon, MD (FL License ME 84789), hereby attest under penalty of perjury that the medical services provided to the above-named patient are medically necessary and directly related to injuries sustained in the motor vehicle accident of ${f.doa}.\n\nThe patient presented with: ${(f.chiefComplaints || []).join(", ") || "injuries from MVA"}.\n\nTreatment rendered is consistent with accepted medical practice for personal injury protection cases under Florida Statute §627.736.\n\n\nPhysician Signature: _________________________ Date: ${dos}\nDr. Armando Falcon, MD | NPI: 1447295126 | FL #ME 84789\n\nNotary: _________________________ Commission #: _________\nExpiration: ___________\n\n${"─".repeat(50)}\nWellness Healthcare Clinic Corp · Doral, FL 33166`;
          if (key === "emcCert") return `EMERGENCY MEDICAL CONDITION (EMC) CERTIFICATION\n${"─".repeat(50)}\nFL §627.732 | FL §627.736\n\nPatient: ${ptName}\nDOB: ${f.ptDOB}\nDate of Accident: ${f.doa}\nDate of Initial Evaluation: ${dos}\n\nCERTIFICATION\n\nI, Dr. Armando Falcon, MD, hereby certify that the above-named patient presents with an Emergency Medical Condition as defined under Florida Statute §627.732(4).\n\nThe patient's condition could reasonably be expected to result in:\n  [X] Serious jeopardy to patient health\n  [X] Serious impairment to bodily functions\n  [X] Serious dysfunction of any bodily organ or part\n\nClinical Findings:\nPatient sustained injuries in MVA on ${f.doa}. Presenting complaints include ${(f.chiefComplaints || []).join(", ") || "musculoskeletal injuries"}. Pain level: ${f.painScore || "7"}/10.\n\nPhysical examination reveals objective findings consistent with acute traumatic injury requiring medical intervention beyond the $2,500 non-EMC limit.\n\nThis certification entitles the patient to the full $10,000 PIP benefit.\n\n\nPhysician Signature: _________________________ Date: ${dos}\nDr. Armando Falcon, MD | NPI: 1447295126 | FL #ME 84789\n\n${"─".repeat(50)}\nWellness Healthcare Clinic Corp · Doral, FL 33166`;
          return "";
        };

        return (
          <div>
            <SectionHead title="Legal Documents" sub="Generate, preview, edit, and print required forms" />
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { key: "patientForms", label: "Patient Consent Forms", sub: "Consent forms for patient signature", icon: "📋" },
                { key: "oirAffidavit", label: "OIR B1-1571 + Sworn Affidavit", sub: "For physician signature after scan", icon: "📜" },
                { key: "emcCert", label: "EMC Certification", sub: "Emergency Medical Condition — initial visit", icon: "⚕️" },
              ].map(doc => (
                <div key={doc.key} style={{
                  padding: "14px 18px", background: C.bgElevated, borderRadius: 10,
                  border: `1px solid ${docStatus[doc.key] ? C.green + "44" : C.border}`,
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 20 }}>{doc.icon}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>{doc.label}</div>
                        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{doc.sub}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {docStatus[doc.key] && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: C.green, padding: "3px 8px", borderRadius: 6, background: C.greenBg, border: `1px solid ${C.green}33` }}>Generated ✓</span>
                      )}
                      <button onClick={() => { setDocStatus(prev => ({ ...prev, [doc.key]: true })); setPreviewDoc({ key: doc.key, label: doc.label, content: generateDocContent(doc.key) }); }} disabled={locked} style={{
                        padding: "8px 16px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                        cursor: locked ? "not-allowed" : "pointer",
                        background: docStatus[doc.key] ? C.bgElevated : C.blue,
                        color: docStatus[doc.key] ? C.textMuted : C.white,
                        border: `1px solid ${docStatus[doc.key] ? C.border : C.blue}`,
                      }}>{docStatus[doc.key] ? "Regenerate" : "Generate"}</button>
                      {docStatus[doc.key] && (
                        <button onClick={() => setPreviewDoc({ key: doc.key, label: doc.label, content: generateDocContent(doc.key) })} style={{
                          padding: "8px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                          cursor: "pointer", background: C.bgElevated, color: C.blueBright,
                          border: `1px solid ${C.blue}44`,
                        }}>Preview</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* PDF Preview Panel */}
            {previewDoc && (
              <div style={{ marginTop: 16, background: C.bgCard, border: `1px solid ${C.blue}44`, borderRadius: 12, overflow: "hidden" }}>
                <div style={{ padding: "10px 16px", background: C.bgElevated, borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.blueBright }}>Preview: {previewDoc.label}</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => window.print()} style={{ padding: "5px 12px", borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: "pointer", background: C.green, color: C.white, border: "none" }}>Print</button>
                    <button onClick={() => { setDocStatus(prev => ({ ...prev, [previewDoc.key]: true })); setPreviewDoc({ ...previewDoc, content: generateDocContent(previewDoc.key) }); }} style={{ padding: "5px 12px", borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: "pointer", background: C.amber, color: "#000", border: "none" }}>Regenerate</button>
                    <button onClick={() => setPreviewDoc(null)} style={{ padding: "5px 12px", borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: "pointer", background: C.bgElevated, color: C.textMuted, border: `1px solid ${C.border}` }}>Close</button>
                  </div>
                </div>
                <textarea
                  value={previewDoc.content}
                  onChange={e => setPreviewDoc({ ...previewDoc, content: e.target.value })}
                  style={{ width: "100%", minHeight: 400, padding: "16px 20px", background: "#fff", color: "#000", fontSize: 12, fontFamily: "Georgia, 'Times New Roman', 'Palatino Linotype', serif", lineHeight: 1.8, border: "none", outline: "none", boxSizing: "border-box", resize: "vertical" }}
                />
              </div>
            )}

            {/* Handoff to MD */}
            {isStaff && phase === "staff" && (
              <div style={{ marginTop: 24, textAlign: "center" }}>
                <button onClick={handoffToMD} style={{
                  padding: "12px 32px", borderRadius: 8, fontSize: 13, fontWeight: 800,
                  cursor: "pointer", background: C.blue, color: C.white, border: "none",
                  boxShadow: `0 0 20px ${C.blueGlow}`, letterSpacing: "0.06em", textTransform: "uppercase",
                }}>Ready for Physician Review →</button>
              </div>
            )}
          </div>
        );
      }

      // ════════════════════════════════════════════════════════════════════
      // MD STEP 5: MEDICAL INTERVIEW
      // ════════════════════════════════════════════════════════════════════
      case "interview":
        return (
          <div>
            <SectionHead title="Medical Interview" sub="Chief complaints, pain, allergies, history" />
            <PillSelect label="Chief Complaints" multi options={[
              "Neck Pain", "Upper Back Pain", "Lower Back Pain", "Headache",
              "Left Shoulder Pain", "Right Shoulder Pain",
              "Left Knee Pain", "Right Knee Pain",
              "Left Hip Pain", "Right Hip Pain",
              "Numbness/Tingling", "Dizziness", "Jaw Pain", "Chest Pain",
            ]} value={f.chiefComplaints} onChange={v => u("chiefComplaints", v)} disabled={locked} />

            <PainScoreRow value={f.painScore} onChange={v => u("painScore", v)} disabled={locked} />

            <PillSelect label="Pain Onset" options={["Immediate", "Gradual", "Delayed"]} value={f.painOnset} onChange={v => u("painOnset", v)} disabled={locked} />

            <PillSelect label="Allergies" multi options={["NKDA", "Penicillin", "Sulfa", "NSAIDs", "Codeine", "Latex", "Iodine", "Other"]} value={f.allergies} onChange={v => u("allergies", v)} disabled={locked} />

            {/* Current Medications -- add/remove list */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 7 }}>Current Medications</div>
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <input value={medInput} onChange={e => setMedInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && medInput.trim()) { setMedList(prev => [...prev, medInput.trim()]); setMedInput(""); } }}
                  placeholder="Type medication and press Enter"
                  disabled={locked}
                  style={{
                    flex: 1, padding: "9px 13px", background: C.bgElevated, border: `1px solid ${C.border}`,
                    borderRadius: 8, fontSize: 12, color: C.textPrimary, outline: "none", fontFamily: "inherit",
                  }} />
                <button onClick={() => { if (medInput.trim()) { setMedList(prev => [...prev, medInput.trim()]); setMedInput(""); } }}
                  disabled={locked || !medInput.trim()}
                  style={{
                    padding: "8px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                    cursor: "pointer", background: C.blue, color: C.white, border: "none",
                    opacity: medInput.trim() ? 1 : 0.4,
                  }}>+ Add</button>
              </div>
              {medList.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {medList.map((med, i) => (
                    <span key={i} style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "5px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: C.bluePale, color: C.blueBright, border: `1px solid ${C.blue}44`,
                    }}>
                      {med}
                      {!locked && (
                        <span onClick={() => setMedList(prev => prev.filter((_, idx) => idx !== i))}
                          style={{ cursor: "pointer", fontSize: 14, lineHeight: 1, opacity: 0.7 }}>\u00D7</span>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <PillSelect label="Past Medical History" multi options={[
              "None", "HTN", "Diabetes", "Asthma", "COPD", "Cardiac", "Thyroid",
              "Arthritis", "Prior Back Surgery", "Prior Neck Surgery", "Depression", "Anxiety",
            ]} value={f.pmh} onChange={v => u("pmh", v)} disabled={locked} />

            <PillSelect label="ER Visit After Accident" options={["Yes", "No"]} value={f.erVisit} onChange={v => u("erVisit", v)} disabled={locked} />
            {f.erVisit === "Yes" && (
              <div style={{ marginBottom: 14 }}>
                <Input label="ER Facility" value={f.erFacility} onChange={e => u("erFacility", e.target.value)} disabled={locked} />
              </div>
            )}

            <PillSelect label="Prior Accidents" options={["Yes", "No"]} value={f.priorAccidents} onChange={v => u("priorAccidents", v)} disabled={locked} />
            {f.priorAccidents === "Yes" && (
              <div style={{ marginBottom: 14 }}>
                <Input label="Prior Accident Year" value={f.priorAccidentYear} onChange={e => u("priorAccidentYear", e.target.value)} disabled={locked} />
              </div>
            )}
          </div>
        );

      // ════════════════════════════════════════════════════════════════════
      // MD STEP 6: PHYSICAL EXAM
      // ════════════════════════════════════════════════════════════════════
      case "exam": {
        // ── Data-driven region definitions ─────────────────────────────
        const ONSET_OPTIONS = ["Immediate", "Within hours", "Next day", "Gradual over days"];

        const EXAM_REGIONS = [
          {
            id: "cervical", label: "Cervical Spine", color: C.amber, icon: "\u{1F9B4}",
            palpOptions: ["Paraspinal TTP", "Facet TTP", "Muscle Spasm", "Trigger Points"],
            romTests: [
              { key: "r_cflex", label: "Chin to Chest", desc: "Flexion ~50\u00B0", command: "Tuck your chin to your chest", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_cext",  label: "Look at Ceiling", desc: "Extension ~60\u00B0", command: "Look up at the ceiling", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_clatR", label: "Ear to Shoulder R", desc: "Lateral ~45\u00B0", command: "Tilt your right ear to your right shoulder", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_clatL", label: "Ear to Shoulder L", desc: "Lateral ~45\u00B0", command: "Tilt your left ear to your left shoulder", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_crotR", label: "Look Over Shoulder R", desc: "Rotation ~70\u00B0", command: "Turn and look over your right shoulder", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_crotL", label: "Look Over Shoulder L", desc: "Rotation ~70\u00B0", command: "Turn and look over your left shoulder", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          {
            id: "thoracic", label: "Thoracic Spine", color: C.amber, icon: "\u{1F9B4}",
            palpOptions: ["Paraspinal TTP", "Costovertebral TTP", "Muscle Spasm", "Trigger Points"],
            romTests: [],
          },
          {
            id: "lumbar", label: "Lumbar Spine", color: C.amber, icon: "\u{1F9B4}",
            palpOptions: ["Paraspinal TTP", "Facet TTP", "SI Joint TTP", "Muscle Spasm", "Trigger Points"],
            romTests: [
              { key: "r_lflex", label: "Fingertip to Floor", desc: "Flexion ~60\u00B0", command: "Bend forward and reach for the floor", opts: ["Normal \u22644in","Mid-shin","Knee level","Cannot"] },
              { key: "r_lext",  label: "Backward Lean", desc: "Extension ~25\u00B0", command: "Lean backward gently", opts: ["Normal","Partial","Minimal","Cannot"] },
              { key: "r_lsbR",  label: "Side Bend Right", desc: "~25\u00B0", command: "Bend to the right side", opts: ["Normal","Partial","Minimal","Cannot"] },
              { key: "r_lsbL",  label: "Side Bend Left", desc: "~25\u00B0", command: "Bend to the left side", opts: ["Normal","Partial","Minimal","Cannot"] },
            ],
          },
          {
            id: "shoulder", label: "Shoulder (R/L)", color: C.purple, icon: "\u{1F4AA}",
            palpOptions: ["TTP", "Swelling", "Crepitus", "Impingement"],
            romTests: [
              { key: "r_sohR",  label: "Raise Arm Overhead R", desc: "~180\u00B0", command: "Raise your right arm straight up", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_sohL",  label: "Raise Arm Overhead L", desc: "~180\u00B0", command: "Raise your left arm straight up", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_sbhR",  label: "Reach Behind Head R", desc: "~90\u00B0 ER", command: "Reach behind your head with your right hand", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_sbhL",  label: "Reach Behind Head L", desc: "~90\u00B0 ER", command: "Reach behind your head with your left hand", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_stirR", label: "Tuck In Shirt R", desc: "IR ~70\u00B0", command: "Reach behind your back as if tucking in your shirt (right)", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_stirL", label: "Tuck In Shirt L", desc: "IR ~70\u00B0", command: "Reach behind your back as if tucking in your shirt (left)", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          {
            id: "elbow", label: "Elbow (R/L)", color: C.purple, icon: "\u{1F4AA}",
            palpOptions: ["TTP", "Swelling", "Crepitus"],
            romTests: [
              { key: "r_etsR",  label: "Touch Own Shoulder R", desc: "~145\u00B0", command: "Bend your right elbow and touch your shoulder", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_etsL",  label: "Touch Own Shoulder L", desc: "~145\u00B0", command: "Bend your left elbow and touch your shoulder", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_edkR",  label: "Turn Doorknob R", desc: "~90\u00B0", command: "Rotate your right forearm as if turning a doorknob", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_edkL",  label: "Turn Doorknob L", desc: "~90\u00B0", command: "Rotate your left forearm as if turning a doorknob", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          {
            id: "wrist", label: "Wrist (R/L)", color: C.purple, icon: "\u{270B}",
            palpOptions: ["TTP", "Swelling", "Crepitus"],
            romTests: [
              { key: "r_wbR", label: "Bend Wrist Back R", desc: "~70\u00B0", command: "Bend your right wrist backward", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_wbL", label: "Bend Wrist Back L", desc: "~70\u00B0", command: "Bend your left wrist backward", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_wfR", label: "Bend Wrist Forward R", desc: "~80\u00B0", command: "Bend your right wrist forward", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_wfL", label: "Bend Wrist Forward L", desc: "~80\u00B0", command: "Bend your left wrist forward", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          {
            id: "hand", label: "Hand / Grip (R/L)", color: C.purple, icon: "\u{270A}",
            palpOptions: ["TTP", "Swelling"],
            romTests: [
              { key: "r_hfR", label: "Make Fist R", desc: "", command: "Make a tight fist with your right hand", opts: ["Full","Partial","Minimal","Cannot"] },
              { key: "r_hfL", label: "Make Fist L", desc: "", command: "Make a tight fist with your left hand", opts: ["Full","Partial","Minimal","Cannot"] },
              { key: "r_hpR", label: "Pinch Test R", desc: "", command: "Pinch my finger with your right thumb and index finger", opts: ["Normal","Weak","Cannot"] },
              { key: "r_hpL", label: "Pinch Test L", desc: "", command: "Pinch my finger with your left thumb and index finger", opts: ["Normal","Weak","Cannot"] },
            ],
          },
          {
            id: "hip", label: "Hip (R/L)", color: C.green, icon: "\u{1F9B5}",
            palpOptions: ["TTP", "Swelling", "Crepitus"],
            romTests: [
              { key: "r_hcarR", label: "Get In Car R", desc: "~120\u00B0", command: "Lift your right leg as if stepping into a car", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_hcarL", label: "Get In Car L", desc: "~120\u00B0", command: "Lift your left leg as if stepping into a car", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_hssR",  label: "Step Sideways R", desc: "~45\u00B0", command: "Step sideways to the right", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_hssL",  label: "Step Sideways L", desc: "~45\u00B0", command: "Step sideways to the left", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          {
            id: "knee", label: "Knee (R/L)", color: C.green, icon: "\u{1F9B5}",
            palpOptions: ["TTP", "Effusion", "Crepitus", "McMurray"],
            romTests: [
              { key: "r_kchR", label: "Rise from Chair R", desc: "~135\u00B0", command: "Stand up from a seated position using your right leg", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_kchL", label: "Rise from Chair L", desc: "~135\u00B0", command: "Stand up from a seated position using your left leg", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_ksuR", label: "Step Up R", desc: "", command: "Step up onto a platform with your right leg", opts: ["Stable","Unsteady","Cannot"] },
              { key: "r_ksuL", label: "Step Up L", desc: "", command: "Step up onto a platform with your left leg", opts: ["Stable","Unsteady","Cannot"] },
            ],
          },
          {
            id: "ankle", label: "Ankle (R/L)", color: C.green, icon: "\u{1F9B6}",
            palpOptions: ["TTP", "Swelling", "Crepitus"],
            romTests: [
              { key: "r_ahtwR", label: "Heel-Toe Walk R", desc: "~20\u00B0 DF", command: "Walk heel-to-toe in a straight line", opts: ["Normal","Antalgic","Severe","Cannot"] },
              { key: "r_ahtwL", label: "Heel-Toe Walk L", desc: "~20\u00B0 DF", command: "Walk heel-to-toe in a straight line", opts: ["Normal","Antalgic","Severe","Cannot"] },
              { key: "r_attR",  label: "Stand on Tiptoes R", desc: "~50\u00B0 PF", command: "Stand on the toes of your right foot", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_attL",  label: "Stand on Tiptoes L", desc: "~50\u00B0 PF", command: "Stand on the toes of your left foot", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
        ];

        // ── Functional ROM test card renderer ──────────────────────────
        const RomTestCard = ({ test }) => {
          const val = f[test.key];
          const optColor = (opt) => {
            const first = test.opts[0];
            const last = test.opts[test.opts.length - 1];
            if (opt === first) return C.green;
            if (opt === last || opt === "Cannot") return C.red;
            return C.amber;
          };
          return (
            <div style={{
              background: C.bgCard, borderRadius: 10, border: `1px solid ${C.border}`,
              padding: "10px 14px", marginBottom: 8,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.textPrimary }}>{test.label}</span>
                {test.desc && <span style={{ fontSize: 10, color: C.textMuted, fontWeight: 600 }}>{test.desc}</span>}
              </div>
              <div style={{ fontSize: 10, fontStyle: "italic", color: C.textSecond, marginBottom: 8 }}>
                &quot;{test.command}&quot;
              </div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {test.opts.map(opt => {
                  const sel = val === opt;
                  const clr = optColor(opt);
                  return (
                    <button key={opt} onClick={() => !locked && u(test.key, sel ? "" : opt)} disabled={locked} style={{
                      padding: "5px 12px", borderRadius: 16, fontSize: 10, fontWeight: 700,
                      cursor: locked ? "not-allowed" : "pointer",
                      background: sel ? clr + "18" : C.bgElevated,
                      color: sel ? clr : C.textMuted,
                      border: `1.5px solid ${sel ? clr : C.border}`,
                      boxShadow: sel ? `0 0 8px ${clr}25` : "none",
                      transition: "all 0.15s", opacity: locked ? 0.5 : 1,
                    }}>{opt}</button>
                  );
                })}
              </div>
            </div>
          );
        };

        // ── Region card renderer ───────────────────────────────────────
        const RegionCard = ({ region }) => {
          const painKey = `pain_${region.id}`;
          const onsetKey = `painOnset_${region.id}`;
          const palpKey = `palp_${region.id}`;
          const romKey = `rom_${region.id}`;
          const isDecreased = f[romKey] === "Decreased";
          const romDone = region.romTests.length > 0 && region.romTests.some(t => f[t.key]);
          return (
            <div style={{
              padding: 16, background: C.bgElevated, borderRadius: 10,
              border: `1px solid ${isDecreased ? C.amber + "66" : C.border}`, marginBottom: 12,
            }}>
              <div style={{
                fontSize: 12, fontWeight: 800, color: region.color, marginBottom: 14,
                letterSpacing: "0.06em", textTransform: "uppercase",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span>{region.icon}</span> {region.label}
                </div>
                {romDone && <span style={{ fontSize: 9, color: C.green, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: C.greenBg, border: `1px solid ${C.green}33` }}>ROM Documented ✓</span>}
              </div>

              {/* Row 1: Pain Score + Pain Onset */}
              <PainScoreRow value={f[painKey]} onChange={v => u(painKey, v)} disabled={locked} />
              <PillSelect label="Pain Onset" options={ONSET_OPTIONS} value={f[onsetKey]} onChange={v => u(onsetKey, v)} disabled={locked} />

              {/* Row 2: Palpation + ROM */}
              <PillSelect label="Palpation (Multi)" multi options={region.palpOptions} value={f[palpKey]} onChange={v => u(palpKey, v)} disabled={locked} />
              <PillSelect label="ROM" options={["Normal", "Decreased"]} value={f[romKey]} onChange={v => u(romKey, v)} disabled={locked} />

              {/* Row 3: If Decreased → show button to open Functional ROM modal */}
              {isDecreased && region.romTests.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <button onClick={() => setRomModal(region)} disabled={locked} style={{
                    width: "100%", padding: "10px 16px", borderRadius: 8, cursor: locked ? "not-allowed" : "pointer",
                    background: romDone ? C.greenBg : C.amberBg,
                    border: `1px solid ${romDone ? C.green : C.amber}44`,
                    color: romDone ? C.green : C.amber,
                    fontSize: 12, fontWeight: 700, textAlign: "left",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                  }}>
                    <span>{romDone ? "✓ Functional ROM Documented — Click to Review/Edit" : "⚠ ROM Decreased — Click to Document Functional ROM"}</span>
                    <span style={{ fontSize: 11 }}>→</span>
                  </button>
                </div>
              )}
            </div>
          );
        };

        // ── ROM MODAL ──────────────────────────────────────────────────────
        const RomModal = () => {
          if (!romModal) return null;
          return (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, overflow: "auto" }}
              onClick={e => { if (e.target === e.currentTarget) setRomModal(null); }}>
              <div style={{ background: C.bgCard, border: `1px solid ${C.amber}44`, borderRadius: 16, width: "100%", maxWidth: 800, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                {/* Header */}
                <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, background: C.bgElevated, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 800, color: C.amber, textTransform: "uppercase", letterSpacing: "0.12em" }}>Functional ROM Assessment</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.textPrimary, display: "flex", alignItems: "center", gap: 8 }}>
                      <span>{romModal.icon}</span> {romModal.label}
                    </div>
                    <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>Physician observes patient performing each movement. Select result.</div>
                  </div>
                  <button onClick={() => setRomModal(null)} style={{ padding: "6px 14px", borderRadius: 8, cursor: "pointer", background: C.bgElevated, border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 12, fontWeight: 700 }}>✕ Close</button>
                </div>
                {/* Tests */}
                <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
                  <div style={{ padding: "8px 12px", background: C.blueGlow, border: `1px solid ${C.blue}33`, borderRadius: 8, marginBottom: 16, fontSize: 10, color: C.blueBright }}>
                    Results auto-populate ICD-10 and SOAP note. Each result maps to an approximate degree range.
                  </div>
                  {romModal.romTests.map(test => (
                    <RomTestCard key={test.key} test={test} />
                  ))}
                </div>
                {/* Footer */}
                <div style={{ padding: "12px 20px", borderTop: `1px solid ${C.border}`, background: C.bgElevated, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                  <div style={{ fontSize: 10, color: C.textMuted }}>
                    {romModal.romTests.filter(t => f[t.key]).length} / {romModal.romTests.length} tests completed
                  </div>
                  <button onClick={() => setRomModal(null)} style={{ padding: "8px 20px", borderRadius: 8, cursor: "pointer", background: C.blue, border: "none", color: C.white, fontSize: 12, fontWeight: 700 }}>Done</button>
                </div>
              </div>
            </div>
          );
        };

        return (
          <div>
            <SectionHead title="Physical Examination" sub="HEENT, Spine, Extremities — binary assessment + functional ROM" />

            {/* HEENT / General */}
            <div style={{ padding: 16, background: C.bgElevated, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.blueBright, marginBottom: 12, letterSpacing: "0.06em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 8 }}>
                <span>{"\u{1F9E0}"}</span> General / HEENT / Neuro
              </div>
              <PillSelect label="General Status" options={["Alert & Oriented x4", "Lethargic", "Confused"]} value={f.generalStatus} onChange={v => u("generalStatus", v)} disabled={locked} />
              <PillSelect label="Ambulation" options={["Normal Gait", "Antalgic Gait", "Uses Assistive Device"]} value={f.ambulation} onChange={v => u("ambulation", v)} disabled={locked} />
              <PillSelect label="HEENT" multi options={["Normocephalic", "Atraumatic", "PERRL", "TMs Clear", "Oropharynx Normal"]} value={f.heent} onChange={v => u("heent", v)} disabled={locked} />
              <PillSelect label="Neurological" multi options={["Grossly Intact", "DTRs Symmetric", "Sensation Intact", "Motor 5/5", "Babinski Negative"]} value={f.neuro} onChange={v => u("neuro", v)} disabled={locked} />
            </div>

            {/* All body regions rendered from data */}
            {EXAM_REGIONS.map(region => (
              <RegionCard key={region.id} region={region} />
            ))}

            {/* Functional ROM Modal */}
            <RomModal />
          </div>
        );
      }

      // ════════════════════════════════════════════════════════════════════
      // MD STEP 7: DIAGNOSIS + PLAN
      // ════════════════════════════════════════════════════════════════════
      case "dx": {
        // ── AUTO-PRESELECT ICD-10 from complaints + exam findings ──────
        const ICD_REGIONS = [
          { region: "Cervical Spine", color: C.blue, codes: [
            { code: "S13.4XXA", desc: "Cervical Sprain (Ligament)", type: "SPR", typeColor: "#EF5350" },
            { code: "S16.1XXA", desc: "Cervical Strain (Muscle)", type: "STR", typeColor: "#FFB74D" },
            { code: "M54.2", desc: "Cervicalgia (Neck Pain)", typeColor: "#42A5F5" },
            { code: "M62.830", desc: "Muscle Spasm — Cervical" },
            { code: "G44.309", desc: "Post-Traumatic Headache" },
          ]},
          { region: "Thoracic Spine", color: C.blue, codes: [
            { code: "S23.3XXA", desc: "Thoracic Sprain", type: "SPR", typeColor: "#EF5350" },
            { code: "S29.012A", desc: "Thoracic Strain", type: "STR", typeColor: "#FFB74D" },
            { code: "M54.6", desc: "Thoracic Pain", typeColor: "#42A5F5" },
          ]},
          { region: "Lumbar / Low Back", color: C.blue, codes: [
            { code: "S33.5XXA", desc: "Lumbar Sprain (Ligament)", type: "SPR", typeColor: "#EF5350" },
            { code: "S39.012A", desc: "Lumbar Strain (Muscle)", type: "STR", typeColor: "#FFB74D" },
            { code: "M54.5", desc: "Low Back Pain (Lumbago)", typeColor: "#42A5F5" },
            { code: "M54.4", desc: "Lumbago with Sciatica" },
            { code: "M54.16", desc: "Lumbar Radiculopathy" },
          ]},
          { region: "Shoulder", color: C.blue, hasLat: true, codes: [
            { code: "S43.421A", desc: "Sprain — Shoulder", type: "SPR", typeColor: "#EF5350", hasLat: true },
            { code: "S46.011A", desc: "Strain — Shoulder", type: "STR", typeColor: "#FFB74D", hasLat: true },
            { code: "M25.511", desc: "Pain in Shoulder" },
          ]},
          { region: "Elbow", color: C.blue, codes: [
            { code: "S53.401A", desc: "Sprain — Elbow", type: "SPR", typeColor: "#EF5350" },
            { code: "S56.111A", desc: "Strain — Elbow", type: "STR", typeColor: "#FFB74D" },
            { code: "M25.521", desc: "Pain in Elbow" },
          ]},
          { region: "Wrist", color: C.blue, codes: [
            { code: "S63.501A", desc: "Sprain — Wrist", type: "SPR", typeColor: "#EF5350" },
            { code: "S66.111A", desc: "Strain — Wrist", type: "STR", typeColor: "#FFB74D" },
            { code: "M25.531", desc: "Pain in Wrist" },
          ]},
          { region: "Hand", color: C.blue, codes: [
            { code: "S63.90XA", desc: "Sprain — Hand", type: "SPR", typeColor: "#EF5350" },
            { code: "S66.911A", desc: "Strain — Hand", type: "STR", typeColor: "#FFB74D" },
            { code: "M79.641", desc: "Pain in Hand" },
          ]},
          { region: "Hip", color: C.blue, codes: [
            { code: "S73.101A", desc: "Sprain — Hip", type: "SPR", typeColor: "#EF5350" },
            { code: "S76.011A", desc: "Strain — Hip", type: "STR", typeColor: "#FFB74D" },
            { code: "M25.551", desc: "Pain in Hip" },
          ]},
          { region: "Knee", color: C.blue, hasLat: true, codes: [
            { code: "S83.401A", desc: "Sprain — Knee (Ligament)", type: "SPR", typeColor: "#EF5350", hasLat: true },
            { code: "S86.811A", desc: "Strain — Knee", type: "STR", typeColor: "#FFB74D", hasLat: true },
            { code: "M25.561", desc: "Pain in Knee" },
            { code: "M62.831", desc: "Muscle Spasm — Knee" },
          ]},
          { region: "Ankle", color: C.blue, codes: [
            { code: "S93.401A", desc: "Sprain — Ankle", type: "SPR", typeColor: "#EF5350" },
            { code: "S96.811A", desc: "Strain — Ankle", type: "STR", typeColor: "#FFB74D" },
            { code: "M25.571", desc: "Pain in Ankle" },
            { code: "M62.841", desc: "Muscle Spasm — Ankle" },
          ]},
          { region: "Psychological", color: C.purple, codes: [
            { code: "F43.10", desc: "PTSD Unspecified" },
            { code: "F41.1", desc: "Generalized Anxiety Disorder" },
            { code: "F32.9", desc: "Major Depressive Episode" },
          ]},
        ];

        // Auto-preselect based on complaints + exam findings
        const autoPreselect = () => {
          const codes = [];
          const cc = f.chiefComplaints || [];
          // Cervical
          if (cc.includes("Neck Pain") || (f.palp_cervical || []).length > 0 || f.rom_cervical === "Decreased") {
            codes.push("S13.4XXA", "S16.1XXA", "M54.2");
            if ((f.palp_cervical || []).includes("Muscle Spasm")) codes.push("M62.830");
          }
          // Headache
          if (cc.includes("Headache") || cc.includes("Dizziness")) codes.push("G44.309");
          // Thoracic
          if (cc.includes("Upper Back Pain") || (f.palp_thoracic || []).length > 0 || f.rom_thoracic === "Decreased") {
            codes.push("S23.3XXA", "S29.012A", "M54.6");
          }
          // Lumbar
          if (cc.includes("Lower Back Pain") || (f.palp_lumbar || []).length > 0 || f.rom_lumbar === "Decreased") {
            codes.push("S33.5XXA", "S39.012A", "M54.5");
          }
          // Shoulders
          if (cc.includes("Right Shoulder Pain") || cc.includes("Left Shoulder Pain") || (f.palp_shoulder || []).length > 0) {
            codes.push("S43.421A", "S46.011A", "M25.511");
          }
          // Knees
          if (cc.includes("Right Knee Pain") || cc.includes("Left Knee Pain") || (f.palp_knee || []).length > 0) {
            codes.push("S83.401A", "S86.811A", "M25.561");
          }
          // Hips
          if (cc.includes("Right Hip Pain") || cc.includes("Left Hip Pain") || (f.palp_hip || []).length > 0) {
            codes.push("S73.101A", "S76.011A", "M25.551");
          }
          // Numbness
          if (cc.includes("Numbness/Tingling")) {
            if (cc.includes("Neck Pain") || (f.palp_cervical || []).length > 0) codes.push("M54.12");
            if (cc.includes("Lower Back Pain") || (f.palp_lumbar || []).length > 0) codes.push("M54.16");
          }
          u("icdCodes", [...new Set(codes)]);
        };

        // Toggle ICD code
        const togICD = (code) => {
          const cur = f.icdCodes || [];
          u("icdCodes", cur.includes(code) ? cur.filter(c => c !== code) : [...cur, code]);
        };

        // ICD Row component
        const ICDRow = ({ code, desc, type, typeColor, hasLat }) => {
          const on = (f.icdCodes || []).includes(code);
          const borderCol = type === "SPR" ? "#EF5350" : type === "STR" ? "#FFB74D" : on ? C.blue : C.border;
          return (
            <div onClick={() => !locked && togICD(code)} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
              background: on ? (type === "SPR" ? "#EF535011" : type === "STR" ? "#FFB74D11" : C.blueGlow) : C.bgElevated,
              border: `1.5px solid ${on ? borderCol : C.border}`,
              borderRadius: 8, marginBottom: 5, cursor: locked ? "default" : "pointer",
              transition: "all 0.15s",
            }}>
              {type && (
                <span style={{ fontSize: 9, fontWeight: 900, padding: "2px 6px", borderRadius: 4,
                  background: typeColor + "22", color: typeColor, border: `1px solid ${typeColor}44`,
                }}>{type}</span>
              )}
              <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 800,
                color: on ? (typeColor || C.blueBright) : C.textMuted, minWidth: 72,
              }}>{code}</span>
              <span style={{ fontSize: 12, color: on ? C.textPrimary : C.textSecond, flex: 1,
                fontWeight: on ? 700 : 400,
              }}>{desc}</span>
              {hasLat && on && (
                <div style={{ display: "flex", gap: 5 }} onClick={e => e.stopPropagation()}>
                  {["R", "L", "Both"].map(side => {
                    const latOn = (f.icdLat || {})[code] === side;
                    return (
                      <button key={side} onClick={() => u("icdLat", { ...(f.icdLat || {}), [code]: side })} style={{
                        padding: "3px 10px", borderRadius: 5, fontSize: 10, fontWeight: 800, cursor: "pointer",
                        border: `1.5px solid ${latOn ? C.blue : C.border}`,
                        background: latOn ? C.blueGlow : "transparent",
                        color: latOn ? C.blueBright : C.textMuted,
                      }}>{side}</button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        };

        return (
          <div>
            <SectionHead title="Diagnoses & Plan of Treatment" sub="Sprains & strains listed first per FL PIP documentation standard" />

            {/* ICD-10 Section */}
            <div style={{ padding: 16, background: C.bgCard, borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: C.textPrimary }}>ICD-10 Diagnoses</span>
                  <span style={{ fontSize: 9, fontWeight: 800, padding: "3px 8px", borderRadius: 4, background: C.blueGlow, border: `1px solid ${C.blue}33`, color: C.blueBright }}>MD · SPRAIN/STRAIN FIRST · R/L/BOTH</span>
                </div>
                <button onClick={autoPreselect} disabled={locked} style={{
                  padding: "8px 16px", borderRadius: 8, fontSize: 11, fontWeight: 800, cursor: locked ? "not-allowed" : "pointer",
                  background: C.green, color: "#fff", border: "none", boxShadow: `0 0 12px ${C.green}44`,
                }}>Auto: Core MVA Codes</button>
              </div>

              {ICD_REGIONS.map(region => {
                const hasSelected = region.codes.some(c => (f.icdCodes || []).includes(c.code));
                return (
                  <div key={region.region} style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: region.color, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>{region.region}</div>
                    {region.codes.map(c => (
                      <ICDRow key={c.code} {...c} />
                    ))}
                  </div>
                );
              })}

              <div style={{ fontSize: 10, color: C.textMuted, marginTop: 8 }}>Selected: {(f.icdCodes || []).length} codes</div>
            </div>

            {/* Plan of Treatment */}
            <div style={{ padding: 16, background: C.bgCard, borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: C.textPrimary }}>Plan of Treatment</span>
                <span style={{ fontSize: 9, fontWeight: 800, padding: "3px 8px", borderRadius: 4, background: "#EF535022", border: "1px solid #EF535044", color: "#EF5350" }}>MD ORDERS</span>
              </div>

              {/* E/M Level */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>E/M Level {isInitial ? "New Patient" : "Established Patient"}</div>
                <PillSelect options={isInitial
                  ? ["99205 — New Patient · High Complexity", "99206 — New Patient · High MDM", "99204 — New Patient · Moderate"]
                  : ["99214 — Established · Moderate", "99213 — Established · Low"]}
                  value={f.emCode} onChange={v => u("emCode", v)} disabled={locked} />
              </div>

              {/* PT Orders */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>
                  PT Orders · <span style={{ color: C.textPrimary, fontWeight: 800 }}>5x/Week x 4 Weeks Default (Initial Visit)</span>
                </div>
                <div style={{ display: "flex", gap: 20, marginTop: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 9, color: C.textMuted, marginBottom: 6, fontWeight: 700 }}>Frequency</div>
                    <PillSelect options={["5x/week", "3x/week", "4x/week", "2x/week", "Daily PRN"]}
                      value={f.ptFreq} onChange={v => u("ptFreq", v)} disabled={locked} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 9, color: C.textMuted, marginBottom: 6, fontWeight: 700 }}>Duration</div>
                    <PillSelect options={["4 weeks", "6 weeks", "8 weeks", "12 weeks"]}
                      value={f.ptDuration} onChange={v => u("ptDuration", v)} disabled={locked} />
                  </div>
                </div>
              </div>

              {/* PT Modalities */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>PT Modalities</div>
                <PillSelect multi options={[
                  "97010 Hot/Cold Pack", "97012 Mech Traction", "97014 E-Stim Unattend", "97035 Ultrasound",
                  "97110 Therap Exercise", "97140 Manual Therapy", "97530 Therap Activities",
                  "97032 E-Stim Attend", "97018 Paraffin Bath", "97112 Neuromuscular Re-ed",
                ]} value={f.cptCodes} onChange={v => u("cptCodes", v)} disabled={locked} />
              </div>

              {/* Follow-up */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Follow-Up</div>
                <PillSelect options={["2 weeks", "1 week", "3 weeks", "4 weeks", "PRN"]}
                  value={f.followUp} onChange={v => u("followUp", v)} disabled={locked} />
              </div>

              {/* Medications + Referrals */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Input label="Medications (Rx)" value={f.rxMedications} onChange={e => u("rxMedications", e.target.value)} disabled={locked} placeholder="e.g., Ibuprofen 400mg, Cyclobenzaprine 10mg" />
                <Input label="Referrals" value={f.referrals} onChange={e => u("referrals", e.target.value)} disabled={locked} placeholder="e.g., Orthopedic eval, MRI" />
              </div>
            </div>

            {/* ═══════ IMAGING ORDERS — CLINICAL RULES ENGINE ═══════ */}
            {isInitial && (() => {
              const cc = f.chiefComplaints || [];
              const icd = f.icdCodes || [];

              // Clinical rules: auto-detect which imaging is indicated
              const IMAGING_RULES = [
                { region: "Cervical Spine", cpt: "72052", views: "AP, Lateral, Obliques", icon: "🦴",
                  trigger: cc.includes("Neck Pain") || f.rom_cervical === "Decreased" || (f.palp_cervical||[]).includes("Muscle Spasm") || icd.some(c => ["S13.4XXA","M54.2","S16.1XXA"].includes(c)),
                  linkedICD: icd.filter(c => ["S13.4XXA","S16.1XXA","M54.2","M62.830","G44.309"].includes(c)),
                  justification: "Post-traumatic neck pain with decreased range of motion, paraspinal tenderness, and/or muscle spasm following motor vehicle accident. Rule out fracture, dislocation, or instability." },
                { region: "Thoracic Spine", cpt: "72072", views: "AP, Lateral", icon: "🦴",
                  trigger: cc.includes("Upper Back Pain") || f.rom_thoracic === "Decreased" || (f.palp_thoracic||[]).includes("Muscle Spasm") || icd.some(c => ["S23.3XXA","M54.6"].includes(c)),
                  linkedICD: icd.filter(c => ["S23.3XXA","S29.012A","M54.6"].includes(c)),
                  justification: "Post-traumatic mid-back pain with muscle spasm and restricted thoracic mobility following motor vehicle accident. Rule out fracture or structural injury." },
                { region: "Lumbar Spine", cpt: "72100", views: "AP, Lateral", icon: "🦴",
                  trigger: cc.includes("Lower Back Pain") || f.rom_lumbar === "Decreased" || (f.palp_lumbar||[]).includes("Muscle Spasm") || icd.some(c => ["S33.5XXA","M54.5","M54.4","M54.16"].includes(c)),
                  linkedICD: icd.filter(c => ["S33.5XXA","S39.012A","M54.5","M54.4","M54.16"].includes(c)),
                  justification: "Post-traumatic low back pain with decreased range of motion, tenderness, and/or muscle spasm following motor vehicle accident. Rule out fracture, dislocation, or instability." },
                { region: "Shoulder", cpt: "73030", views: "AP, Axillary, Y-view", icon: "💪",
                  trigger: cc.some(c => c.includes("Shoulder")) || (f.palp_shoulder||[]).length > 0 || f.rom_shoulder === "Decreased" || icd.some(c => ["S43.421A","S46.011A","M25.511","M25.512"].includes(c)),
                  linkedICD: icd.filter(c => ["S43.421A","S46.011A","M25.511","M25.512"].includes(c)),
                  justification: "Post-traumatic shoulder pain with decreased range of motion and tenderness following trauma. Rule out fracture, dislocation, or joint pathology." },
                { region: "Elbow", cpt: "73080", views: "AP, Lateral", icon: "💪",
                  trigger: (f.palp_elbow||[]).length > 0 || f.rom_elbow === "Decreased" || icd.some(c => ["S53.401A","S56.111A","M25.521"].includes(c)),
                  linkedICD: icd.filter(c => ["S53.401A","S56.111A","M25.521"].includes(c)),
                  justification: "Post-traumatic elbow pain with tenderness and decreased motion following trauma. Rule out fracture, dislocation, or joint injury." },
                { region: "Wrist", cpt: "73110", views: "PA, Lateral, Oblique", icon: "✋",
                  trigger: (f.palp_wrist||[]).length > 0 || f.rom_wrist === "Decreased" || icd.some(c => ["S63.501A","S66.111A","M25.531"].includes(c)),
                  linkedICD: icd.filter(c => ["S63.501A","S66.111A","M25.531"].includes(c)),
                  justification: "Post-traumatic wrist pain with tenderness and reduced motion following trauma. Rule out fracture or acute bony injury." },
                { region: "Hand", cpt: "73130", views: "PA, Lateral, Oblique", icon: "✋",
                  trigger: (f.palp_hand||[]).length > 0 || icd.some(c => ["S63.90XA","S66.911A","M79.641"].includes(c)),
                  linkedICD: icd.filter(c => ["S63.90XA","S66.911A","M79.641"].includes(c)),
                  justification: "Post-traumatic hand pain with tenderness, swelling, and/or decreased functional use following trauma. Rule out fracture or acute structural injury." },
                { region: "Hip", cpt: "73502", views: "AP Pelvis, Frog Lateral", icon: "🦵",
                  trigger: cc.some(c => c.includes("Hip")) || (f.palp_hip||[]).length > 0 || f.rom_hip === "Decreased" || icd.some(c => ["S73.101A","S76.011A","M25.551","M25.552"].includes(c)),
                  linkedICD: icd.filter(c => ["S73.101A","S76.011A","M25.551","M25.552"].includes(c)),
                  justification: "Post-traumatic hip pain with painful ambulation and restricted motion following trauma. Rule out fracture, dislocation, or acute osseous injury." },
                { region: "Knee", cpt: "73562", views: "AP, Lateral, Sunrise", icon: "🦵",
                  trigger: cc.some(c => c.includes("Knee")) || (f.palp_knee||[]).length > 0 || f.rom_knee === "Decreased" || icd.some(c => ["S83.401A","S86.811A","M25.561","M25.562"].includes(c)),
                  linkedICD: icd.filter(c => ["S83.401A","S86.811A","M25.561","M25.562"].includes(c)),
                  justification: "Post-traumatic knee pain with swelling, tenderness, and/or limited motion following trauma. Rule out fracture, effusion, or joint injury." },
                { region: "Ankle", cpt: "73600", views: "AP, Lateral, Mortise", icon: "🦶",
                  trigger: (f.palp_ankle||[]).length > 0 || f.rom_ankle === "Decreased" || icd.some(c => ["S93.401A","S96.811A","M25.571"].includes(c)),
                  linkedICD: icd.filter(c => ["S93.401A","S96.811A","M25.571"].includes(c)),
                  justification: "Post-traumatic ankle pain with weight-bearing difficulty, tenderness, and/or swelling following trauma. Rule out fracture or joint disruption." },
                { region: "Foot", cpt: "73620", views: "AP, Lateral, Oblique", icon: "🦶",
                  trigger: cc.some(c => c.includes("Foot")) || icd.some(c => ["M79.671","M79.672"].includes(c)),
                  linkedICD: icd.filter(c => ["M79.671","M79.672"].includes(c)),
                  justification: "Post-traumatic foot pain with gait disturbance, tenderness, and/or swelling following trauma. Rule out fracture or acute structural injury." },
              ];

              const autoSelected = IMAGING_RULES.filter(r => r.trigger);
              const manualOverride = f.xrayOrders || [];

              const toggleImaging = (cpt) => {
                const cur = f.xrayOrders || [];
                u("xrayOrders", cur.includes(cpt) ? cur.filter(c => c !== cpt) : [...cur, cpt]);
              };

              // Auto-select on first render (no hook needed — runs inline)
              if (autoSelected.length > 0 && (f.xrayOrders || []).length === 0) {
                setTimeout(() => u("xrayOrders", autoSelected.map(r => r.cpt)), 0);
              }

              return (
                <div style={{ padding: 16, background: C.bgCard, borderRadius: 12, border: `1px solid ${C.amber}33`, marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: C.amber, letterSpacing: "0.06em", textTransform: "uppercase" }}>Imaging Orders — MAZEL Medical</div>
                      <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>Auto-selected from clinical findings · Click to add/remove</div>
                    </div>
                    {autoSelected.length > 0 && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 6, background: C.greenBg, border: `1px solid ${C.green}33`, color: C.green }}>
                        {autoSelected.length} indicated
                      </span>
                    )}
                  </div>

                  {/* Imaging Tiles */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
                    {IMAGING_RULES.map(rule => {
                      const on = (f.xrayOrders || []).includes(rule.cpt);
                      const autoOn = rule.trigger;
                      return (
                        <div key={rule.cpt} onClick={() => !locked && toggleImaging(rule.cpt)} style={{
                          padding: "12px 14px", borderRadius: 10, cursor: locked ? "default" : "pointer",
                          background: on ? C.amberBg : C.bgElevated,
                          border: `1.5px solid ${on ? C.amber : autoOn ? C.amber + "44" : C.border}`,
                          transition: "all 0.15s",
                          opacity: on ? 1 : autoOn ? 0.9 : 0.5,
                        }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 16 }}>{rule.icon}</span>
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: on ? C.amber : C.textPrimary }}>{rule.region}</div>
                                <div style={{ fontSize: 10, color: C.textMuted }}>{rule.views}</div>
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: 11, fontWeight: 800, fontFamily: "monospace", color: on ? C.amber : C.textMuted }}>{rule.cpt}</div>
                              {on && <div style={{ fontSize: 9, color: C.green, fontWeight: 700 }}>✓ Ordered</div>}
                              {!on && autoOn && <div style={{ fontSize: 9, color: C.amber, fontWeight: 700 }}>Indicated</div>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Order Preview */}
                  {(f.xrayOrders || []).length > 0 && (
                    <div style={{ padding: 14, background: C.bgElevated, borderRadius: 10, border: `1px solid ${C.border}` }}>
                      {/* Clinic Letterhead */}
                      <div style={{ textAlign: "center", marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 13, fontWeight: 900, color: C.textPrimary, letterSpacing: "0.04em" }}>WELLNESS HEALTHCARE CLINIC CORP</div>
                        <div style={{ fontSize: 10, color: C.textSecond }}>8180 NW 36th St, Suite 213 · Doral, FL 33166</div>
                        <div style={{ fontSize: 10, color: C.textSecond }}>Ph: (305) 994-9500 · Fax: (786) 441-2179</div>
                        <div style={{ fontSize: 10, color: C.textSecond, marginTop: 2 }}>Armando Falcon, MD · FL License ME 84789 · NPI 1447295126</div>
                      </div>
                      {/* Referral Facility */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, padding: "8px 12px", background: C.amberBg, borderRadius: 8, border: `1px solid ${C.amber}33` }}>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 800, color: C.amber, textTransform: "uppercase", letterSpacing: "0.1em" }}>Imaging Referral — X-Ray Orders</div>
                          <div style={{ fontSize: 9, color: C.textMuted, marginTop: 2 }}>Patient: {f.ptFirst} {f.ptLast} · DOB: {f.ptDOB} · DOA: {f.doa}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 10, fontWeight: 800, color: C.amber }}>MAZEL DIAGNOSTIC CENTER INC.</div>
                          <div style={{ fontSize: 9, color: C.textMuted }}>8150 S.W. 8TH STREET SUITE 119 · MIAMI, FL 33144</div>
                          <div style={{ fontSize: 9, color: C.textMuted }}>305-261-3020 · Fax: 305-261-3070</div>
                        </div>
                      </div>
                      {IMAGING_RULES.filter(r => (f.xrayOrders || []).includes(r.cpt)).map(rule => (
                        <div key={rule.cpt} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
                          <div style={{ fontSize: 13, fontWeight: 900, color: C.textPrimary, marginBottom: 4, fontFamily: "Georgia, 'Times New Roman', serif", letterSpacing: "0.02em" }}>
                            {rule.region} X-ray ({rule.views}) — CPT {rule.cpt}
                          </div>
                          <div style={{ fontSize: 10, color: C.textSecond, lineHeight: 1.6, marginBottom: 6 }}>
                            {rule.justification}
                          </div>
                          {rule.linkedICD.length > 0 && (
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 9, color: C.textMuted }}>Linked Dx:</span>
                              {rule.linkedICD.map(c => (
                                <span key={c} style={{ fontSize: 9, fontFamily: "monospace", fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: C.blueGlow, color: C.blueBright, border: `1px solid ${C.blue}33` }}>{c}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Imaging Results Upload -- FOLLOW-UP ONLY */}
            {!isInitial && (
              <div style={{ padding: 24, background: C.bgElevated, borderRadius: 10, border: `2px dashed ${C.border}`, textAlign: "center", cursor: "pointer" }}>
                <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>{"📁"}</div>
                <div style={{ fontSize: 12, color: C.textMuted, fontWeight: 600 }}>Drop X-ray results here or click to upload</div>
              </div>
            )}
          </div>
        );
      }

      // ════════════════════════════════════════════════════════════════════
      // FOLLOW-UP STEP 1: REGION STATUS CLASSIFICATION
      // ════════════════════════════════════════════════════════════════════
      case "status": {
        const cc = caseData?.chiefComplaints || [];
        const REGION_MAP = [
          { id: "cervical", label: "Cervical Spine", icon: "\u{1F9B4}", trigger: cc.includes("Neck Pain") },
          { id: "thoracic", label: "Thoracic Spine", icon: "\u{1F9B4}", trigger: cc.includes("Mid-Back Pain") || cc.includes("Upper Back Pain") },
          { id: "lumbar",   label: "Lumbar Spine",   icon: "\u{1F9B4}", trigger: cc.includes("Low Back Pain") || cc.includes("Lower Back Pain") },
          { id: "shoulder", label: "Shoulder",       icon: "\u{1F4AA}", trigger: cc.some(c => c.includes("Shoulder")) },
          { id: "elbow",    label: "Elbow",          icon: "\u{1F4AA}", trigger: cc.some(c => c.includes("Elbow")) },
          { id: "wrist",    label: "Wrist",          icon: "\u{270B}", trigger: cc.some(c => c.includes("Wrist")) },
          { id: "hand",     label: "Hand",           icon: "\u{270A}", trigger: cc.some(c => c.includes("Hand")) },
          { id: "hip",      label: "Hip",            icon: "\u{1F9B5}", trigger: cc.some(c => c.includes("Hip")) },
          { id: "knee",     label: "Knee",           icon: "\u{1F9B5}", trigger: cc.some(c => c.includes("Knee")) },
          { id: "ankle",    label: "Ankle",          icon: "\u{1F9B6}", trigger: cc.some(c => c.includes("Ankle")) },
        ];
        const activeRegions = REGION_MAP.filter(r => r.trigger);
        const STATUS_OPTIONS = ["Improved", "Persistent", "Worsened", "Resolved"];
        const STATUS_COLORS = { Improved: C.green, Persistent: C.amber, Worsened: C.red, Resolved: C.textMuted };

        // ICD-10 codes mapped to each region
        const REGION_ICD_MAP = {
          cervical: ["S13.4XXA", "S16.1XXA", "M54.2", "M62.830", "G44.309"],
          thoracic: ["S23.3XXA", "S29.012A", "M54.6"],
          lumbar:   ["S33.5XXA", "S39.012A", "M54.5", "M54.4", "M54.16"],
          shoulder: ["S43.421A", "S46.011A", "M25.511", "M25.512"],
          elbow:    ["S53.401A", "S56.111A", "M25.521"],
          wrist:    ["S63.501A", "S66.111A", "M25.531"],
          hand:     ["S63.90XA", "S66.911A", "M79.641"],
          hip:      ["S73.101A", "S76.011A", "M25.551", "M25.552"],
          knee:     ["S83.401A", "S86.811A", "M25.561", "M25.562"],
          ankle:    ["S93.401A", "S96.811A", "M25.571"],
        };

        // ICD-10 descriptions
        const ICD_DESC = {
          "S13.4XXA": "Cervical Sprain", "S16.1XXA": "Cervical Muscle Strain", "M54.2": "Cervicalgia",
          "M62.830": "Muscle Spasm, Cervical", "G44.309": "Post-Traumatic Headache",
          "S23.3XXA": "Thoracic Sprain", "S29.012A": "Thoracic Muscle Strain", "M54.6": "Thoracic Pain",
          "S33.5XXA": "Lumbar Sprain", "S39.012A": "Lumbar Muscle Strain", "M54.5": "Low Back Pain",
          "M54.4": "Lumbago with Sciatica", "M54.16": "Radiculopathy, Lumbar",
          "S43.421A": "Shoulder Sprain", "S46.011A": "Shoulder Muscle Strain",
          "M25.511": "Shoulder Pain, Right", "M25.512": "Shoulder Pain, Left",
          "S53.401A": "Elbow Sprain", "S56.111A": "Elbow Muscle Strain", "M25.521": "Elbow Pain",
          "S63.501A": "Wrist Sprain", "S66.111A": "Wrist Muscle Strain", "M25.531": "Wrist Pain",
          "S63.90XA": "Hand Sprain", "S66.911A": "Hand Muscle Strain", "M79.641": "Hand Pain",
          "S73.101A": "Hip Sprain", "S76.011A": "Hip Muscle Strain",
          "M25.551": "Hip Pain, Right", "M25.552": "Hip Pain, Left",
          "S83.401A": "Knee Sprain", "S86.811A": "Knee Muscle Strain",
          "M25.561": "Knee Pain, Right", "M25.562": "Knee Pain, Left",
          "S93.401A": "Ankle Sprain", "S96.811A": "Ankle Muscle Strain", "M25.571": "Ankle Pain",
        };

        // Default CPT codes for PT treatment plan
        const DEFAULT_CPT = [
          { code: "97010", desc: "Hot/Cold Pack" },
          { code: "97014", desc: "E-Stim" },
          { code: "97035", desc: "Ultrasound" },
          { code: "97110", desc: "Therapeutic Exercise" },
          { code: "97140", desc: "Manual Therapy" },
          { code: "97530", desc: "Therapeutic Activities" },
        ];
        const cptList = (caseData?.cpt && caseData.cpt.length > 0)
          ? caseData.cpt.map(c => typeof c === "string" ? { code: c, desc: DEFAULT_CPT.find(d => d.code === c)?.desc || c } : c)
          : DEFAULT_CPT;

        // ICD status color helper
        const ICD_STATUS_OPTS = ["Improved", "Stable", "Resolved"];
        const ICD_STATUS_COLORS = { Improved: C.green, Stable: C.amber, Resolved: C.textMuted };

        // Initial findings summary helper
        const getInitialFindings = (regionId) => {
          const basePain = previousPain[regionId] || 7;
          const summaries = {
            cervical: `Pain ${basePain}/10 | Palpation: Paraspinal TTP, Muscle Spasm | ROM: Decreased | Functional: Limited cervical rotation and flexion`,
            thoracic: `Pain ${basePain}/10 | Palpation: Paraspinal TTP, Muscle Spasm | ROM: Decreased | Functional: Limited thoracic extension`,
            lumbar:   `Pain ${basePain}/10 | Palpation: Paraspinal TTP, SI Joint TTP | ROM: Decreased | Functional: Limited lumbar flexion and extension`,
            shoulder: `Pain ${basePain}/10 | Palpation: TTP, Crepitus | ROM: Decreased | Functional: Limited overhead reach and abduction`,
            elbow:    `Pain ${basePain}/10 | Palpation: TTP | ROM: Decreased | Functional: Limited elbow flexion/extension`,
            wrist:    `Pain ${basePain}/10 | Palpation: TTP | ROM: Decreased | Functional: Limited wrist flexion/extension`,
            hand:     `Pain ${basePain}/10 | Palpation: TTP | ROM: Decreased | Functional: Limited grip strength`,
            hip:      `Pain ${basePain}/10 | Palpation: TTP | ROM: Decreased | Functional: Limited hip flexion and rotation`,
            knee:     `Pain ${basePain}/10 | Palpation: TTP, Effusion | ROM: Decreased | Functional: Limited weight-bearing and stair climbing`,
            ankle:    `Pain ${basePain}/10 | Palpation: TTP, Swelling | ROM: Decreased | Functional: Limited dorsiflexion and gait`,
          };
          // Pull from caseData if available
          if (caseData?.initialFindings?.[regionId]) return caseData.initialFindings[regionId];
          return summaries[regionId] || `Pain ${basePain}/10 | Palpation: TTP | ROM: Decreased`;
        };

        return (
          <div>
            <SectionHead title="Region Status Classification" sub="Review baseline findings, classify each diagnosis and treatment" />
            {activeRegions.length === 0 && (
              <div style={{ padding: 32, textAlign: "center", color: C.textMuted, fontSize: 13 }}>
                No regions found from initial visit chief complaints.
              </div>
            )}
            {activeRegions.map(region => {
              const regionIcds = REGION_ICD_MAP[region.id] || [];
              return (
                <div key={region.id} style={{
                  padding: 20, background: C.bgElevated, borderRadius: 12,
                  border: `1px solid ${regionStatus[region.id] ? (STATUS_COLORS[regionStatus[region.id]] || C.border) + "66" : C.border}`,
                  marginBottom: 16,
                }}>
                  {/* ── Header: Region name + ICD badges ── */}
                  <div style={{
                    display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 14,
                  }}>
                    <div style={{
                      fontSize: 13, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
                      display: "flex", alignItems: "center", gap: 8,
                      color: regionStatus[region.id] ? STATUS_COLORS[regionStatus[region.id]] : C.textPrimary,
                    }}>
                      <span>{region.icon}</span> {region.label}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {regionIcds.map(code => (
                        <span key={code} style={{
                          fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 5,
                          background: C.bluePale, color: C.blueBright, border: `1px solid ${C.blue}44`,
                          fontFamily: "monospace", letterSpacing: "0.03em",
                        }}>{code}</span>
                      ))}
                    </div>
                  </div>

                  {/* ── Initial Findings Summary (read-only) ── */}
                  <div style={{
                    padding: 12, background: C.bgCard, borderRadius: 8,
                    border: `1px solid ${C.border}`, marginBottom: 14,
                  }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>
                      Initial Visit Findings
                    </div>
                    <div style={{ fontSize: 11, color: C.textSecond, lineHeight: 1.5 }}>
                      {getInitialFindings(region.id)}
                    </div>
                  </div>

                  {/* ── Overall Region Status ── */}
                  <div style={{ marginBottom: 16 }}>
                    <PillSelect label="Overall Region Status" options={STATUS_OPTIONS} value={regionStatus[region.id] || ""}
                      onChange={v => setRegionStatus(prev => ({ ...prev, [region.id]: v }))} disabled={locked} />
                  </div>

                  {/* ── Current Dx Status per ICD-10 ── */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>
                      Diagnosis Status
                    </div>
                    {regionIcds.map(code => {
                      const currentStatus = icdStatus[code] || "";
                      return (
                        <div key={code} style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          padding: "8px 12px", background: C.bgCard, borderRadius: 8,
                          border: `1px solid ${currentStatus ? (ICD_STATUS_COLORS[currentStatus] || C.border) + "44" : C.border}`,
                          marginBottom: 6,
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: C.textPrimary, fontFamily: "monospace" }}>{code}</span>
                            <span style={{ fontSize: 10, color: C.textSecond, marginLeft: 8 }}>{ICD_DESC[code] || code}</span>
                          </div>
                          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                            {ICD_STATUS_OPTS.map(opt => {
                              const isActive = currentStatus === opt;
                              const clr = ICD_STATUS_COLORS[opt];
                              return (
                                <button key={opt} disabled={locked} onClick={() => setIcdStatus(prev => ({ ...prev, [code]: isActive ? "" : opt }))}
                                  style={{
                                    fontSize: 9, fontWeight: 700, padding: "3px 10px", borderRadius: 6,
                                    border: `1px solid ${isActive ? clr + "66" : C.border}`,
                                    background: isActive ? clr + "18" : "transparent",
                                    color: isActive ? clr : C.textMuted,
                                    cursor: locked ? "not-allowed" : "pointer",
                                    transition: "all 0.15s ease",
                                  }}>{opt}</button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* ── CPT Management ── */}
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>
                      Treatment / CPT Management
                    </div>
                    {cptList.map(cpt => {
                      const currentCpt = cptStatus[cpt.code] || "";
                      return (
                        <div key={cpt.code} style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          padding: "8px 12px", background: C.bgCard, borderRadius: 8,
                          border: `1px solid ${currentCpt === "Continue" ? C.green + "44" : currentCpt === "Stop" ? C.red + "44" : C.border}`,
                          marginBottom: 6,
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: C.textPrimary, fontFamily: "monospace" }}>{cpt.code}</span>
                            <span style={{ fontSize: 10, color: C.textSecond, marginLeft: 8 }}>{cpt.desc}</span>
                          </div>
                          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                            {[{ label: "Continue", clr: C.green }, { label: "Stop", clr: C.red }].map(opt => {
                              const isActive = currentCpt === opt.label;
                              return (
                                <button key={opt.label} disabled={locked}
                                  onClick={() => setCptStatus(prev => ({ ...prev, [cpt.code]: isActive ? "" : opt.label }))}
                                  style={{
                                    fontSize: 9, fontWeight: 700, padding: "3px 10px", borderRadius: 6,
                                    border: `1px solid ${isActive ? opt.clr + "66" : C.border}`,
                                    background: isActive ? opt.clr + "18" : "transparent",
                                    color: isActive ? opt.clr : C.textMuted,
                                    cursor: locked ? "not-allowed" : "pointer",
                                    transition: "all 0.15s ease",
                                  }}>{opt.label}</button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      // ════════════════════════════════════════════════════════════════════
      // FOLLOW-UP STEP 2: FOCUSED PHYSICAL EXAM
      // ════════════════════════════════════════════════════════════════════
      case "fuExam": {
        const cc2 = caseData?.chiefComplaints || [];
        const FU_REGIONS = [
          { id: "cervical", label: "Cervical Spine", color: C.amber, icon: "\u{1F9B4}",
            trigger: cc2.includes("Neck Pain"),
            palpOptions: ["Paraspinal TTP", "Facet TTP", "Muscle Spasm", "Trigger Points"],
            romTests: [
              { key: "r_cflex", label: "Chin to Chest", desc: "Flexion ~50\u00B0", command: "Tuck your chin to your chest", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_cext",  label: "Look at Ceiling", desc: "Extension ~60\u00B0", command: "Look up at the ceiling", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_clatR", label: "Ear to Shoulder R", desc: "Lateral ~45\u00B0", command: "Tilt your right ear to your right shoulder", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_clatL", label: "Ear to Shoulder L", desc: "Lateral ~45\u00B0", command: "Tilt your left ear to your left shoulder", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_crotR", label: "Look Over Shoulder R", desc: "Rotation ~70\u00B0", command: "Turn and look over your right shoulder", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_crotL", label: "Look Over Shoulder L", desc: "Rotation ~70\u00B0", command: "Turn and look over your left shoulder", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          { id: "thoracic", label: "Thoracic Spine", color: C.amber, icon: "\u{1F9B4}",
            trigger: cc2.includes("Mid-Back Pain") || cc2.includes("Upper Back Pain"),
            palpOptions: ["Paraspinal TTP", "Costovertebral TTP", "Muscle Spasm", "Trigger Points"],
            romTests: [],
          },
          { id: "lumbar", label: "Lumbar Spine", color: C.amber, icon: "\u{1F9B4}",
            trigger: cc2.includes("Low Back Pain") || cc2.includes("Lower Back Pain"),
            palpOptions: ["Paraspinal TTP", "Facet TTP", "SI Joint TTP", "Muscle Spasm", "Trigger Points"],
            romTests: [
              { key: "r_lflex", label: "Fingertip to Floor", desc: "Flexion ~60\u00B0", command: "Bend forward and reach for the floor", opts: ["Normal \u22644in","Mid-shin","Knee level","Cannot"] },
              { key: "r_lext",  label: "Backward Lean", desc: "Extension ~25\u00B0", command: "Lean backward gently", opts: ["Normal","Partial","Minimal","Cannot"] },
              { key: "r_lsbR",  label: "Side Bend Right", desc: "~25\u00B0", command: "Bend to the right side", opts: ["Normal","Partial","Minimal","Cannot"] },
              { key: "r_lsbL",  label: "Side Bend Left", desc: "~25\u00B0", command: "Bend to the left side", opts: ["Normal","Partial","Minimal","Cannot"] },
            ],
          },
          { id: "shoulder", label: "Shoulder (R/L)", color: C.purple, icon: "\u{1F4AA}",
            trigger: cc2.some(c => c.includes("Shoulder")),
            palpOptions: ["TTP", "Swelling", "Crepitus", "Impingement"],
            romTests: [
              { key: "r_sohR", label: "Raise Arm Overhead R", desc: "~180\u00B0", command: "Raise your right arm straight up", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_sohL", label: "Raise Arm Overhead L", desc: "~180\u00B0", command: "Raise your left arm straight up", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_sbhR", label: "Reach Behind Head R", desc: "~90\u00B0 ER", command: "Reach behind your head with your right hand", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_sbhL", label: "Reach Behind Head L", desc: "~90\u00B0 ER", command: "Reach behind your head with your left hand", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          { id: "knee", label: "Knee (R/L)", color: C.green, icon: "\u{1F9B5}",
            trigger: cc2.some(c => c.includes("Knee")),
            palpOptions: ["TTP", "Effusion", "Crepitus", "McMurray"],
            romTests: [
              { key: "r_kchR", label: "Rise from Chair R", desc: "~135\u00B0", command: "Stand up from a seated position using your right leg", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_kchL", label: "Rise from Chair L", desc: "~135\u00B0", command: "Stand up from a seated position using your left leg", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          { id: "ankle", label: "Ankle (R/L)", color: C.green, icon: "\u{1F9B6}",
            trigger: cc2.some(c => c.includes("Ankle")),
            palpOptions: ["TTP", "Swelling", "Crepitus"],
            romTests: [
              { key: "r_ahtwR", label: "Heel-Toe Walk R", desc: "~20\u00B0 DF", command: "Walk heel-to-toe in a straight line", opts: ["Normal","Antalgic","Severe","Cannot"] },
              { key: "r_ahtwL", label: "Heel-Toe Walk L", desc: "~20\u00B0 DF", command: "Walk heel-to-toe in a straight line", opts: ["Normal","Antalgic","Severe","Cannot"] },
            ],
          },
          { id: "hip", label: "Hip (R/L)", color: C.green, icon: "\u{1F9B5}",
            trigger: cc2.some(c => c.includes("Hip")),
            palpOptions: ["TTP", "Swelling", "Crepitus"],
            romTests: [
              { key: "r_hcarR", label: "Get In Car R", desc: "~120\u00B0", command: "Lift your right leg as if stepping into a car", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_hcarL", label: "Get In Car L", desc: "~120\u00B0", command: "Lift your left leg as if stepping into a car", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          { id: "wrist", label: "Wrist (R/L)", color: C.purple, icon: "\u{270B}",
            trigger: cc2.some(c => c.includes("Wrist")),
            palpOptions: ["TTP", "Swelling", "Crepitus"],
            romTests: [
              { key: "r_wbR", label: "Bend Wrist Back R", desc: "~70\u00B0", command: "Bend your right wrist backward", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_wbL", label: "Bend Wrist Back L", desc: "~70\u00B0", command: "Bend your left wrist backward", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          { id: "elbow", label: "Elbow (R/L)", color: C.purple, icon: "\u{1F4AA}",
            trigger: cc2.some(c => c.includes("Elbow")),
            palpOptions: ["TTP", "Swelling", "Crepitus"],
            romTests: [
              { key: "r_etsR", label: "Touch Own Shoulder R", desc: "~145\u00B0", command: "Bend your right elbow and touch your shoulder", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_etsL", label: "Touch Own Shoulder L", desc: "~145\u00B0", command: "Bend your left elbow and touch your shoulder", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          { id: "hand", label: "Hand (R/L)", color: C.purple, icon: "\u{270A}",
            trigger: cc2.some(c => c.includes("Hand")),
            palpOptions: ["TTP", "Swelling"],
            romTests: [
              { key: "r_hfR", label: "Make Fist R", desc: "", command: "Make a tight fist with your right hand", opts: ["Full","Partial","Minimal","Cannot"] },
              { key: "r_hfL", label: "Make Fist L", desc: "", command: "Make a tight fist with your left hand", opts: ["Full","Partial","Minimal","Cannot"] },
            ],
          },
        ];

        // Only show regions that are NOT resolved
        const examRegions = FU_REGIONS.filter(r => r.trigger && regionStatus[r.id] !== "Resolved");

        return (
          <div>
            <SectionHead title="Focused Physical Exam" sub="Only examining regions still symptomatic" />

            {examRegions.length === 0 && (
              <div style={{ padding: 32, textAlign: "center", color: C.textMuted, fontSize: 13 }}>
                All regions resolved or no regions to examine.
              </div>
            )}

            {examRegions.map(region => {
              const painKey = `pain_${region.id}`;
              const palpKey = `palp_${region.id}`;
              const romKey = `rom_${region.id}`;
              const prevPain = previousPain[region.id] || 7;
              const isDecreased = f[romKey] === "Decreased";
              const romDone = region.romTests.length > 0 && region.romTests.some(t => f[t.key]);

              return (
                <div key={region.id} style={{
                  padding: 16, background: C.bgElevated, borderRadius: 10,
                  border: `1px solid ${isDecreased ? C.amber + "66" : C.border}`, marginBottom: 12,
                }}>
                  <div style={{
                    fontSize: 12, fontWeight: 800, color: region.color, marginBottom: 14,
                    letterSpacing: "0.06em", textTransform: "uppercase",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span>{region.icon}</span> {region.label}
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                        background: regionStatus[region.id] === "Improved" ? C.greenBg : regionStatus[region.id] === "Worsened" ? C.redBg : C.amberBg,
                        border: `1px solid ${regionStatus[region.id] === "Improved" ? C.green : regionStatus[region.id] === "Worsened" ? C.red : C.amber}33`,
                        color: regionStatus[region.id] === "Improved" ? C.green : regionStatus[region.id] === "Worsened" ? C.red : C.amber,
                      }}>{regionStatus[region.id] || "Pending"}</span>
                    </div>
                    {romDone && <span style={{ fontSize: 9, color: C.green, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: C.greenBg, border: `1px solid ${C.green}33` }}>ROM Documented</span>}
                  </div>

                  {/* Pain Score with comparison */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4, fontWeight: 600 }}>
                      Pain Score (previous: <span style={{ color: C.amber, fontWeight: 700 }}>{prevPain}/10</span>)
                    </div>
                    <PainScoreRow value={f[painKey]} onChange={v => u(painKey, v)} disabled={locked} />
                  </div>

                  {/* Palpation */}
                  <PillSelect label="Palpation (Multi)" multi options={region.palpOptions} value={f[palpKey]} onChange={v => u(palpKey, v)} disabled={locked} />

                  {/* ROM */}
                  <PillSelect label="ROM" options={["Normal", "Decreased"]} value={f[romKey]} onChange={v => u(romKey, v)} disabled={locked} />

                  {/* Functional ROM button if decreased */}
                  {isDecreased && region.romTests.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <button onClick={() => setRomModal(region)} disabled={locked} style={{
                        width: "100%", padding: "10px 16px", borderRadius: 8, cursor: locked ? "not-allowed" : "pointer",
                        background: romDone ? C.greenBg : C.amberBg,
                        border: `1px solid ${romDone ? C.green : C.amber}44`,
                        color: romDone ? C.green : C.amber,
                        fontSize: 12, fontWeight: 700, textAlign: "left",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                      }}>
                        <span>{romDone ? "\u2713 Functional ROM Documented \u2014 Click to Review/Edit" : "\u26A0 ROM Decreased \u2014 Click to Document Functional ROM"}</span>
                        <span style={{ fontSize: 11 }}>→</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      // ════════════════════════════════════════════════════════════════════
      // FOLLOW-UP STEP 3: PT DATA REVIEW
      // ════════════════════════════════════════════════════════════════════
      case "ptReview": {
        const basePainScore = parseInt(caseData?.painScore) || 7;
        const currentAvgPain = (() => {
          const cc3 = caseData?.chiefComplaints || [];
          const regionIds = ["cervical","thoracic","lumbar","shoulder","elbow","wrist","hand","hip","knee","ankle"];
          const pains = regionIds.filter(id => {
            return cc3.some(c => c.toLowerCase().includes(id === "cervical" ? "neck" : id));
          }).map(id => parseInt(f[`pain_${id}`]) || 0).filter(p => p > 0);
          return pains.length ? Math.round(pains.reduce((a,b) => a + b, 0) / pains.length) : basePainScore - 2;
        })();

        const ptData = [
          { label: "Pain Trend", value: `${basePainScore}/10 → ${currentAvgPain}/10 across sessions`, icon: "\u{1F4C9}", color: currentAvgPain < basePainScore ? C.green : C.amber },
          { label: "Tolerance", value: "Patient tolerated all modalities without adverse reaction", icon: "\u2705", color: C.green },
          { label: "ROM Progress", value: "Improving", icon: "\u{1F4C8}", color: C.green },
          { label: "Strength", value: "Improving", icon: "\u{1F4AA}", color: C.green },
          { label: "ADL Status", value: "Conserved", icon: "\u{1F3C3}", color: C.green },
        ];

        const modalities = (caseData?.cptCodes || f.cptCodes || []).map(c => {
          const desc = { "97010": "Hot/Cold Pack", "97012": "Mech Traction", "97014": "E-Stim", "97035": "Ultrasound", "97110": "Therap Exercise", "97140": "Manual Therapy", "97530": "Therap Activities", "97032": "E-Stim Attend", "97112": "Neuromuscular Re-ed" };
          const code = c.split(" ")[0];
          return desc[code] || c;
        });

        return (
          <div>
            <SectionHead title="Physical Therapy Review" sub="Summary from PT daily and weekly notes" />

            {/* Modalities Used */}
            <div style={{ padding: 16, background: C.bgElevated, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.blueBright, marginBottom: 10, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Modalities Utilized
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {modalities.length > 0 ? modalities.map((m, i) => (
                  <span key={i} style={{
                    padding: "4px 10px", borderRadius: 12, fontSize: 10, fontWeight: 700,
                    background: C.blueGlow, color: C.blueBright, border: `1px solid ${C.blue}33`,
                  }}>{m}</span>
                )) : (
                  <span style={{ fontSize: 11, color: C.textMuted }}>No modalities documented yet</span>
                )}
              </div>
            </div>

            {/* Progress Summary */}
            {ptData.map((item, i) => (
              <div key={i} style={{
                padding: 14, background: C.bgElevated, borderRadius: 10,
                border: `1px solid ${C.border}`, marginBottom: 8,
                display: "flex", alignItems: "center", gap: 14,
              }}>
                <span style={{ fontSize: 20 }}>{item.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 2 }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: item.color }}>
                    {item.value}
                  </div>
                </div>
              </div>
            ))}

            {/* Read-only note */}
            <div style={{ padding: 12, background: C.blueGlow, borderRadius: 8, border: `1px solid ${C.blue}22`, marginTop: 12 }}>
              <div style={{ fontSize: 10, color: C.blueBright, fontWeight: 600, fontStyle: "italic" }}>
                This data is read-only and reflects PT session records. Edit PT notes in the PT module.
              </div>
            </div>
          </div>
        );
      }

      // ════════════════════════════════════════════════════════════════════
      // FOLLOW-UP STEP 4: ASSESSMENT & PLAN CONTINUATION
      // ════════════════════════════════════════════════════════════════════
      case "fuDx": {
        const ICD_DESC_FU = {
          "S13.4XXA": "Cervical sprain", "S16.1XXA": "Cervical strain", "S23.3XXA": "Thoracic sprain",
          "S29.012A": "Thoracic strain", "S33.5XXA": "Lumbar sprain", "S39.012A": "Lumbar strain",
          "M54.2": "Cervicalgia", "M54.5": "Low back pain", "M54.6": "Thoracic pain",
          "M62.830": "Muscle spasm", "G44.309": "Post-traumatic headache",
          "M54.12": "Cervical radiculopathy", "M54.16": "Lumbar radiculopathy", "M54.4": "Lumbago with sciatica",
          "M25.511": "R shoulder pain", "M25.512": "L shoulder pain",
          "M25.521": "Elbow pain", "M25.531": "Wrist pain",
          "M25.551": "R hip pain", "M25.552": "L hip pain",
          "M25.561": "R knee pain", "M25.562": "L knee pain",
          "M25.571": "Ankle pain", "M79.641": "Hand pain",
          "S43.421A": "Shoulder sprain", "S46.011A": "Shoulder strain",
          "S53.401A": "Elbow sprain", "S56.111A": "Elbow strain",
          "S63.501A": "Wrist sprain", "S66.111A": "Wrist strain",
          "S63.90XA": "Hand sprain", "S66.911A": "Hand strain",
          "S73.101A": "Hip sprain", "S76.011A": "Hip strain",
          "S83.401A": "Knee sprain", "S86.811A": "Knee strain",
          "S93.401A": "Ankle sprain", "S96.811A": "Ankle strain",
          "F43.10": "PTSD", "F41.1": "GAD", "F32.9": "Depression",
        };

        // Map ICD codes to region for status lookup
        const ICD_TO_REGION = {
          "S13.4XXA": "cervical", "S16.1XXA": "cervical", "M54.2": "cervical", "M54.12": "cervical", "G44.309": "cervical",
          "S23.3XXA": "thoracic", "S29.012A": "thoracic", "M54.6": "thoracic",
          "S33.5XXA": "lumbar", "S39.012A": "lumbar", "M54.5": "lumbar", "M54.16": "lumbar", "M54.4": "lumbar",
          "M62.830": "lumbar",
          "M25.511": "shoulder", "M25.512": "shoulder", "S43.421A": "shoulder", "S46.011A": "shoulder",
          "M25.521": "elbow", "S53.401A": "elbow", "S56.111A": "elbow",
          "M25.531": "wrist", "S63.501A": "wrist", "S66.111A": "wrist",
          "M79.641": "hand", "S63.90XA": "hand", "S66.911A": "hand",
          "M25.551": "hip", "M25.552": "hip", "S73.101A": "hip", "S76.011A": "hip",
          "M25.561": "knee", "M25.562": "knee", "S83.401A": "knee", "S86.811A": "knee",
          "M25.571": "ankle", "S93.401A": "ankle", "S96.811A": "ankle",
        };

        const STATUS_BADGE = {
          Improved:   { bg: C.greenBg, border: C.green, color: C.green },
          Persistent: { bg: C.amberBg, border: C.amber, color: C.amber },
          Worsened:   { bg: C.redBg || "#F4433622", border: C.red, color: C.red },
          Resolved:   { bg: C.bgElevated, border: C.textMuted, color: C.textMuted },
        };

        const icdCodes = f.icdCodes || caseData?.icd10 || [];

        return (
          <div>
            <SectionHead title="Assessment & Plan of Care Continuation" sub="Diagnosis status and treatment continuation orders" />

            {/* Diagnosis Status Cards */}
            <div style={{ padding: 16, background: C.bgCard, borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.textPrimary, marginBottom: 14, letterSpacing: "0.04em" }}>
                Active Diagnoses
              </div>
              {icdCodes.map(code => {
                const desc = ICD_DESC_FU[code] || code;
                const region = ICD_TO_REGION[code];
                const status = region ? (regionStatus[region] || "Persistent") : "Persistent";
                const badge = STATUS_BADGE[status] || STATUS_BADGE.Persistent;
                return (
                  <div key={code} style={{
                    padding: 12, background: C.bgElevated, borderRadius: 8,
                    border: `1px solid ${C.border}`, marginBottom: 6,
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <div>
                      <span style={{ fontSize: 12, fontWeight: 800, color: C.blueBright, marginRight: 8 }}>{code}</span>
                      <span style={{ fontSize: 12, color: C.textSecond }}>{desc}</span>
                    </div>
                    <span style={{
                      fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 6,
                      background: badge.bg, border: `1px solid ${badge.border}44`, color: badge.color,
                      letterSpacing: "0.04em", textTransform: "uppercase",
                    }}>{status}</span>
                  </div>
                );
              })}
              {icdCodes.length === 0 && (
                <div style={{ fontSize: 11, color: C.textMuted, padding: 12 }}>No ICD-10 codes from initial visit.</div>
              )}
            </div>

            {/* POC Continuation */}
            <div style={{ padding: 16, background: C.bgCard, borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: C.textPrimary }}>Plan of Care Continuation</span>
                <span style={{ fontSize: 9, fontWeight: 800, padding: "3px 8px", borderRadius: 4, background: "#EF535022", border: "1px solid #EF535044", color: "#EF5350" }}>MD ORDERS</span>
              </div>

              {/* E/M Level */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>E/M Level Established Patient</div>
                <PillSelect options={["99214 \u2014 Established \u00B7 Moderate", "99213 \u2014 Established \u00B7 Low"]}
                  value={f.emCode} onChange={v => u("emCode", v)} disabled={locked} />
              </div>

              {/* PT Frequency + Duration */}
              <div style={{ display: "flex", gap: 20, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 9, color: C.textMuted, marginBottom: 6, fontWeight: 700 }}>PT Frequency</div>
                  <PillSelect options={["3x/week", "2x/week", "4x/week", "5x/week", "Daily PRN"]}
                    value={f.ptFreq} onChange={v => u("ptFreq", v)} disabled={locked} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 9, color: C.textMuted, marginBottom: 6, fontWeight: 700 }}>Duration</div>
                  <PillSelect options={["4 weeks", "6 weeks", "8 weeks", "12 weeks"]}
                    value={f.ptDuration} onChange={v => u("ptDuration", v)} disabled={locked} />
                </div>
              </div>

              {/* Follow-up */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Follow-Up</div>
                <PillSelect options={["2 weeks", "1 week", "3 weeks", "4 weeks", "PRN"]}
                  value={f.followUp} onChange={v => u("followUp", v)} disabled={locked} />
              </div>

              {/* Medications + Referrals */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Input label="Medications (Rx)" value={f.rxMedications} onChange={e => u("rxMedications", e.target.value)} disabled={locked} placeholder="e.g., Ibuprofen 400mg, Cyclobenzaprine 10mg" />
                <Input label="Referrals" value={f.referrals} onChange={e => u("referrals", e.target.value)} disabled={locked} placeholder="e.g., Orthopedic eval, MRI" />
              </div>
            </div>

            {/* Imaging Results Upload */}
            <div style={{ padding: 16, background: C.bgCard, borderRadius: 12, border: `1px solid ${C.border}`, marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.textPrimary, marginBottom: 12, letterSpacing: "0.04em" }}>
                Imaging Results (from MAZEL)
              </div>
              <div style={{ padding: 24, background: C.bgElevated, borderRadius: 10, border: `2px dashed ${C.border}`, textAlign: "center", cursor: "pointer" }}>
                <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>{"\u{1F4C1}"}</div>
                <div style={{ fontSize: 12, color: C.textMuted, fontWeight: 600 }}>Drop X-ray results here or click to upload</div>
                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 4 }}>Accepts PDF, DICOM, or image files from MAZEL Diagnostic</div>
              </div>
            </div>
          </div>
        );
      }

      // ════════════════════════════════════════════════════════════════════
      // FOLLOW-UP STEP 5: AI NOTE (FOLLOW-UP)
      // ════════════════════════════════════════════════════════════════════
      case "fuNote":
        return (
          <div>
            <SectionHead title="AI-Generated Follow-Up Report" sub="Review, download, and sign" />

            {!generatedNote && !signed && (
              <div style={{ textAlign: "center", padding: 40 }}>
                <div style={{ fontSize: 42, marginBottom: 16, opacity: 0.3 }}>{"\u2728"}</div>
                <div style={{ fontSize: 14, color: C.textMuted, marginBottom: 20 }}>
                  Generate a comprehensive follow-up evaluation report from all selections
                </div>
                <button onClick={generateFollowUpSOAP} style={{
                  padding: "14px 36px", borderRadius: 10, fontSize: 14, fontWeight: 800,
                  cursor: "pointer", background: C.blue, color: C.white, border: "none",
                  boxShadow: `0 0 24px ${C.blueGlow}`, letterSpacing: "0.06em", textTransform: "uppercase",
                }}>Generate Follow-Up Report</button>
              </div>
            )}

            {generatedNote && (
              <div>
                {/* Note panel */}
                <div style={{
                  padding: 20, background: "#0A0C10", borderRadius: 10,
                  border: `1px solid ${C.border}`, maxHeight: 420, overflow: "auto",
                  fontFamily: "Georgia, 'Times New Roman', 'Palatino Linotype', serif", fontSize: 12,
                  color: C.textSecond, lineHeight: 1.8, whiteSpace: "pre-wrap",
                  marginBottom: 16,
                }}>{generatedNote}</div>

                {/* Action buttons */}
                {!signed && (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button onClick={generateFollowUpSOAP} style={{
                      padding: "10px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                      cursor: "pointer", background: "transparent", color: C.textMuted,
                      border: `1px solid ${C.border}`, letterSpacing: "0.04em", textTransform: "uppercase",
                    }}>Regenerate</button>
                    <button onClick={() => setPdfPreview(true)} style={{
                      padding: "10px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                      cursor: "pointer", background: "transparent", color: C.textSecond,
                      border: `1px solid ${C.border}`, letterSpacing: "0.04em", textTransform: "uppercase",
                    }}>PDF Preview</button>
                    <button onClick={() => {
                      const blob = new Blob([generatedNote], { type: "text/plain" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `MD_FollowUp_${f.ptLast}_${f.ptFirst}_${new Date().toISOString().split("T")[0]}.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }} style={{
                      padding: "10px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                      cursor: "pointer", background: C.bgElevated, color: C.blueBright,
                      border: `1px solid ${C.blue}44`, letterSpacing: "0.04em", textTransform: "uppercase",
                    }}>Download PDF</button>
                    <button onClick={handleSign} style={{
                      padding: "10px 24px", borderRadius: 8, fontSize: 11, fontWeight: 800,
                      cursor: "pointer", background: C.green, color: C.white, border: "none",
                      boxShadow: `0 0 16px ${C.green}30`, letterSpacing: "0.06em", textTransform: "uppercase",
                      marginLeft: "auto",
                    }}>Sign & Lock</button>
                  </div>
                )}

                {/* Signed success */}
                {signed && (
                  <div style={{
                    padding: 20, background: C.greenBg, borderRadius: 10,
                    border: `1px solid ${C.green}44`, textAlign: "center", marginTop: 12,
                  }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>{"\u2705"}</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.green, marginBottom: 4 }}>Note Signed & Locked</div>
                    <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
                      Signed by Dr. A. Falcon, MD on {new Date().toLocaleString()} — This record is now locked.
                    </div>
                    <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                      <button onClick={() => {
                        const blob = new Blob([generatedNote], { type: "text/plain" });
                        const a = document.createElement("a");
                        a.href = URL.createObjectURL(blob);
                        a.download = `Report_${caseData?.id || "visit"}_${new Date().toISOString().split("T")[0]}.txt`;
                        a.click();
                      }} style={{ padding: "8px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", background: C.blue, color: C.white, border: "none" }}>Download Report</button>
                      <button onClick={() => {
                        const iframe = document.createElement("iframe");
                        iframe.style.display = "none";
                        document.body.appendChild(iframe);
                        iframe.contentDocument.write("<html><head><title>Print Report</title><style>body{font-family:Georgia,'Times New Roman',serif;padding:40px;max-width:750px;margin:0 auto;line-height:1.8;font-size:12px;white-space:pre-wrap;color:#000;}</style></head><body>" + generatedNote.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/\n/g,"<br>") + "</body></html>");
                        iframe.contentDocument.close();
                        iframe.contentWindow.focus();
                        iframe.contentWindow.print();
                        setTimeout(() => document.body.removeChild(iframe), 1000);
                      }} style={{ padding: "8px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", background: C.bgElevated, color: C.textPrimary, border: `1px solid ${C.border}` }}>Print</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      // ════════════════════════════════════════════════════════════════════
      // MD STEP 8: AI NOTE + SIGN
      // ════════════════════════════════════════════════════════════════════
      case "aiNote":
        return (
          <div>
            <SectionHead title="AI-Generated SOAP Note" sub="Review, download, and sign" />

            {!generatedNote && !signed && (
              <div style={{ textAlign: "center", padding: 40 }}>
                <div style={{ fontSize: 42, marginBottom: 16, opacity: 0.3 }}>{"✨"}</div>
                <div style={{ fontSize: 14, color: C.textMuted, marginBottom: 20 }}>
                  Generate a comprehensive SOAP note from all binary selections
                </div>
                <button onClick={generateSOAP} style={{
                  padding: "14px 36px", borderRadius: 10, fontSize: 14, fontWeight: 800,
                  cursor: "pointer", background: C.blue, color: C.white, border: "none",
                  boxShadow: `0 0 24px ${C.blueGlow}`, letterSpacing: "0.06em", textTransform: "uppercase",
                }}>Generate SOAP Note</button>
              </div>
            )}

            {generatedNote && (
              <div>
                {/* Note panel */}
                <div style={{
                  padding: 20, background: "#0A0C10", borderRadius: 10,
                  border: `1px solid ${C.border}`, maxHeight: 420, overflow: "auto",
                  fontFamily: "Georgia, 'Times New Roman', 'Palatino Linotype', serif", fontSize: 12,
                  color: C.textSecond, lineHeight: 1.8, whiteSpace: "pre-wrap",
                  marginBottom: 16,
                }}>{generatedNote}</div>

                {/* Action buttons */}
                {!signed && (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button onClick={generateSOAP} style={{
                      padding: "10px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                      cursor: "pointer", background: "transparent", color: C.textMuted,
                      border: `1px solid ${C.border}`, letterSpacing: "0.04em", textTransform: "uppercase",
                    }}>Regenerate</button>
                    <button onClick={() => setPdfPreview(true)} style={{
                      padding: "10px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                      cursor: "pointer", background: "transparent", color: C.textSecond,
                      border: `1px solid ${C.border}`, letterSpacing: "0.04em", textTransform: "uppercase",
                    }}>PDF Preview</button>
                    <button onClick={() => {
                      const blob = new Blob([generatedNote], { type: "text/plain" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `MD_Note_${f.ptLast}_${f.ptFirst}_${new Date().toISOString().split("T")[0]}.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }} style={{
                      padding: "10px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                      cursor: "pointer", background: C.bgElevated, color: C.blueBright,
                      border: `1px solid ${C.blue}44`, letterSpacing: "0.04em", textTransform: "uppercase",
                    }}>Download PDF</button>
                    <button onClick={handleSign} style={{
                      padding: "10px 24px", borderRadius: 8, fontSize: 11, fontWeight: 800,
                      cursor: "pointer", background: C.green, color: C.white, border: "none",
                      boxShadow: `0 0 16px ${C.green}30`, letterSpacing: "0.06em", textTransform: "uppercase",
                      marginLeft: "auto",
                    }}>Sign & Lock</button>
                  </div>
                )}

                {/* Signed success */}
                {signed && (
                  <div style={{
                    padding: 20, background: C.greenBg, borderRadius: 10,
                    border: `1px solid ${C.green}44`, textAlign: "center", marginTop: 12,
                  }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>\u2705</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.green, marginBottom: 4 }}>Note Signed & Locked</div>
                    <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
                      Signed by Dr. A. Falcon, MD on {new Date().toLocaleString()} — This record is now locked.
                    </div>
                    <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                      <button onClick={() => {
                        const blob = new Blob([generatedNote], { type: "text/plain" });
                        const a = document.createElement("a");
                        a.href = URL.createObjectURL(blob);
                        a.download = `Report_${caseData?.id || "visit"}_${new Date().toISOString().split("T")[0]}.txt`;
                        a.click();
                      }} style={{ padding: "8px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", background: C.blue, color: C.white, border: "none" }}>Download Report</button>
                      <button onClick={() => {
                        const iframe = document.createElement("iframe");
                        iframe.style.display = "none";
                        document.body.appendChild(iframe);
                        iframe.contentDocument.write("<html><head><title>Print Report</title><style>body{font-family:Georgia,'Times New Roman',serif;padding:40px;max-width:750px;margin:0 auto;line-height:1.8;font-size:12px;white-space:pre-wrap;color:#000;}</style></head><body>" + generatedNote.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/\n/g,"<br>") + "</body></html>");
                        iframe.contentDocument.close();
                        iframe.contentWindow.focus();
                        iframe.contentWindow.print();
                        setTimeout(() => document.body.removeChild(iframe), 1000);
                      }} style={{ padding: "8px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", background: C.bgElevated, color: C.textPrimary, border: `1px solid ${C.border}` }}>Print</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      // ════════════════════════════════════════════════════════════════════
      // FINAL STEP 1: FINAL REGIONAL ASSESSMENT
      // ════════════════════════════════════════════════════════════════════
      case "finalStatus": {
        const ccF = caseData?.chiefComplaints || [];
        const FINAL_REGION_MAP = [
          { id: "cervical", label: "Cervical Spine", icon: "\u{1F9B4}", trigger: ccF.includes("Neck Pain") },
          { id: "thoracic", label: "Thoracic Spine", icon: "\u{1F9B4}", trigger: ccF.includes("Mid-Back Pain") || ccF.includes("Upper Back Pain") },
          { id: "lumbar",   label: "Lumbar Spine",   icon: "\u{1F9B4}", trigger: ccF.includes("Low Back Pain") || ccF.includes("Lower Back Pain") },
          { id: "shoulder", label: "Shoulder",       icon: "\u{1F4AA}", trigger: ccF.some(c => c.includes("Shoulder")) },
          { id: "elbow",    label: "Elbow",          icon: "\u{1F4AA}", trigger: ccF.some(c => c.includes("Elbow")) },
          { id: "wrist",    label: "Wrist",          icon: "\u{270B}", trigger: ccF.some(c => c.includes("Wrist")) },
          { id: "hand",     label: "Hand",           icon: "\u{270A}", trigger: ccF.some(c => c.includes("Hand")) },
          { id: "hip",      label: "Hip",            icon: "\u{1F9B5}", trigger: ccF.some(c => c.includes("Hip")) },
          { id: "knee",     label: "Knee",           icon: "\u{1F9B5}", trigger: ccF.some(c => c.includes("Knee")) },
          { id: "ankle",    label: "Ankle",          icon: "\u{1F9B6}", trigger: ccF.some(c => c.includes("Ankle")) },
        ];
        const finalActiveRegions = FINAL_REGION_MAP.filter(r => r.trigger);
        const FINAL_STATUS_OPTIONS = ["Resolved", "Improved with Residual", "Persistent", "Plateaued", "Worsened"];
        const FINAL_STATUS_COLORS = { "Resolved": C.green, "Improved with Residual": C.blueBright, "Persistent": C.amber, "Plateaued": C.textMuted, "Worsened": C.red };

        const FINAL_REGION_ICD_MAP = {
          cervical: ["S13.4XXA", "S16.1XXA", "M54.2", "M62.830", "G44.309"],
          thoracic: ["S23.3XXA", "S29.012A", "M54.6"],
          lumbar:   ["S33.5XXA", "S39.012A", "M54.5", "M54.4", "M54.16"],
          shoulder: ["S43.421A", "S46.011A", "M25.511", "M25.512"],
          elbow:    ["S53.401A", "S56.111A", "M25.521"],
          wrist:    ["S63.501A", "S66.111A", "M25.531"],
          hand:     ["S63.90XA", "S66.911A", "M79.641"],
          hip:      ["S73.101A", "S76.011A", "M25.551", "M25.552"],
          knee:     ["S83.401A", "S86.811A", "M25.561", "M25.562"],
          ankle:    ["S93.401A", "S96.811A", "M25.571"],
        };

        const FINAL_ICD_DESC = {
          "S13.4XXA": "Cervical Sprain", "S16.1XXA": "Cervical Muscle Strain", "M54.2": "Cervicalgia",
          "M62.830": "Muscle Spasm, Cervical", "G44.309": "Post-Traumatic Headache",
          "S23.3XXA": "Thoracic Sprain", "S29.012A": "Thoracic Muscle Strain", "M54.6": "Thoracic Pain",
          "S33.5XXA": "Lumbar Sprain", "S39.012A": "Lumbar Muscle Strain", "M54.5": "Low Back Pain",
          "M54.4": "Lumbago with Sciatica", "M54.16": "Radiculopathy, Lumbar",
          "S43.421A": "Shoulder Sprain", "S46.011A": "Shoulder Muscle Strain",
          "M25.511": "Shoulder Pain, Right", "M25.512": "Shoulder Pain, Left",
          "S53.401A": "Elbow Sprain", "S56.111A": "Elbow Muscle Strain", "M25.521": "Elbow Pain",
          "S63.501A": "Wrist Sprain", "S66.111A": "Wrist Muscle Strain", "M25.531": "Wrist Pain",
          "S63.90XA": "Hand Sprain", "S66.911A": "Hand Muscle Strain", "M79.641": "Hand Pain",
          "S73.101A": "Hip Sprain", "S76.011A": "Hip Muscle Strain",
          "M25.551": "Hip Pain, Right", "M25.552": "Hip Pain, Left",
          "S83.401A": "Knee Sprain", "S86.811A": "Knee Muscle Strain",
          "M25.561": "Knee Pain, Right", "M25.562": "Knee Pain, Left",
          "S93.401A": "Ankle Sprain", "S96.811A": "Ankle Muscle Strain", "M25.571": "Ankle Pain",
        };

        const getFinalInitialFindings = (regionId) => {
          const basePain = previousPain[regionId] || 7;
          const summaries = {
            cervical: `Pain ${basePain}/10 | Palpation: Paraspinal TTP, Muscle Spasm | ROM: Decreased | Functional: Limited cervical rotation and flexion`,
            thoracic: `Pain ${basePain}/10 | Palpation: Paraspinal TTP, Muscle Spasm | ROM: Decreased | Functional: Limited thoracic extension`,
            lumbar:   `Pain ${basePain}/10 | Palpation: Paraspinal TTP, SI Joint TTP | ROM: Decreased | Functional: Limited lumbar flexion and extension`,
            shoulder: `Pain ${basePain}/10 | Palpation: TTP, Crepitus | ROM: Decreased | Functional: Limited overhead reach and abduction`,
            elbow:    `Pain ${basePain}/10 | Palpation: TTP | ROM: Decreased | Functional: Limited elbow flexion/extension`,
            wrist:    `Pain ${basePain}/10 | Palpation: TTP | ROM: Decreased | Functional: Limited wrist flexion/extension`,
            hand:     `Pain ${basePain}/10 | Palpation: TTP | ROM: Decreased | Functional: Limited grip strength`,
            hip:      `Pain ${basePain}/10 | Palpation: TTP | ROM: Decreased | Functional: Limited hip flexion and rotation`,
            knee:     `Pain ${basePain}/10 | Palpation: TTP, Effusion | ROM: Decreased | Functional: Limited weight-bearing and stair climbing`,
            ankle:    `Pain ${basePain}/10 | Palpation: TTP, Swelling | ROM: Decreased | Functional: Limited dorsiflexion and gait`,
          };
          if (caseData?.initialFindings?.[regionId]) return caseData.initialFindings[regionId];
          return summaries[regionId] || `Pain ${basePain}/10 | Palpation: TTP | ROM: Decreased`;
        };

        return (
          <div>
            <SectionHead title="Final Regional Assessment" sub="Review every initially injured region" />
            {finalActiveRegions.length === 0 && (
              <div style={{ padding: 32, textAlign: "center", color: C.textMuted, fontSize: 13 }}>
                No regions found from initial visit chief complaints.
              </div>
            )}
            {finalActiveRegions.map(region => {
              const regionIcds = FINAL_REGION_ICD_MAP[region.id] || [];
              const currentFinalStatus = finalRegionStatus[region.id] || "";
              const statusColor = FINAL_STATUS_COLORS[currentFinalStatus] || C.textPrimary;
              return (
                <div key={region.id} style={{
                  padding: 20, background: C.bgElevated, borderRadius: 12,
                  border: `1px solid ${currentFinalStatus ? statusColor + "66" : C.border}`,
                  marginBottom: 16,
                }}>
                  {/* Header: Region name + ICD badges */}
                  <div style={{
                    display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 14,
                  }}>
                    <div style={{
                      fontSize: 13, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
                      display: "flex", alignItems: "center", gap: 8,
                      color: currentFinalStatus ? statusColor : C.textPrimary,
                    }}>
                      <span>{region.icon}</span> {region.label}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {regionIcds.map(code => (
                        <span key={code} style={{
                          fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 5,
                          background: C.bluePale, color: C.blueBright, border: `1px solid ${C.blue}44`,
                          fontFamily: "monospace", letterSpacing: "0.03em",
                        }}>{code} <span style={{ fontFamily: "inherit", fontSize: 8, opacity: 0.7 }}>{FINAL_ICD_DESC[code] || ""}</span></span>
                      ))}
                    </div>
                  </div>

                  {/* Initial Findings Summary */}
                  <div style={{
                    padding: 12, background: C.bgCard, borderRadius: 8,
                    border: `1px solid ${C.border}`, marginBottom: 14,
                  }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>
                      Initial Visit Findings
                    </div>
                    <div style={{ fontSize: 11, color: C.textSecond, lineHeight: 1.5 }}>
                      {getFinalInitialFindings(region.id)}
                    </div>
                  </div>

                  {/* Final Status PillSelect */}
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>
                      Final Status
                    </div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {FINAL_STATUS_OPTIONS.map(opt => {
                        const isActive = currentFinalStatus === opt;
                        const clr = FINAL_STATUS_COLORS[opt];
                        return (
                          <button key={opt} disabled={locked}
                            onClick={() => setFinalRegionStatus(prev => ({ ...prev, [region.id]: isActive ? "" : opt }))}
                            style={{
                              fontSize: 10, fontWeight: 700, padding: "6px 14px", borderRadius: 8,
                              border: `1px solid ${isActive ? clr + "66" : C.border}`,
                              background: isActive ? clr + "18" : "transparent",
                              color: isActive ? clr : C.textMuted,
                              cursor: locked ? "not-allowed" : "pointer",
                              transition: "all 0.15s ease",
                            }}>{opt}</button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      // ════════════════════════════════════════════════════════════════════
      // FINAL STEP 2: FINAL FOCUSED PHYSICAL EXAM
      // ════════════════════════════════════════════════════════════════════
      case "finalExam": {
        const ccFE = caseData?.chiefComplaints || [];
        const FINAL_EXAM_REGIONS = [
          { id: "cervical", label: "Cervical Spine", color: C.amber, icon: "\u{1F9B4}",
            trigger: ccFE.includes("Neck Pain"),
            palpOptions: ["Paraspinal TTP", "Facet TTP", "Muscle Spasm", "Trigger Points"],
            romTests: [
              { key: "r_cflex", label: "Chin to Chest", desc: "Flexion ~50\u00B0", command: "Tuck your chin to your chest", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_cext",  label: "Look at Ceiling", desc: "Extension ~60\u00B0", command: "Look up at the ceiling", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_clatR", label: "Ear to Shoulder R", desc: "Lateral ~45\u00B0", command: "Tilt your right ear to your right shoulder", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_clatL", label: "Ear to Shoulder L", desc: "Lateral ~45\u00B0", command: "Tilt your left ear to your left shoulder", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_crotR", label: "Look Over Shoulder R", desc: "Rotation ~70\u00B0", command: "Turn and look over your right shoulder", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_crotL", label: "Look Over Shoulder L", desc: "Rotation ~70\u00B0", command: "Turn and look over your left shoulder", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          { id: "thoracic", label: "Thoracic Spine", color: C.amber, icon: "\u{1F9B4}",
            trigger: ccFE.includes("Mid-Back Pain") || ccFE.includes("Upper Back Pain"),
            palpOptions: ["Paraspinal TTP", "Costovertebral TTP", "Muscle Spasm", "Trigger Points"],
            romTests: [],
          },
          { id: "lumbar", label: "Lumbar Spine", color: C.amber, icon: "\u{1F9B4}",
            trigger: ccFE.includes("Low Back Pain") || ccFE.includes("Lower Back Pain"),
            palpOptions: ["Paraspinal TTP", "Facet TTP", "SI Joint TTP", "Muscle Spasm", "Trigger Points"],
            romTests: [
              { key: "r_lflex", label: "Fingertip to Floor", desc: "Flexion ~60\u00B0", command: "Bend forward and reach for the floor", opts: ["Normal \u22644in","Mid-shin","Knee level","Cannot"] },
              { key: "r_lext",  label: "Backward Lean", desc: "Extension ~25\u00B0", command: "Lean backward gently", opts: ["Normal","Partial","Minimal","Cannot"] },
              { key: "r_lsbR",  label: "Side Bend Right", desc: "~25\u00B0", command: "Bend to the right side", opts: ["Normal","Partial","Minimal","Cannot"] },
              { key: "r_lsbL",  label: "Side Bend Left", desc: "~25\u00B0", command: "Bend to the left side", opts: ["Normal","Partial","Minimal","Cannot"] },
            ],
          },
          { id: "shoulder", label: "Shoulder (R/L)", color: C.purple, icon: "\u{1F4AA}",
            trigger: ccFE.some(c => c.includes("Shoulder")),
            palpOptions: ["TTP", "Swelling", "Crepitus", "Impingement"],
            romTests: [
              { key: "r_sohR", label: "Raise Arm Overhead R", desc: "~180\u00B0", command: "Raise your right arm straight up", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_sohL", label: "Raise Arm Overhead L", desc: "~180\u00B0", command: "Raise your left arm straight up", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_sbhR", label: "Reach Behind Head R", desc: "~90\u00B0 ER", command: "Reach behind your head with your right hand", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_sbhL", label: "Reach Behind Head L", desc: "~90\u00B0 ER", command: "Reach behind your head with your left hand", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          { id: "knee", label: "Knee (R/L)", color: C.green, icon: "\u{1F9B5}",
            trigger: ccFE.some(c => c.includes("Knee")),
            palpOptions: ["TTP", "Effusion", "Crepitus", "McMurray"],
            romTests: [
              { key: "r_kchR", label: "Rise from Chair R", desc: "~135\u00B0", command: "Stand up from a seated position using your right leg", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_kchL", label: "Rise from Chair L", desc: "~135\u00B0", command: "Stand up from a seated position using your left leg", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          { id: "ankle", label: "Ankle (R/L)", color: C.green, icon: "\u{1F9B6}",
            trigger: ccFE.some(c => c.includes("Ankle")),
            palpOptions: ["TTP", "Swelling", "Crepitus"],
            romTests: [
              { key: "r_ahtwR", label: "Heel-Toe Walk R", desc: "~20\u00B0 DF", command: "Walk heel-to-toe in a straight line", opts: ["Normal","Antalgic","Severe","Cannot"] },
              { key: "r_ahtwL", label: "Heel-Toe Walk L", desc: "~20\u00B0 DF", command: "Walk heel-to-toe in a straight line", opts: ["Normal","Antalgic","Severe","Cannot"] },
            ],
          },
          { id: "hip", label: "Hip (R/L)", color: C.green, icon: "\u{1F9B5}",
            trigger: ccFE.some(c => c.includes("Hip")),
            palpOptions: ["TTP", "Swelling", "Crepitus"],
            romTests: [
              { key: "r_hcarR", label: "Get In Car R", desc: "~120\u00B0", command: "Lift your right leg as if stepping into a car", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_hcarL", label: "Get In Car L", desc: "~120\u00B0", command: "Lift your left leg as if stepping into a car", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          { id: "wrist", label: "Wrist (R/L)", color: C.purple, icon: "\u{270B}",
            trigger: ccFE.some(c => c.includes("Wrist")),
            palpOptions: ["TTP", "Swelling", "Crepitus"],
            romTests: [
              { key: "r_wbR", label: "Bend Wrist Back R", desc: "~70\u00B0", command: "Bend your right wrist backward", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_wbL", label: "Bend Wrist Back L", desc: "~70\u00B0", command: "Bend your left wrist backward", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          { id: "elbow", label: "Elbow (R/L)", color: C.purple, icon: "\u{1F4AA}",
            trigger: ccFE.some(c => c.includes("Elbow")),
            palpOptions: ["TTP", "Swelling", "Crepitus"],
            romTests: [
              { key: "r_etsR", label: "Touch Own Shoulder R", desc: "~145\u00B0", command: "Bend your right elbow and touch your shoulder", opts: ["Full","Partial","Limited","Cannot"] },
              { key: "r_etsL", label: "Touch Own Shoulder L", desc: "~145\u00B0", command: "Bend your left elbow and touch your shoulder", opts: ["Full","Partial","Limited","Cannot"] },
            ],
          },
          { id: "hand", label: "Hand (R/L)", color: C.purple, icon: "\u{270A}",
            trigger: ccFE.some(c => c.includes("Hand")),
            palpOptions: ["TTP", "Swelling"],
            romTests: [
              { key: "r_hfR", label: "Make Fist R", desc: "", command: "Make a tight fist with your right hand", opts: ["Full","Partial","Minimal","Cannot"] },
              { key: "r_hfL", label: "Make Fist L", desc: "", command: "Make a tight fist with your left hand", opts: ["Full","Partial","Minimal","Cannot"] },
            ],
          },
        ];

        // Show ALL regions (not just non-resolved)
        const finalExamRegions = FINAL_EXAM_REGIONS.filter(r => r.trigger);

        return (
          <div>
            <SectionHead title="Final Physical Examination" sub="Examine all initially injured regions" />

            {finalExamRegions.length === 0 && (
              <div style={{ padding: 32, textAlign: "center", color: C.textMuted, fontSize: 13 }}>
                No regions to examine.
              </div>
            )}

            {finalExamRegions.map(region => {
              const painKey = `pain_${region.id}`;
              const palpKey = `palp_${region.id}`;
              const romKey = `rom_${region.id}`;
              const prevPain = previousPain[region.id] || 7;
              const isDecreased = f[romKey] === "Decreased";
              const romDone = region.romTests.length > 0 && region.romTests.some(t => f[t.key]);
              const fStatus = finalRegionStatus[region.id] || "";
              const fStatusColor = { "Resolved": C.green, "Improved with Residual": C.blueBright, "Persistent": C.amber, "Plateaued": C.textMuted, "Worsened": C.red }[fStatus] || C.textPrimary;

              return (
                <div key={region.id} style={{
                  padding: 16, background: C.bgElevated, borderRadius: 10,
                  border: `1px solid ${fStatus ? fStatusColor + "66" : C.border}`, marginBottom: 12,
                }}>
                  <div style={{
                    fontSize: 12, fontWeight: 800, color: region.color, marginBottom: 14,
                    letterSpacing: "0.06em", textTransform: "uppercase",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span>{region.icon}</span> {region.label}
                      {fStatus && <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                        background: fStatusColor + "18",
                        border: `1px solid ${fStatusColor}33`,
                        color: fStatusColor,
                      }}>{fStatus}</span>}
                    </div>
                    {romDone && <span style={{ fontSize: 9, color: C.green, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: C.greenBg, border: `1px solid ${C.green}33` }}>ROM Documented</span>}
                  </div>

                  {/* Pain Score with comparison */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4, fontWeight: 600 }}>
                      Pain Score (initial: <span style={{ color: C.amber, fontWeight: 700 }}>{prevPain}/10</span>)
                    </div>
                    <PainScoreRow value={f[painKey]} onChange={v => u(painKey, v)} disabled={locked} />
                  </div>

                  {/* Palpation */}
                  <PillSelect label="Palpation (Multi)" multi options={region.palpOptions} value={f[palpKey]} onChange={v => u(palpKey, v)} disabled={locked} />

                  {/* ROM */}
                  <PillSelect label="ROM" options={["Normal", "Decreased"]} value={f[romKey]} onChange={v => u(romKey, v)} disabled={locked} />

                  {/* Functional ROM button if decreased */}
                  {isDecreased && region.romTests.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <button onClick={() => setRomModal(region)} disabled={locked} style={{
                        width: "100%", padding: "10px 16px", borderRadius: 8, cursor: locked ? "not-allowed" : "pointer",
                        background: romDone ? C.greenBg : C.amberBg,
                        border: `1px solid ${romDone ? C.green : C.amber}44`,
                        color: romDone ? C.green : C.amber,
                        fontSize: 12, fontWeight: 700, textAlign: "left",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                      }}>
                        <span>{romDone ? "\u2713 Functional ROM Documented \u2014 Click to Review/Edit" : "\u26A0 ROM Decreased \u2014 Click to Document Functional ROM"}</span>
                        <span style={{ fontSize: 11 }}>\u2192</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      // ════════════════════════════════════════════════════════════════════
      // FINAL STEP 3: FUNCTIONAL STATUS ASSESSMENT
      // ════════════════════════════════════════════════════════════════════
      case "finalFunc": {
        const FUNC_FIELDS = [
          { key: "work",    label: "Work Tolerance",     options: ["Normal", "Improved", "Limited", "N/A"] },
          { key: "driving", label: "Driving Tolerance",  options: ["Normal", "Improved", "Limited", "N/A"] },
          { key: "sleep",   label: "Sleep Quality",      options: ["Normal", "Improved", "Limited"] },
          { key: "mobility",label: "Mobility",           options: ["Normal", "Improved", "Limited"] },
        ];

        return (
          <div>
            <SectionHead title="Functional Status" sub="Current functional capacity and ADL tolerance" />
            {FUNC_FIELDS.map(field => (
              <div key={field.key} style={{ marginBottom: 16 }}>
                <PillSelect
                  label={field.label}
                  options={field.options}
                  value={funcStatus[field.key]}
                  onChange={v => setFuncStatus(prev => ({ ...prev, [field.key]: v }))}
                  disabled={locked}
                />
              </div>
            ))}
          </div>
        );
      }

      // ════════════════════════════════════════════════════════════════════
      // FINAL STEP 4: MMI DETERMINATION
      // ════════════════════════════════════════════════════════════════════
      case "mmi": {
        return (
          <div>
            <SectionHead title="Maximum Medical Improvement" sub="Clinical determination" />

            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
              {/* MMI Reached card */}
              <div onClick={() => !locked && setMmiReached(true)} style={{
                padding: 24, borderRadius: 12, cursor: locked ? "not-allowed" : "pointer",
                background: mmiReached === true ? C.greenBg : C.bgElevated,
                border: `2px solid ${mmiReached === true ? C.green : C.border}`,
                transition: "all 0.2s ease",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: "50%",
                    border: `2px solid ${mmiReached === true ? C.green : C.textMuted}`,
                    background: mmiReached === true ? C.green : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.2s ease",
                  }}>
                    {mmiReached === true && <span style={{ color: C.white, fontSize: 12, fontWeight: 800 }}>\u2713</span>}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: mmiReached === true ? C.green : C.textPrimary }}>
                    MMI Reached
                  </div>
                </div>
                <div style={{ fontSize: 12, color: C.textSecond, lineHeight: 1.6, marginLeft: 32 }}>
                  Patient has stabilized. No significant additional improvement expected with continued conservative care.
                </div>
              </div>

              {/* MMI Not Reached card */}
              <div onClick={() => !locked && setMmiReached(false)} style={{
                padding: 24, borderRadius: 12, cursor: locked ? "not-allowed" : "pointer",
                background: mmiReached === false ? C.bluePale : C.bgElevated,
                border: `2px solid ${mmiReached === false ? C.blueBright : C.border}`,
                transition: "all 0.2s ease",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: "50%",
                    border: `2px solid ${mmiReached === false ? C.blueBright : C.textMuted}`,
                    background: mmiReached === false ? C.blueBright : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.2s ease",
                  }}>
                    {mmiReached === false && <span style={{ color: C.white, fontSize: 12, fontWeight: 800 }}>\u2713</span>}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: mmiReached === false ? C.blueBright : C.textPrimary }}>
                    MMI Not Reached
                  </div>
                </div>
                <div style={{ fontSize: 12, color: C.textSecond, lineHeight: 1.6, marginLeft: 32 }}>
                  Measurable improvement is still occurring. Further improvement is reasonably expected.
                </div>
              </div>
            </div>

            {/* Conditional message */}
            {mmiReached === true && (
              <div style={{
                padding: 16, background: C.greenBg, borderRadius: 10,
                border: `1px solid ${C.green}44`, marginBottom: 16, textAlign: "center",
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>
                  Patient will be discharged from active treatment
                </div>
              </div>
            )}
            {mmiReached === false && (
              <div style={{
                padding: 16, background: C.bluePale, borderRadius: 10,
                border: `1px solid ${C.blueBright}44`, marginBottom: 16, textAlign: "center",
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.blueBright }}>
                  Continued PT remains medically necessary
                </div>
              </div>
            )}

            {/* MMI Disclaimer */}
            <div style={{
              padding: 16, background: C.bgCard, borderRadius: 10,
              border: `1px solid ${C.border}`,
            }}>
              <div style={{ fontSize: 11, color: C.textMuted, lineHeight: 1.7, fontStyle: "italic" }}>
                Determination of Maximum Medical Improvement is based on the patient's history, objective findings, and response to treatment, within a reasonable degree of medical probability. Any impairment rating should be established in accordance with the AMA Guides.
              </div>
            </div>
          </div>
        );
      }

      // ════════════════════════════════════════════════════════════════════
      // FINAL STEP 5: DISCHARGE NOTE GENERATOR
      // ════════════════════════════════════════════════════════════════════
      case "finalNote":
        return (
          <div>
            <SectionHead title="Discharge Note" sub="Generate, review, download, and sign" />

            {!generatedNote && !signed && (
              <div style={{ textAlign: "center", padding: 40 }}>
                <div style={{ fontSize: 42, marginBottom: 16, opacity: 0.3 }}>{"\u2728"}</div>
                <div style={{ fontSize: 14, color: C.textMuted, marginBottom: 20 }}>
                  Generate a comprehensive discharge / final visit report from all selections
                </div>
                <button onClick={generateFinalSOAP} style={{
                  padding: "14px 36px", borderRadius: 10, fontSize: 14, fontWeight: 800,
                  cursor: "pointer", background: C.blue, color: C.white, border: "none",
                  boxShadow: `0 0 24px ${C.blueGlow}`, letterSpacing: "0.06em", textTransform: "uppercase",
                }}>Generate Discharge Note</button>
              </div>
            )}

            {generatedNote && (
              <div>
                {/* Note panel */}
                <div style={{
                  padding: 20, background: "#0A0C10", borderRadius: 10,
                  border: `1px solid ${C.border}`, maxHeight: 420, overflow: "auto",
                  fontFamily: "Georgia, 'Times New Roman', 'Palatino Linotype', serif", fontSize: 12,
                  color: C.textSecond, lineHeight: 1.8, whiteSpace: "pre-wrap",
                  marginBottom: 16,
                }}>{generatedNote}</div>

                {/* Action buttons */}
                {!signed && (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button onClick={generateFinalSOAP} style={{
                      padding: "10px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                      cursor: "pointer", background: "transparent", color: C.textMuted,
                      border: `1px solid ${C.border}`, letterSpacing: "0.04em", textTransform: "uppercase",
                    }}>Regenerate</button>
                    <button onClick={() => setPdfPreview(true)} style={{
                      padding: "10px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                      cursor: "pointer", background: "transparent", color: C.textSecond,
                      border: `1px solid ${C.border}`, letterSpacing: "0.04em", textTransform: "uppercase",
                    }}>PDF Preview</button>
                    <button onClick={() => {
                      const blob = new Blob([generatedNote], { type: "text/plain" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `MD_Discharge_${f.ptLast}_${f.ptFirst}_${new Date().toISOString().split("T")[0]}.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }} style={{
                      padding: "10px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                      cursor: "pointer", background: C.bgElevated, color: C.blueBright,
                      border: `1px solid ${C.blue}44`, letterSpacing: "0.04em", textTransform: "uppercase",
                    }}>Download PDF</button>
                    <button onClick={handleSign} style={{
                      padding: "10px 24px", borderRadius: 8, fontSize: 11, fontWeight: 800,
                      cursor: "pointer", background: C.green, color: C.white, border: "none",
                      boxShadow: `0 0 16px ${C.green}30`, letterSpacing: "0.06em", textTransform: "uppercase",
                      marginLeft: "auto",
                    }}>Sign & Lock</button>
                  </div>
                )}

                {/* Signed success */}
                {signed && (
                  <div style={{
                    padding: 20, background: C.greenBg, borderRadius: 10,
                    border: `1px solid ${C.green}44`, textAlign: "center", marginTop: 12,
                  }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>{"\u2705"}</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.green, marginBottom: 4 }}>Note Signed & Locked</div>
                    <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
                      Signed by Dr. A. Falcon, MD on {new Date().toLocaleString()} — This record is now locked.
                    </div>
                    <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                      <button onClick={() => {
                        const blob = new Blob([generatedNote], { type: "text/plain" });
                        const a = document.createElement("a");
                        a.href = URL.createObjectURL(blob);
                        a.download = `Report_${caseData?.id || "visit"}_${new Date().toISOString().split("T")[0]}.txt`;
                        a.click();
                      }} style={{ padding: "8px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", background: C.blue, color: C.white, border: "none" }}>Download Report</button>
                      <button onClick={() => {
                        const iframe = document.createElement("iframe");
                        iframe.style.display = "none";
                        document.body.appendChild(iframe);
                        iframe.contentDocument.write("<html><head><title>Print Report</title><style>body{font-family:Georgia,'Times New Roman',serif;padding:40px;max-width:750px;margin:0 auto;line-height:1.8;font-size:12px;white-space:pre-wrap;color:#000;}</style></head><body>" + generatedNote.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/\n/g,"<br>") + "</body></html>");
                        iframe.contentDocument.close();
                        iframe.contentWindow.focus();
                        iframe.contentWindow.print();
                        setTimeout(() => document.body.removeChild(iframe), 1000);
                      }} style={{ padding: "8px 20px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", background: C.bgElevated, color: C.textPrimary, border: `1px solid ${C.border}` }}>Print</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      default:
        return <div style={{ color: C.textMuted, padding: 40, textAlign: "center" }}>Step not found</div>;
    }
  };

  // ── PDF PREVIEW MODAL ───────────────────────────────────────────────
  if (pdfPreview && generatedNote) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(0,0,0,0.9)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ background: "#fff", borderRadius: 8, width: "100%", maxWidth: 800, maxHeight: "95vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <div style={{ padding: "10px 20px", background: "#1565C0", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <span style={{ color: "#fff", fontSize: 13, fontWeight: 800 }}>Report Preview — {f.ptLast}, {f.ptFirst}</span>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => {
                const iframe = document.createElement("iframe");
                iframe.style.display = "none";
                document.body.appendChild(iframe);
                iframe.contentDocument.write("<html><head><style>body{font-family:Georgia,serif;padding:40px;max-width:750px;margin:0 auto;line-height:1.8;font-size:12px;white-space:pre-wrap;color:#000;}</style></head><body>" + generatedNote.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/\n/g,"<br>") + "</body></html>");
                iframe.contentDocument.close();
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
                setTimeout(() => document.body.removeChild(iframe), 1000);
              }} style={{ padding: "5px 14px", borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: "pointer", background: "#fff", border: "none", color: "#1565C0" }}>Print</button>
              <button onClick={() => {
                const blob = new Blob([generatedNote], { type: "text/plain" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `Report_${caseData?.id || "visit"}_${new Date().toISOString().split("T")[0]}.txt`;
                a.click();
              }} style={{ padding: "5px 14px", borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: "pointer", background: "#fff", border: "none", color: "#1565C0" }}>Download</button>
              <button onClick={() => setPdfPreview(false)} style={{ padding: "5px 14px", borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: "pointer", background: "#EF5350", border: "none", color: "#fff" }}>Close</button>
            </div>
          </div>
          {/* Content */}
          <div style={{ flex: 1, overflow: "auto", padding: "40px 50px", background: "#fff" }}>
            <pre style={{ fontFamily: "Georgia, 'Times New Roman', 'Palatino Linotype', serif", fontSize: 12, lineHeight: 1.8, color: "#000", whiteSpace: "pre-wrap", margin: 0 }}>{generatedNote}</pre>
          </div>
        </div>
      </div>
    );
  }

  // ── VISIT TYPE SELECTOR ──────────────────────────────────────────────
  if (!visitType) {
    const suggestedType = (!caseData?.visits || caseData.visits <= 1) ? "initial" : "follow_up";
    return (
      <div style={{ position: "fixed", inset: 0, background: C.bg, zIndex: 300, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "16px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 800, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.12em" }}>MD Visit</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.textPrimary }}>{caseData?.firstName || caseData?.patient} {caseData?.lastName || ""}</div>
          </div>
          <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 8, cursor: "pointer", background: C.bgElevated, border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 12, fontWeight: 700 }}>Cancel</button>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ maxWidth: 600, width: "100%", padding: "0 20px" }}>
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: C.textPrimary, marginBottom: 8 }}>Select Visit Type</div>
              <div style={{ fontSize: 12, color: C.textMuted }}>Case: {caseData?.id} | Previous visits: {caseData?.visits || 0} | DOA: {caseData?.accident}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { type: "initial", label: "Initial Evaluation", desc: "Full 8-step form: Patient, accident, insurance, legal docs, interview, exam, dx+plan, AI report", suggested: suggestedType === "initial" },
                { type: "follow_up", label: "Follow-Up Visit", desc: "Focused 5-step: Region status, focused exam, PT review, assessment, continuance report", suggested: suggestedType === "follow_up" },
                { type: "final", label: "Final / Discharge", desc: "Discharge assessment, outcome, MMI determination, full case summary" },
              ].map(opt => (
                <div key={opt.type} onClick={() => !opt.disabled && setVisitType(opt.type)} style={{
                  padding: "20px 24px", borderRadius: 12, cursor: opt.disabled ? "default" : "pointer",
                  background: opt.suggested ? C.blueGlow : C.bgCard,
                  border: `2px solid ${opt.suggested ? C.blue : C.border}`,
                  opacity: opt.disabled ? 0.5 : 1,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: C.textPrimary, marginBottom: 4 }}>{opt.label}</div>
                      <div style={{ fontSize: 12, color: C.textSecond, lineHeight: 1.6 }}>{opt.desc}</div>
                    </div>
                    {opt.suggested && <span style={{ fontSize: 11, fontWeight: 700, color: C.blueBright, padding: "4px 12px", borderRadius: 6, background: C.blue + "22", border: `1px solid ${C.blue}44`, whiteSpace: "nowrap" }}>Suggested</span>}
                    {opt.disabled && <span style={{ fontSize: 11, fontWeight: 700, color: C.amber, padding: "4px 12px", borderRadius: 6, background: C.amberBg, border: `1px solid ${C.amber}33`, whiteSpace: "nowrap" }}>Coming Soon</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── MAIN LAYOUT ────────────────────────────────────────────────────────
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 300,
      background: C.bg, display: "flex", flexDirection: "column",
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    }}>

      {/* ══ TOP BAR ══════════════════════════════════════════════════════ */}
      <div style={{
        padding: "12px 24px", background: C.bgCard,
        borderBottom: `1px solid ${C.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button onClick={onClose} style={{
            padding: "6px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700,
            cursor: "pointer", background: "transparent", border: `1px solid ${C.border}`,
            color: C.textMuted, letterSpacing: "0.04em", textTransform: "uppercase",
          }}>← Back</button>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.textPrimary, letterSpacing: "-0.01em" }}>
              {f.ptFirst} {f.ptLast || "New Patient"}
            </div>
            <div style={{ fontSize: 11, color: C.textMuted }}>
              Case: {caseData?.id || "New"} &middot; DOA: {f.doa || "--"}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", padding: "4px 12px",
            borderRadius: 20, fontSize: 10, fontWeight: 800,
            background: isInitial ? C.bluePale : C.purpleBg,
            color: isInitial ? C.blueBright : C.purple,
            border: `1px solid ${isInitial ? C.blue + "33" : C.purple + "33"}`,
            letterSpacing: "0.08em", textTransform: "uppercase",
          }}>{isInitial ? "Initial Visit" : "Follow-Up"}</span>
          {locked && (
            <span style={{
              display: "inline-flex", alignItems: "center", padding: "4px 12px",
              borderRadius: 20, fontSize: 10, fontWeight: 800,
              background: C.greenBg, color: C.green, border: `1px solid ${C.green}33`,
              letterSpacing: "0.08em", textTransform: "uppercase",
            }}>Signed ✓</span>
          )}
          <span style={{
            display: "inline-flex", alignItems: "center", padding: "4px 12px",
            borderRadius: 20, fontSize: 10, fontWeight: 800,
            background: phase === "md" ? C.bluePale : C.amberBg,
            color: phase === "md" ? C.blueBright : C.amber,
            border: `1px solid ${phase === "md" ? C.blue + "33" : C.amber + "33"}`,
            letterSpacing: "0.08em", textTransform: "uppercase",
          }}>{phase === "md" ? "Physician" : "Staff"}</span>
        </div>
      </div>

      {/* ══ BODY: SIDEBAR + CONTENT ═════════════════════════════════════ */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

        {/* ── LEFT SIDEBAR: STEP NAV ─────────────────────────────────── */}
        <div style={{
          width: 200, background: C.bgCard, borderRight: `1px solid ${C.border}`,
          padding: "16px 0", overflowY: "auto", flexShrink: 0,
        }}>
          {/* Staff Steps */}
          <div style={{
            fontSize: 9, fontWeight: 800, color: C.amber, letterSpacing: "0.14em",
            textTransform: "uppercase", padding: "4px 16px 8px", opacity: 0.7,
          }}>Staff Phase</div>
          {STAFF_STEPS.map((s, i) => {
            const active = step === s.id;
            const accessible = phase === "staff" || phase === "md" || isMD;
            return (
              <button key={s.id} onClick={() => { if (accessible) setStep(s.id); }}
                disabled={!accessible}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  width: "100%", padding: "10px 16px", border: "none",
                  background: active ? C.bgHover : "transparent",
                  cursor: accessible ? "pointer" : "not-allowed",
                  borderLeft: active ? `3px solid ${C.blue}` : "3px solid transparent",
                  transition: "all 0.15s",
                }}>
                <span style={{ fontSize: 16, opacity: active ? 1 : 0.4 }}>{s.icon}</span>
                <span style={{
                  fontSize: 12, fontWeight: active ? 700 : 500,
                  color: active ? C.textPrimary : C.textMuted,
                }}>{s.label}</span>
              </button>
            );
          })}

          {/* Divider */}
          <div style={{ height: 1, background: C.border, margin: "12px 16px" }} />

          {/* MD Steps */}
          <div style={{
            fontSize: 9, fontWeight: 800, color: C.blueBright, letterSpacing: "0.14em",
            textTransform: "uppercase", padding: "4px 16px 8px", opacity: 0.7,
          }}>Physician Phase</div>
          {mdSteps.map((s, i) => {
            const active = step === s.id;
            const accessible = phase === "md" || isMD;
            return (
              <button key={s.id} onClick={() => { if (accessible) setStep(s.id); }}
                disabled={!accessible}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  width: "100%", padding: "10px 16px", border: "none",
                  background: active ? C.bgHover : "transparent",
                  cursor: accessible ? "pointer" : "not-allowed",
                  borderLeft: active ? `3px solid ${C.blue}` : "3px solid transparent",
                  transition: "all 0.15s",
                  opacity: accessible ? 1 : 0.35,
                }}>
                <span style={{ fontSize: 16, opacity: active ? 1 : 0.4 }}>{s.icon}</span>
                <span style={{
                  fontSize: 12, fontWeight: active ? 700 : 500,
                  color: active ? C.textPrimary : C.textMuted,
                }}>{s.label}</span>
              </button>
            );
          })}
        </div>

        {/* ── RIGHT CONTENT ──────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px" }}>
          {renderStep()}
        </div>
      </div>

      {/* ══ BOTTOM BAR ═══════════════════════════════════════════════════ */}
      <div style={{
        padding: "10px 24px", background: C.bgCard,
        borderTop: `1px solid ${C.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div style={{ fontSize: 11, color: C.textMuted, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{
            width: 6, height: 6, borderRadius: "50%",
            background: draftSaved ? C.green : C.amber,
            boxShadow: `0 0 6px ${draftSaved ? C.green : C.amber}`,
          }} />
          {draftSaved ? "Draft saved" : "Saving..."}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={goPrev} disabled={!canPrev || locked} style={{
            padding: "8px 18px", borderRadius: 8, fontSize: 11, fontWeight: 700,
            cursor: canPrev && !locked ? "pointer" : "not-allowed",
            background: "transparent", color: C.textMuted, border: `1px solid ${C.border}`,
            opacity: canPrev ? 1 : 0.35, letterSpacing: "0.04em", textTransform: "uppercase",
          }}>← Previous</button>
          {/* Special: last staff step shows "Hand Off to MD" instead of Next */}
          {phase === "staff" && step === "legal" ? (
            <button onClick={handoffToMD} style={{
              padding: "8px 22px", borderRadius: 8, fontSize: 11, fontWeight: 800,
              cursor: "pointer", background: C.blue, color: C.white, border: "none",
              boxShadow: `0 0 14px ${C.blueGlow}`, letterSpacing: "0.04em", textTransform: "uppercase",
            }}>Ready for MD →</button>
          ) : (
            <button onClick={goNext} disabled={!canNext || locked} style={{
              padding: "8px 22px", borderRadius: 8, fontSize: 11, fontWeight: 700,
              cursor: canNext && !locked ? "pointer" : "not-allowed",
              background: canNext ? C.blue : "transparent",
              color: canNext ? C.white : C.textMuted,
              border: canNext ? "none" : `1px solid ${C.border}`,
              opacity: canNext ? 1 : 0.35, letterSpacing: "0.04em", textTransform: "uppercase",
              boxShadow: canNext ? `0 0 14px ${C.blueGlow}` : "none",
            }}>Next →</button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── PT SESSION MODULE ─────────────────────────────────────────────────────
// All-binary inputs, AI generates narrative. Props: { caseData, onClose }
// ───────────────────────────────────────────────────────────────────────────
// PillSelect reused from mdVisit above

const PTSessionModule = ({ caseData, onClose }) => {
  const [sessionType, setSessionType] = React.useState('daily');
  const [locked, setLocked] = React.useState(false);

  // ── DAILY STATE ──────────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const [daily, setDaily] = React.useState({
    sessionDate: today,
    sessionNum: '',
    totalAuthorized: '',
    painStart: null,
    painEnd: null,
    modalities: [],
    exercises: [],
    response: '',
    functionalProgress: '',
    compliance: '',
    barriers: [],
    aiNote: '',
    aiGenerated: false,
  });
  const ud = (k, v) => setDaily(f => ({ ...f, [k]: v }));

  // ── WEEKLY STATE ─────────────────────────────────────────────────────────
  const [weekly, setWeekly] = React.useState({
    weekNum: '',
    summaryDate: today,
    painStart: '',
    painEnd: '',
    romProgress: '',
    strengthProgress: '',
    adlProgress: '',
    painManagement: '',
    overallStatus: '',
    aiNote: '',
    aiGenerated: false,
  });
  const uw = (k, v) => setWeekly(f => ({ ...f, [k]: v }));

  // ── CPT AUTO-POPULATION from MD treatment plan ─────────────────────────
  // Map modalities to CPT codes
  const MOD_TO_CPT = {
    'Hot Pack': '97010', 'Ice/Cold Pack': '97010',
    'Ultrasound': '97035',
    'E-Stim (TENS)': '97014', 'E-Stim (NMES)': '97032',
    'Traction (Cervical)': '97012', 'Traction (Lumbar)': '97012',
    'Manual Therapy': '97140', 'Massage/Soft Tissue': '97140',
    'Therapeutic Exercise': '97110', 'Gait Training': '97116',
    'Kinesiotaping': '97530',
  };
  const CPT_LABELS = {
    '97010': 'Hot/Cold Packs', '97012': 'Mechanical Traction', '97014': 'E-Stim (unattended)',
    '97032': 'E-Stim (attended)', '97035': 'Ultrasound', '97110': 'Therapeutic Exercise',
    '97116': 'Gait Training', '97140': 'Manual Therapy', '97530': 'Therapeutic Activities',
  };

  // Auto-derive CPT codes from selected modalities
  const autoCPT = React.useMemo(() => {
    const codes = new Set();
    (daily.modalities || []).forEach(m => { if (MOD_TO_CPT[m]) codes.add(MOD_TO_CPT[m]); });
    // Add exercise codes
    if ((daily.exercises || []).length > 0) codes.add('97110');
    return [...codes].sort();
  }, [daily.modalities, daily.exercises]);

  // Pre-populate modalities from caseData.cpt if available (from MD treatment plan)
  React.useEffect(() => {
    if (caseData?.cpt && daily.modalities.length === 0) {
      const preselect = [];
      const cptSet = new Set(caseData.cpt);
      if (cptSet.has('97010')) preselect.push('Hot Pack');
      if (cptSet.has('97035')) preselect.push('Ultrasound');
      if (cptSet.has('97014')) preselect.push('E-Stim (TENS)');
      if (cptSet.has('97032')) preselect.push('E-Stim (NMES)');
      if (cptSet.has('97140')) preselect.push('Manual Therapy');
      if (cptSet.has('97110')) preselect.push('Therapeutic Exercise');
      if (cptSet.has('97530')) preselect.push('Kinesiotaping');
      if (preselect.length > 0) ud('modalities', preselect);
    }
  }, []);

  // ── LISTS ────────────────────────────────────────────────────────────────
  const MODALITIES = [
    'Hot Pack', 'Ice/Cold Pack', 'Ultrasound', 'E-Stim (TENS)', 'E-Stim (NMES)',
    'Traction (Cervical)', 'Traction (Lumbar)', 'Manual Therapy',
    'Massage/Soft Tissue', 'Therapeutic Exercise', 'Gait Training', 'Kinesiotaping'
  ];
  const EXERCISES = [
    'Cervical AROM', 'Cervical Strengthening', 'Lumbar Stabilization',
    'Core Strengthening', 'Shoulder AROM', 'Hip Strengthening',
    'Balance/Proprioception', 'McKenzie Extension', 'Postural Training',
    'Stretching -- Hamstrings', 'Stretching -- Hip Flexors', 'Neural Mobilization'
  ];
  const RESPONSE_OPTS = ['Tolerated Well', 'Mild Discomfort', 'Significant Pain', 'Adverse Reaction'];
  const FUNC_PROGRESS_OPTS = ['Improving', 'Plateau', 'Declining', 'No Change'];
  const COMPLIANCE_OPTS = ['Compliant', 'Partially Compliant', 'Non-Compliant'];
  const BARRIER_OPTS = ['None', 'Sleep Disturbance', 'Work Demands', 'Anxiety', 'Limited HEP Compliance', 'Transportation Issues'];
  const PROGRESS_3 = ['Improving', 'Stable', 'Declining'];
  const OVERALL_OPTS = ['On Track', 'Needs Adjustment', 'At Risk'];

  // ── PAIN ROW ─────────────────────────────────────────────────────────────
  const PainRow = ({ label, value, onChange, accentColor }) => (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', gap: 4 }}>
        {[...Array(11)].map((_, i) => (
          <button key={i} onClick={() => onChange(i)} style={{
            flex: 1, padding: '7px 0', borderRadius: 5, cursor: 'pointer', fontSize: 10, fontWeight: 800,
            border: `2px solid ${value === i ? accentColor : C.border}`,
            background: value === i ? accentColor + '33' : 'transparent',
            color: value === i ? C.textPrimary : C.textMuted,
          }}>{i}</button>
        ))}
      </div>
    </div>
  );

  // ── AI GENERATE DAILY ────────────────────────────────────────────────────
  const generateDaily = () => {
    const d = daily;
    const pt = caseData?.patient || caseData?.firstName + ' ' + caseData?.lastName || '[Patient]';
    const cid = caseData?.id || '[ID]';
    const doi = caseData?.accident || '[DOI]';
    const painDiff = (d.painStart !== null && d.painEnd !== null) ? d.painStart - d.painEnd : 0;
    const painTrend = painDiff > 0 ? 'improved ' + painDiff + ' points' : painDiff < 0 ? 'increased ' + Math.abs(painDiff) + ' points' : 'unchanged';

    const note = `${"═".repeat(50)}
  WELLNESS HEALTHCARE CLINIC CORP
  8180 NW 36th St, Suite 213 · Doral, FL 33166
  Ph: (305) 994-9500 · Fax: (786) 441-2179
${"═".repeat(50)}

PT DAILY SESSION NOTE -- ${d.sessionDate}
Session #${d.sessionNum || '?'} of ${d.totalAuthorized || '?'} Authorized Sessions
Patient: ${pt} | Case: ${cid} | DOI: ${doi}

SUBJECTIVE:
Patient reports pain level ${d.painStart !== null ? d.painStart : '?'}/10 at start of session.${d.barriers.length > 0 && !d.barriers.includes('None') ? ' Reported barriers to recovery: ' + d.barriers.join(', ') + '.' : ''} Patient compliance: ${d.compliance || 'Not assessed'}.

TREATMENT PROVIDED:
Modalities Applied: ${d.modalities.length ? d.modalities.join(', ') : '[None selected]'}
Therapeutic Exercises Performed: ${d.exercises.length ? d.exercises.join(', ') : '[None selected]'}

OBJECTIVE / RESPONSE TO TREATMENT:
Pain at end of session: ${d.painEnd !== null ? d.painEnd : '?'}/10 (${painTrend}).
Response to treatment: ${d.response || 'Not assessed'}.
Functional progress: ${d.functionalProgress || 'Patient demonstrating measurable improvement in functional mobility and pain tolerance with therapeutic intervention'}.

ASSESSMENT:
Patient continues to demonstrate functional deficits consistent with injuries sustained in MVA on ${doi}. Physical therapy is medically necessary at current frequency to restore pre-accident functional baseline and prevent chronicity of musculoskeletal injury. Discharge at this time would result in measurable functional loss.

PLAN:
Continue current treatment protocol per physician order. Progress exercise difficulty as tolerated. Patient education provided regarding home exercise program and posture. Next session per schedule.

CPT CODES BILLED THIS SESSION:
${autoCPT.length ? autoCPT.map(c => `  ${c} — ${({'97010':'Hot/Cold therapy','97012':'Mechanical traction','97014':'E-Stim (unattended)','97032':'E-Stim (attended)','97035':'Ultrasound','97110':'Therapeutic exercise','97116':'Gait training','97140':'Manual therapy','97530':'Therapeutic activities'})[c] || c}`).join('\n') : '  Pending'}

MEDICAL NECESSITY FOR CONTINUANCE:
Continued physical therapy is medically necessary to restore pre-accident functional baseline. The patient demonstrates ongoing functional deficits and pain levels requiring skilled therapeutic intervention. Discontinuation at this time would result in regression of functional gains and increased risk of chronic pain syndrome.

${"─".repeat(50)}
Treating PT/PTA: _________________________ Date: ${d.sessionDate}
Supervising Physician: Dr. Armando Falcon, MD
FL License: ME 84789 | NPI: 1447295126
Facility: Wellness Healthcare Clinic Corp
8180 NW 36th St, Suite 213, Doral, FL 33166
${"─".repeat(50)}
Generated by PI Master™ · RenuviaMD® Network`;

    ud('aiNote', note);
    ud('aiGenerated', true);
  };

  // ── AI GENERATE WEEKLY ───────────────────────────────────────────────────
  const generateWeekly = () => {
    const w = weekly;
    const pt = caseData?.patient || caseData?.firstName + ' ' + caseData?.lastName || '[Patient]';
    const cid = caseData?.id || '[ID]';
    const doi = caseData?.accident || '[DOI]';

    const note = `${"═".repeat(50)}
  WELLNESS HEALTHCARE CLINIC CORP
  8180 NW 36th St, Suite 213 · Doral, FL 33166
  Ph: (305) 994-9500 · Fax: (786) 441-2179
${"═".repeat(50)}

WEEKLY PT PROGRESS SUMMARY -- Week ${w.weekNum || '?'}
Patient: ${pt} | Case: ${cid} | DOI: ${doi}
Summary Date: ${w.summaryDate}

PAIN TREND: ${w.painStart || '?'}/10 --> ${w.painEnd || '?'}/10 across sessions this week.

PROGRESS ASSESSMENT:
ROM Progress: ${w.romProgress || 'Not assessed'}
Strength Progress: ${w.strengthProgress || 'Not assessed'}
ADL Status: Conserved
Functional Progress: ${w.adlProgress || 'Improving'}
Pain Management: ${w.painManagement || 'Not assessed'}
Overall Status: ${w.overallStatus || 'Not assessed'}

WEEKLY ASSESSMENT:
Patient demonstrates ${w.overallStatus === 'On Track' ? 'consistent measurable progress toward functional goals established at evaluation' : w.overallStatus === 'At Risk' ? 'limited progress with identified barriers requiring treatment plan modification' : 'progress requiring reassessment of treatment frequency and goals'}. Physical therapy continues to be medically necessary as patient has not reached pre-accident functional baseline. Continued PT at current frequency is justified by objective functional deficits and measurable week-over-week improvement in ROM and pain scores.

PLAN FOR NEXT WEEK:
Progress therapeutic exercise intensity. ${w.overallStatus === 'Needs Adjustment' || w.overallStatus === 'At Risk' ? 'Modify treatment approach to address plateau/barriers. ' : ''}Reassess short-term goals. Physician re-evaluation recommended to confirm continued medical necessity and PT authorization.

MEDICAL NECESSITY STATEMENT:
Physical therapy is medically necessary for this patient due to: (1) documented ROM deficits below pre-accident baseline; (2) objective muscle spasm and tenderness on examination; (3) measurable functional limitations in ADLs directly attributable to MVA injuries of ${doi}; (4) ${w.romProgress === 'Improving' || w.strengthProgress === 'Improving' ? 'week-over-week objective improvement confirming therapeutic benefit of continued intervention' : 'ongoing deficits requiring continued skilled intervention to prevent functional decline'}.

${"─".repeat(50)}
Supervising Physician: Dr. Armando Falcon, MD
FL License: ME 84789 | NPI: 1447295126
Facility: Wellness Healthcare Clinic Corp
8180 NW 36th St, Suite 213, Doral, FL 33166
Signature: _________________________ Date: ${w.summaryDate}
${"─".repeat(50)}
Generated by PI Master™ · RenuviaMD® Network`;

    uw('aiNote', note);
    uw('aiGenerated', true);
  };

  // ── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: C.bgCard, borderRadius: 16, width: '100%', maxWidth: 860, maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', border: '1px solid #E65100' }}>

        {/* ── HEADER ── */}
        <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}`, background: C.bgElevated, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#E65100', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Physical Therapy Module</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.textPrimary }}>{caseData?.patient || (caseData?.firstName + ' ' + caseData?.lastName)} -- {caseData?.id}</div>
            <div style={{ fontSize: 11, color: C.textMuted }}>DOI: {caseData?.accident} | PIP Only</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {[['daily', 'Daily Session'], ['weekly', 'Weekly Summary']].map(([id, lbl]) => (
              <button key={id} onClick={() => setSessionType(id)} style={{
                padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 800,
                border: `2px solid ${sessionType === id ? '#E65100' : C.border}`,
                background: sessionType === id ? 'rgba(230,81,0,0.15)' : 'transparent',
                color: sessionType === id ? '#FFAB91' : C.textMuted,
              }}>{lbl}</button>
            ))}
            <button onClick={onClose} style={{ padding: '6px 12px', borderRadius: 6, cursor: 'pointer', background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 13 }}>X</button>
          </div>
        </div>

        {/* ── BODY ── */}
        <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>

          {/* ════════════ DAILY SESSION ════════════ */}
          {sessionType === 'daily' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Session Info Row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5 }}>Session Date</div>
                  <input type="date" value={daily.sessionDate} onChange={e => ud('sessionDate', e.target.value)} style={{ width: '100%', padding: '10px 13px', background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.textPrimary, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5 }}>Session #</div>
                  <input type="number" value={daily.sessionNum} onChange={e => ud('sessionNum', e.target.value)} placeholder="e.g., 5" style={{ width: '100%', padding: '10px 13px', background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.textPrimary, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5 }}>Total Authorized Sessions</div>
                  <input type="number" value={daily.totalAuthorized} onChange={e => ud('totalAuthorized', e.target.value)} placeholder="e.g., 24" style={{ width: '100%', padding: '10px 13px', background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.textPrimary, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }} />
                </div>
              </div>

              {/* Pain Scores */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <PainRow label="Pain at Start (0-10)" value={daily.painStart} onChange={v => ud('painStart', v)} accentColor="#EF5350" />
                <PainRow label="Pain at End (0-10)" value={daily.painEnd} onChange={v => ud('painEnd', v)} accentColor={C.green} />
              </div>

              {/* Modalities */}
              <PillSelect label="Modalities Used" options={MODALITIES} value={daily.modalities} onChange={v => ud('modalities', v)} multi />

              {/* Exercises */}
              <PillSelect label="Therapeutic Exercises" options={EXERCISES} value={daily.exercises} onChange={v => ud('exercises', v)} multi />

              {/* Response to Treatment */}
              <PillSelect label="Response to Treatment" options={RESPONSE_OPTS} value={daily.response} onChange={v => ud('response', v)} />

              {/* Functional Progress */}
              <PillSelect label="Functional Progress" options={FUNC_PROGRESS_OPTS} value={daily.functionalProgress} onChange={v => ud('functionalProgress', v)} />

              {/* Patient Compliance */}
              <PillSelect label="Patient Compliance" options={COMPLIANCE_OPTS} value={daily.compliance} onChange={v => ud('compliance', v)} />

              {/* Barriers to Recovery */}
              <PillSelect label="Barriers to Recovery" options={BARRIER_OPTS} value={daily.barriers} onChange={v => ud('barriers', v)} multi />

              {/* Auto-populated CPT Codes */}
              {autoCPT.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                    CPT Codes (Auto-populated from selections)
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {autoCPT.map(code => (
                      <span key={code} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700, fontFamily: 'monospace', background: C.blueGlow, border: `1px solid ${C.blue}44`, color: C.blueBright }}>
                        {code} — {CPT_LABELS[code] || code}
                      </span>
                    ))}
                  </div>
                  <div style={{ fontSize: 9, color: C.textMuted, marginTop: 6 }}>
                    Billing: Wellness Healthcare Clinic Corp · Rendering: Dr. A. Falcon, MD · NPI: 1447295126
                  </div>
                </div>
              )}

              {/* ICD-10 from case (auto-populated) */}
              {caseData?.icd10 && caseData.icd10.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                    ICD-10 Diagnosis (from MD evaluation)
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {caseData.icd10.map(code => (
                      <span key={code} style={{ padding: '5px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700, fontFamily: 'monospace', background: C.greenBg, border: `1px solid ${C.green}33`, color: C.green }}>
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ════════════ WEEKLY SUMMARY ════════════ */}
          {sessionType === 'weekly' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Week Info Row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5 }}>Week Number</div>
                  <input type="number" value={weekly.weekNum} onChange={e => uw('weekNum', e.target.value)} placeholder="e.g., 1" style={{ width: '100%', padding: '10px 13px', background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.textPrimary, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5 }}>Summary Date</div>
                  <input type="date" value={weekly.summaryDate} onChange={e => uw('summaryDate', e.target.value)} style={{ width: '100%', padding: '10px 13px', background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, color: C.textPrimary, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 5 }}>Pain Trend (Start / End)</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="number" min="0" max="10" value={weekly.painStart} onChange={e => uw('painStart', e.target.value)} placeholder="8" style={{ width: 60, padding: '8px', background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textPrimary, fontSize: 13, textAlign: 'center' }} />
                    <span style={{ color: C.textMuted, fontWeight: 700 }}>--></span>
                    <input type="number" min="0" max="10" value={weekly.painEnd} onChange={e => uw('painEnd', e.target.value)} placeholder="5" style={{ width: 60, padding: '8px', background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textPrimary, fontSize: 13, textAlign: 'center' }} />
                  </div>
                </div>
              </div>

              {/* Progress Pills */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <PillSelect label="ROM Progress" options={PROGRESS_3} value={weekly.romProgress} onChange={v => uw('romProgress', v)} />
                <PillSelect label="Strength Progress" options={PROGRESS_3} value={weekly.strengthProgress} onChange={v => uw('strengthProgress', v)} />
                <PillSelect label="Functional Progress" options={PROGRESS_3} value={weekly.adlProgress} onChange={v => uw('adlProgress', v)} />
                <PillSelect label="Pain Management" options={PROGRESS_3} value={weekly.painManagement} onChange={v => uw('painManagement', v)} />
              </div>

              <PillSelect label="Overall Status" options={OVERALL_OPTS} value={weekly.overallStatus} onChange={v => uw('overallStatus', v)} />
            </div>
          )}

          {/* ── AI NOTE SECTION ── */}
          <div style={{ marginTop: 20, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#E65100', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                AI-Generated {sessionType === 'daily' ? 'Daily Session Note' : 'Weekly Progress Summary'}
              </div>
              <button onClick={sessionType === 'daily' ? generateDaily : generateWeekly} disabled={locked} style={{
                padding: '8px 16px', borderRadius: 8, cursor: locked ? 'not-allowed' : 'pointer',
                background: 'linear-gradient(135deg, #E65100, #BF360C)', border: 'none',
                color: '#fff', fontWeight: 800, fontSize: 11, opacity: locked ? 0.4 : 1,
              }}>
                AI Generate {sessionType === 'daily' ? 'Daily Note' : 'Weekly Summary'}
              </button>
            </div>
            {(sessionType === 'daily' ? daily.aiGenerated : weekly.aiGenerated) && (
              <div style={{ marginBottom: 8, padding: '6px 12px', background: 'rgba(46,125,50,0.1)', borderRadius: 6, border: `1px solid ${C.green}`, fontSize: 11, color: C.green }}>
                Generated -- includes medical necessity statement and PIP peer review defense language
              </div>
            )}
            <textarea
              value={sessionType === 'daily' ? daily.aiNote : weekly.aiNote}
              onChange={e => sessionType === 'daily' ? ud('aiNote', e.target.value) : uw('aiNote', e.target.value)}
              readOnly={locked}
              placeholder="Click generate to create the AI-drafted note..."
              style={{
                width: '100%', minHeight: 280, padding: '14px', background: C.bgElevated,
                border: `1px solid ${(sessionType === 'daily' ? daily.aiGenerated : weekly.aiGenerated) ? '#E65100' : C.border}`,
                borderRadius: 10, color: C.textPrimary, fontSize: 12, lineHeight: 1.8,
                resize: 'vertical', fontFamily: "Georgia, 'Times New Roman', 'Palatino Linotype', serif", boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.border}`, background: C.bgElevated, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 11, color: C.textMuted }}>PT Module | {sessionType === 'daily' ? 'Daily Session' : 'Weekly Summary'} | {new Date().toLocaleDateString()}</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, cursor: 'pointer', background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 12, fontWeight: 700 }}>Cancel</button>
            <button onClick={() => { setLocked(true); alert('PT note signed and locked'); }} disabled={locked} style={{
              padding: '9px 20px', borderRadius: 8, cursor: locked ? 'not-allowed' : 'pointer',
              background: locked ? C.bgElevated : '#E65100', border: 'none',
              color: locked ? C.textMuted : '#fff', fontWeight: 800, fontSize: 12,
              opacity: locked ? 0.5 : 1,
            }}>
              {locked ? 'Locked' : 'Sign & Lock Note'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// --- CMS-1500 VIEWER MODAL ----------------------------------------------------
// Renders a real-looking CMS-1500 form populated from case + batch data
// -----------------------------------------------------------------------------
const CMS1500Viewer = ({ caseData, batchData, onClose }) => {
  if (!caseData) return null;
  const c = caseData;
  const b = batchData || {};

  const Field = ({ label, value, w, style }) => (
    <div style={{ flex: w ? undefined : 1, width: w, padding: '2px 4px', borderRight: '1px solid #999', fontSize: 8, lineHeight: 1.3, ...style }}>
      <div style={{ fontSize: 6, color: '#666', textTransform: 'uppercase', marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: 8, color: '#000', fontWeight: 600 }}>{value || ''}</div>
    </div>
  );

  const Row = ({ children, style }) => (
    <div style={{ display: 'flex', borderBottom: '1px solid #999', minHeight: 18, ...style }}>
      {children}
    </div>
  );

  const Check = ({ label, checked }) => (
    <span style={{ fontSize: 7, marginRight: 8 }}>
      <span style={{ display: 'inline-block', width: 8, height: 8, border: '1px solid #000', marginRight: 2, textAlign: 'center', lineHeight: '8px', fontSize: 6 }}>
        {checked ? 'X' : ''}
      </span>
      {label}
    </span>
  );

  const diagCodes = c.icd10 || ['M54.5'];
  const cptCodes = c.cpt || ['99203'];

  // Auto modifier 25: E/M + PT on same day
  const EM_CODES = ['99203','99204','99213','99214'];
  const PT_CODES = ['97010','97014','97032','97035','97110','97140','97530','97112'];
  const hasEM = cptCodes.some(x => EM_CODES.includes(x));
  const hasPT = cptCodes.some(x => PT_CODES.includes(x));
  const sameDayMDPT = hasEM && hasPT;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, overflow: 'auto' }}>
      <div style={{ background: '#fff', width: 680, maxHeight: '95vh', overflow: 'auto', borderRadius: 4, boxShadow: '0 4px 30px rgba(0,0,0,0.5)' }}>
        {/* Close bar */}
        <div style={{ background: '#1565C0', padding: '6px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 1 }}>
          <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>CMS-1500 -- {c.id} -- {c.lastName}, {c.firstName}</span>
          <button onClick={onClose} style={{ background: '#fff', border: 'none', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Close</button>
        </div>

        <div style={{ padding: '8px 12px' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', borderBottom: '2px solid #000', paddingBottom: 4, marginBottom: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.1em' }}>HEALTH INSURANCE CLAIM FORM</div>
            <div style={{ fontSize: 7, color: '#666' }}>APPROVED BY NATIONAL UNIFORM CLAIM COMMITTEE (NUCC) 02/12</div>
          </div>

          {/* Box 1 - Payer type */}
          <Row>
            <div style={{ flex: 1, padding: '2px 4px', fontSize: 7 }}>
              1. <Check label="MEDICARE" /> <Check label="MEDICAID" /> <Check label="TRICARE" /> <Check label="CHAMPVA" /> <Check label="GROUP" /> <Check label="FECA" /> <Check label="OTHER" checked />
            </div>
            <Field label="1a. INSURED'S ID NUMBER" value={c.policy || 'FL-PIP-' + (c.id || '').slice(-6)} w={200} />
          </Row>

          {/* Box 2-3 */}
          <Row>
            <Field label="2. PATIENT'S NAME (Last, First, MI)" value={c.lastName + ', ' + c.firstName} />
            <Field label="3. PATIENT'S BIRTH DATE" value={c.dob} w={120} />
            <div style={{ width: 60, padding: '2px 4px', fontSize: 7 }}>SEX<br /><Check label="M" checked={c.sex === 'M'} /> <Check label="F" checked={c.sex === 'F'} /></div>
          </Row>

          {/* Box 4-5 */}
          <Row>
            <Field label="4. INSURED'S NAME" value="SAME" />
            <Field label="5. PATIENT'S ADDRESS" value={(c.zip || '331XX') + ', FL'} />
          </Row>

          {/* Box 6-7 */}
          <Row>
            <div style={{ flex: 1, padding: '2px 4px', fontSize: 7 }}>
              6. PATIENT RELATIONSHIP TO INSURED<br />
              <Check label="Self" checked /> <Check label="Spouse" /> <Check label="Child" /> <Check label="Other" />
            </div>
            <Field label="7. INSURED'S ADDRESS" value="SAME" />
          </Row>

          {/* Box 10 - Condition related to */}
          <Row>
            <div style={{ flex: 1, padding: '2px 4px', fontSize: 7 }}>
              10a. EMPLOYMENT? <Check label="YES" /> <Check label="NO" checked /><br />
              10b. AUTO ACCIDENT? <Check label="YES" checked /> <Check label="NO" /> PLACE: FL<br />
              10c. OTHER ACCIDENT? <Check label="YES" /> <Check label="NO" checked />
            </div>
            <Field label="11. INSURED'S POLICY / GROUP" value={c.insurance || 'PIP Auto'} />
          </Row>

          {/* Box 14 */}
          <Row>
            <Field label="14. DATE OF CURRENT ILLNESS/INJURY" value={c.accident || ''} />
            <Field label="15. OTHER DATE" value="" />
            <Field label="17. NAME OF REFERRING PHYSICIAN" value="" />
          </Row>

          {/* Box 21 - Diagnosis */}
          <Row style={{ background: '#f0f4f8' }}>
            <div style={{ flex: 1, padding: '4px 6px' }}>
              <div style={{ fontSize: 7, color: '#666', marginBottom: 2 }}>21. DIAGNOSIS OR NATURE OF ILLNESS OR INJURY (ICD-10-CM)</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {diagCodes.map((code, i) => (
                  <div key={i} style={{ fontSize: 9, fontWeight: 700 }}>
                    <span style={{ fontSize: 7, color: '#666' }}>{String.fromCharCode(65 + i)}.</span> {code}
                  </div>
                ))}
              </div>
            </div>
          </Row>

          {/* Box 24 - Service Lines */}
          <div style={{ borderBottom: '1px solid #999' }}>
            <div style={{ display: 'flex', background: '#e8edf2', borderBottom: '1px solid #999', fontSize: 6, fontWeight: 700 }}>
              <div style={{ width: 65, padding: '2px 3px', borderRight: '1px solid #999' }}>DATE(S) OF SVC</div>
              <div style={{ width: 30, padding: '2px 3px', borderRight: '1px solid #999' }}>POS</div>
              <div style={{ width: 50, padding: '2px 3px', borderRight: '1px solid #999' }}>CPT/HCPCS</div>
              <div style={{ width: 30, padding: '2px 3px', borderRight: '1px solid #999' }}>MOD</div>
              <div style={{ width: 50, padding: '2px 3px', borderRight: '1px solid #999' }}>DX PTR</div>
              <div style={{ flex: 1, padding: '2px 3px', borderRight: '1px solid #999' }}>CHARGES</div>
              <div style={{ width: 30, padding: '2px 3px' }}>UNITS</div>
            </div>
            {cptCodes.slice(0, 6).map((code, i) => (
              <div key={i} style={{ display: 'flex', borderBottom: '1px solid #ddd', fontSize: 8 }}>
                <div style={{ width: 65, padding: '2px 3px', borderRight: '1px solid #999' }}>{c.accident || ''}</div>
                <div style={{ width: 30, padding: '2px 3px', borderRight: '1px solid #999' }}>11</div>
                <div style={{ width: 50, padding: '2px 3px', borderRight: '1px solid #999', fontWeight: 700 }}>{code}</div>
                <div style={{ width: 30, padding: '2px 3px', borderRight: '1px solid #999', fontWeight: (sameDayMDPT && EM_CODES.includes(code)) ? 700 : 400, color: (sameDayMDPT && EM_CODES.includes(code)) ? '#1565C0' : '#000' }}>{(sameDayMDPT && EM_CODES.includes(code)) ? '25' : ''}</div>
                <div style={{ width: 50, padding: '2px 3px', borderRight: '1px solid #999' }}>A</div>
                <div style={{ flex: 1, padding: '2px 3px', borderRight: '1px solid #999', textAlign: 'right' }}>
                  ${(80 + i * 25).toFixed(2)}
                </div>
                <div style={{ width: 30, padding: '2px 3px', textAlign: 'center' }}>1</div>
              </div>
            ))}
          </div>

          {/* Box 25-33 */}
          <Row>
            <Field label="25. FEDERAL TAX ID" value="XX-XXXXXXX" />
            <Field label="26. PATIENT ACCOUNT NO." value={c.id || ''} />
            <div style={{ width: 80, padding: '2px 4px', fontSize: 7 }}>
              27. ACCEPT ASSIGNMENT?<br />
              <Check label="YES" checked /> <Check label="NO" />
            </div>
            <Field label="28. TOTAL CHARGE" value={'$' + (c.amtBilled || cptCodes.length * 105).toLocaleString()} />
          </Row>

          <Row>
            <Field label="31. SIGNATURE OF PHYSICIAN (Rendering)" value="Armando Falcon, MD · NPI: 1447295126" />
            <Field label="32. SERVICE FACILITY" value="Wellness Healthcare Clinic Corp · 8180 NW 36th St Ste 213, Doral FL 33166" />
            <Field label="33. BILLING PROVIDER (Clinic)" value="Wellness Healthcare Clinic Corp · Doral FL 33166" />
          </Row>

          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: 6, fontSize: 7, color: '#999' }}>
            NUCC Instruction Manual available at www.nucc.org | Generated by RenuviaMD Gate
          </div>
        </div>
      </div>
    </div>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('PI Master Error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || 'Unknown error';
      return (
        <div style={{ minHeight:'100vh', background:'#0D0F14', display:'flex',
          alignItems:'center', justifyContent:'center', flexDirection:'column',
          gap:20, fontFamily:'system-ui', padding:40 }}>
          <div style={{ fontSize:48 }}>⚡</div>
          <div style={{ fontSize:22, fontWeight:900, color:'#EDF0F7', letterSpacing:'-0.02em' }}>
            PI Master™ — Session Error
          </div>
          <div style={{ maxWidth:520, padding:'20px 24px', background:'#13161E',
            border:'1px solid #EF535044', borderRadius:12, textAlign:'center' }}>
            <div style={{ fontSize:12, color:'#EF5350', fontWeight:800, marginBottom:8,
              textTransform:'uppercase', letterSpacing:'0.1em' }}>Error Caught</div>
            <div style={{ fontSize:11, color:'#8A93B0', lineHeight:1.7, marginBottom:16 }}>
              {msg.slice(0, 200)}
            </div>
            <div style={{ fontSize:11, color:'#525A75', marginBottom:16 }}>
              Your clinical data is safe. This is a display error only.
            </div>
            <button
              onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
              style={{ padding:'10px 24px', background:'#2D7DD2', border:'none',
                borderRadius:8, color:'#fff', fontWeight:800, fontSize:12,
                cursor:'pointer', letterSpacing:'0.06em' }}>
              ↺ RELOAD APP
            </button>
          </div>
          <div style={{ fontSize:10, color:'#525A75', textAlign:'center', maxWidth:400 }}>
            If this persists: hard-reload with Cmd+Shift+R · Clear cache · Call support<br/>
            PI Master™ · RenuviaMD® · drfalcon@renuviamd.com
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}


function App() {
  const [auth, setAuth]         = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [screen, setScreen]     = useState("dashboard");
  const [history, setHistory]   = useState([]);
  const [openCase, setOpenCase]   = useState(null);
  const [toast, setToast]         = useState(null);
  const [visitModal, setVisitModal] = useState(null);
  const [ptModal, setPtModal]     = useState(null);

  const navigate = (dest) => {
    setHistory(h => [...h, screen]);
    setScreen(dest);
  };

  const goBack = () => {
    setHistory(h => {
      const prev = [...h];
      const last = prev.pop();
      setScreen(last || "dashboard");
      return prev;
    });
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data } = await sbGetSession();
        const session = data?.session;
        if (session?.user) {
          const e = session.user.email?.toLowerCase() || '';
          let role = 'physician';
          if (e.includes('staff') || e.includes('front'))     role = 'staff';
          else if (e.includes('pt') || e.includes('therapy')) role = 'pt';
          else if (e.includes('admin')) role = 'admin';
          setAuth(role);
        }
      } catch(e) { /* demo mode */ }
      setAuthLoading(false);
    };
    checkSession();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') { setAuth(null); setScreen('dashboard'); setHistory([]); }
    });
    return () => subscription?.unsubscribe();
  }, []);

  const handleLogout = async () => {
    await sbSignOut();
    setAuth(null);
    setScreen('dashboard');
    setHistory([]);
  };

  const showToast = (msg, color = C.green) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 3000);
  };

  if (authLoading) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 40 }}>⚡</div>
      <div style={{ fontSize: 20, fontWeight: 900, color: C.textPrimary, letterSpacing: '-0.02em' }}>PI Master™</div>
      <div style={{ fontSize: 12, color: C.textMuted, letterSpacing: '0.1em' }}>Checking session...</div>
    </div>
  );

  if (!auth) return <LoginScreen onLogin={(role, email) => { setAuth(role); setScreen("dashboard"); setHistory([]); }} />;

  const canGoBack = history.length > 0;

  const renderScreen = () => {
    const back = canGoBack ? goBack : null;
    switch (screen) {
      case "dashboard":
        if (auth === 'staff') return <StaffDashboard onNav={navigate} onOpenCase={c => setOpenCase(c)} auth={auth} onBack={back} />;
        if (auth === 'pt') return <PTDashboardView onNav={navigate} onOpenCase={c => setOpenCase(c)} onStartPT={c => setPtModal(c)} auth={auth} onBack={back} />;
        return <Dashboard onNav={navigate} onOpenCase={c => setOpenCase(c)} auth={auth} onBack={back} />;
      case "patients":
        if (auth === 'staff') return <StaffDashboard onNav={navigate} onOpenCase={c => setOpenCase(c)} auth={auth} onBack={back} />;
        if (auth === 'pt') return <PTDashboardView onNav={navigate} onOpenCase={c => setOpenCase(c)} onStartPT={c => setPtModal(c)} auth={auth} onBack={back} />;
        return <Dashboard onNav={navigate} onOpenCase={c => setOpenCase(c)} auth={auth} onBack={back} />;
      case "billing-codes":  return <BillingCodesScreen onBack={back} />;
      case "xray-orders":    return <PlaceholderScreen title="X-Ray Orders" icon="🔬" sprint="Coming Soon" features={["Order imaging for any patient", "Auto-email to MAZEL Medical", "Track order status and results"]} onBack={back} />;
      case "business-assoc": return <PlaceholderScreen title="Business Associates" icon="🏥" sprint="Coming Soon" features={["Manage imaging centers", "Add referral partners", "Track business relationships"]} onBack={back} />;
      default:               return <Dashboard onNav={navigate} onOpenCase={c => setOpenCase(c)} auth={auth} onBack={back} />;
    }
  };

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "'Helvetica Neue', Arial, sans-serif", overflow: "hidden", background: C.bg }}>
      <Sidebar screen={screen} onNav={navigate} onLogout={handleLogout} auth={auth} onOpenPatient={c => setOpenCase(c)} />
      {renderScreen()}
      {openCase && !visitModal && !ptModal && <CaseDetail c={openCase} onClose={() => setOpenCase(null)} onStartVisit={p => { setVisitModal(p); }} onStartPT={p => { setPtModal(p); }} />}
      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, background: C.bgCard, border: `1px solid ${toast.color}44`, borderRadius: 10, padding: "12px 20px", fontSize: 13, fontWeight: 700, color: toast.color, boxShadow: `0 0 20px ${toast.color}22`, zIndex: 200, display: "flex", alignItems: "center", gap: 8 }}>
          {toast.msg}
        </div>
      )}
      {visitModal && <MDVisitModule caseData={visitModal} onClose={() => setVisitModal(null)} auth={auth} />}
      {ptModal && <PTSessionModule caseData={ptModal} onClose={() => setPtModal(null)} />}
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(ErrorBoundary, null, React.createElement(App)));
